import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useRegisterSW } from "virtual:pwa-register/react";
import { isEphemeral } from "./lib/storage.js";
import { DWM_LOGO_URI } from "./assets/logos.js";
import AccountPanel from "./components/AccountPanel.jsx";
import HeaderAccount from "./components/HeaderAccount.jsx";
import {
  GERMINATION_GENERAL_TIPS, SEED_MIX_GUIDANCE, G, CROP_IMAGES, CROP_ICON_PATHS,
  MEDIUM_GUIDANCE, GERMINATION, CROPS, CROP_KEYS, CATEGORY_ORDER, CROP_CATEGORY,
} from "./data/crops.js";
import { DEFAULT_PUMP_SCHEDULES } from "./data/pumpSchedules.js";

// Public OAuth Web client id, injected at build time from wrangler.jsonc vars.
// Public by design — it ships in the bundle and is validated by origin, not secrecy.
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || "";

// Website-friendly system font stack: use each platform's native UI font
// (San Francisco / Segoe UI / Roboto …) so text feels native and nothing is
// downloaded. Replaces the previously self-hosted Inter.
const SYSTEM_FONT = 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji"';





// Reference: at the standard 2:2:1 mix (2g MB + 2g CalNit + 1g Epsom per gal),
// the resulting solution measures roughly EC 2.4. We use this as our calibration
// constant; per-crop fullStrengthEc lets us scale gram amounts appropriately
// for crops that need richer solutions than this baseline.
const REFERENCE_EC = 2.4;

// EC temperature compensation.
// Conductivity rises with temperature (~2%/°C for nutrient solutions). Meters
// report against a reference temperature (usually 25°C). A warm reservoir reads
// HIGHER than its true 25°C-equivalent strength, so a raw reading taken at 30°C
// overstates how much nutrient is actually there — correcting it downward reveals
// you may need to add more. This returns the 25°C-equivalent EC.
//   corrected = raw / (1 + coeff/100 × (tempC − refTemp))
function compensateEc(rawEc, tempC, refTemp = 25, coeffPct = 2) {
  if (rawEc == null || tempC == null || !Number.isFinite(rawEc) || !Number.isFinite(tempC)) return null;
  const factor = 1 + (coeffPct / 100) * (tempC - refTemp);
  if (factor <= 0) return null;
  return rawEc / factor;
}

// Measured/spec power values for Dwayne's setup
const POWER_DEFAULTS = {
  nftWattage: 658.5,      // Measured: 2× "1HP" submersible pumps combined at 106.65V
  towerWattage: 425,      // Spec: 5 × 85W tower pumps combined
  nurseryWattage: 25,     // Small nursery pump (e.g. 25W submersible for 10-gal reservoir)
  electricityRate: 50,    // JMD per kWh (Jamaica residential avg, late 2025/early 2026)
  smartPlugMaxW: 1800,    // 15A × 120V smart plug rating
};

// Default farm system layout — user-editable in Settings.
// These drive the gallon quick-select buttons, pump schedule notes,
// power cost apportionment, and the footer/overview stats.
const SYSTEM_DEFAULTS = {
  hasNft: true,              // whether the user runs an NFT system
  nftChannels: 5,            // number of NFT channels
  nftPlantsPerChannel: 250,  // plant capacity per channel
  nftReservoirGal: 200,      // shared NFT reservoir size (gallons)
  hasTower: true,            // whether the user runs a tower system
  towerCount: 5,             // number of vertical towers
  towerBucketGal: 10,        // per-tower bucket/sump size (gallons)
  towerMixTankGal: 55,       // shared tower nutrient mixing tank (gallons)
  hasNursery: true,          // whether the user has a nursery setup
  nurseryGal: 10,            // nursery reservoir size (gallons)
  nurserySlots: 200,         // cube slot capacity in the nursery
  defaultTab: "calc",        // which tab opens on load
  ppmScale: 700,             // EC→ppm conversion: 500 (US/Truncheon), 640 (EU/Eutech), 700 (Hanna/AU)
  ecTempComp: true,          // apply temperature compensation to EC readings
  ecRefTemp: 25,             // meter reference temperature (°C) — usually 25
  ecTempCoeff: 2,            // % conductivity change per °C (typical 1.9–2.1 for nutrient solutions)
  reservoirChangeDays: 10,    // full nutrient solution refresh interval (days); reminder fires past this
  setupComplete: false,       // first-run wizard: false only for a brand-new install (see the load grandfather)
  theme: "dark",              // "dark" | "light" — drives [data-theme] on the root; dark is the original look
};

// Default nursery duration (days) per crop family — time spent in the nursery
// channel between first true leaf and transplant to main system. Does NOT
// include the humidity tray / germination phase (see HUMIDITY_TRAY_DAYS).
// Calibrated against operational practice: leafy greens reach first true leaf
// ~day 10–14 from sowing and only need ~1 week in nursery before transplant.
const NURSERY_DURATIONS = {
  arugula:        7, basil:         14, bell_pepper:   28, bok_choy:       7,
  cauliflower:   14, chives:        14, cilantro:      10, green_onion:   14, kale:          10,
  lettuce:        7, mint:          14, oregano:       18, parsley:       14, rosemary:      21,
  sage:          18, scotch_bonnet: 28, spinach:       10,
  thyme:         18, tomato:        21,
};

// Default humidity-tray duration before nursery (days)
const HUMIDITY_TRAY_DAYS = 7;

// Crops that default to having a nursery phase (vs going direct to system)
// null entries in NURSERY_DURATIONS skip nursery entirely (e.g. root crops, runner-propagated crops)
const DEFAULT_USES_NURSERY = (cropKey) => NURSERY_DURATIONS[cropKey] != null;

// Nursery EC/pH targets per crop — roughly 60–70% of seedling EC (slight step-down
// from seedling, since plant has true leaves and is conditioning for transplant).
// pH ranges match the crop's seedling stage. Used by the Calculator in Nursery mode.
const NURSERY_TARGETS = {
  arugula:       { ecMin: 0.4, ecMax: 0.7, ecTarget: 0.5, phMin: 6.0, phMax: 6.8, phTarget: 6.4 },
  basil:         { ecMin: 0.4, ecMax: 0.7, ecTarget: 0.5, phMin: 5.5, phMax: 6.3, phTarget: 5.9 },
  bell_pepper:   { ecMin: 0.5, ecMax: 0.9, ecTarget: 0.7, phMin: 5.5, phMax: 6.3, phTarget: 5.9 },
  bok_choy:      { ecMin: 0.4, ecMax: 0.7, ecTarget: 0.5, phMin: 6.0, phMax: 6.5, phTarget: 6.2 },
  cauliflower:   { ecMin: 0.4, ecMax: 0.8, ecTarget: 0.6, phMin: 6.0, phMax: 6.5, phTarget: 6.2 },
  chives:        { ecMin: 0.4, ecMax: 0.7, ecTarget: 0.5, phMin: 6.0, phMax: 6.8, phTarget: 6.4 },
  cilantro:      { ecMin: 0.4, ecMax: 0.7, ecTarget: 0.5, phMin: 6.0, phMax: 6.8, phTarget: 6.4 },
  green_onion:   { ecMin: 0.4, ecMax: 0.7, ecTarget: 0.5, phMin: 6.0, phMax: 6.5, phTarget: 6.2 },
  kale:          { ecMin: 0.4, ecMax: 0.8, ecTarget: 0.6, phMin: 5.5, phMax: 6.3, phTarget: 5.9 },
  lettuce:       { ecMin: 0.4, ecMax: 0.7, ecTarget: 0.5, phMin: 5.5, phMax: 6.0, phTarget: 5.8 },
  mint:          { ecMin: 0.4, ecMax: 0.7, ecTarget: 0.5, phMin: 5.5, phMax: 6.3, phTarget: 5.9 },
  oregano:       { ecMin: 0.3, ecMax: 0.6, ecTarget: 0.4, phMin: 6.0, phMax: 7.0, phTarget: 6.5 },
  parsley:       { ecMin: 0.4, ecMax: 0.7, ecTarget: 0.5, phMin: 5.5, phMax: 6.3, phTarget: 5.9 },
  rosemary:      { ecMin: 0.3, ecMax: 0.6, ecTarget: 0.4, phMin: 5.5, phMax: 7.0, phTarget: 6.0 },
  sage:          { ecMin: 0.3, ecMax: 0.6, ecTarget: 0.4, phMin: 5.5, phMax: 6.5, phTarget: 5.9 },
  scotch_bonnet: { ecMin: 0.5, ecMax: 0.9, ecTarget: 0.7, phMin: 5.5, phMax: 6.0, phTarget: 5.8 },
  spinach:       { ecMin: 0.4, ecMax: 0.8, ecTarget: 0.6, phMin: 6.0, phMax: 6.8, phTarget: 6.4 },
  thyme:         { ecMin: 0.3, ecMax: 0.6, ecTarget: 0.4, phMin: 5.5, phMax: 7.0, phTarget: 6.3 },
  tomato:        { ecMin: 0.5, ecMax: 0.9, ecTarget: 0.7, phMin: 5.5, phMax: 6.3, phTarget: 5.9 },
};

// Climate type definitions — drive crop notes, pump schedules, and pest expectations
const CLIMATE_TYPES = {
  tropical: {
    label: "Tropical",
    description: "Hot year-round (24–34°C), high humidity, two rainy seasons. Examples: Jamaica, southern Florida, equatorial regions.",
    seasonLabels: ["Cool (Dec–Feb)", "Shoulder (Mar–May, Oct–Nov)", "Hot/Wet (Jun–Sep)"],
    monthsToSeason: [0,0,1,1,1,2,2,2,2,1,1,0], // Jan=0..Dec=11 → season index
  },
  subtropical: {
    label: "Subtropical",
    description: "Hot summers, mild winters. Examples: north Florida, south Texas, Mediterranean coast.",
    seasonLabels: ["Cool (Nov–Mar)", "Shoulder (Apr–May, Sep–Oct)", "Hot (Jun–Aug)"],
    monthsToSeason: [0,0,0,1,1,2,2,2,1,1,0,0],
  },
  mediterranean: {
    label: "Mediterranean",
    description: "Hot dry summers, cool wet winters. Examples: California coast, Mediterranean basin, South Africa.",
    seasonLabels: ["Cool/Wet (Nov–Mar)", "Mild (Apr–May, Oct)", "Hot/Dry (Jun–Sep)"],
    monthsToSeason: [0,0,0,1,1,2,2,2,2,1,0,0],
  },
  temperate: {
    label: "Temperate",
    description: "Four distinct seasons, growing season ~6 months. Examples: northeast US, UK, central Europe.",
    seasonLabels: ["Cold (Nov–Mar)", "Spring/Fall (Apr–May, Sep–Oct)", "Summer (Jun–Aug)"],
    monthsToSeason: [0,0,0,1,1,2,2,2,1,1,0,0],
  },
  coolTemperate: {
    label: "Cool Temperate",
    description: "Short growing season, cool summers. Examples: northern UK, Scandinavia, Canada south of boreal.",
    seasonLabels: ["Cold (Oct–Apr)", "Spring/Fall (May, Sep)", "Summer (Jun–Aug)"],
    monthsToSeason: [0,0,0,0,1,2,2,2,1,0,0,0],
  },
  cold: {
    label: "Cold / Continental",
    description: "Long cold winters, short hot summers. Examples: Canadian prairies, northern Europe inland.",
    seasonLabels: ["Cold (Sep–May)", "Spring/Fall (Apr/May, Sep)", "Summer (Jun–Aug)"],
    monthsToSeason: [0,0,0,0,1,2,2,2,1,0,0,0],
  },
};

// Currency options — symbol used in displays, code for ISO clarity, default rate stored separately
const CURRENCIES = {
  JMD: { symbol: "JMD", code: "JMD", locale: "en-JM",  name: "Jamaican Dollar" },
  USD: { symbol: "$",   code: "USD", locale: "en-US",  name: "US Dollar" },
  CAD: { symbol: "CA$", code: "CAD", locale: "en-CA",  name: "Canadian Dollar" },
  GBP: { symbol: "£",   code: "GBP", locale: "en-GB",  name: "British Pound" },
  EUR: { symbol: "€",   code: "EUR", locale: "en-IE",  name: "Euro" },
  TTD: { symbol: "TT$", code: "TTD", locale: "en-TT",  name: "Trinidad & Tobago Dollar" },
  BBD: { symbol: "Bds$",code: "BBD", locale: "en-BB",  name: "Barbados Dollar" },
  AUD: { symbol: "AU$", code: "AUD", locale: "en-AU",  name: "Australian Dollar" },
  ZAR: { symbol: "R",   code: "ZAR", locale: "en-ZA",  name: "South African Rand" },
  GHS: { symbol: "GH₵", code: "GHS", locale: "en-GH",  name: "Ghanaian Cedi" },
  NGN: { symbol: "₦",   code: "NGN", locale: "en-NG",  name: "Nigerian Naira" },
  INR: { symbol: "₹",   code: "INR", locale: "en-IN",  name: "Indian Rupee" },
};

// Location & climate defaults — preserves Portmore/Jamaica experience as the starting point
/**
 * ISO 3166-1 alpha-2 country -> currency, limited to the currencies above.
 * A country that is not listed leaves the user's currency alone rather than
 * guessing: showing the wrong currency on cost figures is worse than showing a
 * currency they already chose.
 */
const COUNTRY_CURRENCY = {
  JM: "JMD",
  US: "USD",
  CA: "CAD",
  GB: "GBP",
  TT: "TTD",
  BB: "BBD",
  AU: "AUD",
  ZA: "ZAR",
  GH: "GHS",
  NG: "NGN",
  IN: "INR",
  // Eurozone
  IE: "EUR", DE: "EUR", FR: "EUR", ES: "EUR", IT: "EUR", NL: "EUR", BE: "EUR",
  PT: "EUR", AT: "EUR", FI: "EUR", GR: "EUR", SK: "EUR", SI: "EUR", LT: "EUR",
  LV: "EUR", EE: "EUR", CY: "EUR", MT: "EUR", LU: "EUR", HR: "EUR",
};

const LOCATION_DEFAULTS = {
  // Deliberately empty. A hardcoded place (this used to say "Portmore, Jamaica")
  // silently gives every grower somebody else's forecast, and the EC targets,
  // VPD and pump cadences all key off local conditions. An unset location is
  // visible and fixable; a wrong one is neither.
  name: "",                      // free-text location label
  climateType: "tropical",        // key into CLIMATE_TYPES
  currency: "JMD",                // key into CURRENCIES
  showWeather: true,              // opt-in: fetch local weather from Open-Meteo (only feature that leaves the device)
  lat: null,                      // cached geocode result for the location name (avoids re-geocoding each load)
  lon: null,
  resolvedName: null,             // the name the cached lat/lon were resolved from
};

// Format a monetary amount using the current location's currency
// Pass `compact: true` for short forms like "$1.2k"
function formatCurrency(amount, currency = "JMD", compact = false) {
  const cur = CURRENCIES[currency] || CURRENCIES.JMD;
  if (!Number.isFinite(amount)) return `${cur.symbol} 0`;
  if (compact && Math.abs(amount) >= 1000) {
    return `${cur.symbol} ${(amount / 1000).toFixed(1)}k`;
  }
  return `${cur.symbol} ${Math.round(amount).toLocaleString(cur.locale)}`;
}

// Map a WMO weather-interpretation code (from Open-Meteo) to a short label and
// emoji for the conditions bar. Codes we don't recognise fall back to a cloud.
// Reference: https://open-meteo.com/en/docs (WMO code table)
function weatherCodeInfo(code) {
  const map = {
    0:  { label: "Clear",            emoji: "☀️" },
    1:  { label: "Mainly clear",     emoji: "🌤️" },
    2:  { label: "Partly cloudy",    emoji: "⛅" },
    3:  { label: "Overcast",         emoji: "☁️" },
    45: { label: "Fog",              emoji: "🌫️" },
    48: { label: "Rime fog",         emoji: "🌫️" },
    51: { label: "Light drizzle",    emoji: "🌦️" },
    53: { label: "Drizzle",          emoji: "🌦️" },
    55: { label: "Heavy drizzle",    emoji: "🌦️" },
    56: { label: "Freezing drizzle", emoji: "🌧️" },
    57: { label: "Freezing drizzle", emoji: "🌧️" },
    61: { label: "Light rain",       emoji: "🌦️" },
    63: { label: "Rain",             emoji: "🌧️" },
    65: { label: "Heavy rain",       emoji: "🌧️" },
    66: { label: "Freezing rain",    emoji: "🌧️" },
    67: { label: "Freezing rain",    emoji: "🌧️" },
    71: { label: "Light snow",       emoji: "🌨️" },
    73: { label: "Snow",             emoji: "🌨️" },
    75: { label: "Heavy snow",       emoji: "❄️" },
    77: { label: "Snow grains",      emoji: "🌨️" },
    80: { label: "Rain showers",     emoji: "🌦️" },
    81: { label: "Rain showers",     emoji: "🌧️" },
    82: { label: "Violent showers",  emoji: "⛈️" },
    85: { label: "Snow showers",     emoji: "🌨️" },
    86: { label: "Snow showers",     emoji: "❄️" },
    95: { label: "Thunderstorm",     emoji: "⛈️" },
    96: { label: "Thunderstorm",     emoji: "⛈️" },
    99: { label: "Thunderstorm",     emoji: "⛈️" },
  };
  return map[code] || { label: "—", emoji: "☁️" };
}

// Air vapour pressure deficit (kPa) from air temp (°C) + relative humidity (%).
// Saturation vapour pressure via Tetens; VPD = SVP × (1 − RH/100). A strong
// grower metric for transpiration / tip-burn (high) and mildew (low) risk.
function computeVpd(tempC, rh) {
  if (tempC == null || rh == null || !Number.isFinite(tempC) || !Number.isFinite(rh)) return null;
  const svp = 0.6108 * Math.exp((17.27 * tempC) / (tempC + 237.3));
  return svp * (1 - rh / 100);
}

// Stage-appropriate VPD target band (kPa).
function vpdBand(stageLabel) {
  const s = (stageLabel || "").toLowerCase();
  if (/seedling|transplant|prop|germ|nursery|cutting|clone/.test(s)) return { lo: 0.4, hi: 0.8 };
  if (/flower|fruit|mature|head|bulb|late/.test(s)) return { lo: 1.2, hi: 1.6 };
  return { lo: 0.8, hi: 1.2 }; // vegetative / default
}

// Status + colour for a VPD reading against its stage band.
function vpdStatus(vpd, band) {
  if (vpd == null) return { tone: "var(--text-muted)", label: "—" };
  if (vpd < band.lo) return { tone: "#64b5f6", label: "humid" }; // too humid — mildew risk
  if (vpd > band.hi) return { tone: "#f0a820", label: "dry" };   // too dry — stress / tip-burn
  return { tone: "#78c854", label: "ideal" };
}

// Format a date using current location's locale
function formatDate(isoDate, opts = { month: "short", day: "numeric" }, locale = "en-JM") {
  try {
    return new Date(isoDate).toLocaleDateString(locale, opts);
  } catch {
    return isoDate;
  }
}

// Per-gallon amounts per the MasterBlend label:
// 5 gallons → 12g MB + 12g CalNit + 6g Epsom
// → 2.4g MB + 2.4g CalNit + 1.2g Epsom per gallon at full strength
const RATIOS = {
  masterblend: { gPerGal: 2.4, label: "MasterBlend 4-18-38", short: "MB", color: "#e65100" },
  calciumNitrate: { gPerGal: 2.4, label: "Calcium Nitrate 15-0-0", short: "CalNit", color: "#1565c0" },
  epsom: { gPerGal: 1.2, label: "Magnesium Sulphate", short: "Epsom", color: "#6a1b9a" },
};

// Stage-specific growing tips — keyed by crop key, then stage label
// Each stage has 4 fields: feed (nutrition guidance), env (environment / light / temp),
// watch (common problems and warning signs), action (the one thing to do this stage)
const STAGE_TIPS = {
  arugula: {
    "Seedling": { feed: "Keep EC low (0.5–0.8). Roots are fragile — over-feeding stunts them.", env: "Cool 16–22°C. Bright indirect light. Arugula bolts fast in heat.", watch: "Leggy stretching means light is too low. Move closer to source.", action: "Thin to 1 strongest seedling per cube if you started 2+." },
    "Vegetative": { feed: "Bump EC to 0.8–1.4. Plants are eating actively now.", env: "Keep below 24°C if possible. Heat triggers premature bolting.", watch: "Yellowing lower leaves = nitrogen low. Refresh reservoir.", action: "Start harvesting outer leaves once 6+ true leaves appear (cut-and-come-again)." },
    "Mature": { feed: "EC 1.2–1.8. Plants nearing peak demand.", env: "Watch for flower stalk forming — temperatures above 25°C accelerate bolting.", watch: "Bitter flavor signals heat stress or over-mature leaves.", action: "Harvest full plant within 28–35 days. Don't wait for bolt." },
  },
  basil: {
    "Seedling": { feed: "EC 0.4–0.8. Basil is sensitive to over-feeding when young.", env: "Loves heat: 22–28°C. Light from sowing helps germination.", watch: "Damping off if too wet. Keep medium damp, not soggy.", action: "Wait until 2–3 true leaves form before transplanting." },
    "Vegetative": { feed: "EC 0.8–1.6. Steady feeding for leaf production.", env: "24–28°C ideal. Bright light produces dense, aromatic leaves.", watch: "Flower buds appearing = pinch them off immediately to extend leaf production.", action: "Pinch the top set of leaves above a node to encourage bushy growth." },
    "Mature": { feed: "EC 1.4–2.0. Strongest feeding stage.", env: "Hot conditions intensify aroma. Avoid cold drafts.", watch: "Black spots on leaves = water on foliage + poor airflow. Avoid wetting leaves.", action: "Harvest by pinching stems above leaf nodes. Never strip the whole plant." },
  },
  bell_pepper: {
    "Seedling": { feed: "EC 0.6–1.0. Slow start — peppers are patient.", env: "Warm: 22–28°C. Cooler temps stall growth completely.", watch: "Purple-tinged leaves = phosphorus locked out (often pH or cold).", action: "Be patient. Peppers grow slowly for 3–4 weeks before taking off." },
    "Vegetative": { feed: "EC 1.2–1.8. Steady N-heavy feeding for foliage.", env: "Daytime 24–28°C. Strong light essential for stocky plants.", watch: "Leggy stretching = needs more light. Aphids love new growth.", action: "Stake or net plants now — peppers get top-heavy when fruiting." },
    "Flowering": { feed: "EC 1.8–2.2. Phosphorus and potassium drive flower set.", env: "Avoid temperature swings. Above 30°C drops flowers without setting fruit.", watch: "Flower drop without fruit = heat stress, low humidity, or no pollination.", action: "Gently shake plants daily to encourage self-pollination." },
    "Fruiting": { feed: "EC 2.0–2.5. Heaviest feed — calcium critical for fruit walls.", env: "Steady warmth. Fruits ripen 60–90 days from flower in good conditions.", watch: "Blossom end rot (black bottom) = calcium uptake issue. Check pH and EC.", action: "Harvest at color change. Peppers store nutrients for the next flush." },
  },
  bok_choy: {
    "Seedling": { feed: "EC 0.5–0.8. Light feed for cool-loving brassica.", env: "Cool: 15–22°C ideal. Bok choy hates heat — bolts immediately above 26°C.", watch: "Damping off in stale, wet medium. Increase airflow.", action: "In hot climates, choose your planting time carefully — the hot season is risky." },
    "Vegetative": { feed: "EC 0.8–1.5. Steady feeding for compact rosettes.", env: "Keep below 24°C. Bright light prevents leggy stretching.", watch: "Bolting (flower stalk) = heat stress. Harvest immediately if you see it.", action: "Light frequent feeding produces tender stems. Don't overdo nitrogen." },
    "Mature": { feed: "EC 1.2–1.8. Final push for full heads.", env: "Hot weather will trigger bolt within days. Watch temperatures.", watch: "Tip burn on inner leaves = calcium issue or rapid growth swings.", action: "Harvest whole plants at 30–40 days. Don't let them flower." },
  },
  cauliflower: {
    "Seedling": { feed: "EC 0.5–0.8. Light feed. Brassicas dislike rich starts.", env: "Cool: 15–20°C. Cauliflower is one of the most heat-sensitive crops.", watch: "Stunted purple seedlings = too cold OR phosphorus locked out.", action: "In tropical climates, plan plantings for the coolest months. In temperate climates, fall or spring." },
    "Vegetative": { feed: "EC 1.0–1.6. Steady nitrogen for leaf canopy.", env: "Keep below 22°C. Heat ruins head formation completely.", watch: "Yellowing margins = boron or magnesium deficiency.", action: "Build a strong leaf canopy now — heads form better under shaded conditions." },
    "Head Formation": { feed: "EC 1.4–1.8. Higher K for tight, white heads.", env: "Below 22°C critical. Heat creates ricey, loose, off-color heads.", watch: "Yellow-tinged heads = sun exposure. Pinch outer leaves over the head to blanch.", action: "Harvest when heads reach full size and curds are still tight — don't wait." },
  },
  chives: {
    "Seedling": { feed: "EC 0.5–1.0. Onion family is slow — be patient.", env: "Cool to moderate: 18–22°C. Bright light essential.", watch: "Spindly seedlings = bunch multiple plants per cube (they grow as clumps).", action: "Don't thin chive seedlings — 6–10 per cube is normal and desired." },
    "Vegetative": { feed: "EC 1.2–1.8. Onion family likes steady moderate feeding.", env: "Tolerates 16–26°C. Full sun in cool weather, partial shade in heat.", watch: "Tip yellowing = old growth or sulfur/nitrogen imbalance.", action: "First harvest at 60+ days. Cut whole stems 2cm above the base." },
    "Mature": { feed: "EC 1.4–2.0. Mature clumps tolerate stronger feed.", env: "Will continue producing for months in good conditions.", watch: "Flower stalks reduce leaf production — clip them off unless you want seed.", action: "Harvest by cutting outer stems. Plant continues regrowing from base." },
  },
  cilantro: {
    "Seedling": { feed: "EC 0.5–1.0. Cilantro hates root disturbance.", env: "Cool: 15–22°C. Bolts fast in heat — choose your coolest season.", watch: "Yellowing seedlings = over-watering or too warm.", action: "Don't transplant — sow directly into final position." },
    "Vegetative": { feed: "EC 1.0–1.6. Light steady feeding.", env: "Keep below 24°C. Above 27°C cilantro bolts within days.", watch: "Tall stalks with smaller, feathery leaves = bolting starting.", action: "Stagger plantings every 2 weeks for continuous harvest." },
    "Mature": { feed: "EC 1.2–1.8. Brief peak window.", env: "In hot climates, bolting is inevitable — harvest aggressively.", watch: "Once bolted, leaves get bitter. Bolted plants are coriander seed crops.", action: "Harvest outer stems. Whole-plant harvest at 35–45 days before bolt." },
  },
  kale: {
    "Seedling": { feed: "EC 0.5–0.9. Steady cool-weather feeder.", env: "Cool 16–22°C ideal but kale tolerates tropical heat better than other brassicas.", watch: "Aphids and cabbage worms love young kale. Inspect undersides.", action: "Transplant after 3–4 true leaves." },
    "Vegetative": { feed: "EC 1.0–1.6. Nitrogen-hungry for leaf mass.", env: "Tolerates 18–28°C. Above 30°C leaves get tough.", watch: "Holes in leaves = caterpillars. Check undersides daily.", action: "Pick outer leaves once they're palm-sized — never strip the plant." },
    "Mature": { feed: "EC 1.2–1.8. Mature kale handles strong feeding.", env: "Cooler weather sweetens flavor. Plants produce 3+ months.", watch: "Yellowing lowest leaves is normal — pluck and continue harvesting upward.", action: "Harvest weekly. Leave at least 6 leaves at the center for continued growth." },
  },
  lettuce: {
    "Seedling": { feed: "EC 0.4–0.8. Very light. Lettuce roots burn easily.", env: "Cool 15–22°C ideal. Light from sowing helps.", watch: "Damping off if too wet. Leggy stretching if light too low.", action: "Transplant at 2–3 true leaves with visible white roots (~10–14 days). Don't wait for 4+ leaves — roots bind in the cube." },
    "Vegetative": { feed: "EC 0.8–1.2. Lettuce stays light all the way through.", env: "Keep below 24°C. Lettuce bolts in heat — a year-round challenge in tropical climates.", watch: "Tip burn = calcium uptake issue (often EC too high or rapid swings).", action: "Stagger plantings every 2 weeks. Consider shade cloth in hot months." },
    "Mature": { feed: "EC 1.0–1.4. Final stretch — don't overfeed.", env: "Bolting starts above 25°C. Harvest immediately if you see stalk.", watch: "Milky sap when cut = mature. Bitterness = past peak or heat-stressed.", action: "Whole-plant harvest at 30–45 days. Don't let it bolt." },
  },
  mint: {
    "Seedling": { feed: "EC 0.5–1.0. Mint is hardy and fast.", env: "Tolerates 16–28°C. Loves humidity.", watch: "Tiny seeds — easily over-watered. Keep medium damp not soaked.", action: "Allow seedlings to establish strong roots before first harvest." },
    "Vegetative": { feed: "EC 1.0–1.8. Mint is vigorous and tolerates strong feed.", env: "Bright light keeps leaves dense. Tolerates partial shade.", watch: "Powdery mildew in humid conditions — increase airflow.", action: "Pinch growing tips weekly to keep plants bushy and productive." },
    "Mature": { feed: "EC 1.2–2.0. Established mint is nearly indestructible.", env: "Productive for many months. Spreads aggressively via runners.", watch: "Flower spikes reduce leaf flavor — clip them off for stronger leaves.", action: "Cut stems 5cm above base; plant regrows in 7–10 days." },
  },
  oregano: {
    "Seedling": { feed: "EC 0.5–1.0. Mediterranean herb — wants lean conditions.", env: "Warm and bright: 20–28°C. Tolerates heat better than basil.", watch: "Slow start is normal. Don't over-water — it kills oregano fast.", action: "Patience. Oregano takes 4–6 weeks to look like anything." },
    "Vegetative": { feed: "EC 1.0–1.6. Moderate steady feeding.", env: "Loves tropical heat and sun. Strong flavor develops in bright light.", watch: "Pale leaves = over-watering OR over-feeding (Mediterranean herbs prefer lean).", action: "Pinch tops to encourage bushy growth." },
    "Mature": { feed: "EC 1.2–1.8. Established plants are tough.", env: "Most flavorful in hot, dry conditions. Productive 6+ months.", watch: "Woody stems = old growth. Harvest pushes new tender growth.", action: "Cut stems above leaf nodes — never below half the plant's height." },
  },
  parsley: {
    "Seedling": { feed: "EC 0.5–1.0. Notoriously slow germinator.", env: "Cool to moderate 18–24°C. Tolerates more heat than cilantro.", watch: "First leaves take 2–3 weeks. Don't give up too early.", action: "Soak seeds 24h before sowing to speed germination." },
    "Vegetative": { feed: "EC 1.2–1.8. Hardy and forgiving.", env: "Tolerates 18–28°C. Less heat-sensitive than cilantro.", watch: "Yellowing lower leaves = age (normal) or nitrogen low (refresh).", action: "Harvest outer stems from the base — never cut the central growing point." },
    "Mature": { feed: "EC 1.4–2.0. Established parsley is robust.", env: "Productive for 4–6 months before declining.", watch: "Bolting in 2nd year (or summer heat) signals end of useful life.", action: "Pick stems from outside in. Cut at the base, not partway up." },
  },
  rosemary: {
    "Seedling": { feed: "EC 0.5–1.0. Mediterranean — wants minimal feeding.", env: "Warm and bright. Critical: NEVER use a humidity dome (rot risk).", watch: "Wet feet kill rosemary. Better dry than wet.", action: "Be very patient — germination is slow and erratic. Many growers start from cuttings." },
    "Vegetative": { feed: "EC 1.0–1.6. Light steady feed.", env: "Loves tropical heat. Bright light is essential.", watch: "Browning lower stems = root rot. Reduce watering immediately.", action: "Pinch growing tips at 6–8 nodes for bushy plants." },
    "Mature": { feed: "EC 1.2–1.8. Established rosemary thrives on neglect.", env: "Long-lived. Productive for years if not over-watered.", watch: "Tough woody stems are normal. New growth is the tender harvest.", action: "Harvest tender green stems above leaf nodes — never cut into hardened wood." },
  },
  sage: {
    "Seedling": { feed: "EC 0.5–1.0. Like rosemary, wants lean conditions.", env: "Warm 20–26°C. Bright light. Slow grower.", watch: "Damping off if too wet. Don't crowd seedlings.", action: "Allow 8–10 weeks before first significant harvest." },
    "Vegetative": { feed: "EC 1.0–1.6. Light feeding produces best flavor.", env: "Loves heat and sun. Tolerates tropical conditions well.", watch: "Powdery mildew in humid conditions — increase spacing and airflow.", action: "Pinch tips to encourage branching." },
    "Mature": { feed: "EC 1.2–1.8. Robust mature plants.", env: "Productive for 2–3 years before declining.", watch: "Flowering reduces leaf flavor — clip stalks unless you want seeds.", action: "Harvest 2–3 leaves at a time from each branch. Leave most of the plant intact." },
  },
  green_onion: {
    "Seedling": { feed: "EC 0.6–1.0. Onion family germinates slowly.", env: "Cool to moderate: 18–24°C. Tolerates tropical heat reasonably well.", watch: "Spindly seedlings = normal. Multiple plants per cube is desired.", action: "Don't thin — scallions grow as clumps." },
    "Vegetative": { feed: "EC 1.0–1.6. Onions need steady moderate feed.", env: "Bright light. 18–28°C tolerable.", watch: "Yellow tips = water stress or sulfur/nitrogen imbalance.", action: "First harvest at 60+ days. Cut whole stems 2cm above the base — they regrow." },
    "Mature": { feed: "EC 1.4–2.0. Mature clumps tolerate stronger feeding.", env: "Can produce for many months with consistent harvesting.", watch: "Bulbing at base = onion variety mistakenly planted as scallion.", action: "Cut whole stems flush with the base; plant regrows in 14–21 days." },
  },
  scotch_bonnet: {
    "Seedling": { feed: "EC 0.6–1.0. Slow patient start, like all peppers.", env: "Hot 24–30°C — Scotch Bonnet loves tropical weather.", watch: "Cold below 20°C stalls growth completely. Won't recover quickly.", action: "Be patient. First 4–6 weeks look like nothing is happening." },
    "Vegetative": { feed: "EC 1.2–1.8. Steady nitrogen for foliage.", env: "Loves heat. 25–32°C ideal. Strong light essential.", watch: "Leggy plants = need more light. Aphids love new growth.", action: "Stake plants early — heavy fruit loads will pull them over." },
    "Flowering": { feed: "EC 1.8–2.2. Phosphorus drives flower set.", env: "Tolerates tropical heat well. Light wind helps pollination.", watch: "Flower drop = heat above 35°C, very low humidity, or no air movement.", action: "Shake plants gently each morning to spread pollen." },
    "Fruiting": { feed: "EC 2.0–2.5. Heavy feed — calcium for fruit walls.", env: "Hot and bright produces hottest fruits. Heat = capsaicin.", watch: "Blossom end rot = calcium uptake. Check pH (6.0–6.5 ideal).", action: "Harvest as fruits turn yellow-orange-red. Color = full heat development." },
  },
  spinach: {
    "Seedling": { feed: "EC 0.5–1.0. Cool-weather crop, light feeder.", env: "Cool: 10–22°C. Spinach hates heat — a challenge in tropical climates.", watch: "Yellow seedlings = too warm. Spinach bolts above 24°C.", action: "Plant only in coolest months (Dec–Feb). Skip the rest of the year." },
    "Vegetative": { feed: "EC 0.8–1.4. Moderate feeding.", env: "Below 22°C critical. Above 24°C triggers bolt.", watch: "Tall central stalk forming = bolting. Harvest immediately.", action: "Cut-and-come-again harvest of outer leaves stretches the harvest window." },
    "Mature": { feed: "EC 1.2–1.8. Brief window before bolt.", env: "Plants only last 30–40 days in warm conditions.", watch: "Bitter flavor = past peak. Bolted plants are not worth harvesting.", action: "Harvest whole plants at 30–45 days. Don't push your luck with heat." },
  },
  thyme: {
    "Seedling": { feed: "EC 0.4–0.8. Mediterranean — keep lean.", env: "Warm bright dry: 20–26°C. NO humidity dome (rot risk).", watch: "Thyme seedlings are tiny and slow. Don't over-water.", action: "Patience. First 6 weeks look like nothing happening." },
    "Vegetative": { feed: "EC 1.0–1.6. Light feed produces best flavor.", env: "Loves heat. Bright light essential.", watch: "Browning stems at base = root rot. Reduce watering.", action: "Pinch tips to encourage branching." },
    "Mature": { feed: "EC 1.2–1.8. Established thyme is hardy.", env: "Productive 2–3 years before declining and needing replacement.", watch: "Woody base is normal. New green growth is the harvest.", action: "Harvest tender green stems — never cut into hardened wood." },
  },
  tomato: {
    "Seedling": { feed: "EC 0.6–1.0. Light start to build root mass.", env: "Warm 22–28°C. Bright light = stocky plants.", watch: "Leggy stretching = light too far. Lower the source.", action: "Bury seedlings deep at transplant — tomatoes root along buried stem." },
    "Vegetative": { feed: "EC 1.2–1.8. Steady nitrogen for foliage and stem strength.", env: "Loves tropical heat. 22–30°C optimal.", watch: "Lower leaves yellowing = nitrogen low OR water stress. Check both.", action: "Stake or trellis NOW. Tomatoes get massive fast." },
    "Flowering": { feed: "EC 1.8–2.2. Phosphorus + potassium drive fruit set.", env: "Above 32°C drops flowers without setting. Watch for heat spikes.", watch: "Flower drop without fruit = heat or no pollination. Shake plants gently.", action: "Hand-shake flowering trusses daily to release pollen." },
    "Fruiting": { feed: "EC 2.0–2.5. Heaviest feed — calcium critical for fruit walls.", env: "Steady warmth ripens fruit. Cold below 18°C stalls ripening.", watch: "Blossom end rot (black bottom) = calcium uptake issue. Check pH 6.0–6.5.", action: "Harvest at first color change — fruits ripen well off the vine." },
  },
};

const EVENT_TYPES = [
  { key: "reservoir_check", label: "Reservoir Check", icon: "🔬", color: "var(--active)", needsValue: false },
  { key: "ec_reading", label: "EC Reading", icon: "⚡", color: "#ffd54f", needsValue: true },
  { key: "ph_reading", label: "pH Reading", icon: "🧪", color: "#64b5f6", needsValue: true },
  { key: "water_temp", label: "Water Temp", icon: "🌡️", color: "#4fc3f7", needsValue: true },
  { key: "farm_temp", label: "Farm Temp", icon: "🌤️", color: "#ffb74d", needsValue: true },
  { key: "nutrient_add", label: "Nutrients Added", icon: "🌱", color: "#78c854", needsValue: false },
  { key: "ph_down", label: "pH Down (H₂SO₄)", icon: "🔽", color: "#ef5350", needsValue: true },
  { key: "ph_up", label: "pH Up", icon: "🔼", color: "#42a5f5", needsValue: true },
  { key: "water_change", label: "Water Change", icon: "💧", color: "#26c6da", needsValue: true },
  { key: "pump_check", label: "Pump Check", icon: "🔌", color: "#ffa726", needsValue: false },
  { key: "harvest", label: "Harvest", icon: "🧺", color: "#90c870", needsValue: true },
  { key: "note", label: "General Note", icon: "📝", color: "#bdbdbd", needsValue: false },
];

// ══════════════════════════════════════════════
// GROWS — Active plantings per system
// Each grow tracks crop, system, planting date, current stage, and lifecycle
// ══════════════════════════════════════════════
const GROW_SYSTEMS = [
  { key: "nft", label: "NFT", icon: "🟦", color: "#1976d2" },
  { key: "tower", label: "Tower", icon: "🗼", color: "#7b1fa2" },
  { key: "nursery", label: "Nursery", icon: "🌱", color: "#43a047" },
  { key: "other", label: "Other", icon: "🪴", color: "var(--text-soft)" },
];

// Per-crop grow-system suitability.
// rating: "ideal" | "good" | "marginal" | "not_recommended"
// note: short reason shown when marginal/not_recommended is selected
const SYSTEM_COMPATIBILITY = {
  arugula:       { nft: { rating: "good" },                                                                   tower: { rating: "ideal" } },
  basil:         { nft: { rating: "good" },                                                                   tower: { rating: "ideal" } },
  bell_pepper:   { nft: { rating: "ideal" },                                                                  tower: { rating: "not_recommended", note: "Fruit weight + deep taproot — pepper plants tip towers and outgrow the net cup zone" } },
  bok_choy:      { nft: { rating: "ideal" },                                                                  tower: { rating: "good", note: "Full-size heads can crowd the tower; baby bok choy works best" } },
  cauliflower:   { nft: { rating: "ideal" },                                                                  tower: { rating: "not_recommended", note: "Head weight tips towers; needs horizontal space cauliflower can't get vertically" } },
  chives:        { nft: { rating: "good" },                                                                   tower: { rating: "ideal" } },
  cilantro:      { nft: { rating: "good" },                                                                   tower: { rating: "ideal" } },
  green_onion:   { nft: { rating: "good" },                                                                   tower: { rating: "ideal" } },
  kale:          { nft: { rating: "ideal" },                                                                  tower: { rating: "marginal", note: "Baby kale OK; mature kale gets too heavy and shades plants below it" } },
  lettuce:       { nft: { rating: "ideal" },                                                                  tower: { rating: "ideal" } },
  mint:          { nft: { rating: "good" },                                                                   tower: { rating: "ideal" } },
  oregano:       { nft: { rating: "marginal", note: "NFT can stay too wet for Mediterranean herbs" },         tower: { rating: "ideal", note: "Tower drainage is excellent for Mediterranean herbs" } },
  parsley:       { nft: { rating: "good" },                                                                   tower: { rating: "ideal" } },
  rosemary:      { nft: { rating: "not_recommended", note: "NFT is far too wet — causes root rot fast" },     tower: { rating: "ideal", note: "Tower drainage is perfect for this drought-loving herb" } },
  sage:          { nft: { rating: "marginal", note: "Prefers drier roots than NFT typically provides" },      tower: { rating: "ideal" } },
  scotch_bonnet: { nft: { rating: "ideal" },                                                                  tower: { rating: "not_recommended", note: "Plant size + fruit weight — peppers grow too large for towers" } },
  spinach:       { nft: { rating: "good" },                                                                   tower: { rating: "good" } },
  thyme:         { nft: { rating: "marginal", note: "NFT holds too much moisture for thyme" },                tower: { rating: "ideal" } },
  tomato:        { nft: { rating: "ideal" },                                                                  tower: { rating: "not_recommended", note: "Fruit weight + deep root system — tomatoes outgrow towers within weeks" } },
};

// Get rating metadata for badge styling
const SYSTEM_RATING_META = {
  ideal:           { label: "Ideal",           icon: "⭐", color: "#78c854", bg: "rgba(120,200,84,0.12)", border: "rgba(120,200,84,0.4)" },
  good:            { label: "Good",            icon: "✓",  color: "#90c870", bg: "rgba(144,200,112,0.08)", border: "rgba(144,200,112,0.3)" },
  marginal:        { label: "Marginal",        icon: "⚠",  color: "#f0a820", bg: "rgba(240,168,32,0.1)",   border: "rgba(240,168,32,0.35)" },
  not_recommended: { label: "Not recommended", icon: "✕",  color: "#ef5350", bg: "rgba(239,83,80,0.1)",    border: "rgba(239,83,80,0.4)" },
};

const GROW_STATUSES = [
  { key: "active", label: "Active", color: "#5cb85c" },
  { key: "harvested", label: "Harvested", color: "#9e9e9e" },
  { key: "failed", label: "Failed", color: "#ef5350" },
];

// Calculate days elapsed since plantingDate
function daysSince(isoDate) {
  if (!isoDate) return 0;
  const d = new Date(isoDate);
  const now = new Date();
  return Math.floor((now - d) / (1000 * 60 * 60 * 24));
}

// Projected harvest timeline for a grow.
//
// Day-counting always runs from the sow date (plantingDate) — the app's
// "day 1 = seed in tray" convention. The harvest ESTIMATE, however, re-anchors
// once a grow is actually transplanted: `daysToHarvest` is authored as a
// from-sowing figure that already includes the planned nursery time, so a grow
// transplanted late (or early) shifts its harvest date by the same amount
// instead of staying pinned to sow + daysToHarvest. A grow that never had a
// nursery phase, or is still in it, keeps the simple sow + daysToHarvest clock.
//
// Returns { days, totalDays, span, harvest, progress, reanchored } where `span`
// is the projected sow→harvest length in days (== totalDays unless re-anchored).
function growTimeline(grow) {
  const totalDays = Math.max(1, CROPS[grow?.crop]?.daysToHarvest || 30);
  const sow = parseGrowDate(grow?.plantingDate) || new Date();
  const days = daysSince(grow?.plantingDate);
  const harvest = new Date(sow);
  if (grow?.transplantedAt && grow?.phase === "active" && grow?.nurseryDays != null) {
    // Planned sowing→transplant = humidity tray + nursery channel. Whatever of
    // daysToHarvest remains after that is the time the plant needs in-system.
    const plannedToTransplant = HUMIDITY_TRAY_DAYS + grow.nurseryDays;
    const remaining = Math.max(1, totalDays - plannedToTransplant);
    harvest.setTime(new Date(grow.transplantedAt).getTime());
    harvest.setDate(harvest.getDate() + remaining);
  } else {
    harvest.setDate(harvest.getDate() + totalDays);
  }
  const span = Math.max(1, Math.round((harvest - sow) / 86400000));
  const progress = Math.min(100, (days / span) * 100);
  return { days, totalDays, span, harvest, progress, reanchored: span !== totalDays };
}

// Suggest current stage based on days elapsed and crop's stage definitions
function suggestStage(crop, daysElapsed) {
  // Map weeks-based sub labels to approximate day boundaries
  // Crude but works: assume stages roughly evenly distribute over daysToHarvest
  if (!crop || !crop.stages || crop.stages.length === 0) return 0;
  const stageCount = crop.stages.length;
  if (stageCount === 1) return 0;
  // Guard against bad/missing data and future-dated plantings
  const days = Math.max(0, Number.isFinite(daysElapsed) ? daysElapsed : 0);
  const totalDays = Math.max(1, crop.daysToHarvest || 30);
  // Last stage starts at ~60% of total days, earlier stages distribute first 60%
  if (days >= totalDays * 0.6) return stageCount - 1;
  const earlyDays = Math.max(1, totalDays * 0.6); // never 0 — avoids divide-by-zero
  const stagesPerEarly = stageCount - 1;
  const perStage = earlyDays / stagesPerEarly; // stagesPerEarly >= 1 here, safe
  return Math.min(stageCount - 1, Math.floor(days / perStage));
}

function isoToday() {
  const d = new Date();
  return d.toISOString().split("T")[0];
}

// Convert a <input type="date"> value ("YYYY-MM-DD") to a stored timestamp,
// anchored at local noon so it never slips a day when shown in a behind-UTC
// timezone. Empty input → null (no date).
function dateInputToIso(dateStr) {
  if (!dateStr) return null;
  const d = new Date(`${dateStr}T12:00:00`);
  return isNaN(d) ? null : d.toISOString();
}

// Convert a stored timestamp back to a local "YYYY-MM-DD" for a date input.
function isoToDateInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

// Parse a grow date for display/arithmetic. A bare "YYYY-MM-DD" (how sow dates
// are stored) is read at LOCAL noon, not UTC midnight, so it never slips to the
// previous day when formatted in a behind-UTC timezone. Timestamps pass through.
function parseGrowDate(value) {
  if (!value) return null;
  const d = /^\d{4}-\d{2}-\d{2}$/.test(value) ? new Date(`${value}T12:00:00`) : new Date(value);
  return isNaN(d) ? null : d;
}

// Format a grow date (date-only string or timestamp) without timezone drift.
function fmtGrowDate(value, opts = { month: "short", day: "numeric", year: "numeric" }) {
  const d = parseGrowDate(value);
  return d ? d.toLocaleDateString("en-JM", opts) : "";
}

// Clamp a numeric text input to >= 0 (and optional max). Returns a string for controlled inputs.
// Empty string passes through so the field can be cleared.
function clampNonNegative(raw, max = 99999) {
  if (raw === "" || raw == null) return "";
  const n = parseFloat(raw);
  if (!Number.isFinite(n)) return "";
  if (n < 0) return "0";
  if (n > max) return String(max);
  return raw;
}

// ══════════════════════════════════════════════
// PUMP SCHEDULES — Portmore, JM (50% shade cloth)
// Tuned for warm tropical climate with minimal seasonal swing
// ══════════════════════════════════════════════
const SEASONS = {
  cool: { label: "Cool Season", sub: "Dec–Feb", months: [11, 0, 1], color: "#42a5f5", icon: "❄️" },
  shoulder: { label: "Shoulder Season", sub: "Mar–May, Oct–Nov", months: [2, 3, 4, 9, 10], color: "#78c854", icon: "🌤️" },
  hot: { label: "Hot Season", sub: "Jun–Sep", months: [5, 6, 7, 8], color: "#ef5350", icon: "🔥" },
};

// Generate cycles every `intervalMin` minutes from startHour to endHour
// Used for Agrotonomy-style tower schedules: 3 min ON every 15 min
function genCycles(startHour, endHour, onMin, intervalMin) {
  const cycles = [];
  let totalMin = startHour * 60;
  const endTotalMin = endHour * 60;
  while (totalMin < endTotalMin) {
    cycles.push({
      hour: Math.floor(totalMin / 60),
      startMin: totalMin % 60,
      on: onMin,
    });
    totalMin += intervalMin;
  }
  return cycles;
}

// Generate cycles at specific minute offsets within each hour of a range
// Used for tower schedules that interleave with NFT cycles
// ─────────────────────────────────────────────────────────────────────────────
// PUMP SCHEDULE ENGINE
// Single source of truth for pump schedules. The farm's pumps, circuits, and
// per-season watering intent are described as data below; the engine generates
// each system's optimal, temperature-scaled cadence independently (systems may
// run at the same time — surge/overlap avoidance was intentionally removed, see
// CLAUDE.md §1), then maps the result into the shape the app already uses.
// Editing PUMP_CONFIG regenerates every season, timeline, and PDF-facing total.
// ─────────────────────────────────────────────────────────────────────────────

// Merge overlapping [start,end] intervals.
function _mergeIntervals(list) {
  if (!list.length) return [];
  const sorted = [...list].sort((a, b) => a[0] - b[0]);
  const out = [sorted[0].slice()];
  for (let i = 1; i < sorted.length; i++) {
    const last = out[out.length - 1];
    if (sorted[i][0] <= last[1]) last[1] = Math.max(last[1], sorted[i][1]);
    else out.push(sorted[i].slice());
  }
  return out;
}

// Free gaps in [winStart,winEnd] after removing occupied intervals (+ trailing buffer).
function _freeIntervals(occupied, winStart, winEnd, buffer) {
  const padded = occupied.map(([s, e]) => [Math.max(winStart, s), Math.min(winEnd, e + buffer)]);
  const merged = _mergeIntervals(padded);
  const free = [];
  let cur = winStart;
  for (const [s, e] of merged) {
    if (s > cur) free.push([cur, s]);
    cur = Math.max(cur, e);
  }
  if (cur < winEnd) free.push([cur, winEnd]);
  return free;
}

// Evenly distribute `count` cycles of `duration` across free intervals.
function _distributeCycles(free, count, duration) {
  const usable = free.filter(([s, e]) => e - s >= duration);
  const totalLen = usable.reduce((a, [s, e]) => a + (e - s), 0);
  const starts = [];
  let remaining = count;
  usable.forEach(([s, e], idx) => {
    if (remaining <= 0) return;
    const len = e - s;
    const capacity = Math.floor(len / duration);
    let nHere = idx === usable.length - 1 ? remaining : Math.round(count * (len / totalLen));
    nHere = Math.min(nHere, remaining, capacity);
    if (nHere > 0) {
      const slot = len / nHere;
      for (let i = 0; i < nHere; i++) {
        let start = s + i * slot;
        if (start + duration > e) start = e - duration;
        starts.push(Math.round(start));
      }
      remaining -= nHere;
    }
  });
  return starts.sort((a, b) => a - b);
}

// Two pumps conflict if they share a breaker and their combined draw (one running
// while the other hits startup surge) exceeds it. Pumps under 5% of the breaker
// rating are electrically trivial and conflict with nothing.
function _computeConflicts(pumps, circuitsById, surgeFactor) {
  const conflicts = new Set();
  const key = (a, b) => [a, b].sort().join("|");
  for (let i = 0; i < pumps.length; i++) {
    for (let j = i + 1; j < pumps.length; j++) {
      const A = pumps[i], B = pumps[j];
      const cA = circuitsById[A.circuitId], cB = circuitsById[B.circuitId];
      if (!cA || !cB || cA.breakerGroup !== cB.breakerGroup) continue;
      const limit = Math.min(cA.breakerWatts, cB.breakerWatts);
      const bothRunning = A.watts + B.watts;
      const trivial = A.watts < limit * 0.05 || B.watts < limit * 0.05;
      const surge = Math.max(A.watts + B.watts * surgeFactor, B.watts + A.watts * surgeFactor);
      if (bothRunning > limit || (!trivial && surge > limit)) conflicts.add(key(A.id, B.id));
    }
  }
  return conflicts;
}

// Generate cycles for one season. Each system runs its own optimal cadence
// INDEPENDENTLY — systems may run at the same time (surge/overlap avoidance was
// intentionally removed; see CLAUDE.md §1). Temperature is factored via the
// season config and the midday peak window. Mirror of generateSeason in
// src/lib/pumpEngine.js — keep the two in sync.
// Returns { pumpId: [{hour, startMin, on}] } — note `on` to match legacy consumers.
function _generateSeason(pumps, _circuitsById, cfg) {
  const { opStart, opEnd, peakStart, peakEnd } = cfg;
  const out = {};
  for (const pump of pumps) {
    out[pump.id] = [];
    const pat = pump.pattern;
    for (let h = opStart; h < opEnd; h++) {
      const isPeak = h >= peakStart && h < peakEnd;
      if (pat.type === "anchor") {
        out[pump.id].push({ hour: h, startMin: 0, on: isPeak && pat.peakDuration ? pat.peakDuration : pat.duration });
      } else if (pat.type === "cycles") {
        const dur = isPeak && pat.peakDuration ? pat.peakDuration : pat.duration;
        const n = isPeak && pat.peakCyclesPerHour ? pat.peakCyclesPerHour : pat.cyclesPerHour;
        _distributeCycles([[0, 60]], n, dur).forEach(m => out[pump.id].push({ hour: h, startMin: m, on: dur }));
      } else if (pat.type === "periodic") {
        if ((h - opStart) % pat.everyHours !== 0) continue;
        const dur = isPeak && pat.peakDuration ? pat.peakDuration : pat.duration;
        out[pump.id].push({ hour: h, startMin: pat.startMinute ?? 0, on: dur });
      }
    }
    out[pump.id].sort((a, b) => (a.hour * 60 + a.startMin) - (b.hour * 60 + b.startMin));
  }
  return out;
}

// ── Farm configuration (the single source of truth) ──
// NFT + Nursery on Smart Plug A, Tower pumps on Smart Plug B, both assumed on one
// household breaker (conservative). Per-season intent drives the generated cycles.
const PUMP_CONFIG = {
  circuits: {
    plugA: { breakerWatts: 1800, breakerGroup: "main" },
    plugB: { breakerWatts: 1800, breakerGroup: "main" },
  },
  // opEnd/peak windows differ by season (hot runs an hour later, wider peak).
  seasons: {
    cool: { opStart: 6, opEnd: 19, peakStart: 10, peakEnd: 17, surgeFactor: 3, buffer: 2,
            label: "Cool Season (Dec–Feb)" },
    shoulder: { opStart: 6, opEnd: 19, peakStart: 10, peakEnd: 17, surgeFactor: 3, buffer: 2,
                label: "Shoulder Season (Mar–May, Oct–Nov)" },
    hot: { opStart: 6, opEnd: 20, peakStart: 10, peakEnd: 17, surgeFactor: 3, buffer: 2,
           label: "Hot Season (Jun–Sep)" },
  },
  // Per-season pump patterns — optimal, industry-standard cadences that scale
  // with seasonal temperature (hotter → more water). Each pump keeps a stable
  // id/circuit; only the watering intent changes with the season. Systems run
  // independently and may overlap (surge avoidance removed — see CLAUDE.md §1).
  //
  //   NFT     → continuous film flow during the operating window (anchor, on:60).
  //             The thin film must keep moving so roots get nutrient + oxygen.
  //   Tower   → frequent short cycles; roots need air between cycles, so we cycle
  //             rather than run continuously. Cycles/hour rise with temperature
  //             and again at the midday peak.
  //   Nursery → gentle brief pulses; more frequent in the hot season.
  pumpsBySeason: {
    cool: [
      { id: "nft", watts: 658, circuitId: "plugA", pattern: { type: "anchor", duration: 60, peakDuration: 60 } },
      { id: "tower", watts: 425, circuitId: "plugB", pattern: { type: "cycles", cyclesPerHour: 3, duration: 5, peakCyclesPerHour: 4 } },
      { id: "nursery", watts: 25, circuitId: "plugA", pattern: { type: "periodic", everyHours: 2, duration: 5, startMinute: 0 } },
    ],
    shoulder: [
      { id: "nft", watts: 658, circuitId: "plugA", pattern: { type: "anchor", duration: 60, peakDuration: 60 } },
      { id: "tower", watts: 425, circuitId: "plugB", pattern: { type: "cycles", cyclesPerHour: 4, duration: 5, peakCyclesPerHour: 5 } },
      { id: "nursery", watts: 25, circuitId: "plugA", pattern: { type: "periodic", everyHours: 2, duration: 6, startMinute: 0 } },
    ],
    hot: [
      { id: "nft", watts: 658, circuitId: "plugA", pattern: { type: "anchor", duration: 60, peakDuration: 60 } },
      { id: "tower", watts: 425, circuitId: "plugB", pattern: { type: "cycles", cyclesPerHour: 5, duration: 5, peakCyclesPerHour: 6 } },
      { id: "nursery", watts: 25, circuitId: "plugA", pattern: { type: "periodic", everyHours: 1, duration: 6, startMinute: 0 } },
    ],
  },
};

// Metadata that isn't generated (names, icons, notes stay editorial).
const PUMP_META = {
  nft: {
    name: "NFT / PVC System", icon: "🟦", color: "#1976d2",
    notes: "30 min ON / 30 min OFF through the day — the daytime window widens with the season (to 6 PM cool, 7 PM shoulder, 8 PM hot) — plus a few short overnight maintenance pulses so the film never stalls for long.",
  },
  tower: {
    name: "Tower System", icon: "🗼", color: "#7b1fa2",
    notes: "Short 15-min runs spaced through the day, more frequent as it heats up (down to 15 on / 15 off in the hot season), plus a couple of overnight pulses. Tower roots hang in air and need to breathe between runs. For best midday root health, also shade the tower tank.",
  },
  nursery: {
    name: "Nursery System", icon: "🌱", color: "#43a047",
    notes: "Brief 5-min spray/drip pulses every few hours (more often in heat). Medium should be moist, not continuously saturated — flooding tiny roots damages them and long dry spells shrink the cubes.",
  },
};

// Assemble the PUMP_SCHEDULES shape every consumer (timeline, summaries, runtime
// totals) expects, from the explicit seasonal program in src/data/pumpSchedules.js.
// `custom` (optional) overrides any system/season with a user-defined cycle list;
// anything it omits falls back to the grower's default program.
function buildPumpSchedules(custom) {
  const systems = {};
  for (const sysId of Object.keys(PUMP_META)) {
    systems[sysId] = { ...PUMP_META[sysId], schedules: {} };
  }
  for (const [seasonKey, cfg] of Object.entries(PUMP_CONFIG.seasons)) {
    for (const sysId of Object.keys(PUMP_META)) {
      const cycles = custom?.[seasonKey]?.[sysId] ?? DEFAULT_PUMP_SCHEDULES[seasonKey][sysId] ?? [];
      systems[sysId].schedules[seasonKey] = { label: cfg.label, cycles };
    }
  }
  return systems;
}

function genHourlyCycles(startHour, endHour, onMin, minuteOffsets) {
  const cycles = [];
  for (let h = startHour; h < endHour; h++) {
    for (const m of minuteOffsets) {
      cycles.push({ hour: h, startMin: m, on: onMin });
    }
  }
  return cycles;
}

// Live binding: every consumer (module functions and the component) reads this
// same reference, so rebuilding it when a user's custom schedule changes updates
// them all without threading. Rebuilt in a component effect (see below).
let PUMP_SCHEDULES = buildPumpSchedules();

function getCurrentSeason() {
  const month = new Date().getMonth();
  for (const [key, s] of Object.entries(SEASONS)) {
    if (s.months.includes(month)) return key;
  }
  return "shoulder";
}

// Generate today's pump events from the new cycle list
function generatePumpEvents(system, seasonKey) {
  const sched = PUMP_SCHEDULES[system].schedules[seasonKey];
  const events = [];
  const now = new Date();
  const baseDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);

  for (const cycle of sched.cycles) {
    const start = new Date(baseDay);
    start.setHours(cycle.hour, cycle.startMin || 0, 0, 0);
    const end = new Date(start.getTime() + cycle.on * 60000);
    events.push({
      type: "ON",
      start, end,
      period: cycle.hour < 12 ? "morning" : cycle.hour < 17 ? "midday" : "evening",
      onMin: cycle.on,
    });
  }
  return events;
}

// Compute schedule summary stats
function summarizeSchedule(scheduleObj) {
  const cycles = scheduleObj.cycles;
  if (!cycles || cycles.length === 0) return { totalRunMin: 0, totalCycles: 0, firstHour: null, lastHour: null };
  const totalRunMin = cycles.reduce((s, c) => s + c.on, 0);
  const firstCycle = cycles[0];
  const lastCycle = cycles[cycles.length - 1];
  const lastEndMin = lastCycle.hour * 60 + (lastCycle.startMin || 0) + lastCycle.on;
  return {
    totalRunMin,
    totalCycles: cycles.length,
    firstHour: firstCycle.hour,
    firstMin: firstCycle.startMin || 0,
    lastEndMin,
  };
}

function formatHourMin(hour, min = 0) {
  const d = new Date();
  d.setHours(hour, min, 0, 0);
  return d.toLocaleTimeString("en-JM", { hour: "numeric", minute: "2-digit", hour12: true });
}

// Check whether NFT and Tower cycles ever overlap for a given season
// Returns array of conflict objects, empty if no overlaps
function detectScheduleOverlap(seasonKey) {
  const nftCycles = PUMP_SCHEDULES.nft.schedules[seasonKey].cycles;
  const towerCycles = PUMP_SCHEDULES.tower.schedules[seasonKey].cycles;
  const conflicts = [];

  const toRange = (c) => {
    const start = c.hour * 60 + (c.startMin || 0);
    return { start, end: start + c.on };
  };

  for (const n of nftCycles) {
    const nr = toRange(n);
    for (const t of towerCycles) {
      const tr = toRange(t);
      // Overlap if NFT start < Tower end AND Tower start < NFT end
      if (nr.start < tr.end && tr.start < nr.end) {
        conflicts.push({ nft: n, tower: t });
      }
    }
  }
  return conflicts;
}

function findCurrentAndNextEvent(events) {
  const now = new Date();
  let current = null;
  let next = null;
  for (const e of events) {
    if (now >= e.start && now < e.end) current = e;
    if (e.start > now && !next) next = e;
  }
  return { current, next };
}

function formatTime(d) {
  return d.toLocaleTimeString("en-JM", { hour: "2-digit", minute: "2-digit", hour12: true });
}

function formatDuration(ms) {
  const mins = Math.floor(ms / 60000);
  const secs = Math.floor((ms % 60000) / 1000);
  if (mins > 0) return `${mins}m ${secs}s`;
  return `${secs}s`;
}

// Combined all-systems pump timeline (SVG). Shows NFT / Tower / Nursery across a
// full 24-hour day so the overnight maintenance pulses are visible alongside the
// daytime runs. A live "now" marker tracks the current time when viewing today.
function CombinedScheduleTimeline({ seasonKey, hasNft = true, hasTower = true, hasNursery, nowMin }) {
  const DAY_START = 0;         // 12:00 AM
  const DAY_END = 24 * 60;     // 12:00 AM next day
  const SPAN = DAY_END - DAY_START;
  const W = 320, H = 132;
  const LABEL_W = 52, RIGHT_PAD = 8;
  const TRACK_W = W - LABEL_W - RIGHT_PAD;
  const x = (absMin) => LABEL_W + ((absMin - DAY_START) / SPAN) * TRACK_W;

  // Only the systems the user runs; rows stack from the top so there are no gaps.
  const rowDefs = [];
  if (hasNft) rowDefs.push({ key: "nft", label: "NFT", color: "#1976d2" });
  if (hasTower) rowDefs.push({ key: "tower", label: "Tower", color: "#7b1fa2" });
  if (hasNursery) rowDefs.push({ key: "nursery", label: "Nursery", color: "#43a047" });
  const rows = rowDefs.map((r, i) => ({ ...r, y: 30 + i * 32 }));

  const ROW_H = 20;

  // Hour gridlines every 3 hours across the full day
  const hourMarks = [];
  for (let h = 0; h <= 24; h += 3) hourMarks.push(h);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto", display: "block" }} role="img" aria-label={`Combined pump schedule timeline for ${seasonKey} season`}>
      {/* Night shading (before 6 AM and after 8 PM) to set off the daytime window */}
      <rect x={x(0)} y={22} width={x(6 * 60) - x(0)} height={H - 38} fill="rgba(0,0,0,0.18)" />
      <rect x={x(20 * 60)} y={22} width={x(24 * 60) - x(20 * 60)} height={H - 38} fill="rgba(0,0,0,0.18)" />

      {/* Hour gridlines + labels */}
      {hourMarks.map(h => {
        const px = x(h * 60);
        const hr12 = (h === 0 || h === 24) ? "12a" : h === 12 ? "12p" : h > 12 ? `${h - 12}p` : `${h}a`;
        return (
          <g key={h}>
            <line x1={px} y1={22} x2={px} y2={H - 16} stroke="var(--border)" strokeWidth="0.5" />
            <text x={px} y={14} fill="var(--text-soft)" fontSize="7" textAnchor="middle" fontFamily="inherit">{hr12}</text>
          </g>
        );
      })}

      {/* Rows */}
      {rows.map(row => {
        const cycles = PUMP_SCHEDULES[row.key].schedules[seasonKey].cycles;
        return (
          <g key={row.key}>
            <text x={LABEL_W - 6} y={row.y + ROW_H / 2 + 3} fill="var(--text-soft2)" fontSize="9" fontWeight="700" textAnchor="end" fontFamily="inherit">{row.label}</text>
            {/* Track background */}
            <rect x={LABEL_W} y={row.y} width={TRACK_W} height={ROW_H} rx="3" fill="rgba(255,255,255,0.03)" />
            {/* Cycle blocks */}
            {cycles.map((c, i) => {
              const startAbs = c.hour * 60 + (c.startMin || 0);
              const bx = x(startAbs);
              const bw = Math.max(1.2, (c.on / SPAN) * TRACK_W);
              return <rect key={i} x={bx} y={row.y} width={bw} height={ROW_H} rx="1.5" fill={row.color} opacity="0.9" />;
            })}
          </g>
        );
      })}

      {/* Live "now" marker */}
      {nowMin != null && nowMin >= DAY_START && nowMin <= DAY_END && (
        <g>
          <line x1={x(nowMin)} y1={22} x2={x(nowMin)} y2={H - 16} stroke="var(--active)" strokeWidth="1.2" />
          <circle cx={x(nowMin)} cy={22} r="2.5" fill="var(--active)" />
          <text x={x(nowMin)} y={H - 6} fill="var(--active)" fontSize="7" fontWeight="700" textAnchor="middle" fontFamily="inherit">now</text>
        </g>
      )}
    </svg>
  );
}

function gMeasure(g) { return g > 17 ? `≈ ${(g / 17.1).toFixed(1)} tbsp` : `≈ ${(g / 5.7).toFixed(1)} tsp`; }

// Calculate pre-dissolution water for a stock solution.
// Rule: ~100g salt per liter is well within solubility limits for MasterBlend/CalNit/Epsom.
// Floor at 1 L so the smallest tanks still get enough water to fully dissolve.
// Cap at 20% of tank volume so very small tanks don't request more than the tank holds.
// Returns { liters, gallons, displayGal, displayL } formatted for UI.
function dilutionWater(totalGrams, tankGallons) {
  const TANK_LITERS = tankGallons * 3.785;
  const idealL = Math.max(1, totalGrams / 100);
  const cappedL = Math.min(idealL, TANK_LITERS * 0.2);
  const gal = cappedL / 3.785;
  // Nice display: under 1 gal show liters first, over 1 gal show gallons first
  const displayGal = gal >= 1 ? `${gal.toFixed(1)} gal` : `${(gal * 4).toFixed(1)} qt`;
  const displayL = `${cappedL.toFixed(1)} L`;
  return { liters: cappedL, gallons: gal, displayGal, displayL };
}
function fmtDate(iso) {
  const d = new Date(iso);
  return { day: d.toLocaleDateString("en-JM", { weekday: "short", month: "short", day: "numeric" }), time: d.toLocaleTimeString("en-JM", { hour: "2-digit", minute: "2-digit", hour12: true }) };
}

const card = {
  background: "linear-gradient(180deg, var(--bg-card) 0%, var(--bg-card2) 100%)",
  borderRadius: 14,
  padding: "16px",
  marginBottom: 12,
  border: "1px solid var(--border)",
  boxShadow: "0 4px 24px rgba(0,0,0,0.3)",
};
const secLbl = {
  fontSize: 11,
  textTransform: "uppercase",
  letterSpacing: "0.2em",
  color: "var(--active)",
  marginBottom: 10,
  fontWeight: 800,
};

// Renders a crop's image override if present, otherwise its emoji
// Consistent line-icon set (replaces emoji throughout the UI chrome)
const ICON_PATHS = {
  calculator: <><rect x="4" y="2" width="16" height="20" rx="2"/><line x1="8" y1="6" x2="16" y2="6"/><line x1="8" y1="14" x2="16" y2="18"/><line x1="8" y1="18" x2="12" y2="18"/></>,
  sprout: <><path d="M7 20h10"/><path d="M12 20v-8"/><path d="M12 12C12 8 9 5 4 5c0 5 3 7 8 7Z"/><path d="M12 10c0-3 2.5-6 7-6 0 4-3 6-7 6Z"/></>,
  droplet: <path d="M12 2.5S5 10 5 14.5a7 7 0 0 0 14 0C19 10 12 2.5 12 2.5Z"/>,
  clipboard: <><rect x="5" y="4" width="14" height="17" rx="2"/><path d="M9 4V3a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v1"/><line x1="9" y1="10" x2="15" y2="10"/><line x1="9" y1="14" x2="15" y2="14"/><line x1="9" y1="18" x2="13" y2="18"/></>,
  clock: <><circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 15 14"/></>,
  gear: <><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></>,
  leaf: <><path d="M11 20A7 7 0 0 1 4 13c0-5 4-9 16-9 0 10-5 16-9 16Z"/><path d="M4 20c4-8 8-11 13-13"/></>,
  flask: <><path d="M9 3h6M10 3v6l-5 9a1.5 1.5 0 0 0 1.3 2.2h11.4A1.5 1.5 0 0 0 19 18l-5-9V3"/><line x1="7.5" y1="14" x2="16.5" y2="14"/></>,
  bolt: <polygon points="13 2 4 14 11 14 10 22 19 10 12 10 13 2"/>,
  test: <><path d="M9 2h6M10 2v14a3 3 0 0 0 6 0V2"/><line x1="10" y1="9" x2="16" y2="9"/></>,
  warning: <><path d="M12 3 2 20h20L12 3Z"/><line x1="12" y1="9" x2="12" y2="14"/><line x1="12" y1="17.5" x2="12" y2="17.5"/></>,
  download: <><path d="M12 3v12"/><polyline points="7 11 12 16 17 11"/><path d="M4 19h16"/></>,
  upload: <><path d="M12 16V4"/><polyline points="7 9 12 4 17 9"/><path d="M4 19h16"/></>,
  search: <><circle cx="11" cy="11" r="7"/><line x1="16.5" y1="16.5" x2="21" y2="21"/></>,
  globe: <><circle cx="12" cy="12" r="9"/><line x1="3" y1="12" x2="21" y2="12"/><path d="M12 3a14 14 0 0 1 0 18 14 14 0 0 1 0-18Z"/></>,
};

function Icon({ name, size = 18, color = "currentColor", strokeWidth = 1.8, style = {} }) {
  const path = ICON_PATHS[name];
  if (!path) return null;
  return (
    <svg
      width={size} height={size} viewBox="0 0 24 24"
      fill="none" stroke={color} strokeWidth={strokeWidth}
      strokeLinecap="round" strokeLinejoin="round"
      style={{ display: "inline-block", verticalAlign: "middle", flexShrink: 0, ...style }}
      aria-hidden="true"
    >
      {path}
    </svg>
  );
}

// Lightweight SVG line chart for EC/pH/temp trends — no external deps
function TrendChart({ data, color = "var(--active)", label = "", unit = "", height = 140, minY = null, maxY = null, safeMin = null, safeMax = null }) {
  if (!data || data.length === 0) {
    return (
      <div style={{ height, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, color: "var(--text-soft)", background: "rgba(0,0,0,0.2)", borderRadius: 8 }}>
        No {label} data yet — log entries to see trends
      </div>
    );
  }
  const W = 600, H = height, PAD_L = 38, PAD_R = 12, PAD_T = 10, PAD_B = 26;
  const plotW = W - PAD_L - PAD_R, plotH = H - PAD_T - PAD_B;
  const values = data.map(d => d.value);
  const yMin = minY != null ? minY : Math.min(...values, safeMin ?? Infinity) * 0.95;
  const yMax = maxY != null ? maxY : Math.max(...values, safeMax ?? -Infinity) * 1.05;
  const yRange = Math.max(0.01, yMax - yMin);
  const xMin = new Date(data[0].timestamp).getTime();
  const xMax = new Date(data[data.length - 1].timestamp).getTime();
  const xRange = Math.max(1, xMax - xMin);
  const xFor = ts => PAD_L + ((new Date(ts).getTime() - xMin) / xRange) * plotW;
  const yFor = v => PAD_T + plotH - ((v - yMin) / yRange) * plotH;
  const pts = data.map(d => `${xFor(d.timestamp).toFixed(1)},${yFor(d.value).toFixed(1)}`).join(" ");
  const areaPts = `${PAD_L},${PAD_T + plotH} ${pts} ${(PAD_L + plotW).toFixed(1)},${PAD_T + plotH}`;
  // Y-axis ticks (4 evenly spaced)
  const yTicks = Array.from({ length: 5 }, (_, i) => yMin + (yRange * i / 4));
  // X-axis dates (start, mid, end)
  const xTicks = [data[0], data[Math.floor(data.length / 2)], data[data.length - 1]];
  const last = data[data.length - 1];

  return (
    <div style={{ position: "relative" }}>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto", display: "block" }}>
        {/* Safe range band */}
        {safeMin != null && safeMax != null && (
          <rect x={PAD_L} y={yFor(safeMax)} width={plotW} height={Math.max(0, yFor(safeMin) - yFor(safeMax))}
            fill={color} opacity="0.07" />
        )}
        {/* Grid lines + Y labels */}
        {yTicks.map((t, i) => (
          <g key={i}>
            <line x1={PAD_L} y1={yFor(t)} x2={PAD_L + plotW} y2={yFor(t)} stroke="rgba(255,255,255,0.05)" strokeWidth="1" />
            <text x={PAD_L - 6} y={yFor(t) + 3} fill="var(--text-soft)" fontSize="9" textAnchor="end" fontFamily="inherit">
              {t.toFixed(yRange < 2 ? 2 : 1)}
            </text>
          </g>
        ))}
        {/* Area fill */}
        <polygon points={areaPts} fill={color} opacity="0.12" />
        {/* Line */}
        <polyline points={pts} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        {/* Data points */}
        {data.map((d, i) => (
          <circle key={i} cx={xFor(d.timestamp)} cy={yFor(d.value)} r={i === data.length - 1 ? 4 : 2.5} fill={color} stroke="var(--bg-card2)" strokeWidth="1.5">
            <title>{new Date(d.timestamp).toLocaleString("en-JM", { dateStyle: "short", timeStyle: "short" })}: {d.value}{unit}</title>
          </circle>
        ))}
        {/* X-axis labels */}
        {xTicks.map((d, i) => (
          <text key={i} x={xFor(d.timestamp)} y={H - 8} fill="var(--text-soft)" fontSize="9" textAnchor={i === 0 ? "start" : i === 2 ? "end" : "middle"} fontFamily="inherit">
            {new Date(d.timestamp).toLocaleDateString("en-JM", { month: "short", day: "numeric" })}
          </text>
        ))}
      </svg>
      {/* Latest value pill */}
      <div style={{ position: "absolute", top: 4, right: 8, background: "rgba(0,0,0,0.55)", border: `1px solid ${color}40`, borderRadius: 7, padding: "3px 8px", fontSize: 11, fontWeight: 700, color }}>
        Latest: {last.value}{unit}
      </div>
    </div>
  );
}

function CropIcon({ cropKey, emoji, size = 22, style = {} }) {
  // 1) Per-crop illustrated icon (preferred) — the sliced raster pack.
  const imgSrc = cropKey && CROP_IMAGES[cropKey];
  if (imgSrc) {
    const baseStyle = {
      width: size, height: size,
      objectFit: "contain",
      display: "inline-block",
      verticalAlign: "middle",
      filter: "drop-shadow(0 3px 6px rgba(0,0,0,0.4))",
    };
    // Allow full style override (e.g. width/height as "80%" in the modal grid)
    const merged = { ...baseStyle, ...style };
    return <img src={imgSrc} alt="" width={merged.width} height={merged.height} style={merged} />;
  }
  // 2) On-brand inline SVG line-icon fallback (themeable, renders everywhere).
  const paths = cropKey && CROP_ICON_PATHS[cropKey];
  if (paths) {
    const color = (cropKey && CROPS[cropKey]?.color) || "currentColor";
    const merged = { width: size, height: size, display: "inline-block", verticalAlign: "middle", color, flexShrink: 0, ...style };
    return (
      <svg viewBox="0 0 24 24" width={merged.width} height={merged.height} fill="none"
        stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"
        style={merged} role="img" aria-label={CROPS[cropKey]?.name || "crop"}
        dangerouslySetInnerHTML={{ __html: paths }} />
    );
  }
  // 3) Emoji fallback
  return <span style={{ fontSize: size, lineHeight: 1, ...style }}>{emoji}</span>;
}

// Collapsible panel header — tap to toggle section open/closed
// YaadGrow collapsible panel header — circular icon, uppercase label, summary, badge pill, chevron
// Collapsible Settings section — a card whose body toggles open/closed.
function SettingsSection({ title, open, onToggle, cardStyle, children }) {
  return (
    <div style={cardStyle || card}>
      <button
        onClick={onToggle}
        aria-expanded={open}
        style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, background: "none", border: "none", cursor: "pointer", padding: 0, fontFamily: "inherit", textAlign: "left" }}
      >
        <span style={{ ...secLbl, marginBottom: 0 }}>{title}</span>
        <span style={{ color: "var(--text-soft)", fontSize: 11, transform: open ? "rotate(90deg)" : "none", transition: "transform 0.2s", flexShrink: 0 }}>▶</span>
      </button>
      {open && <div style={{ marginTop: 14 }}>{children}</div>}
    </div>
  );
}

function PanelHeader({ icon, label, summary, badge, badgeType = "green", isOpen, onToggle }) {
  // Badge colour system matching the mockup
  const badgeStyles = {
    green:  { background: "#1a3d1a", border: "1px solid #2d6a2d", color: "#78e05a" },
    blue:   { background: "#0f2a3d", border: "1px solid #1e4a6d", color: "#5ab8e8" },
    amber:  { background: "#2d1e00", border: "1px solid #5a3c00", color: "#f0a820" },
  };
  const bStyle = badgeStyles[badgeType] || badgeStyles.green;

  return (
    <button
      onClick={onToggle}
      aria-expanded={isOpen}
      style={{
        width: "100%", display: "flex", alignItems: "center", gap: 14,
        background: "transparent",
        border: "none", cursor: "pointer",
        padding: isOpen ? "14px 16px 12px" : "14px 16px",
        borderBottom: isOpen ? "1px solid rgba(255,255,255,0.06)" : "none",
        borderRadius: isOpen ? "14px 14px 0 0" : 14,
        fontFamily: "inherit",
        transition: "background 0.15s",
        textAlign: "left",
      }}
    >
      {/* Circular icon container */}
      <div style={{
        width: 48, height: 48, borderRadius: "50%",
        background: "rgba(0,0,0,0.35)",
        border: "1px solid var(--border-soft)",
        display: "flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0, fontSize: 20,
      }} aria-hidden="true">
        {icon}
      </div>

      {/* Text block */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, fontWeight: 800, color: "var(--active)", textTransform: "uppercase", letterSpacing: "0.18em", marginBottom: 3 }}>{label}</div>
        {summary && (
          <div style={{ fontSize: 12, color: "var(--text-muted)", lineHeight: 1.4 }}>{summary}</div>
        )}
      </div>

      {/* Right — badge + chevron */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
        {badge && (
          <span style={{
            ...bStyle,
            fontSize: 11, fontWeight: 700, padding: "4px 10px",
            borderRadius: 8, whiteSpace: "nowrap",
          }}>
            {badge}
          </span>
        )}
        <span style={{
          fontSize: 16, color: "var(--text-soft)",
          transform: isOpen ? "rotate(90deg)" : "none",
          display: "inline-block", transition: "transform 0.2s",
        }} aria-hidden="true">›</span>
      </div>
    </button>
  );
}

export default function FarmDashboard() {
  const [tab, setTab] = useState("calc");

  // Clicking the wordmark returns to the default tab and the top of the page.
  const goHome = useCallback(() => {
    setTab("calc");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);
  const [cropKey, setCropKey] = useState("lettuce");
  const [stageIdx, setStageIdx] = useState(1);
  const [baseEc, setBaseEc] = useState(0.5);
  const [gallons, setGallons] = useState(200);
  const [customEc, setCustomEc] = useState(null);
  const [calcStep, setCalcStep] = useState(1); // mobile calculator wizard: 1 Crop&Stage · 2 Mix Inputs · 3 Result
  const [logs, setLogs] = useState([]);
  const [logType, setLogType] = useState("ec_reading");
  const [logValue, setLogValue] = useState("");
  const [logNotes, setLogNotes] = useState("");
  // Consolidated "Reservoir Check" — captures EC, pH, temp in one pass
  const [rcEc, setRcEc] = useState("");
  const [rcPh, setRcPh] = useState("");
  const [rcTemp, setRcTemp] = useState("");
  const [rcFreshChange, setRcFreshChange] = useState(false);

  // Pump tab state
  const [pumpSystem, setPumpSystem] = useState("nft");
  const [seasonOverride, setSeasonOverride] = useState(null); // null = auto from month
  const [tick, setTick] = useState(0);

  // Responsive layout
  const [viewportWidth, setViewportWidth] = useState(typeof window !== "undefined" ? window.innerWidth : 1280);
  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const isMobile = viewportWidth < 640;
  const isTablet = viewportWidth >= 640 && viewportWidth < 1100;
  const isCompact = viewportWidth < 1100;

  // Service-worker update prompt. registerType is "prompt" (vite.config.js), so a
  // new deployed build installs in the background and waits; needRefresh flips
  // true and we show an "Update available — Reload" banner. updateServiceWorker
  // (true) activates the waiting SW and reloads, so returning users aren't a
  // visit behind. Check again for updates whenever the tab regains focus.
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl, reg) {
      if (!reg) return;
      const check = () => { if (document.visibilityState === "visible") reg.update(); };
      document.addEventListener("visibilitychange", check);
      setInterval(check, 60 * 60 * 1000); // also check hourly, for tabs left open all day
    },
  });

  // Mobile calculator wizard: on phones the long calculator scroll is split into
  // three guided steps. On desktop every step is shown (wizard off) so the layout
  // is unchanged. Visibility is toggled via `display` so React state is preserved.
  const calcWizard = isMobile;
  const showStep1 = !calcWizard || calcStep === 1; // Crop & Stage
  const showStep2 = !calcWizard || calcStep === 2; // Mix Inputs (base EC, tank, target, pH)
  const showStep3 = !calcWizard || calcStep === 3; // Result (Add Now + Mixing)

  // Crop search filter
  const [cropSearch, setCropSearch] = useState("");
  const [cropModalOpen, setCropModalOpen] = useState(false);

  // Power cost settings (persisted)
  const [nftWattage, setNftWattage] = useState(POWER_DEFAULTS.nftWattage);
  const [towerWattage, setTowerWattage] = useState(POWER_DEFAULTS.towerWattage);
  const [nurseryWattage, setNurseryWattage] = useState(POWER_DEFAULTS.nurseryWattage);
  const [electricityRate, setElectricityRate] = useState(POWER_DEFAULTS.electricityRate);
  const [actualNftKwh, setActualNftKwh] = useState("");
  const [actualTowerKwh, setActualTowerKwh] = useState("");
  const [showPowerCost, setShowPowerCost] = useState(false);
  const [powerCostLoaded, setPowerCostLoaded] = useState(false);

  // System layout settings (user-editable in Settings tab, persisted)
  const [sysSettings, setSysSettings] = useState(SYSTEM_DEFAULTS);
  const [sysSettingsLoaded, setSysSettingsLoaded] = useState(false);
  const updateSysSetting = (key, value) => setSysSettings(s => ({ ...s, [key]: value }));

  // Colour theme: set [data-theme] on the document root so the light-mode CSS
  // variable overrides take effect. Dark is the default and equals :root, so
  // existing users and a not-yet-loaded state both render the original dark look.
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", sysSettings.theme === "light" ? "light" : "dark");
  }, [sysSettings.theme]);

  // Rebuild the live PUMP_SCHEDULES binding whenever the user's custom schedules
  // change (or on first settings load). Everything that reads PUMP_SCHEDULES then
  // reflects the override; setTick forces an immediate re-render.
  useEffect(() => {
    PUMP_SCHEDULES = buildPumpSchedules(sysSettings.customPumpSchedules);
    setTick(t => t + 1);
  }, [sysSettings.customPumpSchedules]);

  // Write a cycle list to customPumpSchedules[season][system]; `cycles === null`
  // clears the override for that system/season (falls back to the default program).
  const setCustomCycles = (season, system, cycles) => {
    setSysSettings(s => {
      const cur = s.customPumpSchedules || {};
      const seasonObj = { ...(cur[season] || {}) };
      if (cycles === null) delete seasonObj[system];
      else seasonObj[system] = cycles;
      const next = { ...cur };
      if (Object.keys(seasonObj).length) next[season] = seasonObj;
      else delete next[season];
      return { ...s, customPumpSchedules: Object.keys(next).length ? next : undefined };
    });
  };
  const [pumpEditSystem, setPumpEditSystem] = useState("nft");   // custom pump editor: selected system
  const [pumpEditSeason, setPumpEditSeason] = useState("shoulder"); // custom pump editor: selected season

  // Which grow systems the user runs. A disabled system is removed from the pump
  // schedules, power/energy calcs, the timeline, and the stat strip. Default true
  // so existing users (whose settings predate these flags) keep all three.
  const hasNft = sysSettings.hasNft !== false;
  const hasTower = sysSettings.hasTower !== false;
  const systemEnabled = { nft: hasNft, tower: hasTower, nursery: sysSettings.hasNursery !== false };
  const enabledSystems = ["nft", "tower", "nursery"].filter(k => systemEnabled[k]);

  // If the system being viewed on the Pumps tab gets disabled, switch to the
  // first one that's still enabled.
  useEffect(() => {
    if (!systemEnabled[pumpSystem]) {
      const first = ["nft", "tower", "nursery"].find(k => systemEnabled[k]);
      if (first) setPumpSystem(first);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pumpSystem, hasNft, hasTower, sysSettings.hasNursery]);

  // Location & climate (drives climate notes, currency, locale)
  const [userLocation, setUserLocation] = useState(LOCATION_DEFAULTS);
  const [userLocationLoaded, setUserLocationLoaded] = useState(false);
  const updateLocation = (key, value) => setUserLocation(loc => ({ ...loc, [key]: value }));

  // Auto-detect the device location via the browser Geolocation API (built-in,
  // free, permission-gated, HTTPS-only). Fills in coordinates + a reverse-geocoded
  // place name so the user doesn't have to type it. Manual entry stays the source
  // of truth — the device's location may not be the farm's, so this only prefills.
  // "Use my location" is a helper to SET the farm location, not a second location.
  // The farm location is the single source of truth for all weather/agronomy, so
  // detection never overwrites it silently: we detect, then ask the user to
  // confirm before replacing the farm location.
  const [geoStatus, setGeoStatus] = useState("idle"); // idle | locating | confirm | denied | unavailable | done
  const [geoPending, setGeoPending] = useState(null);  // { name, lat, lon } awaiting confirmation
  const [setupStep, setSetupStep] = useState(1);       // first-run wizard step: 1 welcome · 2 location · 3 systems
  const detectLocation = () => {
    if (typeof navigator === "undefined" || !navigator.geolocation) { setGeoStatus("unavailable"); return; }
    setGeoStatus("locating");
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lon = pos.coords.longitude;
        let name = `${lat.toFixed(3)}, ${lon.toFixed(3)}`; // coord fallback if reverse-geocode fails
        let countryCode = null;
        try {
          // Reverse-geocode coords → a human label (BigDataCloud: free, no key, CORS). Best-effort.
          const rUrl = `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lon}&localityLanguage=en`;
          const rRes = await fetch(rUrl);
          if (rRes.ok) {
            const j = await rRes.json();
            const place = j.city || j.locality || j.principalSubdivision;
            if (place && j.countryName) name = `${place}, ${j.countryName}`;
            else if (j.countryName) name = j.countryName;
            // The same response already carries the country, so the currency
            // comes free — no second request.
            countryCode = j.countryCode || null;
          }
        } catch { /* keep the coordinate label */ }
        // Hold the result for confirmation instead of applying it — the user may
        // not be at the farm, so we never silently replace the farm location.
        setGeoPending({ name, lat, lon, currency: COUNTRY_CURRENCY[countryCode] || null });
        setGeoStatus("confirm");
      },
      (err) => { setGeoStatus(err && err.code === 1 ? "denied" : "unavailable"); },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 10 * 60 * 1000 }
    );
  };
  // Apply the detected fix as the farm location. Setting the name + cached fix
  // makes the weather effect (which keys on name) reuse these coords without
  // re-geocoding. Enabling weather ensures the new fix is actually used.
  const confirmDetectedLocation = () => {
    if (!geoPending) return;
    const { name, lat, lon, currency } = geoPending;
    // Currency follows the detected country when we recognise it. Costs are
    // entered and read in one place, so leaving JMD on a farm in Ghana makes
    // every figure on the screen quietly wrong.
    setUserLocation(loc => ({
      ...loc, lat, lon, name, resolvedName: name, showWeather: true,
      ...(currency ? { currency } : {}),
    }));
    setGeoPending(null);
    setGeoStatus("done");
  };
  const cancelDetectedLocation = () => { setGeoPending(null); setGeoStatus("idle"); };

  // Refresh the location automatically, but ONLY where the browser has already
  // been granted permission. Without this the app shows its hardcoded default
  // ("Portmore, Jamaica") to everyone until they think to press Detect, so a
  // grower in another parish silently gets the wrong forecast.
  //
  // Gated on Permissions API state === "granted" so nobody ever sees an
  // unprompted permission dialog: a user who has not granted it is untouched and
  // still has the button. Runs once per load, after settings have loaded so it
  // does not race the stored value.
  const autoDetectedRef = useRef(false);
  useEffect(() => {
    if (!userLocationLoaded || autoDetectedRef.current) return;
    // Only ever fills a blank. Someone who typed "Kingston" keeps it, even if
    // their GPS says otherwise — they may be planning a farm they are not at.
    if (userLocation.name && userLocation.name.trim()) return;
    if (typeof navigator === "undefined" || !navigator.permissions || !navigator.geolocation) return;
    let cancelled = false;
    navigator.permissions
      .query({ name: "geolocation" })
      .then((status) => {
        if (cancelled || status.state !== "granted") return;
        autoDetectedRef.current = true;
        detectLocation();
      })
      .catch(() => {
        // Firefox historically rejected for "geolocation"; fall back to the button.
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userLocationLoaded, userLocation.name]);

  // First-run wizard: shown only for a brand-new install (see the settings-load
  // grandfather). Skipping or finishing marks it complete so it never returns.
  const showSetup = sysSettingsLoaded && userLocationLoaded && sysSettings.setupComplete === false;
  const finishSetup = () => { updateSysSetting("setupComplete", true); setGeoStatus("idle"); setGeoPending(null); };

  // Live clock + local weather for the conditions bar.
  // `now` ticks the time/date display; `weather` holds the last Open-Meteo read.
  // weatherStatus: "off" | "loading" | "ok" | "notfound" | "error"
  const [now, setNow] = useState(() => new Date());
  const [weather, setWeather] = useState(null);
  const [weatherStatus, setWeatherStatus] = useState("loading");

  // Lightweight toast/snackbar for transient action feedback (saved, exported…).
  // tone: "success" | "error". Auto-dismisses; setting a new one resets the timer.
  const [toast, setToast] = useState(null); // { message, tone, id }
  const showToast = useCallback((message, tone = "success") => {
    setToast({ message, tone, id: Date.now() });
  }, []);
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  // Active grows
  const [grows, setGrows] = useState([]);
  const [growsLoaded, setGrowsLoaded] = useState(false);
  const [activeGrowId, setActiveGrowId] = useState(null);
  const [showNewGrowForm, setShowNewGrowForm] = useState(false);

  // New grow form
  const [newGrowCrop, setNewGrowCrop] = useState("lettuce");
  const [newGrowSystem, setNewGrowSystem] = useState("nft");      // final destination system
  const [newGrowStartsIn, setNewGrowStartsIn] = useState("nursery"); // "nursery" | "direct"
  const [newGrowNurseryDays, setNewGrowNurseryDays] = useState(7);
  const [newGrowDate, setNewGrowDate] = useState(isoToday());
  const [newGrowTransplantDate, setNewGrowTransplantDate] = useState(""); // "" = not transplanted yet
  const [newGrowQty, setNewGrowQty] = useState("");
  const [newGrowLabel, setNewGrowLabel] = useState("");
  const [newGrowNotes, setNewGrowNotes] = useState("");
  const [editingGrowId, setEditingGrowId] = useState(null); // null = creating; otherwise the grow being edited

  // Auto-update startsIn and nurseryDays when crop changes — but not while
  // editing, where the fields were pre-filled from the existing grow.
  useEffect(() => {
    if (editingGrowId) return;
    const usesNursery = DEFAULT_USES_NURSERY(newGrowCrop);
    setNewGrowStartsIn(usesNursery ? "nursery" : "direct");
    if (NURSERY_DURATIONS[newGrowCrop]) {
      setNewGrowNurseryDays(NURSERY_DURATIONS[newGrowCrop]);
    }
  }, [newGrowCrop, editingGrowId]);

  // Transplant dialog
  const [transplantingGrowId, setTransplantingGrowId] = useState(null);
  const [transplantNotes, setTransplantNotes] = useState("");

  // Harvest dialog
  const [harvestingGrowId, setHarvestingGrowId] = useState(null);
  const [harvestWeight, setHarvestWeight] = useState("");
  const [harvestUnit, setHarvestUnit] = useState("kg"); // kg or lb
  const [harvestPrice, setHarvestPrice] = useState("");
  const [harvestNotes, setHarvestNotes] = useState("");
  const [harvestStatus, setHarvestStatus] = useState("harvested"); // harvested or failed

  // Collapsible panel state — which sections are open in Calculator tab
  const [openPanels, setOpenPanels] = useState({
    germination: false,
    ph: false,
    mix: true,
  });
  const togglePanel = (key) => setOpenPanels(p => ({ ...p, [key]: !p[key] }));
  const [germMedium, setGermMedium] = useState("oasis"); // oasis | soil | coco
  const [germTipsOpen, setGermTipsOpen] = useState(false);
  const [seedType, setSeedType] = useState("bare"); // bare | pelleted
  const [storageError, setStorageError] = useState(null); // surfaces storage quota failures
  const [dismissedReservoirReminder, setDismissedReservoirReminder] = useState(false); // resets when a new water change is logged

  // Tick every second when on Pumps tab for live countdown
  useEffect(() => {
    if (tab !== "pumps") return;
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, [tab]);

  // ── Calculations (memoized) ──
  const crop = CROPS[cropKey];
  const safeStageIdx = Math.min(stageIdx, crop.stages.length - 1);
  const stageFromCrop = crop.stages[safeStageIdx];
  const isFlush = stageFromCrop.isFlush === true;

  // ── Nursery mode ──
  // When enabled, EC/pH targets are overridden with crop-specific NURSERY_TARGETS.
  // Auto-enables when the active grow is in nursery phase. User can manually toggle.
  const [nurseryMode, setNurseryMode] = useState(false);
  const nurseryTarget = NURSERY_TARGETS[cropKey] || null;

  // The "effective stage" — uses nursery targets when in nursery mode, otherwise the crop's stage
  const stage = (nurseryMode && nurseryTarget) ? {
    label: "Nursery",
    sub: "pre-transplant",
    ecMin: nurseryTarget.ecMin,
    ecMax: nurseryTarget.ecMax,
    ecTarget: nurseryTarget.ecTarget,
    phMin: nurseryTarget.phMin,
    phMax: nurseryTarget.phMax,
    phTarget: nurseryTarget.phTarget,
  } : stageFromCrop;

  // ── Mixed reservoir mode ──
  // mixCrops is an array of { cropKey, stageIdx } entries. When mixMode === "mixed"
  // and mixCrops.length > 0, the calculator computes a compromise EC/pH for all of them.
  const [mixMode, setMixMode] = useState("single"); // "single" | "mixed"
  const [mixCrops, setMixCrops] = useState([]); // [{cropKey, stageIdx}]
  const [mixModalOpen, setMixModalOpen] = useState(false);

  // Helper: get stage object for a mix entry safely
  const getMixStage = (m) => {
    const c = CROPS[m.cropKey];
    if (!c) return null;
    const idx = Math.min(Math.max(0, m.stageIdx ?? 0), c.stages.length - 1);
    return { crop: c, stage: c.stages[idx], stageIdx: idx };
  };

  // Compute compromise targets across all selected crops
  const compromise = useMemo(() => {
    if (mixMode !== "mixed" || mixCrops.length === 0) return null;
    const items = mixCrops.map(getMixStage).filter(Boolean);
    if (items.length === 0) return null;

    // Find EC intersection
    let ecLo = -Infinity, ecHi = Infinity;
    let phLo = -Infinity, phHi = Infinity;
    items.forEach(({ stage: s }) => {
      ecLo = Math.max(ecLo, s.ecMin);
      ecHi = Math.min(ecHi, s.ecMax);
      phLo = Math.max(phLo, s.phMin);
      phHi = Math.min(phHi, s.phMax);
    });

    const ecOverlaps = ecHi >= ecLo;
    const phOverlaps = phHi >= phLo;

    // EC target: "lowest upper bound" strategy — safest approach for mixed reservoirs
    // When ranges overlap: use the lowest upper bound (intersection ceiling),
    //   which ensures no crop is pushed beyond its safe range.
    // When ranges don't overlap: the lowest upper bound is below some crops' lower bounds,
    //   so we still pick it (sensitive crops protected, hungry crops slightly underfed).
    // This is the conservative choice: better to slightly underfeed kale than burn lettuce.
    const lowestUpperBound = Math.min(...items.map(({ stage: s }) => s.ecMax));
    let ecTarget = lowestUpperBound;
    // But if the lowest upper bound is also above the highest lower bound, we have a real
    // intersection — sit just inside the ceiling for a small safety margin.
    if (ecOverlaps) {
      ecTarget = lowestUpperBound - 0.05; // 0.05 margin below the safe ceiling
    }

    // pH target: midpoint of intersection when overlapping (pH compromises are less risky than EC)
    let phTarget;
    if (phOverlaps) {
      phTarget = (phLo + phHi) / 2;
    } else {
      phTarget = items.reduce((sum, { stage: s }) => sum + s.phTarget, 0) / items.length;
    }

    // Per-crop fit analysis
    const perCrop = items.map(({ crop: c, stage: s, stageIdx }) => {
      const ecOk = ecTarget >= s.ecMin && ecTarget <= s.ecMax;
      const phOk = phTarget >= s.phMin && phTarget <= s.phMax;
      const ecDelta = ecTarget < s.ecMin ? ecTarget - s.ecMin
                    : ecTarget > s.ecMax ? ecTarget - s.ecMax
                    : 0;
      const phDelta = phTarget < s.phMin ? phTarget - s.phMin
                    : phTarget > s.phMax ? phTarget - s.phMax
                    : 0;
      return { crop: c, stage: s, stageIdx, ecOk, phOk, ecDelta, phDelta };
    });

    // Flag agronomic incompatibility: heuristic — if EC range span > 1.0 between min/max
    const minIdeal = Math.min(...items.map(i => i.stage.ecTarget));
    const maxIdeal = Math.max(...items.map(i => i.stage.ecTarget));
    const spread = maxIdeal - minIdeal;
    const incompatible = spread >= 1.0;

    return {
      ecTarget: parseFloat(ecTarget.toFixed(2)),
      phTarget: parseFloat(phTarget.toFixed(2)),
      ecLo: ecOverlaps ? ecLo : null,
      ecHi: ecOverlaps ? ecHi : null,
      phLo: phOverlaps ? phLo : null,
      phHi: phOverlaps ? phHi : null,
      ecOverlaps, phOverlaps,
      perCrop, incompatible, spread,
      itemCount: items.length,
    };
  }, [mixMode, mixCrops]);

  // Active "effective" stage values — either single-crop stage, or compromise targets
  const effectiveTarget = compromise ? compromise.ecTarget : stage.ecTarget;
  const effectivePhTarget = compromise ? compromise.phTarget : stage.phTarget;

  const calc = useMemo(() => {
    const singleTarget = customEc !== null ? customEc : stage.ecTarget;
    const compromiseTarget = compromise ? compromise.ecTarget : null;
    const baseTarget = compromise ? compromiseTarget : singleTarget;
    const effTarget = Math.max(0, baseTarget);
    const nutEc = Math.max(0, effTarget - baseEc);
    const strength = nutEc / REFERENCE_EC;
    const exceedsRef = nutEc > REFERENCE_EC;
    const results = Object.entries(RATIOS).map(([k, r]) => ({
      key: k, ...r,
      totalGrams: r.gPerGal * strength * gallons,
      gPerGalEff: r.gPerGal * strength,
    }));
    return { baseTarget, effTarget, nutEc, strength, exceedsRef, results };
  }, [customEc, stage, baseEc, gallons, compromise]);

  const { baseTarget, effTarget, nutEc, exceedsRef, results } = calc;

  const phStatus = (val) => {
    const v = parseFloat(val);
    if (isNaN(v)) return null;
    if (v >= stage.phMin && v <= stage.phMax) return { ok: true, msg: `✓ In range (${stage.phMin}–${stage.phMax})` };
    if (v < stage.phMin) return { ok: false, msg: `⚠ Too low for ${crop.name} ${stage.label} — adding water raises pH but lowers EC; recheck both after` };
    return { ok: false, msg: `⚠ Too high for ${crop.name} ${stage.label} — add H₂SO₄ drops` };
  };

  // ── Storage ──
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await window.storage.get("farm-logs");
        if (!cancelled && r?.value) setLogs(JSON.parse(r.value));
      } catch (e) {}
    })();
    return () => { cancelled = true; };
  }, []);

  const saveLogs = useCallback(async (nl) => {
    setLogs(nl);
    try {
      await window.storage.set("farm-logs", JSON.stringify(nl));
    } catch (e) {
      // Storage quota exceeded or unavailable — surface it instead of failing silently
      setStorageError("Could not save logs — storage may be full. Export your data as a backup.");
    }
  }, []);

  // Load power cost settings
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await window.storage.get("power-settings");
        if (!cancelled && r?.value) {
          const p = JSON.parse(r.value);
          if (p.nftWattage != null) setNftWattage(p.nftWattage);
          if (p.towerWattage != null) setTowerWattage(p.towerWattage);
          if (p.nurseryWattage != null) setNurseryWattage(p.nurseryWattage);
          if (p.electricityRate != null) setElectricityRate(p.electricityRate);
          if (p.actualNftKwh != null) setActualNftKwh(p.actualNftKwh);
          if (p.actualTowerKwh != null) setActualTowerKwh(p.actualTowerKwh);
        }
      } catch (e) {}
      if (!cancelled) setPowerCostLoaded(true);
    })();
    return () => { cancelled = true; };
  }, []);

  // Save power settings when changed
  useEffect(() => {
    if (!powerCostLoaded) return;
    (async () => {
      try {
        await window.storage.set("power-settings", JSON.stringify({
          nftWattage, towerWattage, nurseryWattage, electricityRate, actualNftKwh, actualTowerKwh,
        }));
      } catch (e) {}
    })();
  }, [nftWattage, towerWattage, nurseryWattage, electricityRate, actualNftKwh, actualTowerKwh, powerCostLoaded]);

  // Load system layout settings
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await window.storage.get("system-settings");
        if (!cancelled && r?.value) {
          const s = JSON.parse(r.value);
          // Merge over defaults so new fields added later still get sane values
          const merged = { ...SYSTEM_DEFAULTS, ...s };
          // Grandfather: settings saved before the first-run wizard existed have
          // no setupComplete field — those are established users, so never show
          // them the wizard. A fresh install has no stored settings at all.
          if (!("setupComplete" in s)) merged.setupComplete = true;
          setSysSettings(merged);
          if (s.defaultTab) setTab(s.defaultTab);
        }
      } catch (e) {}
      if (!cancelled) setSysSettingsLoaded(true);
    })();
    return () => { cancelled = true; };
  }, []);

  // Save system layout settings when changed
  useEffect(() => {
    if (!sysSettingsLoaded) return;
    (async () => {
      try {
        await window.storage.set("system-settings", JSON.stringify(sysSettings));
      } catch (e) {
        setStorageError("Could not save system settings — storage may be full.");
      }
    })();
  }, [sysSettings, sysSettingsLoaded]);

  // Load user location settings
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await window.storage.get("user-location");
        if (!cancelled && r?.value) {
          const loc = JSON.parse(r.value);
          // Merge over defaults so future fields get sane values
          setUserLocation({ ...LOCATION_DEFAULTS, ...loc });
        }
      } catch (e) {}
      if (!cancelled) setUserLocationLoaded(true);
    })();
    return () => { cancelled = true; };
  }, []);

  // Save user location settings when changed
  useEffect(() => {
    if (!userLocationLoaded) return;
    (async () => {
      try {
        await window.storage.set("user-location", JSON.stringify(userLocation));
      } catch (e) {
        setStorageError("Could not save location settings — storage may be full.");
      }
    })();
  }, [userLocation, userLocationLoaded]);

  // Tick the clock once a minute so the conditions bar stays current without
  // burning a re-render every second. Aligns to the top of the next minute.
  useEffect(() => {
    let id;
    const tick = () => setNow(new Date());
    const msToNextMinute = 60000 - (Date.now() % 60000);
    const align = setTimeout(() => {
      tick();
      id = setInterval(tick, 60000);
    }, msToNextMinute);
    return () => { clearTimeout(align); if (id) clearInterval(id); };
  }, []);

  // Fetch local weather from Open-Meteo (free, no API key, CORS-enabled).
  // This is the ONLY feature that contacts an external service — it sends the
  // farm's coordinates to Open-Meteo. It is gated behind userLocation.showWeather
  // and disclosed in Settings → Location & Climate and Safety & Privacy.
  // Coordinates are geocoded once from the location name and cached on
  // userLocation, then reused until the name changes. Refreshes every 20 min.
  useEffect(() => {
    if (!userLocationLoaded) return;
    if (!userLocation.showWeather) { setWeather(null); setWeatherStatus("off"); return; }
    if (!userLocation.name || !userLocation.name.trim()) { setWeatherStatus("notfound"); return; }

    let cancelled = false;
    const controller = new AbortController();

    const load = async () => {
      try {
        setWeatherStatus(s => (s === "ok" ? s : "loading"));
        let { lat, lon, resolvedName } = userLocation;
        // (Re)geocode only when we have no cached fix for the current name.
        if (lat == null || lon == null || resolvedName !== userLocation.name) {
          const geoUrl = `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(userLocation.name.trim())}&count=1&language=en&format=json`;
          const gRes = await fetch(geoUrl, { signal: controller.signal });
          const gJson = await gRes.json();
          const hit = gJson?.results?.[0];
          if (!hit) { if (!cancelled) setWeatherStatus("notfound"); return; }
          lat = hit.latitude;
          lon = hit.longitude;
          if (!cancelled) {
            // Cache the fix so we don't geocode on every load. This does not
            // re-trigger this effect (deps key on name, which is unchanged).
            setUserLocation(loc => ({ ...loc, lat, lon, resolvedName: loc.name }));
          }
        }
        const wUrl = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,relative_humidity_2m,weather_code,wind_speed_10m,uv_index&daily=weather_code,temperature_2m_max,temperature_2m_min,precipitation_probability_max,uv_index_max,sunrise,sunset,et0_fao_evapotranspiration&forecast_days=2&timezone=auto`;
        const wRes = await fetch(wUrl, { signal: controller.signal });
        const wJson = await wRes.json();
        if (cancelled) return;
        const c = wJson?.current;
        if (!c) { setWeatherStatus("error"); return; }
        const d = wJson?.daily;
        const daily = d && Array.isArray(d.time)
          ? d.time.map((t, i) => ({
              date: t,
              tmax: d.temperature_2m_max?.[i],
              tmin: d.temperature_2m_min?.[i],
              pop: d.precipitation_probability_max?.[i],
              uvMax: d.uv_index_max?.[i],
              code: d.weather_code?.[i],
              sunrise: d.sunrise?.[i],
              sunset: d.sunset?.[i],
              et0: d.et0_fao_evapotranspiration?.[i],
            }))
          : [];
        setWeather({
          tempC: c.temperature_2m,
          humidity: c.relative_humidity_2m,
          code: c.weather_code,
          wind: c.wind_speed_10m,
          uv: c.uv_index,
          daily,
          at: Date.now(),
        });
        setWeatherStatus("ok");
      } catch (e) {
        if (!cancelled && e.name !== "AbortError") setWeatherStatus("error");
      }
    };

    load();
    const id = setInterval(load, 20 * 60 * 1000);
    return () => { cancelled = true; controller.abort(); clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userLocation.name, userLocation.showWeather, userLocationLoaded]);

  // ── Grows: load on mount ──
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await window.storage.get("farm-grows");
        if (!cancelled && r?.value) setGrows(JSON.parse(r.value));
      } catch (e) {}
      try {
        const a = await window.storage.get("active-grow-id");
        if (!cancelled && a?.value) setActiveGrowId(a.value);
      } catch (e) {}
      if (!cancelled) setGrowsLoaded(true);
    })();
    return () => { cancelled = true; };
  }, []);

  // Save grows when changed
  const saveGrows = useCallback(async (ng) => {
    setGrows(ng);
    try {
      await window.storage.set("farm-grows", JSON.stringify(ng));
    } catch (e) {
      setStorageError("Could not save grows — storage may be full. Export your data as a backup.");
    }
  }, []);

  // Save active grow id when changed
  useEffect(() => {
    if (!growsLoaded) return;
    (async () => {
      try {
        if (activeGrowId) await window.storage.set("active-grow-id", activeGrowId);
        else await window.storage.delete("active-grow-id");
      } catch (e) {}
    })();
  }, [activeGrowId, growsLoaded]);

  // Clear the grow form back to create-mode defaults and close it.
  const resetGrowForm = useCallback(() => {
    setNewGrowCrop("lettuce");
    setNewGrowSystem("nft");
    setNewGrowStartsIn("nursery");
    setNewGrowNurseryDays(7);
    setNewGrowDate(isoToday());
    setNewGrowTransplantDate("");
    setNewGrowQty("");
    setNewGrowLabel("");
    setNewGrowNotes("");
    setEditingGrowId(null);
    setShowNewGrowForm(false);
  }, []);

  // Create a new grow
  const createGrow = useCallback(() => {
    const cropDef = CROPS[newGrowCrop];
    const initialStage = cropDef ? suggestStage(cropDef, 0) : 0;
    const startsInNursery = newGrowStartsIn === "nursery" && sysSettings.hasNursery !== false;
    // An explicit transplant date only applies to a nursery grow; a direct grow's
    // "planted on" date is already its system-entry date.
    const transplantedAt = startsInNursery ? dateInputToIso(newGrowTransplantDate) : null;
    if (transplantedAt && newGrowTransplantDate < newGrowDate) {
      showToast("Transplant date can't be before the sow date");
      return;
    }
    // Compute target transplant date if going through nursery.
    // Total = humidity tray phase + nursery channel phase, counting from sowing date.
    const transplantTargetDate = startsInNursery
      ? (() => {
          const d = new Date(newGrowDate);
          const nurseryD = Math.max(1, parseInt(newGrowNurseryDays) || 7);
          d.setDate(d.getDate() + HUMIDITY_TRAY_DAYS + nurseryD);
          return d.toISOString().slice(0, 10);
        })()
      : null;
    // A nursery grow with a transplant date is already in its system; otherwise
    // it's still in the nursery. Direct grows go straight to active.
    const alreadyTransplanted = startsInNursery && !!transplantedAt;
    const inNursery = startsInNursery && !transplantedAt;
    const newGrow = {
      id: `grow_${Date.now()}`,
      crop: newGrowCrop,
      system: inNursery ? "nursery" : newGrowSystem,          // current physical location
      destinationSystem: newGrowSystem,                          // where it'll go after nursery
      phase: inNursery ? "nursery" : "active",                   // "nursery" | "active"
      transplantTargetDate,                                       // ISO date or null
      transplantedAt,                                             // set if transplanted (or stated)
      nurseryDays: startsInNursery ? (parseInt(newGrowNurseryDays) || 7) : null,
      plantingDate: newGrowDate,
      quantity: newGrowQty ? parseInt(newGrowQty) : null,
      label: newGrowLabel.trim() || null,
      notes: newGrowNotes.trim() || null,
      status: "active",
      ackedStageIdx: initialStage,
      createdAt: new Date().toISOString(),
    };
    saveGrows([newGrow, ...grows]);
    setActiveGrowId(newGrow.id);
    resetGrowForm();
    // Sync calculator to the new grow's crop
    setCropKey(newGrow.crop);
    showToast(`Started ${newGrow.label || cropDef?.name || "grow"}${alreadyTransplanted ? " (in system)" : ""}`);
  }, [newGrowCrop, newGrowSystem, newGrowStartsIn, newGrowNurseryDays, newGrowDate, newGrowTransplantDate, newGrowQty, newGrowLabel, newGrowNotes, grows, saveGrows, sysSettings.hasNursery, resetGrowForm, showToast]);

  // Open the shared grow form pre-filled to edit an existing grow.
  const openEditGrow = useCallback((grow) => {
    setEditingGrowId(grow.id);
    setNewGrowCrop(grow.crop);
    setNewGrowSystem(grow.destinationSystem || grow.system || "nft");
    setNewGrowStartsIn(grow.nurseryDays != null ? "nursery" : "direct");
    setNewGrowNurseryDays(grow.nurseryDays || NURSERY_DURATIONS[grow.crop] || 7);
    setNewGrowDate(grow.plantingDate);
    setNewGrowTransplantDate(isoToDateInput(grow.transplantedAt));
    setNewGrowQty(grow.quantity != null ? String(grow.quantity) : "");
    setNewGrowLabel(grow.label || "");
    setNewGrowNotes(grow.notes || "");
    setShowNewGrowForm(true);
    // The form lives at the top of the Grows tab; bring it into view when
    // editing a grow further down the list.
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // Save edits back onto the existing grow, in place.
  const saveGrowEdits = useCallback(() => {
    if (!editingGrowId) return;
    const startsInNursery = newGrowStartsIn === "nursery" && sysSettings.hasNursery !== false;
    const nurseryDays = startsInNursery ? (parseInt(newGrowNurseryDays) || 7) : null;
    // Transplant date is only meaningful for a nursery grow. Setting it means the
    // grow is in its system; clearing it means it's (back) in the nursery.
    const transplantedAt = startsInNursery ? dateInputToIso(newGrowTransplantDate) : null;
    if (transplantedAt && newGrowTransplantDate < newGrowDate) {
      showToast("Transplant date can't be before the sow date");
      return;
    }
    const inNursery = startsInNursery && !transplantedAt;
    saveGrows(grows.map((g) => {
      if (g.id !== editingGrowId) return g;
      // Re-derive the planned transplant target from the (possibly edited) sow
      // date while the grow is still in the nursery; once it's in a system the
      // harvest estimate re-anchors off transplantedAt in growTimeline().
      const transplantTargetDate = inNursery
        ? (() => {
            const d = new Date(newGrowDate);
            d.setDate(d.getDate() + HUMIDITY_TRAY_DAYS + nurseryDays);
            return d.toISOString().slice(0, 10);
          })()
        : g.transplantTargetDate;
      return {
        ...g,
        crop: newGrowCrop,
        destinationSystem: newGrowSystem,
        phase: inNursery ? "nursery" : "active",
        // In the nursery → location is the nursery; in a system → the chosen one.
        system: inNursery ? "nursery" : newGrowSystem,
        plantingDate: newGrowDate,
        transplantedAt,
        quantity: newGrowQty ? parseInt(newGrowQty) : null,
        label: newGrowLabel.trim() || null,
        notes: newGrowNotes.trim() || null,
        nurseryDays,
        transplantTargetDate,
      };
    }));
    if (editingGrowId === activeGrowId) setCropKey(newGrowCrop);
    const label = newGrowLabel.trim() || CROPS[newGrowCrop]?.name || "grow";
    resetGrowForm();
    showToast(`Updated ${label}`);
  }, [editingGrowId, newGrowStartsIn, newGrowNurseryDays, newGrowDate, newGrowTransplantDate, newGrowCrop, newGrowSystem, newGrowQty, newGrowLabel, newGrowNotes, grows, saveGrows, sysSettings.hasNursery, activeGrowId, resetGrowForm, showToast]);

  // Transplant a grow from nursery to its destination system
  const transplantGrow = useCallback((growId) => {
    const grow = grows.find(g => g.id === growId);
    if (!grow || grow.phase !== "nursery") return;
    const transplantedAt = new Date().toISOString();
    saveGrows(grows.map(g => g.id === growId ? {
      ...g,
      system: g.destinationSystem || "nft",
      phase: "active",
      transplantedAt,
    } : g));
    // Also create a transplant log entry
    const transplantLog = {
      id: Date.now(),
      type: "transplant",
      value: `${CROPS[grow.crop]?.name || grow.crop} → ${grow.destinationSystem || "nft"}`,
      notes: transplantNotes.trim() || `Transplanted from nursery after ${daysSince(grow.plantingDate)} days`,
      timestamp: transplantedAt,
      growId: grow.id,
    };
    saveLogs([transplantLog, ...logs]);
    setTransplantingGrowId(null);
    setTransplantNotes("");
  }, [grows, logs, saveGrows, saveLogs, transplantNotes]);

  // Harvest/close a grow
  const closeGrow = useCallback(() => {
    if (!harvestingGrowId) return;
    const grow = grows.find(g => g.id === harvestingGrowId);
    if (!grow) return;
    const updated = grows.map(g => g.id === harvestingGrowId ? {
      ...g,
      status: harvestStatus,
      harvestDate: new Date().toISOString(),
      harvestWeight: harvestWeight ? parseFloat(harvestWeight) : null,
      harvestUnit,
      harvestPrice: harvestPrice ? parseFloat(harvestPrice) : null,
      harvestNotes: harvestNotes.trim() || null,
    } : g);
    saveGrows(updated);

    // Also create a harvest event in logs for visibility
    if (harvestStatus === "harvested" && harvestWeight) {
      const cropDef = CROPS[grow.crop];
      const harvestLog = {
        id: Date.now(),
        type: "harvest",
        value: `${harvestWeight} ${harvestUnit}`,
        notes: harvestNotes.trim() || null,
        timestamp: new Date().toISOString(),
        crop: cropDef?.name || grow.crop,
        stage: "Harvest",
        growId: grow.id,
        harvestPrice: harvestPrice ? parseFloat(harvestPrice) : null,
      };
      saveLogs([harvestLog, ...logs]);
    }

    // If closing the active grow, deselect it
    if (activeGrowId === harvestingGrowId) setActiveGrowId(null);
    // Reset harvest form
    setHarvestingGrowId(null);
    setHarvestWeight("");
    setHarvestUnit("kg");
    setHarvestPrice("");
    setHarvestNotes("");
    setHarvestStatus("harvested");
  }, [harvestingGrowId, grows, harvestStatus, harvestWeight, harvestUnit, harvestPrice, harvestNotes, saveGrows, activeGrowId, saveLogs, logs]);

  // Delete a grow (permanent)
  const deleteGrow = useCallback((growId) => {
    if (!confirm("Delete this grow record permanently? Harvest data will be lost.")) return;
    saveGrows(grows.filter(g => g.id !== growId));
    if (activeGrowId === growId) setActiveGrowId(null);
  }, [grows, saveGrows, activeGrowId]);

  // Acknowledge a stage transition — updates the grow's last-seen stage
  const acknowledgeStage = useCallback((growId, stageIdx) => {
    saveGrows(grows.map(g => g.id === growId ? { ...g, ackedStageIdx: stageIdx } : g));
  }, [grows, saveGrows]);

  // Detect stage transitions: returns array of { grow, from, to } for unacknowledged transitions
  const stageTransitions = useMemo(() => {
    return grows
      .filter(g => g.status === "active")
      .map(g => {
        const c = CROPS[g.crop];
        if (!c) return null;
        const days = daysSince(g.plantingDate);
        const currentIdx = suggestStage(c, days);
        const ackedIdx = g.ackedStageIdx ?? 0;
        if (currentIdx > ackedIdx) {
          return {
            grow: g,
            crop: c,
            fromStage: c.stages[ackedIdx],
            toStage: c.stages[currentIdx],
            currentIdx, ackedIdx,
            daysIntoNewStage: days - Math.floor((c.daysToHarvest || 30) * 0.6 * ackedIdx / Math.max(1, c.stages.length - 1)),
          };
        }
        return null;
      })
      .filter(Boolean);
  }, [grows]);

  // Form validation
  const currentEventType = EVENT_TYPES.find(e => e.key === logType);
  const canSubmit = !currentEventType.needsValue || (
    logType === "harvest"
      ? logValue.trim() !== ""
      : (logValue.trim() !== "" && !isNaN(parseFloat(logValue)))
  );

  const addLog = useCallback(() => {
    // Consolidated Reservoir Check: writes individual records (so trends/history
    // stay per-metric) from one entry pass, plus a water_change if it's a fresh mix.
    if (logType === "reservoir_check") {
      const ts = new Date().toISOString();
      const base = { crop: crop.name, stage: stage.label };
      if (activeGrowId) base.growId = activeGrowId;
      const batch = [];
      let idc = Date.now();
      const push = (type, value, notes) => batch.push({ id: idc++, type, value: String(value), notes: notes || "", timestamp: ts, ...base });
      if (rcEc !== "" && Number.isFinite(parseFloat(rcEc))) push("ec_reading", rcEc, logNotes);
      if (rcPh !== "" && Number.isFinite(parseFloat(rcPh))) push("ph_reading", rcPh);
      if (rcTemp !== "" && Number.isFinite(parseFloat(rcTemp))) push("water_temp", rcTemp);
      if (rcFreshChange) push("water_change", rcEc !== "" ? rcEc : "0", "Fresh reservoir mix");
      if (batch.length === 0) return;
      const next = [...batch.reverse(), ...logs];
      saveLogs(next);
      setRcEc(""); setRcPh(""); setRcTemp(""); setRcFreshChange(false); setLogNotes("");
      if (rcFreshChange) setDismissedReservoirReminder(false); // re-arm reminder off the new change
      showToast(batch.length > 1 ? `Logged ${batch.length} readings` : "Reading logged");
      if (next.length >= 500 && logs.length < 500) {
        setStorageError("You have 500+ log entries. Consider exporting older logs to keep the app fast and avoid storage limits.");
      }
      return;
    }
    if (!canSubmit) return;
    const entry = {
      id: Date.now(), type: logType, value: logValue, notes: logNotes,
      timestamp: new Date().toISOString(),
      crop: crop.name, stage: stage.label,
    };
    if (activeGrowId) entry.growId = activeGrowId;
    if (logType === "nutrient_add") {
      entry.calcState = { crop: crop.name, stage: stage.label, targetEc: effTarget, gallons };
      entry.amounts = results.map(r => ({ label: r.short, grams: r.totalGrams.toFixed(1) }));
    }
    if (logType === "water_change") setDismissedReservoirReminder(false); // re-arm reminder
    const next = [entry, ...logs];
    saveLogs(next);
    setLogValue(""); setLogNotes("");
    showToast(`${(EVENT_TYPES.find(et => et.key === logType)?.label) || "Event"} logged`);
    // Soft warning as the log set grows large — encourage periodic export
    if (next.length === 500) {
      setStorageError("You have 500+ log entries. Consider exporting older logs to keep the app fast and avoid storage limits.");
    }
  }, [canSubmit, logType, logValue, logNotes, logs, saveLogs, crop, stage, effTarget, gallons, results, activeGrowId, rcEc, rcPh, rcTemp, rcFreshChange, showToast]);

  // Filtered crop list based on search + restricted-crop visibility
  const visibleCropKeys = CROP_KEYS;
  const filteredCropKeys = useMemo(() => {
    const q = cropSearch.trim().toLowerCase();
    if (!q) return visibleCropKeys;
    return visibleCropKeys.filter(k => {
      const c = CROPS[k];
      return c.name.toLowerCase().includes(q) || c.notes.toLowerCase().includes(q);
    });
  }, [cropSearch, visibleCropKeys]);

  // Memoized log derivations
  const lastEcReading = useMemo(() => logs.find(l => l.type === "ec_reading"), [logs]);
  const lastWaterTemp = useMemo(() => {
    const l = logs.find(x => x.type === "water_temp" && x.value != null && x.value !== "");
    return l ? parseFloat(l.value) : null;
  }, [logs]);
  const lastPhReading = useMemo(() => logs.find(l => l.type === "ph_reading"), [logs]);

  // Real active alerts — drives the "Active Alerts" stat tile (not just storage).
  const activeAlerts = useMemo(() => {
    const a = [];
    if (storageError) a.push({ key: "storage", label: "Storage issue", tone: "#ef5350" });
    if (lastWaterTemp != null && lastWaterTemp > 28) a.push({ key: "warm", label: `Water ${lastWaterTemp}°C`, tone: "#f0a820" });
    if (lastPhReading && parseFloat(lastPhReading.value) > 6.2) a.push({ key: "ph", label: `pH ${parseFloat(lastPhReading.value)} high`, tone: "#f0a820" });
    const fMax = (userLocation.showWeather && weather && Array.isArray(weather.daily) && weather.daily.length)
      ? Math.max(...weather.daily.map(dd => dd.tmax).filter(x => x != null)) : null;
    if (fMax != null && fMax >= 33) a.push({ key: "heat", label: `Heat ${Math.round(fMax)}°C ahead`, tone: "#f0a820" });
    return a;
  }, [storageError, lastWaterTemp, lastPhReading, userLocation.showWeather, weather]);

  // Collapsible Settings sections (all closed by default → a clean section list).
  const [settingsOpen, setSettingsOpen] = useState({});
  const toggleSettings = (id) => setSettingsOpen(s => ({ ...s, [id]: !s[id] }));
  // Mixing procedure is collapsed by default on mobile (reduces calculator density).
  const [showMixing, setShowMixing] = useState(false);

  // Reservoir refresh tracking. Solution goes stale (nutrient imbalance + pH drift)
  // after the configured interval. We derive days since the last full water change
  // from logs — no new grow field needed, so existing grows work too.
  const reservoirStatus = useMemo(() => {
    const intervalDays = sysSettings.reservoirChangeDays ?? 10;
    const lastChange = logs.find(l => l.type === "water_change");
    if (!lastChange) return { hasHistory: false, intervalDays };
    const changed = new Date(lastChange.timestamp);
    const daysSince = Math.floor((Date.now() - changed.getTime()) / 86400000);
    const daysLeft = intervalDays - daysSince;
    return {
      hasHistory: true,
      intervalDays,
      daysSince,
      daysLeft,
      lastChangedISO: lastChange.timestamp,
      due: daysLeft <= 0,
      soon: daysLeft > 0 && daysLeft <= 2,
    };
  }, [logs, sysSettings.reservoirChangeDays]);

  // History view state — switch between trends and the event log
  const [historyView, setHistoryView] = useState("trends"); // "trends" | "events"
  const [trendRange, setTrendRange] = useState(30); // days
  const [trendGrowFilter, setTrendGrowFilter] = useState("all"); // "all" or growId

  // Extract numeric series from logs for trend charts
  const trendSeries = useMemo(() => {
    const now = Date.now();
    const cutoff = now - trendRange * 86400000;
    const inRange = logs.filter(l => {
      const ts = new Date(l.timestamp).getTime();
      if (ts < cutoff) return false;
      if (trendGrowFilter !== "all" && l.growId !== trendGrowFilter) return false;
      return true;
    });
    const pick = (type) => inRange
      .filter(l => l.type === type && l.value !== "" && l.value != null && Number.isFinite(parseFloat(l.value)))
      .map(l => ({ timestamp: l.timestamp, value: parseFloat(l.value) }))
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    return {
      ec: pick("ec_reading"),
      ph: pick("ph_reading"),
      temp: pick("temp_reading"),
    };
  }, [logs, trendRange, trendGrowFilter]);
  // Plain-language interpretation of the trends — turns charts into decision support.
  const trendSummary = useMemo(() => {
    const out = [];
    const fmt = (n) => n.toFixed(2);
    const { ec, ph } = trendSeries;
    if (ec.length >= 2) {
      const d = ec[ec.length - 1].value - ec[0].value;
      if (Math.abs(d) < 0.1) out.push({ tone: "#78c854", text: `EC trending stable (${fmt(ec[0].value)} → ${fmt(ec[ec.length - 1].value)}).` });
      else out.push({ tone: d > 0 ? "#ffd54f" : "#64b5f6", text: `EC ${d > 0 ? "rising" : "falling"} (${fmt(ec[0].value)} → ${fmt(ec[ec.length - 1].value)}). ${d > 0 ? "If above target, top up with plain water." : "Nutrients depleting — top up feed toward target."}` });
    }
    if (ph.length >= 2) {
      const d = ph[ph.length - 1].value - ph[0].value;
      if (Math.abs(d) < 0.15) out.push({ tone: "#78c854", text: `pH stable (${fmt(ph[0].value)} → ${fmt(ph[ph.length - 1].value)}).` });
      else out.push({ tone: "#f0a820", text: `pH drifting ${d > 0 ? "up" : "down"} (${fmt(ph[0].value)} → ${fmt(ph[ph.length - 1].value)}). ${d > 0 ? "Common as nutrients deplete — bring it back down and retest." : "Bring it back up and retest."}` });
    }
    const cutoff = Date.now() - trendRange * 86400000;
    const warm = logs.filter(l => l.type === "water_temp" && Number.isFinite(parseFloat(l.value)) && parseFloat(l.value) > 28 && new Date(l.timestamp).getTime() >= cutoff && (trendGrowFilter === "all" || l.growId === trendGrowFilter)).length;
    if (warm > 0) out.push({ tone: "#ef5350", text: `Water temp above 28 °C ${warm} time${warm > 1 ? "s" : ""} in this range. Warm water holds less oxygen and stresses roots — add shade/cooling and ease the EC target.` });
    return out;
  }, [trendSeries, logs, trendRange, trendGrowFilter]);
  const groupedLogs = useMemo(() => {
    const g = {};
    logs.forEach(l => {
      const dk = new Date(l.timestamp).toLocaleDateString("en-JM", { weekday: "long", year: "numeric", month: "short", day: "numeric" });
      if (!g[dk]) g[dk] = [];
      g[dk].push(l);
    });
    return Object.entries(g);
  }, [logs]);

  // Pump schedule computation (must come before grow economics)
  const autoSeason = useMemo(() => getCurrentSeason(), []);
  const activeSeason = seasonOverride || autoSeason;
  const pumpEvents = useMemo(() => generatePumpEvents(pumpSystem, activeSeason), [pumpSystem, activeSeason]);
  const { current: currentPumpEvent, next: nextPumpEvent } = useMemo(
    () => findCurrentAndNextEvent(pumpEvents),
    [pumpEvents, tick] // eslint-disable-line react-hooks/exhaustive-deps
  );
  // Per-system now/next + daily runtime for the "Pump Status — Now" card.
  const pumpNowNext = useMemo(() => {
    return enabledSystems.map(sys => {
      const events = generatePumpEvents(sys, activeSeason);
      const { current, next } = findCurrentAndNextEvent(events);
      const runMin = events.reduce((s, e) => s + e.onMin, 0);
      return { sys, meta: PUMP_META[sys], current, next, runMin, cycles: events.length };
    });
  }, [activeSeason, hasNft, hasTower, sysSettings.hasNursery, tick]); // eslint-disable-line react-hooks/exhaustive-deps

  // Power cost computation
  const powerCost = useMemo(() => {
    const hasNursery = sysSettings.hasNursery !== false;
    const nftMin = hasNft ? PUMP_SCHEDULES.nft.schedules[activeSeason].cycles.reduce((s, c) => s + c.on, 0) : 0;
    const towerMin = hasTower ? PUMP_SCHEDULES.tower.schedules[activeSeason].cycles.reduce((s, c) => s + c.on, 0) : 0;
    const nurseryMin = hasNursery ? PUMP_SCHEDULES.nursery.schedules[activeSeason].cycles.reduce((s, c) => s + c.on, 0) : 0;
    const nftKwh = (nftMin / 60) * (nftWattage / 1000);
    const towerKwh = (towerMin / 60) * (towerWattage / 1000);
    const nurseryKwh = (nurseryMin / 60) * (nurseryWattage / 1000);
    const totalKwh = nftKwh + towerKwh + nurseryKwh;
    const nftCost = nftKwh * electricityRate;
    const towerCost = towerKwh * electricityRate;
    const nurseryCost = nurseryKwh * electricityRate;
    const totalCost = nftCost + towerCost + nurseryCost;
    return {
      nftMin, towerMin, nurseryMin,
      nftKwh, towerKwh, nurseryKwh, totalKwh,
      nftCost, towerCost, nurseryCost, totalCost,
      weekKwh: totalKwh * 7, weekCost: totalCost * 7,
      monthKwh: totalKwh * 30.4, monthCost: totalCost * 30.4,
    };
  }, [activeSeason, nftWattage, towerWattage, nurseryWattage, electricityRate, hasNft, hasTower, sysSettings.hasNursery]);

  // Memoized grow stats
  const activeGrows = useMemo(() => grows.filter(g => g.status === "active" && g.phase !== "nursery"), [grows]);
  const nurseryGrows = useMemo(() => grows.filter(g => g.status === "active" && g.phase === "nursery"), [grows]);
  const completedGrows = useMemo(() => grows.filter(g => g.status === "harvested"), [grows]);
  const failedGrows = useMemo(() => grows.filter(g => g.status === "failed"), [grows]);
  const activeGrow = useMemo(() => grows.find(g => g.id === activeGrowId), [grows, activeGrowId]);

  // Auto-enable nursery mode when active grow is in nursery phase, off when it's not.
  // User can still manually toggle after this fires.
  useEffect(() => {
    if (activeGrow && activeGrow.phase === "nursery" && NURSERY_TARGETS[activeGrow.crop]) {
      setNurseryMode(true);
    } else if (activeGrow && activeGrow.phase === "active") {
      setNurseryMode(false);
    }
  }, [activeGrow?.id, activeGrow?.phase]);

  // Detect if the Calculator's selected stage differs from what the active grow's days suggest.
  // Suppressed during nursery phase — calculator overrides with NURSERY_TARGETS in that mode.
  const activeGrowStageMismatch = useMemo(() => {
    if (!activeGrow || mixMode === "mixed") return null;
    if (activeGrow.phase === "nursery") return null;
    const c = CROPS[activeGrow.crop];
    if (!c || c !== crop) return null; // only flag mismatch when calculator is on the active grow's crop
    const days = daysSince(activeGrow.plantingDate);
    const suggested = suggestStage(c, days);
    if (suggested === safeStageIdx) return null;
    return { suggested, current: safeStageIdx, suggestedStage: c.stages[suggested] };
  }, [activeGrow, crop, safeStageIdx, mixMode]);

  // Calculate grow economics
  const calculateGrowEconomics = useCallback((grow) => {
    if (!grow) return null;
    const start = new Date(grow.plantingDate);
    const end = grow.harvestDate ? new Date(grow.harvestDate) : new Date();
    const daysActive = Math.max(1, Math.floor((end - start) / (1000 * 60 * 60 * 24)));

    const cropDef = CROPS[grow.crop];
    const cropName = cropDef?.name || grow.crop;
    const relevantLogs = logs.filter(log => {
      const ts = new Date(log.timestamp);
      return ts >= start && ts <= end && (log.growId === grow.id || (!log.growId && log.crop === cropName));
    });

    const nutrientCosts = relevantLogs
      .filter(l => l.type === "nutrient_add" && Array.isArray(l.amounts))
      .reduce((acc, l) => {
        l.amounts.forEach(a => {
          if (!a || a.label == null) return;
          const g = parseFloat(a.grams);
          if (!Number.isFinite(g)) return; // skip missing/garbage values
          acc[a.label] = (acc[a.label] || 0) + g;
        });
        return acc;
      }, {});

    const dailyKwh = grow.system === "nft"
      ? ((PUMP_SCHEDULES.nft.schedules[autoSeason].cycles.reduce((s, c) => s + c.on, 0) / 60) * (nftWattage / 1000))
      : grow.system === "tower"
      ? ((PUMP_SCHEDULES.tower.schedules[autoSeason].cycles.reduce((s, c) => s + c.on, 0) / 60) * (towerWattage / 1000))
      : 0;
    const concurrentGrows = grows.filter(g =>
      g.system === grow.system && g.status === "active" &&
      new Date(g.plantingDate) <= end &&
      (!g.harvestDate || new Date(g.harvestDate) >= start)
    ).length || 1;
    const powerCostJmd = (dailyKwh * daysActive * electricityRate) / concurrentGrows;

    const revenue = grow.harvestPrice || 0;
    const profit = revenue - powerCostJmd;

    return {
      daysActive,
      nutrientCosts,
      totalNutrientGrams: Object.values(nutrientCosts).reduce((s, v) => s + v, 0),
      powerCostJmd,
      revenue,
      profit,
      logCount: relevantLogs.length,
    };
  }, [logs, autoSeason, nftWattage, towerWattage, electricityRate, grows]);

  // Sync calculator to active grow
  const switchToGrow = useCallback((growId) => {
    const g = grows.find(gr => gr.id === growId);
    if (!g) return;
    setActiveGrowId(growId);
    setCropKey(g.crop);
    const days = daysSince(g.plantingDate);
    const cropDef = CROPS[g.crop];
    if (cropDef) {
      setStageIdx(suggestStage(cropDef, days));
    }
  }, [grows]);

  const deleteLog = useCallback((id) => saveLogs(logs.filter(l => l.id !== id)), [logs, saveLogs]);
  const clearLogs = useCallback(() => { if (confirm("Clear all logs?")) saveLogs([]); }, [saveLogs]);
  const selectCrop = (key) => { setCropKey(key); setStageIdx(0); setCustomEc(null); };

  // ── Backup: export all data as a JSON file ──
  const exportData = useCallback(() => {
    const payload = {
      _format: "dwm-hydrotech-backup",
      _version: 1,
      _exportedAt: new Date().toISOString(),
      logs,
      grows,
      activeGrowId,
      systemSettings: sysSettings,
      userLocation,
      powerSettings: { nftWattage, towerWattage, nurseryWattage, electricityRate, actualNftKwh, actualTowerKwh },
    };
    try {
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `dwm-hydrotech-backup-${isoToday()}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      showToast("Backup downloaded");
    } catch (e) {
      setStorageError("Export failed — could not generate the backup file.");
      showToast("Export failed", "error");
    }
  }, [logs, grows, activeGrowId, sysSettings, nftWattage, towerWattage, electricityRate, actualNftKwh, actualTowerKwh, showToast]);

  // ── Backup: import data from a JSON file ──
  const importData = useCallback((e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data = JSON.parse(ev.target.result);
        if (data._format !== "dwm-hydrotech-backup") {
          setStorageError("Import failed — that file isn't a DWM HydroTech backup.");
          return;
        }
        if (!confirm("Import this backup? It will REPLACE all current logs, grows, and settings.")) return;
        if (Array.isArray(data.logs)) saveLogs(data.logs);
        if (Array.isArray(data.grows)) saveGrows(data.grows);
        if (data.activeGrowId !== undefined) setActiveGrowId(data.activeGrowId);
        if (data.systemSettings) setSysSettings({ ...SYSTEM_DEFAULTS, ...data.systemSettings });
        if (data.userLocation) setUserLocation({ ...LOCATION_DEFAULTS, ...data.userLocation });
        if (data.powerSettings) {
          const p = data.powerSettings;
          if (p.nftWattage != null) setNftWattage(p.nftWattage);
          if (p.towerWattage != null) setTowerWattage(p.towerWattage);
          if (p.nurseryWattage != null) setNurseryWattage(p.nurseryWattage);
          if (p.electricityRate != null) setElectricityRate(p.electricityRate);
          if (p.actualNftKwh != null) setActualNftKwh(p.actualNftKwh);
          if (p.actualTowerKwh != null) setActualTowerKwh(p.actualTowerKwh);
        }
        setStorageError(null);
        showToast("Backup imported");
      } catch (err) {
        setStorageError("Import failed — the file could not be read or is corrupted.");
        showToast("Import failed", "error");
      }
    };
    reader.readAsText(file);
    e.target.value = ""; // reset so the same file can be re-selected
  }, [saveLogs, saveGrows, showToast]);

  return (
    <div className="yg-shell" style={{
      minHeight: "100vh",
      background: "radial-gradient(circle at 18% 8%, rgba(140,255,63,0.08), transparent 30%), radial-gradient(circle at 80% 18%, rgba(34,191,255,0.05), transparent 28%), linear-gradient(180deg, var(--shell-a) 0%, var(--shell-b) 48%, var(--shell-c) 100%)",
      fontFamily: SYSTEM_FONT,
      fontSize: 16,
      color: "var(--text-main)",
      padding: isMobile ? "12px 12px 64px" : isTablet ? "18px 16px 72px" : "24px 20px 80px",
      boxSizing: "border-box",
      maxWidth: 1280,
      margin: "0 auto"
    }}>
      {/* Service-worker update banner — a new version is installed and waiting */}
      {needRefresh && (
        <div role="status" style={{ position: "fixed", left: "50%", transform: "translateX(-50%)",
          bottom: isMobile ? 72 : 20, zIndex: 450, width: "calc(100% - 28px)", maxWidth: 420,
          display: "flex", alignItems: "center", gap: 12, padding: "11px 14px", borderRadius: 12,
          background: "linear-gradient(180deg, #10240f 0%, var(--bg-card2) 100%)", border: "1px solid rgba(181,224,38,0.4)",
          boxShadow: "0 8px 32px rgba(0,0,0,0.45)" }}>
          <Icon name="bolt" size={18} color="var(--active)" />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text-strong)" }}>Update available</div>
            <div style={{ fontSize: 11, color: "var(--text-muted)", lineHeight: 1.4 }}>A new version of YaadGrow is ready.</div>
          </div>
          <button onClick={() => updateServiceWorker(true)}
            style={{ padding: "8px 14px", borderRadius: 9, fontSize: 12, fontWeight: 800, cursor: "pointer",
              border: "none", background: "linear-gradient(135deg,#2d6030,#5cb85c)", color: "var(--text-strong)", fontFamily: "inherit", flexShrink: 0 }}>
            Reload
          </button>
          <button onClick={() => setNeedRefresh(false)} aria-label="Dismiss update notice"
            style={{ padding: "6px 8px", background: "transparent", border: "none", color: "var(--text-soft)", fontSize: 15, cursor: "pointer", flexShrink: 0 }}>
            ✕
          </button>
        </div>
      )}

      {/* ═══════ FIRST-RUN SETUP WIZARD ═══════ */}
      {showSetup && (
        <div role="dialog" aria-modal="true" aria-label="Set up your farm"
          style={{ position: "fixed", inset: 0, zIndex: 500, overflowY: "auto",
            background: "radial-gradient(circle at 50% 0%, rgba(140,255,63,0.06), transparent 42%), linear-gradient(180deg, #051206 0%, var(--shell-b) 55%, var(--shell-c) 100%)",
            display: "flex", alignItems: "flex-start", justifyContent: "center",
            padding: isMobile ? "22px 14px 40px" : "48px 20px" }}>
          <div style={{ width: "100%", maxWidth: 460, background: "linear-gradient(180deg, var(--bg-card) 0%, var(--bg-card2) 100%)",
            border: "1px solid rgba(255,255,255,0.08)", borderRadius: 18, boxShadow: "0 12px 48px rgba(0,0,0,0.45)",
            padding: isMobile ? "20px 18px 18px" : "28px 26px 22px" }}>

            {/* Progress */}
            <div style={{ display: "flex", gap: 6, justifyContent: "center", marginBottom: 18 }}>
              {[1, 2, 3].map(n => (
                <div key={n} style={{ width: n === setupStep ? 22 : 7, height: 7, borderRadius: 4, transition: "all .2s",
                  background: n === setupStep ? "var(--active)" : n < setupStep ? "rgba(90,185,90,0.5)" : "rgba(255,255,255,0.12)" }} />
              ))}
            </div>

            {/* Step 1 — Welcome */}
            {setupStep === 1 && (
              <div style={{ textAlign: "center" }}>
                <div style={{ display: "inline-flex", alignItems: "center", marginBottom: 14 }}>
                  <span style={{ fontSize: 30, fontWeight: 900, color: "#f0c020", letterSpacing: -1.5, fontFamily: SYSTEM_FONT, lineHeight: 1 }}>Yaad</span>
                  <span style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
                    <span style={{ fontSize: 30, fontWeight: 900, color: "#5cb85c", letterSpacing: -1.5, fontFamily: SYSTEM_FONT, lineHeight: 1 }}>G</span>
                    <span style={{ position: "absolute", top: -11, left: 4, display: "flex" }}><Icon name="leaf" size={13} color="#5cb85c" strokeWidth={2.2} /></span>
                    <span style={{ fontSize: 30, fontWeight: 900, color: "#5cb85c", letterSpacing: -1.5, fontFamily: SYSTEM_FONT, lineHeight: 1 }}>row</span>
                  </span>
                </div>
                <div style={{ fontSize: 16, fontWeight: 800, color: "var(--text-strong)", marginBottom: 8 }}>Welcome — let's set up your farm</div>
                <div style={{ fontSize: 13, color: "var(--text-muted)", lineHeight: 1.6 }}>
                  A couple of quick questions so the calculator, pump schedules, and weather match your setup. Everything stays on this device, and you can change all of it later in Settings.
                </div>
              </div>
            )}

            {/* Step 2 — Location */}
            {setupStep === 2 && (
              <div>
                <div style={{ fontSize: 15, fontWeight: 800, color: "var(--text-strong)", marginBottom: 3 }}>Where's your farm?</div>
                <div style={{ fontSize: 12, color: "var(--text-soft)", marginBottom: 14, lineHeight: 1.5 }}>Drives local weather, climate-specific advice, and currency.</div>

                <div style={{ fontSize: 10, color: "var(--text-soft)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 5 }}>Farm location</div>
                <input type="text" value={userLocation.name} onChange={e => updateLocation("name", e.target.value)}
                  placeholder="e.g. Portmore, Jamaica" aria-label="Farm location name"
                  style={{ width: "100%", padding: "9px 12px", borderRadius: 8, fontSize: 13, fontWeight: 600, border: "1px solid var(--border-soft)", background: "rgba(0,0,0,0.3)", color: "var(--text-main)", outline: "none", boxSizing: "border-box", fontFamily: "inherit" }} />
                <button onClick={detectLocation} disabled={geoStatus === "locating"}
                  aria-label="Detect my location to set the farm location"
                  style={{ marginTop: 7, display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 12px", borderRadius: 8, fontSize: 12, fontWeight: 700, border: "1px solid rgba(181,224,38,0.3)", background: "rgba(181,224,38,0.08)", color: "var(--active)", cursor: geoStatus === "locating" ? "default" : "pointer", opacity: geoStatus === "locating" ? 0.7 : 1, fontFamily: "inherit" }}>
                  📍 {geoStatus === "locating" ? "Locating…" : "Use my current location"}
                </button>
                {geoStatus === "confirm" && geoPending && (
                  <div style={{ marginTop: 8, padding: "10px 12px", borderRadius: 9, background: "rgba(181,224,38,0.06)", border: "1px solid rgba(181,224,38,0.25)" }}>
                    <div style={{ fontSize: 11.5, color: "var(--text-soft2)", lineHeight: 1.5, marginBottom: 8 }}>
                      Set the farm location to <strong style={{ color: "var(--text-strong)" }}>{geoPending.name}</strong>? Only if you're at the farm.
                      {geoPending.currency && geoPending.currency !== userLocation.currency && (
                        <> Costs will switch to <strong style={{ color: "var(--text-strong)" }}>
                          {(CURRENCIES[geoPending.currency] || {}).name || geoPending.currency}
                        </strong>.</>
                      )}
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button onClick={confirmDetectedLocation} style={{ flex: 1, padding: "7px 10px", borderRadius: 8, fontSize: 12, fontWeight: 800, cursor: "pointer", border: "none", background: "linear-gradient(135deg,#2d6030,#5cb85c)", color: "var(--text-strong)", fontFamily: "inherit" }}>Use it</button>
                      <button onClick={cancelDetectedLocation} style={{ padding: "7px 12px", borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: "pointer", border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.03)", color: "var(--text-muted)", fontFamily: "inherit" }}>Cancel</button>
                    </div>
                  </div>
                )}
                {(geoStatus === "denied" || geoStatus === "unavailable") && (
                  <div style={{ fontSize: 10.5, color: "var(--text-soft)", marginTop: 5 }}>Couldn't get a location — type your farm above instead.</div>
                )}

                <div style={{ fontSize: 10, color: "var(--text-soft)", textTransform: "uppercase", letterSpacing: 1, margin: "14px 0 5px" }}>Climate</div>
                <select value={userLocation.climateType} onChange={e => updateLocation("climateType", e.target.value)} aria-label="Climate type"
                  style={{ width: "100%", padding: "9px 12px", borderRadius: 8, fontSize: 13, fontWeight: 600, border: "1px solid var(--border-soft)", background: "rgba(0,0,0,0.3)", color: "var(--text-main)", outline: "none", boxSizing: "border-box", fontFamily: "inherit", cursor: "pointer" }}>
                  {Object.entries(CLIMATE_TYPES).map(([k, c]) => <option key={k} value={k}>{c.label}</option>)}
                </select>

                <div style={{ fontSize: 10, color: "var(--text-soft)", textTransform: "uppercase", letterSpacing: 1, margin: "14px 0 5px" }}>Currency</div>
                <select value={userLocation.currency} onChange={e => updateLocation("currency", e.target.value)} aria-label="Currency"
                  style={{ width: "100%", padding: "9px 12px", borderRadius: 8, fontSize: 13, fontWeight: 600, border: "1px solid var(--border-soft)", background: "rgba(0,0,0,0.3)", color: "var(--text-main)", outline: "none", boxSizing: "border-box", fontFamily: "inherit", cursor: "pointer" }}>
                  {Object.entries(CURRENCIES).map(([k, c]) => <option key={k} value={k}>{c.symbol} — {c.name} ({c.code})</option>)}
                </select>
              </div>
            )}

            {/* Step 3 — Systems */}
            {setupStep === 3 && (
              <div>
                <div style={{ fontSize: 15, fontWeight: 800, color: "var(--text-strong)", marginBottom: 3 }}>What are you growing with?</div>
                <div style={{ fontSize: 12, color: "var(--text-soft)", marginBottom: 14, lineHeight: 1.5 }}>Turn off anything you don't run — it's removed from schedules, power, and stats.</div>
                {[
                  { key: "hasNft", label: "NFT", color: "#90caf9", on: hasNft, countKey: "nftChannels", countLabel: "Channels", min: 1, max: 50 },
                  { key: "hasTower", label: "Tower", color: "#ce93d8", on: hasTower, countKey: "towerCount", countLabel: "Towers", min: 1, max: 50 },
                  { key: "hasNursery", label: "Nursery", color: "#a5d6a7", on: sysSettings.hasNursery !== false, countKey: "nurseryGal", countLabel: "Reservoir (gal)", min: 1, max: 100 },
                ].map(sys => (
                  <div key={sys.key} style={{ marginBottom: 10, padding: "12px 14px", borderRadius: 11, border: `1px solid ${sys.on ? sys.color + "55" : "rgba(255,255,255,0.08)"}`, background: sys.on ? sys.color + "12" : "rgba(255,255,255,0.02)" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: sys.on ? "var(--text-strong)" : "var(--text-muted)" }}>{sys.label} system</div>
                      <button onClick={() => updateSysSetting(sys.key, !sys.on)} aria-label={`Toggle ${sys.label} system`}
                        style={{ padding: "4px 12px", borderRadius: 14, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                          border: `1px solid ${sys.on ? sys.color + "88" : "rgba(255,255,255,0.15)"}`, background: sys.on ? sys.color + "22" : "rgba(255,255,255,0.03)", color: sys.on ? sys.color : "var(--text-soft)" }}>
                        {sys.on ? "✓ On" : "○ Off"}
                      </button>
                    </div>
                    {sys.on && (
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 10 }}>
                        <span style={{ fontSize: 11, color: "var(--text-soft)" }}>{sys.countLabel}</span>
                        <input type="number" min={sys.min} max={sys.max} value={sysSettings[sys.countKey] ?? SYSTEM_DEFAULTS[sys.countKey]}
                          onChange={e => updateSysSetting(sys.countKey, Math.max(sys.min, Math.min(sys.max, parseInt(e.target.value) || sys.min)))}
                          aria-label={`${sys.label} ${sys.countLabel}`}
                          style={{ width: 84, padding: "7px 10px", borderRadius: 8, fontSize: 14, fontWeight: 700, textAlign: "center", border: "1px solid var(--border-soft)", background: "rgba(0,0,0,0.3)", color: "var(--text-main)", outline: "none", boxSizing: "border-box", fontVariantNumeric: "tabular-nums", fontFamily: "inherit" }} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Footer nav */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 20 }}>
              <button onClick={finishSetup} aria-label="Skip setup"
                style={{ padding: "9px 6px", background: "none", border: "none", color: "var(--text-soft)", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", textDecoration: "underline" }}>
                Skip
              </button>
              <div style={{ display: "flex", gap: 8 }}>
                {setupStep > 1 && (
                  <button onClick={() => setSetupStep(s => Math.max(1, s - 1))}
                    style={{ padding: "10px 14px", borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: "pointer", border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.03)", color: "var(--text-muted)", fontFamily: "inherit" }}>‹ Back</button>
                )}
                {setupStep < 3 ? (
                  <button onClick={() => setSetupStep(s => Math.min(3, s + 1))}
                    style={{ padding: "10px 16px", borderRadius: 10, fontSize: 13, fontWeight: 800, cursor: "pointer", border: "none", background: "linear-gradient(135deg,#2d6030,#5cb85c)", color: "var(--text-strong)", fontFamily: "inherit" }}>
                    {setupStep === 1 ? "Get started ›" : "Next ›"}
                  </button>
                ) : (
                  <button onClick={finishSetup}
                    style={{ padding: "10px 16px", borderRadius: 10, fontSize: 13, fontWeight: 800, cursor: "pointer", border: "none", background: "linear-gradient(135deg,#2d6030,#5cb85c)", color: "var(--text-strong)", fontFamily: "inherit" }}>
                    Finish setup ✓
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        /* Typography uses the platform's native UI font stack (SYSTEM_FONT) — no web font is downloaded. */
        :root {
          /* Dark theme (default). Every dark value equals the colour it replaced,
             so tokenising leaves the dark UI pixel-identical; the light overrides
             below only apply under [data-theme="light"]. Accent colours
             (lime/green/blue/amber/red/purple, crop & system colours) are
             theme-agnostic and intentionally not overridden. */
          --bg-main: #080f08; --bg-card: #0d2010; --bg-card2: #0a1a0d;
          --bg-inset: #0d2818; --panel-a: #162e18; --panel-b: #0f2212;
          --shell-a: #031006; --shell-b: #06180d; --shell-c: #020b05;
          --border: rgba(255,255,255,0.07); --border-soft: rgba(255,255,255,0.1); --border-active: rgba(90,185,90,0.45);
          --text-main: #e8f5e8; --text-bright: #f2fff2; --text-soft2: #c8e8c8; --text-muted: #9bb89b; --text-soft: #7fa87f;
          --text-strong: #fff;
          --active: #b5e026; --active-soft: rgba(181,224,38,0.15);
          --yellow: #f0c020; --green: #5cb85c; --lime: #b5e026;
          --blue: #4da8da; --amber: #f0a020; --red: #e05050; --purple: #9b69d0;
          --badge-green-bg: #1a3d1a; --badge-green: #78e05a;
          --badge-blue-bg: #0f2a3d; --badge-blue: #5ab8e8;
          --badge-amber-bg: #2d1e00; --badge-amber: #f0a820;
        }
        :root[data-theme="light"] {
          --bg-main: #eef2ec; --bg-card: #ffffff; --bg-card2: #f5f8f4;
          --bg-inset: #edf3ea; --panel-a: #eef3ec; --panel-b: #f1f6ef;
          --shell-a: #eef3ea; --shell-b: #f6f9f4; --shell-c: #e9efe6;
          --border: rgba(20,45,20,0.12); --border-soft: rgba(20,45,20,0.18);
          --text-main: #16261a; --text-bright: #0c1810; --text-soft2: #24371f;
          --text-muted: #3f5a3f; --text-soft: #587657; --text-strong: #0c1810;
          /* Lime reads poorly as text on white, so on light it becomes a darker
             leaf-green; it stays the brand lime on dark. */
          --active: #3f6d12; --active-soft: rgba(120,175,20,0.16);
        }
        button { font-family: inherit; }
        button:focus-visible, input:focus-visible, textarea:focus-visible, select:focus-visible {
          outline: 2px solid var(--active); outline-offset: 2px;
          box-shadow: 0 0 0 4px rgba(181,224,38,0.18);
        }
        input[type="range"] { accent-color: var(--active); cursor: pointer; }
        button:disabled { cursor: not-allowed; opacity: 0.45; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
        .yg-panel { animation: fadeIn 0.22s ease both; }
        .yg-attrib span:last-child { transition: color .15s ease; }
        .yg-attrib:hover span:last-child, .yg-attrib:focus-visible span:last-child { color: var(--text-soft2); }
        @media (prefers-reduced-motion: reduce) { * { transition: none !important; animation: none !important; } }
        @media (max-width: 1100px) {
          .yg-shell { padding: 18px 16px 72px !important; }
          .yg-header { flex-wrap: wrap !important; gap: 12px !important; }
          .yg-tabbar { display: grid !important; grid-template-columns: repeat(3, minmax(0, 1fr)) !important; min-height: auto !important; }
          .yg-tabitem { min-height: 48px !important; border-right: none !important; border-bottom: 1px solid var(--border) !important; padding: 11px 8px !important; }
          .yg-tabbar .yg-tabitem:nth-child(n+4) { border-bottom: none !important; }
          .yg-tabbar .yg-tabitem:nth-child(3n) { border-right: none !important; }
          .yg-tabbar .yg-tabitem:not(:nth-child(3n)) { border-right: 1px solid var(--border) !important; }
          .crop-modal-grid { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
          /* stat strip stays 4-across down to the phone breakpoint (tiles are compact) */
        }
        @media (max-width: 640px) {
          /* Extra bottom clearance so content isn't hidden behind the fixed nav */
          .yg-shell { padding: 12px 12px calc(84px + env(safe-area-inset-bottom, 0px)) !important; }
          .yg-header { gap: 10px !important; }
          .yg-header > div { align-items: center !important; }
          /* Phone: the tab bar becomes a fixed bottom navigation (single row,
             icon over label) — the standard mobile app pattern, thumb-reachable. */
          .yg-tabbar {
            position: fixed !important; left: 0 !important; right: 0 !important; bottom: 0 !important;
            z-index: 900 !important;
            display: flex !important;
            grid-template-columns: none !important;
            margin-bottom: 0 !important;
            border: none !important; border-top: 1px solid var(--border-soft) !important; border-radius: 0 !important;
            background: #0b1c0e !important;
            box-shadow: 0 -4px 20px rgba(0,0,0,0.45) !important;
            padding-bottom: env(safe-area-inset-bottom, 0px) !important;
          }
          .yg-tabitem {
            flex-direction: column !important; gap: 3px !important;
            min-height: 54px !important; padding: 7px 2px !important; border-radius: 0 !important;
          }
          /* Reset the tablet grid's cell borders (equal specificity, later wins) */
          .yg-tabbar .yg-tabitem { border-right: none !important; border-bottom: none !important; }
          .yg-tabbar .yg-tabitem:nth-child(3n) { border-right: none !important; }
          .yg-tabbar .yg-tabitem:not(:nth-child(3n)) { border-right: none !important; }
          .yg-tabbar .yg-tabitem:nth-child(n+4) { border-bottom: none !important; }
          .yg-tabitem span:last-child { font-size: 9.5px !important; letter-spacing: 0.1px !important; }
          /* Keep the toast clear of the fixed bottom nav */
          .yg-toast { bottom: calc(72px + env(safe-area-inset-bottom, 0px)) !important; }
          .crop-modal-grid { grid-template-columns: 1fr !important; gap: 10px !important; }
          .yg-statstrip { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
          .yg-backup-btns { flex-direction: column !important; }
          /* Active formula banner stacks on mobile */
          .yg-active-formula { flex-direction: column !important; align-items: flex-start !important; gap: 10px !important; }
          .yg-active-formula > div:last-child { text-align: left !important; }
        }
      `}</style>

      {/* ═══════ EPHEMERAL-STORAGE WARNING ═══════ */}
      {/* Shown when localStorage is unavailable (private mode, locked-down
          webviews). Persistence falls back to memory, so data is lost on reload
          — warn loudly and point to Export Backup. */}
      {isEphemeral() && (
        <div role="alert" style={{
          display: "flex", alignItems: "flex-start", gap: 10,
          background: "rgba(240,168,32,0.1)", border: "1px solid rgba(240,168,32,0.4)",
          borderRadius: 11, padding: "10px 14px", marginBottom: 14,
        }}>
          <span style={{ color: "#f0a820", flexShrink: 0, marginTop: 1 }}>
            <Icon name="warning" size={16} />
          </span>
          <div style={{ fontSize: 11.5, color: "#f0c060", lineHeight: 1.5 }}>
            <strong style={{ color: "#f0a820" }}>Temporary storage — your data won&rsquo;t be saved.</strong>{" "}
            This browser is blocking local storage (private/incognito mode or an
            in-app browser), so anything you log will be lost when you close or
            reload. Open YaadGrow in a normal browser tab, or use Settings &rarr;
            Export Backup before you leave to keep your data.
          </div>
        </div>
      )}

      {/* ═══════ HEADER ═══════ */}
      <div className="yg-header" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, marginBottom: 18, paddingBottom: 14, borderBottom: "1px solid var(--border)" }}>

        {/* Left — YaadGrow brand. The wordmark is the way home: it returns to the
            Calculator tab and scrolls to the top, which is what clicking a logo
            does on every other site. Rendered as a button, not an anchor — there
            is no router, so an href would reload the SPA and lose in-flight state. */}
        <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexShrink: 0 }}>
          <button
            onClick={goHome}
            aria-label="YaadGrow — go to the home screen"
            style={{ display: "flex", alignItems: "center", gap: 0, background: "none", border: "none", padding: 0, cursor: "pointer", fontFamily: "inherit" }}
          >
            <span style={{ fontSize: isMobile ? 26 : 32, fontWeight: 900, color: "#f0c020", letterSpacing: -1.5, fontFamily: SYSTEM_FONT, lineHeight: 1 }}>Yaad</span>
            <span style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
              <span style={{ fontSize: isMobile ? 26 : 32, fontWeight: 900, color: "#5cb85c", letterSpacing: -1.5, fontFamily: SYSTEM_FONT, lineHeight: 1 }}>G</span>
              <span style={{ position: "absolute", top: isMobile ? -10 : -12, left: 4, display: "flex" }}>
                <Icon name="leaf" size={isMobile ? 12 : 15} color="#5cb85c" strokeWidth={2.2} />
              </span>
              <span style={{ fontSize: isMobile ? 26 : 32, fontWeight: 900, color: "#5cb85c", letterSpacing: -1.5, fontFamily: SYSTEM_FONT, lineHeight: 1 }}>row</span>
            </span>
          </button>
          {!isMobile && (
            <span style={{ fontSize: 10, letterSpacing: 2.5, textTransform: "uppercase", color: "var(--text-soft)", fontWeight: 600 }}>Hydroponic Farm Management System</span>
          )}
        </div>

        {/* Right — sign-in, then Powered By Systems Rubix */}
        <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 8 : 14, flexShrink: 0 }}>
          <HeaderAccount
            clientId={GOOGLE_CLIENT_ID}
            isMobile={isMobile}
            onToast={(m, tone) => showToast(m, tone)}
            onOpenSettings={() => { setTab("settings"); window.scrollTo({ top: 0, behavior: "smooth" }); }}
          />
          {/* Attribution as text, not the logo. The primary lockup is 8.21:1 —
              in a header slot it rendered 16.8px tall with a ~7.6px cap height,
              and 4.8px on mobile, so the name was unreadable. Type stays legible
              at every size, scales with the user's font settings, and costs no
              bytes. Kept muted on purpose: this is a credit, not a second brand
              competing with the YaadGrow wordmark opposite. */}
          <a
            className="yg-attrib"
            href="https://systemsrubixjm.com/"
            target="_blank"
            rel="noopener noreferrer"
            style={{ display: "inline-flex", alignItems: "baseline", gap: 5, textDecoration: "none", whiteSpace: "nowrap" }}
          >
            <span style={{ fontSize: 9, letterSpacing: 2, textTransform: "uppercase", color: "var(--text-soft)", fontWeight: 700 }}>
              Powered by
            </span>
            <span style={{ fontSize: isMobile ? 11.5 : 12.5, fontWeight: 700, color: "var(--text-muted)", letterSpacing: 0.2 }}>
              Systems Rubix
            </span>
          </a>
        </div>
      </div>

      {/* ═══════ CONDITIONS BAR — location · live clock · local weather ═══════ */}
      <div className="yg-conditions" style={{
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap",
        background: "linear-gradient(180deg, var(--bg-card) 0%, var(--bg-card2) 100%)",
        border: "1px solid var(--border)", borderRadius: 11,
        padding: "10px 14px", marginBottom: 14,
      }}>
        {/* Location + date */}
        <div style={{ display: "flex", alignItems: "center", gap: 9, minWidth: 0 }}>
          <span style={{ color: "#5cb85c", display: "flex", flexShrink: 0 }}>
            <Icon name="globe" size={16} />
          </span>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text-main)", lineHeight: 1.25, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {userLocation.name || (
                <button
                  onClick={() => { setTab("settings"); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                  style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontFamily: "inherit",
                    fontSize: 13, fontWeight: 700, color: "#f0c020", textDecoration: "underline", textUnderlineOffset: 3 }}
                >
                  Set your location
                </button>
              )}
            </div>
            <div style={{ fontSize: 10.5, color: "var(--text-soft)", letterSpacing: 0.3, lineHeight: 1.3 }}>
              {now.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
            </div>
          </div>
        </div>

        {/* Clock + weather */}
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 22, fontWeight: 900, color: "var(--text-strong)", lineHeight: 1.05, fontVariantNumeric: "tabular-nums", letterSpacing: 0.5 }}>
              {now.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
            </div>
            <div style={{ fontSize: 9, color: "var(--text-soft)", textTransform: "uppercase", letterSpacing: 1 }}>Local time</div>
          </div>

          {userLocation.showWeather && (
            <div style={{ display: "flex", alignItems: "center", gap: 9, paddingLeft: 14, borderLeft: "1px solid rgba(255,255,255,0.08)" }}>
              {weatherStatus === "ok" && weather ? (
                <>
                  <span style={{ fontSize: 22, lineHeight: 1 }} aria-hidden="true">{weatherCodeInfo(weather.code).emoji}</span>
                  <div>
                    <div style={{ fontSize: 16, fontWeight: 800, color: "var(--text-strong)", lineHeight: 1.1, fontVariantNumeric: "tabular-nums" }}>
                      {Math.round(weather.tempC)}°C
                    </div>
                    <div style={{ fontSize: 10, color: "var(--text-muted)", lineHeight: 1.3, whiteSpace: "nowrap" }}>
                      {weatherCodeInfo(weather.code).label} · {Math.round(weather.humidity)}% RH{weather.uv != null ? ` · UV ${Math.round(weather.uv)}` : ""}
                    </div>
                  </div>
                </>
              ) : (
                <div style={{ fontSize: 10.5, color: "var(--text-soft)", maxWidth: 150, lineHeight: 1.35 }}>
                  {weatherStatus === "loading" && "Loading weather…"}
                  {weatherStatus === "notfound" && "No weather for this location"}
                  {weatherStatus === "error" && "Weather unavailable"}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Compact daily outlook (Open-Meteo daily forecast) */}
        {userLocation.showWeather && weatherStatus === "ok" && weather && weather.daily && weather.daily.length > 0 && (() => {
          const fmtT = (iso) => iso ? new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }) : "";
          const today = weather.daily[0], tmr = weather.daily[1];
          const vpd = computeVpd(weather.tempC, weather.humidity);
          const band = vpdBand(stage?.label);
          const vst = vpdStatus(vpd, band);
          return (
            <div style={{ flexBasis: "100%", display: "flex", flexWrap: "wrap", gap: "4px 12px", alignItems: "center", marginTop: 6, paddingTop: 8, borderTop: "1px solid rgba(255,255,255,0.06)", fontSize: 10.5, color: "var(--text-muted)" }}>
              <span><strong style={{ color: "var(--text-main)", fontWeight: 700 }}>Today</strong> {Math.round(today.tmax)}° / {Math.round(today.tmin)}°{today.pop != null ? ` · ${today.pop}% rain` : ""}{today.uvMax != null ? ` · UV ${Math.round(today.uvMax)}` : ""}</span>
              {vpd != null && (
                <span title={`Vapour pressure deficit · ${stage?.label || "stage"} target ${band.lo}–${band.hi} kPa`}>
                  <strong style={{ color: "var(--text-main)", fontWeight: 700 }}>VPD</strong> {vpd.toFixed(1)} kPa <strong style={{ color: vst.tone, fontWeight: 700 }}>{vst.label}</strong>
                </span>
              )}
              {today.et0 != null && <span style={{ color: "var(--text-soft)" }} title="FAO reference evapotranspiration — the day's water-loss rate">ET₀ {today.et0.toFixed(1)} mm</span>}
              {(today.sunrise || today.sunset) && <span style={{ color: "var(--text-soft)" }}>☀ {fmtT(today.sunrise)}–{fmtT(today.sunset)}</span>}
              {tmr && <span style={{ color: "var(--text-soft)" }}><strong style={{ color: "var(--text-muted)", fontWeight: 700 }}>Tomorrow</strong> {Math.round(tmr.tmax)}° / {Math.round(tmr.tmin)}°{tmr.pop != null ? ` · ${tmr.pop}% rain` : ""}</span>}
            </div>
          );
        })()}
      </div>

      {/* ═══════ TAB BAR ═══════ */}
      <div className="yg-tabbar" style={{ display: "flex", background: "var(--bg-card)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, overflow: "hidden", marginBottom: 20, minHeight: 52, boxShadow: "inset 0 0 20px rgba(181,224,38,0.02)" }}>
        {[
          { key: "calc",     label: "Calculator", icon: "calculator" },
          { key: "grows",    label: "Grows",      icon: "sprout" },
          { key: "pumps",    label: "Pumps",      icon: "droplet" },
          { key: "log",      label: "Log",        icon: "clipboard" },
          { key: "history",  label: "History",    icon: "clock" },
          { key: "settings", label: "Settings",   icon: "gear" },
        ].map((t, idx) => {
          const active = tab === t.key;
          return (
            <button className="yg-tabitem" key={t.key} onClick={() => setTab(t.key)}
              style={{
                flex: 1, display: "flex", flexDirection: "row", alignItems: "center",
                justifyContent: "center", gap: 7, padding: "12px 8px",
                border: "none", borderRight: idx < 5 ? "1px solid var(--border)" : "none",
                cursor: "pointer",
                background: active ? "linear-gradient(180deg, var(--panel-a) 0%, var(--panel-b) 100%)" : "transparent",
                boxShadow: active ? "inset 0 0 0 1.5px rgba(90,185,90,0.35)" : "none",
                color: active ? "var(--active)" : "var(--text-soft)",
                fontWeight: active ? 700 : 500,
                position: "relative",
              }}>
              <Icon name={t.icon} size={17} strokeWidth={active ? 2.1 : 1.8} />
              <span style={{ fontSize: 13, letterSpacing: 0.2 }}>{t.label}</span>
              {t.key === "grows" && activeGrows.length > 0 && (
                <span style={{ position: "absolute", top: 5, right: 6, background: "var(--active)", color: "var(--bg-main)", borderRadius: 8, padding: "0 4px", fontSize: 8, fontWeight: 900, lineHeight: "13px" }}>{activeGrows.length}</span>
              )}
              {t.key === "history" && logs.length > 0 && (
                <span style={{ position: "absolute", top: 5, right: 6, background: "var(--active)", color: "var(--bg-main)", borderRadius: 8, padding: "0 4px", fontSize: 8, fontWeight: 900, lineHeight: "13px" }}>{logs.length}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* ═══════ STATS STRIP ═══════ */}
      <div className="yg-statstrip" style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 8, marginBottom: 20 }}>
        {[
          { label: "Active Crops",  value: activeGrows.length || 0, icon: "leaf",     tone: "var(--active)" },
          { label: "NFT Channels",  value: hasNft ? sysSettings.nftChannels : 0, icon: "droplet",  tone: "#4da8da" },
          { label: "Towers",        value: hasTower ? sysSettings.towerCount : 0,  icon: "sprout",   tone: "#9b69d0" },
          { label: "Active Alerts", value: activeAlerts.length, icon: "warning", tone: "#f0a820", alert: true, alertLabel: activeAlerts[0]?.label },
        ].map((item, i) => {
          const lit = item.alert && item.value > 0;
          return (
            <div key={i} title={item.alert && lit ? activeAlerts.map(a => a.label).join(" · ") : undefined} style={{
              display: "flex", alignItems: "center", gap: 8, minWidth: 0,
              background: lit ? "rgba(240,168,32,0.1)" : "var(--bg-card)", border: `1px solid ${lit ? "rgba(240,168,32,0.5)" : "var(--border)"}`,
              boxShadow: lit ? "0 0 0 1px rgba(240,168,32,0.25), 0 4px 16px rgba(240,168,32,0.14)" : "none",
              borderRadius: 10, padding: "8px 10px",
            }}>
              <div style={{
                width: 28, height: 28, borderRadius: "50%",
                background: "rgba(0,0,0,0.35)", border: "1px solid rgba(255,255,255,0.08)",
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                color: lit ? "#f0a820" : item.tone,
              }}>
                <Icon name={item.icon} size={15} />
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 8, color: lit ? "#f0c88a" : "var(--text-soft)", letterSpacing: 0.4, textTransform: "uppercase", lineHeight: 1.2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{lit && item.alertLabel ? item.alertLabel : item.label}</div>
                <div style={{ fontSize: 18, fontWeight: 900, color: lit ? "#f0a820" : "var(--text-strong)", lineHeight: 1.15 }}>{item.value}</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Location prompt — shown until a location is set, and never dismissible.
          This is not a nag: EC targets, VPD, ET0, heat warnings and the
          temperature-scaled pump cadences are all computed from local
          conditions, so with no location the numbers on screen are guesses.
          Saying WHY, and offering both paths, is the difference between a
          request and an interruption. */}
      {userLocationLoaded && !showSetup && !(userLocation.name || "").trim() && (
        <div style={{
          display: "flex", alignItems: "flex-start", gap: 12, flexWrap: "wrap",
          padding: "14px 16px", marginBottom: 14, borderRadius: 12,
          border: "1px solid rgba(240,192,32,0.35)", background: "rgba(240,192,32,0.07)",
        }}>
          <span style={{ color: "#f0c020", display: "flex", flexShrink: 0, marginTop: 1 }}>
            <Icon name="globe" size={18} />
          </span>
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: "#f0c020", marginBottom: 4 }}>
              Choose your location to get accurate numbers
            </div>
            <div style={{ fontSize: 12.5, color: "var(--text-soft2)", lineHeight: 1.6 }}>
              Nutrient strength, VPD, evaporation and the pump schedule are all
              calculated from local temperature and humidity. Without a location
              they fall back to generic values that may not suit your farm.
            </div>
            <div style={{ fontSize: 11, color: "var(--text-soft)", lineHeight: 1.6, marginTop: 6 }}>
              Typing a nearby town sends only that name. Detecting sends your exact
              coordinates — see the <a href="/privacy" style={{ color: "var(--text-muted)" }}>Privacy Policy</a>.
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <button
              onClick={detectLocation}
              disabled={geoStatus === "locating"}
              style={{ padding: "9px 14px", borderRadius: 9, cursor: "pointer", fontFamily: "inherit",
                fontSize: 12.5, fontWeight: 700, color: "#f0c020",
                border: "1px solid rgba(240,192,32,0.45)", background: "rgba(240,192,32,0.14)" }}
            >
              {geoStatus === "locating" ? "Locating…" : "Use my location"}
            </button>
            <button
              onClick={() => { setTab("settings"); window.scrollTo({ top: 0, behavior: "smooth" }); }}
              style={{ padding: "9px 14px", borderRadius: 9, cursor: "pointer", fontFamily: "inherit",
                fontSize: 12.5, fontWeight: 700, color: "var(--text-main)",
                border: "1px solid rgba(255,255,255,0.15)", background: "rgba(255,255,255,0.06)" }}
            >
              Enter it manually
            </button>
          </div>
        </div>
      )}

      {/* Storage error banner */}
      {storageError && (
        <div role="alert" style={{ background: "rgba(224,80,80,0.1)", border: "1px solid rgba(224,80,80,0.3)", borderRadius: 10, padding: "10px 14px", marginBottom: 13, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
          <div style={{ fontSize: 12, color: "#f08080", lineHeight: 1.4, display: "flex", alignItems: "center", gap: 8 }}>
            <Icon name="warning" size={15} color="#f8a0a0" />
            <span><strong style={{ color: "#f8a0a0" }}>Storage warning:</strong> {storageError}</span>
          </div>
          <button onClick={() => setStorageError(null)} aria-label="Dismiss storage warning" style={{ background: "rgba(0,0,0,0.25)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 7, padding: "4px 10px", cursor: "pointer", color: "#f08080", fontSize: 11, flexShrink: 0, fontFamily: "inherit" }}>
            Dismiss
          </button>
        </div>
      )}

      {/* Reservoir refresh reminder — fires when solution is due for a full change */}
      {reservoirStatus.hasHistory && (reservoirStatus.due || reservoirStatus.soon) && !dismissedReservoirReminder && (
        <div role="alert" style={{
          background: reservoirStatus.due ? "rgba(240,168,32,0.1)" : "rgba(79,195,247,0.08)",
          border: `1px solid ${reservoirStatus.due ? "rgba(240,168,32,0.35)" : "rgba(79,195,247,0.25)"}`,
          borderRadius: 10, padding: "10px 14px", marginBottom: 13,
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
        }}>
          <div style={{ fontSize: 12, lineHeight: 1.45, display: "flex", alignItems: "center", gap: 8, color: reservoirStatus.due ? "#ffc890" : "#8ec5e8" }}>
            <span style={{ fontSize: 16 }}>💧</span>
            <span>
              <strong style={{ color: reservoirStatus.due ? "#f0a820" : "#4fc3f7" }}>
                {reservoirStatus.due ? "Reservoir refresh due." : "Reservoir refresh soon."}
              </strong>{" "}
              {reservoirStatus.due
                ? `Solution was last changed ${reservoirStatus.daysSince} day${reservoirStatus.daysSince === 1 ? "" : "s"} ago (target: every ${reservoirStatus.intervalDays}). Stale solution drifts in pH and nutrient balance — mix fresh to keep plants fed evenly.`
                : `Last changed ${reservoirStatus.daysSince} day${reservoirStatus.daysSince === 1 ? "" : "s"} ago — plan a fresh mix in ${reservoirStatus.daysLeft} day${reservoirStatus.daysLeft === 1 ? "" : "s"}.`}
            </span>
          </div>
          <button onClick={() => setDismissedReservoirReminder(true)} aria-label="Dismiss reservoir reminder" style={{ background: "rgba(0,0,0,0.25)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 7, padding: "4px 10px", cursor: "pointer", color: reservoirStatus.due ? "#ffc890" : "#8ec5e8", fontSize: 11, flexShrink: 0, fontFamily: "inherit" }}>
            Dismiss
          </button>
        </div>
      )}

      {/* ═══════ CALCULATOR ═══════ */}
      {tab === "calc" && (<>
        {/* Active Formula banner — calculator context */}
        <div className="yg-active-formula" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14, background: "var(--bg-card)", border: nurseryMode ? "1px solid rgba(67,160,71,0.4)" : "1px solid rgba(181,224,38,0.3)", borderRadius: 14, padding: "14px 18px", marginBottom: 14, boxShadow: nurseryMode ? "0 0 18px rgba(67,160,71,0.08)" : "0 0 18px rgba(181,224,38,0.06)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 13 }}>
            <div style={{ width: 44, height: 44, borderRadius: 11, background: nurseryMode ? "rgba(67,160,71,0.12)" : "rgba(181,224,38,0.1)", border: nurseryMode ? "1px solid rgba(67,160,71,0.3)" : "1px solid rgba(181,224,38,0.25)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Icon name={nurseryMode ? "sprout" : "flask"} size={22} color={nurseryMode ? "#a5d6a7" : "var(--active)"} />
            </div>
            <div>
              <div style={{ fontSize: 10, letterSpacing: 2.5, textTransform: "uppercase", color: nurseryMode ? "#a5d6a7" : "var(--active)", fontWeight: 800, marginBottom: 2 }}>
                {nurseryMode ? "Nursery Mix" : "Active Formula"}
              </div>
              <div style={{ fontSize: isMobile ? 17 : 20, fontWeight: 900, color: "var(--text-strong)", letterSpacing: -0.3, lineHeight: 1.15 }}>MasterBlend 4-18-38 NPK</div>
              <div style={{ fontSize: 11, color: "var(--text-soft)", marginTop: 1 }}>
                {nurseryMode ? "Dilute solution for seedlings" : "3-Part Mix Calculator"}
              </div>
            </div>
          </div>
          <div style={{ textAlign: "right", flexShrink: 0 }}>
            <div style={{ fontSize: 9, color: "var(--text-soft)", textTransform: "uppercase", letterSpacing: 1 }}>Mixing for</div>
            <div style={{ fontSize: 14, fontWeight: 800, color: "var(--text-strong)" }}>{gallons} gal{nurseryMode ? " · 🌱" : ""}</div>
            <div style={{ fontSize: 11, color: nurseryMode ? "#a5d6a7" : "var(--active)", fontWeight: 600 }}>Target EC {effTarget.toFixed(2)}</div>
          </div>
        </div>

        {/* Step indicator — mobile wizard only */}
        {calcWizard && (
          <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
            {[{ n: 1, l: "Crop & Stage" }, { n: 2, l: "Mix Inputs" }, { n: 3, l: "Result" }].map(s => {
              const active = calcStep === s.n;
              const done = calcStep > s.n;
              return (
                <button key={s.n} onClick={() => setCalcStep(s.n)} aria-label={`Step ${s.n}: ${s.l}`} aria-current={active ? "step" : undefined} style={{
                  flex: 1, padding: "8px 5px", borderRadius: 9, cursor: "pointer", fontFamily: "inherit",
                  border: active ? "1.5px solid var(--active)" : "1px solid rgba(255,255,255,0.08)",
                  background: active ? "rgba(181,224,38,0.12)" : done ? "rgba(90,185,90,0.08)" : "rgba(255,255,255,0.02)",
                }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <span style={{
                      width: 18, height: 18, borderRadius: "50%", fontSize: 10, fontWeight: 800,
                      display: "inline-flex", alignItems: "center", justifyContent: "center",
                      background: active ? "var(--active)" : done ? "rgba(90,185,90,0.45)" : "var(--border-soft)",
                      color: active ? "var(--bg-card2)" : "var(--text-main)",
                    }}>{done ? "✓" : s.n}</span>
                  </div>
                  <div style={{ fontSize: 9.5, fontWeight: 700, marginTop: 4, color: active ? "var(--active)" : "var(--text-soft)", textTransform: "uppercase", letterSpacing: 0.4, textAlign: "center" }}>{s.l}</div>
                </button>
              );
            })}
          </div>
        )}

        {/* Active Grow Banner */}
        {showStep1 && activeGrow && (
          <div style={{ ...card, background: `${CROPS[activeGrow.crop]?.color || "#5cb85c"}15`, border: `1px solid ${CROPS[activeGrow.crop]?.color || "#5cb85c"}40`, marginBottom: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ fontSize: 22 }}><CropIcon cropKey={activeGrow.crop} emoji={CROPS[activeGrow.crop]?.icon} size={34} /></div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 1 }}>Active grow</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text-strong)" }}>
                  {activeGrow.label || CROPS[activeGrow.crop]?.name} · {GROW_SYSTEMS.find(s => s.key === activeGrow.system)?.icon} {GROW_SYSTEMS.find(s => s.key === activeGrow.system)?.label}
                </div>
                <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>
                  {activeGrow.phase === "nursery"
                    ? `🌱 Nursery · Day ${daysSince(activeGrow.plantingDate)} of ~${HUMIDITY_TRAY_DAYS + (activeGrow.nurseryDays || NURSERY_DURATIONS[activeGrow.crop] || 7)} since sowing`
                    : `Day ${growTimeline(activeGrow).days} of ~${growTimeline(activeGrow).span}`} · sown {fmtGrowDate(activeGrow.plantingDate, { month: "short", day: "numeric" })}
                  {activeGrow.transplantedAt && ` · transplanted ${fmtGrowDate(activeGrow.transplantedAt, { month: "short", day: "numeric" })}`}
                </div>
              </div>
              <button onClick={() => setActiveGrowId(null)} aria-label="Clear active grow" style={{ background: "rgba(0,0,0,0.2)", border: "1px solid var(--border-soft)", borderRadius: 8, padding: "6px 10px", cursor: "pointer", color: "var(--text-muted)", fontSize: 11 }}>
                Clear
              </button>
            </div>
          </div>
        )}
        {showStep1 && !activeGrow && grows.length > 0 && (
          <div style={{ ...card, background: "rgba(255,213,79,0.06)", border: "1px solid rgba(255,213,79,0.15)", marginBottom: 14, padding: "10px 14px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ fontSize: 12, color: "#ffd54f" }}>
                💡 No active grow — logs won't be linked. Pick one in the Grows tab.
              </div>
              <button onClick={() => setTab("grows")} style={{ background: "rgba(255,213,79,0.15)", border: "1px solid rgba(255,213,79,0.25)", borderRadius: 8, padding: "5px 10px", cursor: "pointer", color: "#ffd54f", fontSize: 11, fontWeight: 600 }}>
                Go to Grows
              </button>
            </div>
          </div>
        )}

        {/* Stage mismatch warning */}
        {showStep1 && activeGrowStageMismatch && (
          <div style={{ background: "rgba(240,168,32,0.08)", border: "1px solid rgba(240,168,32,0.3)", borderRadius: 11, padding: "10px 14px", marginBottom: 14, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1, minWidth: 0 }}>
              <Icon name="warning" size={16} color="#f0a820" />
              <div style={{ fontSize: 12, color: "#ffc890", lineHeight: 1.5 }}>
                This grow has reached <strong style={{ color: "var(--text-strong)" }}>{activeGrowStageMismatch.suggestedStage.label}</strong> stage based on planting date. Calculator is showing <strong style={{ color: "var(--text-strong)" }}>{crop.stages[safeStageIdx].label}</strong>.
              </div>
            </div>
            <button
              onClick={() => { setStageIdx(activeGrowStageMismatch.suggested); setCustomEc(null); acknowledgeStage(activeGrow.id, activeGrowStageMismatch.suggested); }}
              style={{ background: "rgba(240,168,32,0.18)", border: "1px solid rgba(240,168,32,0.4)", borderRadius: 8, padding: "6px 12px", cursor: "pointer", color: "#f0a820", fontSize: 11, fontWeight: 700, fontFamily: "inherit", flexShrink: 0 }}
            >
              Update Stage
            </button>
          </div>
        )}

        {/* ── CROP SELECTOR PANEL ── */}
        <div style={{ ...card, display: showStep1 ? undefined : "none" }}>
          {/* Nursery mode toggle — only shown when crop has nursery targets and nursery is enabled in settings */}
          {nurseryTarget && sysSettings.hasNursery !== false && (
            <div style={{ padding: "12px 16px 0" }}>
              <button
                onClick={() => setNurseryMode(m => !m)}
                style={{
                  width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "10px 12px", borderRadius: 9, cursor: "pointer", fontFamily: "inherit",
                  border: nurseryMode ? "1px solid rgba(67,160,71,0.45)" : "1px solid rgba(255,255,255,0.08)",
                  background: nurseryMode ? "rgba(67,160,71,0.12)" : "rgba(255,255,255,0.02)",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Icon name="sprout" size={15} color={nurseryMode ? "#a5d6a7" : "var(--text-soft)"} />
                  <div style={{ textAlign: "left" }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: nurseryMode ? "#a5d6a7" : "var(--text-muted)" }}>
                      {nurseryMode ? "Nursery mode ON" : "Nursery mode OFF"}
                    </div>
                    <div style={{ fontSize: 10, color: "var(--text-soft)", marginTop: 1 }}>
                      {nurseryMode ? `Mixing dilute solution for seedlings · EC ${nurseryTarget.ecTarget}` : "Mix for seedlings before transplant"}
                    </div>
                  </div>
                </div>
                <div style={{
                  width: 36, height: 20, borderRadius: 10, padding: 2,
                  background: nurseryMode ? "rgba(67,160,71,0.4)" : "rgba(255,255,255,0.08)",
                  position: "relative",
                  transition: "background 0.18s",
                }}>
                  <div style={{
                    width: 16, height: 16, borderRadius: "50%",
                    background: nurseryMode ? "#a5d6a7" : "var(--text-soft)",
                    transform: nurseryMode ? "translateX(16px)" : "translateX(0)",
                    transition: "transform 0.18s, background 0.18s",
                  }} />
                </div>
              </button>
            </div>
          )}

          {/* Mode toggle */}
          <div style={{ padding: "12px 16px 0", display: "flex", gap: 6 }}>
            {[
              { key: "single", label: "Single Crop", icon: "leaf" },
              { key: "mixed",  label: "Mixed Reservoir", icon: "flask" },
            ].map(m => {
              const active = mixMode === m.key;
              return (
                <button key={m.key} onClick={() => setMixMode(m.key)} style={{
                  flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
                  padding: "8px 10px", borderRadius: 8, border: "none", cursor: "pointer",
                  background: active ? "linear-gradient(180deg, var(--panel-a) 0%, var(--panel-b) 100%)" : "rgba(255,255,255,0.02)",
                  boxShadow: active ? "inset 0 0 0 1.5px rgba(90,185,90,0.35)" : "none",
                  color: active ? "var(--active)" : "var(--text-soft)",
                  fontFamily: "inherit", fontWeight: active ? 700 : 500, fontSize: 12,
                }}>
                  <Icon name={m.icon} size={14} />
                  {m.label}
                </button>
              );
            })}
          </div>

          {/* SINGLE CROP MODE */}
          {mixMode === "single" && (
            <>
              <PanelHeader
                icon={<CropIcon cropKey={cropKey} emoji={crop.icon} size={24} />}
                label="Crop & Stage"
                summary={<><strong style={{color:"var(--text-strong)"}}>{crop.name}</strong> · {stage.label} · {nurseryMode && nurseryTarget ? `${NURSERY_DURATIONS[cropKey] || 7}d nursery` : `${crop.daysToHarvest}d`}</>}
                badge={`EC ${stage.ecTarget}`}
                badgeType="green"
                isOpen={true}
                onToggle={() => setCropModalOpen(true)}
              />
              {/* Stage selector */}
              <div style={{ padding: "4px 16px 14px" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                  <div style={{ fontSize: 10, color: "var(--text-soft)", textTransform: "uppercase", letterSpacing: 1.5, fontWeight: 700 }}>Growth Stage</div>
                  <button
                    onClick={() => setCropModalOpen(true)}
                    style={{ fontSize: 11, color: "var(--active)", background: "rgba(181,224,38,0.08)", border: "1px solid rgba(181,224,38,0.2)", borderRadius: 8, padding: "3px 10px", cursor: "pointer", fontFamily: "inherit", fontWeight: 600 }}
                  >
                    Change Crop ›
                  </button>
                </div>
                <div style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 2 }}>
                  {crop.stages.map((s, i) => (
                    <button
                      key={i}
                      onClick={() => { setStageIdx(i); setCustomEc(null); }}
                      style={{
                        flex: crop.stages.length <= 4 ? 1 : "none",
                        minWidth: crop.stages.length > 4 ? 80 : 0,
                        padding: "10px 8px 8px", borderRadius: 10, cursor: "pointer", textAlign: "center",
                        border: safeStageIdx === i ? `2px solid ${crop.color}` : "2px solid rgba(255,255,255,0.08)",
                        background: safeStageIdx === i ? `${crop.color}18` : "rgba(255,255,255,0.02)",
                        fontFamily: "inherit",
                      }}
                    >
                      <div style={{ fontSize: 12, fontWeight: 600, color: safeStageIdx === i ? "var(--text-strong)" : "var(--text-muted)" }}>{s.label}</div>
                      <div style={{ fontSize: 10, color: "var(--text-soft)", marginTop: 2 }}>{s.sub}</div>
                      <div style={{ fontSize: 10, color: safeStageIdx === i ? crop.color : "var(--text-soft)", marginTop: 2, fontWeight: 600 }}>EC {s.ecMin}–{s.ecMax}</div>
                    </button>
                  ))}
                </div>

                {/* Stage tips for currently selected stage */}
                {STAGE_TIPS[cropKey]?.[stage.label] && (
                  <div style={{ marginTop: 14, padding: "12px 14px", borderRadius: 10, background: `${crop.color}08`, border: `1px solid ${crop.color}25` }}>
                    <div style={{ fontSize: 10, fontWeight: 800, color: crop.color, textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 9 }}>
                      {stage.label} Stage Guide
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 8 }}>
                      {[
                        { label: "Feed",   icon: "flask",  color: "var(--active)", text: STAGE_TIPS[cropKey][stage.label].feed },
                        { label: "Env",    icon: "leaf",   color: "#5cb85c", text: STAGE_TIPS[cropKey][stage.label].env },
                        { label: "Watch",  icon: "warning", color: "#f0a820", text: STAGE_TIPS[cropKey][stage.label].watch },
                        { label: "Action", icon: "bolt",   color: "#5ab8e8", text: STAGE_TIPS[cropKey][stage.label].action },
                      ].map((tip, i) => (
                        <div key={i} style={{ padding: "9px 10px", borderRadius: 8, background: "rgba(0,0,0,0.25)" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                            <Icon name={tip.icon} size={12} color={tip.color} />
                            <span style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, color: tip.color }}>{tip.label}</span>
                          </div>
                          <div style={{ fontSize: 11, color: "var(--text-soft2)", lineHeight: 1.5 }}>
                            {tip.text}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {/* MIXED RESERVOIR MODE */}
          {mixMode === "mixed" && (
            <div style={{ padding: "14px 16px 14px" }}>
              {mixCrops.length === 0 ? (
                <div style={{ textAlign: "center", padding: "20px 12px", color: "var(--text-soft)" }}>
                  <div style={{ fontSize: 13, marginBottom: 14, lineHeight: 1.6 }}>
                    Add the crops you have growing in this reservoir.<br/>
                    The calculator will find a compromise EC &amp; pH that suits all of them.
                  </div>
                  <div style={{ display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap" }}>
                    <button onClick={() => setMixModalOpen(true)} style={{
                      padding: "10px 16px", borderRadius: 9, fontSize: 13, fontWeight: 700,
                      border: "1px solid rgba(181,224,38,0.3)", background: "rgba(181,224,38,0.08)",
                      color: "var(--active)", cursor: "pointer", fontFamily: "inherit", display: "inline-flex", alignItems: "center", gap: 7,
                    }}>+ Add Crop</button>
                    {activeGrows.length > 0 && (
                      <button onClick={() => {
                        // Pull from active grows
                        const fromGrows = activeGrows.map(g => {
                          const c = CROPS[g.crop];
                          if (!c) return null;
                          const days = daysSince(g.plantingDate);
                          const idx = suggestStage(c, days);
                          return { cropKey: g.crop, stageIdx: idx };
                        }).filter(Boolean);
                        // Dedup by cropKey + stageIdx
                        const seen = new Set();
                        const unique = fromGrows.filter(m => {
                          const k = `${m.cropKey}-${m.stageIdx}`;
                          if (seen.has(k)) return false;
                          seen.add(k); return true;
                        });
                        setMixCrops(unique);
                      }} style={{
                        padding: "10px 16px", borderRadius: 9, fontSize: 13, fontWeight: 700,
                        border: "1px solid rgba(52,201,255,0.3)", background: "rgba(52,201,255,0.08)",
                        color: "#5ab8e8", cursor: "pointer", fontFamily: "inherit",
                      }}>Auto-load Active Grows ({activeGrows.length})</button>
                    )}
                  </div>
                </div>
              ) : (
                <>
                  {/* Crop chips */}
                  <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 12 }}>
                    {mixCrops.map((m, i) => {
                      const info = getMixStage(m);
                      if (!info) return null;
                      const { crop: c, stage: s, stageIdx } = info;
                      const fit = compromise?.perCrop?.[i];
                      const issue = fit && (!fit.ecOk || !fit.phOk);
                      return (
                        <div key={i} style={{
                          display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderRadius: 9,
                          background: issue ? "rgba(240,168,32,0.06)" : "rgba(255,255,255,0.02)",
                          border: `1px solid ${issue ? "rgba(240,168,32,0.25)" : "rgba(255,255,255,0.06)"}`,
                        }}>
                          <CropIcon cropKey={m.cropKey} emoji={c.icon} size={26} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text-strong)" }}>
                              {c.name} <span style={{ color: "var(--text-soft)", fontWeight: 500 }}>· {s.label}</span>
                            </div>
                            <div style={{ fontSize: 10, color: "var(--text-muted)" }}>
                              Ideal EC {s.ecTarget} ({s.ecMin}–{s.ecMax}) · pH {s.phTarget}
                              {issue && fit.ecDelta !== 0 && (
                                <span style={{ color: "#f0a820", marginLeft: 6 }}>
                                  · {fit.ecDelta > 0 ? `+${fit.ecDelta.toFixed(2)} high` : `${fit.ecDelta.toFixed(2)} low`}
                                </span>
                              )}
                            </div>
                          </div>
                          {/* Stage selector for this crop */}
                          <select value={stageIdx} onChange={e => {
                            const newIdx = parseInt(e.target.value);
                            setMixCrops(prev => prev.map((mc, j) => j === i ? { ...mc, stageIdx: newIdx } : mc));
                          }} style={{
                            background: "rgba(0,0,0,0.3)", border: "1px solid var(--border-soft)",
                            borderRadius: 6, color: "var(--text-main)", fontSize: 11, padding: "4px 6px",
                            fontFamily: "inherit", cursor: "pointer",
                          }}>
                            {c.stages.map((ss, j) => <option key={j} value={j}>{ss.label}</option>)}
                          </select>
                          <button
                            onClick={() => setMixCrops(prev => prev.filter((_, j) => j !== i))}
                            aria-label={`Remove ${c.name}`}
                            style={{ background: "transparent", border: "none", color: "#c97070", fontSize: 14, cursor: "pointer", padding: 4 }}
                          >✕</button>
                        </div>
                      );
                    })}
                  </div>

                  {/* Add another button */}
                  <button onClick={() => setMixModalOpen(true)} style={{
                    width: "100%", padding: "9px", borderRadius: 9, fontSize: 12, fontWeight: 600,
                    border: "1px dashed rgba(181,224,38,0.3)", background: "transparent",
                    color: "var(--active)", cursor: "pointer", fontFamily: "inherit", marginBottom: 12,
                  }}>+ Add Another Crop</button>

                  {/* Compromise Target Card */}
                  {compromise && (
                    <div style={{
                      padding: "12px 14px", borderRadius: 10,
                      background: compromise.ecOverlaps && compromise.phOverlaps ? "rgba(120,224,90,0.05)" : "rgba(240,168,32,0.06)",
                      border: `1px solid ${compromise.ecOverlaps && compromise.phOverlaps ? "rgba(120,224,90,0.25)" : "rgba(240,168,32,0.3)"}`,
                    }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                        <Icon name={compromise.incompatible ? "warning" : "flask"} size={15} color={compromise.incompatible ? "#f0a820" : "#78e05a"} />
                        <div style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: 1.5, color: compromise.incompatible ? "#f0a820" : "#78e05a" }}>
                          {compromise.incompatible ? "Compromise Target — Wide Spread" : "Compromise Target"}
                        </div>
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: compromise.incompatible || !compromise.ecOverlaps ? 10 : 0 }}>
                        <div style={{ padding: "8px 10px", borderRadius: 8, background: "rgba(0,0,0,0.2)" }}>
                          <div style={{ fontSize: 9, color: "var(--text-soft)", textTransform: "uppercase", letterSpacing: 1 }}>Target EC</div>
                          <div style={{ fontSize: 22, fontWeight: 900, color: "var(--active)" }}>{compromise.ecTarget}</div>
                          <div style={{ fontSize: 9, color: "var(--text-soft)", marginTop: 2 }}>
                            {compromise.ecOverlaps
                              ? `Safe for all · ceiling ${compromise.ecHi.toFixed(1)}`
                              : "No clean overlap · safest crop protected"}
                          </div>
                        </div>
                        <div style={{ padding: "8px 10px", borderRadius: 8, background: "rgba(0,0,0,0.2)" }}>
                          <div style={{ fontSize: 9, color: "var(--text-soft)", textTransform: "uppercase", letterSpacing: 1 }}>Target pH</div>
                          <div style={{ fontSize: 22, fontWeight: 900, color: "#64b5f6" }}>{compromise.phTarget}</div>
                          <div style={{ fontSize: 9, color: "var(--text-soft)", marginTop: 2 }}>
                            {compromise.phOverlaps
                              ? `Range overlap: ${compromise.phLo.toFixed(1)}–${compromise.phHi.toFixed(1)}`
                              : "No clean overlap · midpoint chosen"}
                          </div>
                        </div>
                      </div>
                      {compromise.incompatible && (
                        <div style={{ fontSize: 11, color: "#ffc890", lineHeight: 1.5, marginTop: 8 }}>
                          ⚠ The crops in this mix have very different ideal ECs (spread of {compromise.spread.toFixed(1)}). Some may underperform. Consider splitting into two reservoirs if growth is uneven.
                        </div>
                      )}
                      {(!compromise.ecOverlaps || !compromise.phOverlaps) && !compromise.incompatible && (
                        <div style={{ fontSize: 11, color: "#ffc890", lineHeight: 1.5, marginTop: 8 }}>
                          EC target set to protect the most sensitive crop's ceiling. Hungrier crops may need supplemental feeding or a separate reservoir long-term.
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Mix Add Modal */}
        {mixModalOpen && (
          <div
            role="dialog" aria-modal="true" aria-label="Add Crop to Mix"
            onClick={e => { if (e.target === e.currentTarget) setMixModalOpen(false); }}
            style={{ position: "fixed", inset: 0, zIndex: 200, background: "rgba(0,0,0,0.72)", display: "flex", alignItems: "flex-end", justifyContent: "center" }}
          >
            <div style={{ width: "100%", maxWidth: 768, maxHeight: "82vh", background: "var(--bg-card2)", borderRadius: "20px 20px 0 0", display: "flex", flexDirection: "column", overflow: "hidden" }}>
              <div style={{ display: "flex", justifyContent: "center", padding: "10px 0 4px" }}>
                <div style={{ width: 40, height: 4, borderRadius: 2, background: "rgba(255,255,255,0.2)" }} />
              </div>
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", padding: "8px 20px 14px" }}>
                <div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: "var(--text-strong)" }}>Add Crop to Mix</div>
                  <div style={{ fontSize: 12, color: "var(--text-soft)" }}>Select a crop, then choose its current stage</div>
                </div>
                <button onClick={() => setMixModalOpen(false)} style={{ width: 30, height: 30, borderRadius: "50%", background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.12)", cursor: "pointer", color: "var(--text-muted)", fontSize: 14, fontFamily: "inherit" }}>✕</button>
              </div>
              <div style={{ overflowY: "auto", padding: "0 12px 24px" }}>
                <div className="crop-modal-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
                  {visibleCropKeys.map(k => {
                    const c = CROPS[k];
                    const alreadyIn = mixCrops.some(m => m.cropKey === k);
                    return (
                      <button
                        key={k}
                        disabled={alreadyIn}
                        onClick={() => {
                          // Add with default stage 0
                          setMixCrops(prev => [...prev, { cropKey: k, stageIdx: 0 }]);
                          setMixModalOpen(false);
                        }}
                        style={{
                          display: "flex", flexDirection: "column", alignItems: "center",
                          padding: "10px 6px 8px", borderRadius: 12,
                          cursor: alreadyIn ? "not-allowed" : "pointer", opacity: alreadyIn ? 0.4 : 1,
                          border: "2px solid rgba(255,255,255,0.06)", background: "var(--bg-card)",
                          fontFamily: "inherit",
                        }}
                      >
                        <div style={{ width: 72, height: 72, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 6 }}>
                          <CropIcon cropKey={k} emoji={c.icon} size={68} style={{ width: 68, height: 68, objectFit: "contain", filter: "drop-shadow(0 3px 8px rgba(0,0,0,0.5))" }} />
                        </div>
                        <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-soft2)" }}>{c.name}</div>
                        {alreadyIn && <div style={{ fontSize: 9, color: "var(--active)", marginTop: 2 }}>Already added</div>}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Crop Select Modal — full-screen overlay matching the mockup */}
        {cropModalOpen && (
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Select Crop"
            onClick={e => { if (e.target === e.currentTarget) setCropModalOpen(false); }}
            style={{
              position: "fixed", inset: 0, zIndex: 200,
              background: "rgba(0,0,0,0.72)",
              display: "flex", alignItems: "flex-end", justifyContent: "center",
              padding: "0",
            }}
          >
            <div style={{
              width: "100%", maxWidth: isMobile ? "100%" : 768,
              maxHeight: "92vh",
              background: "linear-gradient(180deg, #0a1f10 0%, var(--shell-b) 100%)",
              borderRadius: "20px 20px 0 0",
              display: "flex", flexDirection: "column",
              overflow: "hidden",
              boxShadow: "0 -8px 40px rgba(0,0,0,0.6)",
            }}>
              {/* Drag handle */}
              <div style={{ display: "flex", justifyContent: "center", padding: "10px 0 4px" }}>
                <div style={{ width: 40, height: 4, borderRadius: 2, background: "rgba(255,255,255,0.2)" }} />
              </div>

              {/* Modal header */}
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", padding: "8px 20px 14px" }}>
                <div>
                  <div style={{ fontSize: isMobile ? 26 : 34, fontWeight: 900, color: "var(--text-strong)", marginBottom: 6, letterSpacing: -0.5 }}>Select Crop</div>
                  <div style={{ fontSize: isMobile ? 14 : 17, color: "#9bb89c" }}>Choose a crop to manage in your farm</div>
                </div>
                <button
                  onClick={() => setCropModalOpen(false)}
                  aria-label="Close"
                  style={{
                    width: 34, height: 34, borderRadius: "50%",
                    background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.12)",
                    cursor: "pointer", color: "var(--text-muted)", fontSize: 16, fontFamily: "inherit",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0, marginTop: 4,
                  }}
                >✕</button>
              </div>

              {/* Search */}
              <div style={{ padding: "0 16px 12px" }}>
                <div style={{ position: "relative" }}>
                  <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--text-soft)", pointerEvents: "none", display: "flex" }}><Icon name="search" size={16} /></span>
                  <input
                    type="search"
                    value={cropSearch}
                    onChange={e => setCropSearch(e.target.value)}
                    placeholder="Search crops..."
                    aria-label="Search crops"
                    autoFocus
                    style={{
                      width: "100%", padding: "11px 12px 11px 40px",
                      borderRadius: 12, fontSize: 14,
                      border: "1px solid var(--border-soft)",
                      background: "rgba(0,0,0,0.3)", color: "var(--text-main)",
                      outline: "none", boxSizing: "border-box", fontFamily: "inherit",
                    }}
                  />
                  {cropSearch && (
                    <button onClick={() => setCropSearch("")} aria-label="Clear search"
                      style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "rgba(255,255,255,0.08)", border: "none", borderRadius: "50%", width: 22, height: 22, cursor: "pointer", color: "var(--text-muted)", fontSize: 13, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      ✕
                    </button>
                  )}
                </div>
              </div>

              {/* Crop grid — scrollable */}
              <div style={{ overflowY: "auto", padding: "0 12px 24px", flex: 1 }}>
                {filteredCropKeys.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "40px 20px", fontSize: 14, color: "var(--text-soft)" }}>
                    No crops match "{cropSearch}"
                  </div>
                ) : (
                  (() => {
                    const cardOf = (k) => {
                      const c = CROPS[k];
                      const sel = cropKey === k;
                      return (
                        <button
                          key={k}
                          onClick={() => { selectCrop(k); setCropModalOpen(false); }}
                          style={{
                            display: "flex", flexDirection: "column", alignItems: "center",
                            padding: isMobile ? "14px 10px 12px" : "18px 12px 14px", minHeight: isMobile ? 156 : 184,
                            borderRadius: 12, cursor: "pointer",
                            border: sel ? "2px solid #5df85b" : "2px solid rgba(255,255,255,0.08)",
                            background: sel ? "radial-gradient(circle at 50% 35%, rgba(93,248,91,0.14), rgba(13,32,16,0.92))" : "linear-gradient(180deg, rgba(13,43,23,0.92), rgba(8,31,16,0.96))",
                            boxShadow: sel ? "0 0 20px rgba(93,248,91,0.32), inset 0 0 22px rgba(93,248,91,0.08)" : "inset 0 0 18px rgba(255,255,255,0.015)",
                            fontFamily: "inherit",
                          }}
                        >
                          <div style={{ width: isMobile ? 100 : 126, height: isMobile ? 88 : 110, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 10 }}>
                            <CropIcon
                              cropKey={k}
                              emoji={c.icon}
                              size={isMobile ? 84 : 112}
                              style={{ width: isMobile ? 84 : 112, height: isMobile ? 78 : 104, objectFit: "contain", filter: "drop-shadow(0 8px 18px rgba(0,0,0,0.55))" }}
                            />
                          </div>
                          <div style={{ fontSize: 18, fontWeight: 800, color: sel ? "var(--text-strong)" : "#d7ead7", textAlign: "center", lineHeight: 1.2, marginBottom: 4 }}>
                            {c.name}
                          </div>
                          <div style={{ fontSize: 15, fontWeight: 700, color: sel ? "var(--active)" : "var(--text-muted)" }}>
                            {c.daysToHarvest}d
                          </div>
                          {c.stages && c.stages.length > 0 && (
                            <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-soft)", marginTop: 3 }}>
                              EC {Math.min(...c.stages.map(s => s.ecMin))}–{Math.max(...c.stages.map(s => s.ecMax))}
                            </div>
                          )}
                        </button>
                      );
                    };
                    const gridCols = { display: "grid", gridTemplateColumns: isMobile ? "1fr" : isTablet ? "repeat(2, minmax(0, 1fr))" : "repeat(3, minmax(0, 1fr))", gap: 14 };
                    const groups = CATEGORY_ORDER
                      .map(cat => ({ cat, keys: filteredCropKeys.filter(k => (CROP_CATEGORY[k] || "Other") === cat) }))
                      .filter(g => g.keys.length);
                    // While searching, a single flat grid reads better than headers.
                    if (cropSearch.trim()) {
                      return <div className="crop-modal-grid" style={gridCols}>{filteredCropKeys.map(cardOf)}</div>;
                    }
                    return groups.map(g => (
                      <div key={g.cat} style={{ marginBottom: 18 }}>
                        <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--text-soft)", margin: "2px 4px 10px" }}>{g.cat}</div>
                        <div className="crop-modal-grid" style={gridCols}>{g.keys.map(cardOf)}</div>
                      </div>
                    ));
                  })()
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── GERMINATION PANEL ── */}
        {showStep1 && GERMINATION[cropKey] && (
          <div style={card}>
            <PanelHeader
              icon={<Icon name="sprout" size={22} color="var(--active)" />}
              label="Germination Guide"
            summary={<><strong style={{color:"var(--text-strong)"}}>{GERMINATION[cropKey].daysToSprout}d</strong> to sprout · {GERMINATION[cropKey].optimalTemp}{MEDIUM_GUIDANCE[cropKey] ? ` · ${({oasis:"Oasis",soil:"Soil",coco:"Coco",rockwool:"Rockwool",rapidRooter:"Rapid Rooter",cottonBall:"Cotton Ball"}[MEDIUM_GUIDANCE[cropKey].ideal] || "")} ideal` : ""}</>}
              badge={GERMINATION[cropKey].lightTiming === "during" ? "☀️ Light from sowing" : "🌙→☀️ After sprout"}
              badgeType="blue"
              isOpen={openPanels.germination}
              onToggle={() => togglePanel("germination")}
            />
            {openPanels.germination && (
              <div id="germination-content" style={{ padding: "12px 16px 14px" }}>
                {/* Quick facts */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 8, marginBottom: 12 }}>
                  <div style={{ padding: "8px 10px", borderRadius: 8, background: "rgba(0,0,0,0.2)" }}>
                    <div style={{ fontSize: 10, color: "var(--text-soft)" }}>Sprouts in</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "var(--active)" }}>{GERMINATION[cropKey].daysToSprout} days</div>
                  </div>
                  <div style={{ padding: "8px 10px", borderRadius: 8, background: "rgba(0,0,0,0.2)" }}>
                    <div style={{ fontSize: 10, color: "var(--text-soft)" }}>Optimal temp</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "var(--active)" }}>{GERMINATION[cropKey].optimalTemp}</div>
                  </div>
                  <div style={{ padding: "8px 10px", borderRadius: 8, background: "rgba(0,0,0,0.2)" }}>
                    <div style={{ fontSize: 10, color: "var(--text-soft)" }}>Seed depth</div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-bright)" }}>{GERMINATION[cropKey].seedDepth}</div>
                  </div>
                  <div style={{ padding: "8px 10px", borderRadius: 8, background: "rgba(0,0,0,0.2)" }}>
                    <div style={{ fontSize: 10, color: "var(--text-soft)" }}>Light during germ</div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: GERMINATION[cropKey].lightTiming === "during" ? "#ffd54f" : "#90caf9" }}>
                      {GERMINATION[cropKey].lightTiming === "during" ? "☀️ From sowing" : "🌙→☀️ After sprout"}
                    </div>
                  </div>
                </div>

                {/* Light timing detail */}
                {GERMINATION[cropKey].lightTimingNote && (
                  <div style={{ marginBottom: 12, padding: "10px 12px", borderRadius: 8, background: GERMINATION[cropKey].lightTiming === "during" ? "rgba(255,213,79,0.06)" : "rgba(144,202,249,0.06)", border: `1px solid ${GERMINATION[cropKey].lightTiming === "during" ? "rgba(255,213,79,0.18)" : "rgba(144,202,249,0.18)"}`, fontSize: 12, color: "var(--text-bright)", lineHeight: 1.5 }}>
                    <strong style={{ color: GERMINATION[cropKey].lightTiming === "during" ? "#ffd54f" : "#90caf9" }}>
                      {GERMINATION[cropKey].lightTiming === "during" ? "💡 Light from day one:" : "🌙→💡 Switch to light when:"}
                    </strong>{" "}
                    {GERMINATION[cropKey].lightTimingNote}
                  </div>
                )}

                {/* Cover instruction */}
                <div style={{ marginBottom: 12, padding: "8px 10px", borderRadius: 8, background: "rgba(255,255,255,0.03)", fontSize: 12, color: "var(--text-muted)" }}>
                  <strong style={{ color: "var(--text-bright)" }}>Cover:</strong> {GERMINATION[cropKey].coverInstruction}
                </div>

                {/* Watering method */}
                <div style={{ marginBottom: 12, padding: "10px 12px", borderRadius: 8, background: "rgba(52,201,255,0.06)", border: "1px solid rgba(52,201,255,0.15)" }}>
                  <div style={{ fontSize: 11, color: "#5ab8e8", textTransform: "uppercase", letterSpacing: 1, fontWeight: 700, marginBottom: 6 }}>
                    💧 Watering Method
                  </div>
                  <div style={{ fontSize: 12, color: "#c8e8f8", lineHeight: 1.6 }}>
                    {GERMINATION[cropKey].wateringMethod}
                  </div>
                </div>

                {/* Humidity dome */}
                <div style={{ marginBottom: 12, padding: "10px 12px", borderRadius: 8, background: GERMINATION[cropKey].humidityDome ? "rgba(181,224,38,0.06)" : "rgba(255,150,50,0.06)", border: `1px solid ${GERMINATION[cropKey].humidityDome ? "rgba(181,224,38,0.2)" : "rgba(255,150,50,0.2)"}` }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, color: GERMINATION[cropKey].humidityDome ? "var(--active)" : "#ff9a50" }}>
                      {GERMINATION[cropKey].humidityDome ? "✅ Humidity Dome — Recommended" : "🚫 Humidity Dome — Not Needed"}
                    </div>
                  </div>
                  <div style={{ fontSize: 12, color: GERMINATION[cropKey].humidityDome ? "#d8f0a0" : "#ffc890", lineHeight: 1.6 }}>
                    {GERMINATION[cropKey].humidityDomeNote}
                  </div>
                </div>

                {/* Seeds per hole / Seed type */}
                <div style={{ marginBottom: 12, padding: "10px 12px", borderRadius: 8, background: "rgba(76,175,80,0.06)", border: "1px solid rgba(90,185,90,0.18)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                    <div style={{ fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>Seeds per hole</div>
                    {GERMINATION[cropKey].pelletedAvailable && (
                      <span style={{ fontSize: 9, padding: "2px 6px", borderRadius: 10, background: "rgba(255,213,79,0.15)", border: "1px solid rgba(255,213,79,0.25)", color: "#ffd54f", fontWeight: 700, letterSpacing: 0.5 }}>
                        ⭐ PELLETED OPTION
                      </span>
                    )}
                  </div>

                  {/* Seed type toggle */}
                  <div style={{ display: "flex", gap: 6, marginBottom: 10 }} role="tablist">
                    <button
                      onClick={() => setSeedType("bare")}
                      role="tab"
                      aria-selected={seedType === "bare"}
                      style={{
                        flex: 1, padding: "8px 10px", borderRadius: 8, cursor: "pointer", textAlign: "center",
                        border: seedType === "bare" ? `2px solid ${crop.color}` : "2px solid rgba(255,255,255,0.08)",
                        background: seedType === "bare" ? `${crop.color}18` : "rgba(255,255,255,0.02)",
                        fontFamily: "inherit",
                      }}
                    >
                      <div style={{ fontSize: 14 }}>🌰</div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: seedType === "bare" ? "var(--text-strong)" : "var(--text-muted)", marginTop: 1 }}>Bare / Regular</div>
                    </button>
                    <button
                      onClick={() => setSeedType("pelleted")}
                      disabled={!GERMINATION[cropKey].pelletedAvailable}
                      role="tab"
                      aria-selected={seedType === "pelleted"}
                      style={{
                        flex: 1, padding: "8px 10px", borderRadius: 8,
                        cursor: GERMINATION[cropKey].pelletedAvailable ? "pointer" : "not-allowed",
                        textAlign: "center",
                        opacity: GERMINATION[cropKey].pelletedAvailable ? 1 : 0.4,
                        border: seedType === "pelleted" && GERMINATION[cropKey].pelletedAvailable
                          ? `2px solid ${crop.color}`
                          : "2px solid rgba(255,255,255,0.08)",
                        background: seedType === "pelleted" && GERMINATION[cropKey].pelletedAvailable
                          ? `${crop.color}18`
                          : "rgba(255,255,255,0.02)",
                        fontFamily: "inherit",
                      }}
                    >
                      <div style={{ fontSize: 14 }}>⚪</div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: seedType === "pelleted" && GERMINATION[cropKey].pelletedAvailable ? "var(--text-strong)" : "var(--text-muted)", marginTop: 1 }}>
                        Pelleted{!GERMINATION[cropKey].pelletedAvailable ? " (N/A)" : ""}
                      </div>
                    </button>
                  </div>

                  {/* Count display */}
                  <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", borderRadius: 10, background: "rgba(0,0,0,0.2)" }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 10, color: "var(--text-soft)", textTransform: "uppercase", letterSpacing: 1 }}>Recommended</div>
                      <div style={{ fontSize: 22, fontWeight: 700, color: crop.color, fontVariantNumeric: "tabular-nums" }}>
                        {seedType === "pelleted" && GERMINATION[cropKey].pelletedAvailable
                          ? "1"
                          : GERMINATION[cropKey].seedsPerHole}
                        <span style={{ fontSize: 13, color: "var(--text-muted)", fontWeight: 400, marginLeft: 4 }}>per hole/cube</span>
                      </div>
                    </div>
                    <div style={{ fontSize: 32 }}>
                      {seedType === "pelleted" && GERMINATION[cropKey].pelletedAvailable ? "⚪" : "🌰"}
                    </div>
                  </div>

                  {/* Seed-type-specific tip */}
                  <div style={{ marginTop: 8, fontSize: 11, color: "var(--text-muted)", lineHeight: 1.5 }}>
                    {seedType === "pelleted" && GERMINATION[cropKey].pelletedAvailable ? (
                      <>⚪ <strong style={{ color: "var(--text-strong)" }}>Pelleted seeds</strong> are pre-coated for uniform sizing — sow one per hole, no thinning needed, near 100% germination expected.</>
                    ) : (
                      <>🌰 {GERMINATION[cropKey].seedingNote}</>
                    )}
                  </div>
                </div>

                {/* Climate-specific note */}
                {GERMINATION[cropKey].climateNotes && GERMINATION[cropKey].climateNotes[userLocation.climateType] && (
                  <div style={{ marginBottom: 12, padding: "8px 10px", borderRadius: 8, background: "rgba(255,167,38,0.08)", border: "1px solid rgba(255,167,38,0.2)", fontSize: 12, color: "#ffcc80" }}>
                    📍 <strong>{CLIMATE_TYPES[userLocation.climateType]?.label || "Climate"} tip:</strong> {GERMINATION[cropKey].climateNotes[userLocation.climateType]}
                  </div>
                )}

                {/* Medium selector */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <div style={{ ...secLbl, marginBottom: 0 }}>Growing Medium</div>
                  {MEDIUM_GUIDANCE[cropKey] && (
                    <div style={{ fontSize: 10, color: "#90c870" }}>
                      ⭐ Ideal: <strong style={{ color: "var(--text-strong)" }}>
                        {({
                          oasis: "Oasis",
                          soil: "Soil",
                          coco: "Coco",
                          rockwool: "Rockwool",
                          rapidRooter: "Rapid Rooter",
                          cottonBall: "Cotton Ball",
                          seedMix: "Seed Mix",
                        })[MEDIUM_GUIDANCE[cropKey].ideal]}
                      </strong>
                    </div>
                  )}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6, marginBottom: 10 }} role="tablist">
                  {[
                    { key: "oasis", label: "Oasis", icon: "🟦" },
                    { key: "soil", label: "Soil", icon: "🟫" },
                    { key: "coco", label: "Coco", icon: "🟧" },
                    { key: "rockwool", label: "Rockwool", icon: "🟨" },
                    { key: "rapidRooter", label: "Rapid Rooter", icon: "🟢" },
                    { key: "cottonBall", label: "Cotton Ball", icon: "⚪" },
                    { key: "seedMix", label: "Seed Mix", icon: "🟤" },
                  ].map(m => {
                    const sel = germMedium === m.key;
                    const guidance = MEDIUM_GUIDANCE[cropKey];
                    const isIdeal = guidance && guidance.ideal === m.key;
                    const notRec = guidance && guidance.notRecommended.find(n => n.medium === m.key);
                    return (
                      <button
                        key={m.key}
                        onClick={() => setGermMedium(m.key)}
                        role="tab"
                        aria-selected={sel}
                        aria-label={`${m.label}${isIdeal ? " (ideal)" : ""}${notRec ? " (not recommended)" : ""}`}
                        style={{
                          padding: "10px 4px", borderRadius: 10, cursor: "pointer", textAlign: "center",
                          position: "relative",
                          border: sel
                            ? `2px solid ${crop.color}`
                            : isIdeal
                            ? "2px solid rgba(165,214,167,0.4)"
                            : notRec
                            ? "2px solid rgba(239,83,80,0.25)"
                            : "2px solid rgba(255,255,255,0.08)",
                          background: sel
                            ? `${crop.color}18`
                            : isIdeal
                            ? "rgba(165,214,167,0.06)"
                            : notRec
                            ? "rgba(239,83,80,0.04)"
                            : "rgba(255,255,255,0.02)",
                          opacity: notRec && !sel ? 0.6 : 1,
                        }}
                      >
                        {/* Corner badge */}
                        {isIdeal && (
                          <span style={{
                            position: "absolute", top: -6, right: -4,
                            background: "#78c854", color: "var(--bg-inset)",
                            fontSize: 10, fontWeight: 700,
                            padding: "1px 5px", borderRadius: 8,
                            border: "1px solid var(--bg-inset)",
                          }} aria-hidden="true">⭐</span>
                        )}
                        {notRec && !isIdeal && (
                          <span style={{
                            position: "absolute", top: -6, right: -4,
                            background: "#ef5350", color: "var(--text-strong)",
                            fontSize: 10, fontWeight: 700,
                            padding: "1px 5px", borderRadius: 8,
                            border: "1px solid var(--bg-inset)",
                          }} aria-hidden="true">✕</span>
                        )}
                        <div style={{ fontSize: 16 }}>{m.icon}</div>
                        <div style={{ fontSize: 10, fontWeight: 600, color: sel ? "var(--text-strong)" : "var(--text-muted)", marginTop: 2, lineHeight: 1.2 }}>{m.label}</div>
                        {m.key === "rockwool" && (
                          <div style={{ fontSize: 8, fontWeight: 700, color: "#ffb74d", marginTop: 2, lineHeight: 1 }} aria-label="Requires pH pre-soak">⚠ pH pre-soak</div>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Not-recommended warning (shown when a not-recommended medium is selected) */}
                {MEDIUM_GUIDANCE[cropKey] && MEDIUM_GUIDANCE[cropKey].notRecommended.find(n => n.medium === germMedium) && (
                  <div style={{ marginBottom: 10, padding: "10px 12px", borderRadius: 8, background: "rgba(239,83,80,0.1)", border: "1px solid rgba(239,83,80,0.3)", fontSize: 12, color: "#ef9a9a", lineHeight: 1.5 }}>
                    <strong style={{ color: "#ff8a80" }}>✕ Not recommended for {crop.name.toLowerCase()}</strong>
                    <div style={{ marginTop: 4, color: "#e0a0a0" }}>
                      {MEDIUM_GUIDANCE[cropKey].notRecommended.find(n => n.medium === germMedium).reason}. Consider <strong style={{ color: "var(--text-strong)" }}>
                        {({
                          oasis: "Oasis",
                          soil: "Soil",
                          coco: "Coco",
                          rockwool: "Rockwool",
                          rapidRooter: "Rapid Rooter",
                          cottonBall: "Cotton Ball",
                          seedMix: "Seed Mix",
                        })[MEDIUM_GUIDANCE[cropKey].ideal]}
                      </strong> instead.
                    </div>
                  </div>
                )}

                {/* Ideal medium confirmation (when ideal is selected) */}
                {MEDIUM_GUIDANCE[cropKey] && MEDIUM_GUIDANCE[cropKey].ideal === germMedium && (
                  <div style={{ marginBottom: 10, padding: "8px 12px", borderRadius: 8, background: "rgba(165,214,167,0.08)", border: "1px solid rgba(165,214,167,0.2)", fontSize: 11, color: "#90c870", lineHeight: 1.5 }}>
                    ⭐ <strong style={{ color: "#78c854" }}>Best choice for {crop.name.toLowerCase()}.</strong> This is the recommended medium for this crop.
                  </div>
                )}

                {/* Rockwool pH-soak warning (only shown when rockwool selected) */}
                {germMedium === "rockwool" && (
                  <div style={{ marginBottom: 10, padding: "10px 12px", borderRadius: 8, background: "rgba(255,167,38,0.1)", border: "1px solid rgba(255,167,38,0.3)", fontSize: 12, color: "#ffcc80", lineHeight: 1.5 }}>
                    <strong style={{ color: "#ffb74d" }}>⚠ Critical: Pre-soak rockwool in pH 5.5 water</strong>
                    <div style={{ marginTop: 4, color: "#e0c69f" }}>
                      Rockwool ships at pH 7.0–8.5 (highly alkaline). Sowing directly into untreated cubes will stall or fail germination for most crops. Pre-soak 30+ min (preferably overnight) in pH 5.5 adjusted water before use. Don't squeeze the cubes — let them drain naturally.
                    </div>
                  </div>
                )}

                {/* Rapid Rooter prep note (only when selected) */}
                {germMedium === "rapidRooter" && (
                  <div style={{ marginBottom: 10, padding: "10px 12px", borderRadius: 8, background: "rgba(76,175,80,0.08)", border: "1px solid rgba(90,185,90,0.2)", fontSize: 12, color: "#90c870", lineHeight: 1.5 }}>
                    <strong style={{ color: "#78c854" }}>ℹ Rapid Rooter setup</strong>
                    <div style={{ marginTop: 4, color: "#c5dcc5" }}>
                      Compressed peat plugs, pH-buffered out of the package. Just pre-moisten with pH 5.5–6.5 water until plugs are soft and pliable. No long soak needed. Humidity dome strongly recommended.
                    </div>
                  </div>
                )}

                {/* Cotton ball prep note (only when selected) */}
                {germMedium === "cottonBall" && (
                  <div style={{ marginBottom: 10, padding: "10px 12px", borderRadius: 8, background: "rgba(255,167,38,0.1)", border: "1px solid rgba(255,167,38,0.3)", fontSize: 12, color: "#ffcc80", lineHeight: 1.5 }}>
                    <strong style={{ color: "#ffb74d" }}>⚠ Cotton ball — short-term germination only</strong>
                    <div style={{ marginTop: 4, color: "#e0c69f" }}>
                      Use 100% cotton (not synthetic/polyester — those won't absorb). Dampen with pH 5.8–6.2 water until moist but not dripping, place in shallow tray with humidity dome. <strong>No nutrients</strong> — cotton is inert and offers zero buffering. Transplant <strong>within 5–7 days of sprouting</strong>, before roots tangle in the fibres. Tease the cotton apart gently rather than pulling — cut excess fibres with scissors if needed. High mold risk if kept wet too long: lift the dome daily to air out. Best for fast germinators like lettuce, leafy greens, and quick herbs. Not suitable for taprooted or slow-germinating crops.
                    </div>
                  </div>
                )}

                {/* Seed starting mix note (only when selected) */}
                {germMedium === "seedMix" && (
                  <div style={{ marginBottom: 10, padding: "10px 12px", borderRadius: 8, background: "rgba(141,110,99,0.12)", border: "1px solid rgba(161,136,127,0.35)", fontSize: 12, color: "#d7c3b8", lineHeight: 1.5 }}>
                    <strong style={{ color: "#c8a68f" }}>🟤 Seed starting mix — the nursery-tray method</strong>
                    <div style={{ marginTop: 4, color: "#c9b4a6" }}>
                      A loose soilless blend (usually coco or peat + perlite/vermiculite, lightly pre-charged). Ideal for starting <strong>many seedlings at once</strong> in an open tray or cell pack — cheap, forgiving, and high germination rates. Pre-moisten so it's damp but crumbly, never soggy. Because it's inert-ish and lightly fed, it buffers pH gently but won't hold nutrients long.
                    </div>
                    <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid rgba(161,136,127,0.25)", color: "#e0b89f" }}>
                      <strong style={{ color: "#e8a87c" }}>⚠ Before it goes into your hydro system:</strong> loose mix sheds particles that clog NFT channels and tower/pump lines. When transplanting to <strong>NFT or towers</strong>, gently rinse the root ball in pH'd water to wash away clinging mix, then seat the bare roots into a net pot with clay pebbles or a collar. For <strong>DWC, media beds, or drip</strong>, you can move the whole plug — loose particles matter far less there. Transplant once true leaves and white roots appear (see below), before roots knit too tightly into the tray.
                    </div>
                  </div>
                )}

                {/* Method instructions */}
                <div style={{ padding: "12px", borderRadius: 10, background: `${crop.color}10`, border: `1px solid ${crop.color}25`, fontSize: 13, color: "var(--text-bright)", lineHeight: 1.7 }}>
                  {GERMINATION[cropKey][germMedium] || (germMedium === "seedMix" ? SEED_MIX_GUIDANCE : "Guidance for this medium isn't available for this crop yet — follow the general depth and moisture notes above.")}
                </div>

                {/* Transplant trigger */}
                <div style={{ marginTop: 10, padding: "10px 12px", borderRadius: 8, background: "rgba(76,175,80,0.08)", border: "1px solid rgba(90,185,90,0.2)", fontSize: 12, color: "#90c870" }}>
                  <strong style={{ color: "#78c854" }}>✓ Transplant when:</strong> {GERMINATION[cropKey].transplantWhen}
                </div>

                {/* Common issues */}
                {GERMINATION[cropKey].issues && GERMINATION[cropKey].issues.length > 0 && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontSize: 11, color: "#ef9a9a", fontWeight: 700, marginBottom: 6, textTransform: "uppercase", letterSpacing: 1 }}>⚠ Watch out for</div>
                    <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: "var(--text-soft2)", lineHeight: 1.7 }}>
                      {GERMINATION[cropKey].issues.map((iss, i) => <li key={i}>{iss}</li>)}
                    </ul>
                  </div>
                )}

                {/* General tips toggle */}
                <button
                  onClick={() => setGermTipsOpen(o => !o)}
                  aria-expanded={germTipsOpen}
                  style={{
                    marginTop: 12, width: "100%", padding: "8px", borderRadius: 8, fontSize: 12,
                    border: "1px solid var(--border-soft)", background: "rgba(255,255,255,0.02)",
                    color: "var(--text-muted)", cursor: "pointer",
                  }}
                >
                  {germTipsOpen ? "▼ Hide" : "▶ Show"} general germination tips
                </button>
                {germTipsOpen && (
                  <ul style={{ marginTop: 8, paddingLeft: 18, fontSize: 12, color: "var(--text-muted)", lineHeight: 1.7 }}>
                    {GERMINATION_GENERAL_TIPS.map((tip, i) => <li key={i}>{tip}</li>)}
                  </ul>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── pH / STAGE PANEL ── */}
        <div style={{ ...card, display: showStep2 ? undefined : "none" }}>
          <PanelHeader
            icon={<Icon name="test" size={22} color="var(--active)" />}
            label="Optimal pH"
            summary={<>Range <strong style={{color:"var(--text-strong)"}}>{stage.phMin}–{stage.phMax}</strong> · Ideal <strong style={{color:"var(--text-strong)"}}>{stage.phTarget}</strong> · Stage: {stage.label}</>}
            badge={`pH ${stage.phTarget}`}
            badgeType="blue"
            isOpen={openPanels.ph}
            onToggle={() => togglePanel("ph")}
          />
          {openPanels.ph && (<div style={{ padding: "12px 16px 14px" }}>
          <div style={secLbl}>Growth Stage — {crop.name}</div>
          <div style={{ display: "flex", gap: 5, overflowX: "auto", paddingBottom: 4 }}>
            {crop.stages.map((s, i) => (
              <button key={i} onClick={() => { setStageIdx(i); setCustomEc(null); }} style={{ minWidth: crop.stages.length > 4 ? 90 : 0, flex: crop.stages.length <= 4 ? 1 : "none", padding: "10px 8px 8px", borderRadius: 10, cursor: "pointer", textAlign: "center", border: safeStageIdx === i ? `2px solid ${crop.color}` : "2px solid rgba(255,255,255,0.08)", background: safeStageIdx === i ? `${crop.color}15` : "rgba(255,255,255,0.02)" }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: safeStageIdx === i ? "var(--text-strong)" : "var(--text-soft)" }}>{s.label}</div>
                <div style={{ fontSize: 10, color: "var(--text-soft)", marginTop: 2 }}>{s.sub}</div>
                <div style={{ fontSize: 10, color: "var(--text-soft)", marginTop: 2 }}>EC {s.ecMin}–{s.ecMax}</div>
              </button>
            ))}
          </div>

          {/* pH Guidance */}
          <div style={{ ...secLbl, marginTop: 16 }}>Optimal pH — {crop.name} · {stage.label}</div>

          {/* Range visualization */}
          <div style={{ position: "relative", height: 36, borderRadius: 18, background: "linear-gradient(90deg, #ef5350 0%, #ffb74d 20%, #ffd54f 35%, #78c854 50%, #ffd54f 65%, #ffb74d 80%, #ef5350 100%)", overflow: "hidden", marginBottom: 8 }}>
            {/* Optimal zone highlight */}
            <div style={{
              position: "absolute",
              left: `${((stage.phMin - 4) / 4) * 100}%`,
              width: `${((stage.phMax - stage.phMin) / 4) * 100}%`,
              top: 0, bottom: 0,
              background: "rgba(255,255,255,0.2)",
              borderLeft: "2px solid #fff",
              borderRight: "2px solid #fff",
              boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.3)",
            }} />
            {/* Ideal target marker */}
            <div style={{
              position: "absolute",
              left: `calc(${((stage.phTarget - 4) / 4) * 100}% - 1px)`,
              top: -2, bottom: -2,
              width: 3,
              background: "#fff",
              boxShadow: "0 0 4px rgba(0,0,0,0.5), 0 0 8px rgba(255,255,255,0.6)",
            }} />
            {/* Range value labels inside the optimal zone */}
            <div style={{
              position: "absolute",
              left: `${((stage.phMin - 4) / 4) * 100}%`,
              width: `${((stage.phMax - stage.phMin) / 4) * 100}%`,
              top: 0, bottom: 0,
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "0 6px",
              fontSize: 11, fontWeight: 700, color: "var(--text-strong)",
              textShadow: "0 1px 2px rgba(0,0,0,0.6)",
              fontVariantNumeric: "tabular-nums",
              boxSizing: "border-box",
            }}>
              <span>{stage.phMin}</span>
              <span>{stage.phMax}</span>
            </div>
          </div>

          {/* Axis labels */}
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "var(--text-soft)", marginBottom: 14 }}>
            <span>4.0</span><span>5.0</span><span>6.0</span><span>7.0</span><span>8.0</span>
          </div>

          {/* Range + ideal stats */}
          <div style={{ display: "flex", gap: 8, marginBottom: 10, flexDirection: isMobile ? "column" : "row" }}>
            <div style={{ flex: 1, padding: "10px 12px", borderRadius: 8, background: `${crop.color}10`, border: `1px solid ${crop.color}25`, textAlign: "center" }}>
              <div style={{ fontSize: 10, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 2 }}>Safe Range</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: "var(--text-strong)", fontVariantNumeric: "tabular-nums" }}>{stage.phMin} – {stage.phMax}</div>
            </div>
            <div style={{ flex: 1, padding: "10px 12px", borderRadius: 8, background: "var(--border-soft)", border: "1px solid rgba(255,255,255,0.25)", textAlign: "center" }}>
              <div style={{ fontSize: 10, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 2 }}>Ideal</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: "var(--text-strong)", fontVariantNumeric: "tabular-nums" }}>{stage.phTarget}</div>
            </div>
          </div>

          <div style={{ padding: "8px 12px", borderRadius: 8, background: "rgba(0,0,0,0.15)", marginBottom: 10, fontSize: 11, color: "var(--text-muted)", lineHeight: 1.5 }}>
            Anywhere in the safe range is fine. <strong style={{ color: "var(--text-strong)" }}>{stage.phTarget}</strong> is the sweet spot if you can hit it precisely.
          </div>

          {/* Adjustment instructions */}
          <div style={{ fontSize: 12, color: "var(--text-muted)", lineHeight: 1.7 }}>
            <div style={{ marginBottom: 4 }}>
              <span style={{ color: "#ef5350", fontWeight: 700 }}>▼ Too high?</span> Add 35% Food Grade H₂SO₄ drop by drop, then circulate and recheck after ~15 min.
            </div>
            <div>
              <span style={{ color: "#42a5f5", fontWeight: 700 }}>▲ Too low?</span> Add pH-Up (e.g. potassium hydroxide or a commercial pH-up) in small increments, circulate, and recheck. Only dilute with fresh water if EC is also high — water lowers EC too and isn&rsquo;t a reliable way to raise pH.
            </div>
            <div style={{ marginTop: 6, fontSize: 10.5, color: "var(--text-soft)" }}>
              Doses are estimates — always confirm with your own meter.
            </div>
          </div>
          {/* Persistent acid-safety note (field-facing) */}
          <div style={{ marginTop: 10, padding: "8px 11px", borderRadius: 8, background: "rgba(239,83,80,0.08)", border: "1px solid rgba(239,83,80,0.28)", fontSize: 10.5, color: "#f0b0b0", lineHeight: 1.55 }}>
            <strong style={{ color: "#ef9a9a" }}>⚠ Acid safety:</strong> Wear gloves &amp; eye protection. Always add acid to water — never water to acid. Dose in small increments, circulate, and retest. Work in a ventilated area, away from children and animals.
          </div>
          </div>)}
        </div>

        {/* ── EC / NUTRIENT STRENGTH PANEL ── (holds step 2 inputs + step 3 result) */}
        <div style={{ ...card, display: (showStep2 || showStep3) ? undefined : "none" }}>
          {/* Static section header (was a collapsible that wrapped the whole
              lower calculator — see decomposition note). */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "4px 16px 0" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 9, minWidth: 0 }}>
              <Icon name="bolt" size={20} color="var(--active)" />
              <div style={{ minWidth: 0 }}>
                <div style={{ ...secLbl, marginBottom: 0 }}>EC / Nutrient Strength</div>
                <div style={{ fontSize: 10.5, color: "var(--text-muted)", lineHeight: 1.35 }}>Base <strong style={{color:"var(--text-strong)"}}>{baseEc.toFixed(2)}</strong> · Target <strong style={{color:"var(--text-strong)"}}>{effTarget.toFixed(2)}</strong> · Adding <strong style={{color:"var(--text-strong)"}}>{Math.max(0, effTarget - baseEc).toFixed(2)}</strong> EC</div>
              </div>
            </div>
            <span style={{ fontSize: 10, fontWeight: 800, color: "#f0a820", background: "#2d1e00", border: "1px solid #5a3c00", borderRadius: 8, padding: "3px 8px", whiteSpace: "nowrap", flexShrink: 0 }}>{Math.round(effTarget * (sysSettings.ppmScale || 700))} ppm</span>
          </div>
          <div style={{ padding: "0 16px 14px" }}>

        {/* Step 2 group — base EC + tank + target + conditions (contents = no layout box) */}
        <div style={{ display: showStep2 ? "contents" : "none" }}>
        {/* Water Inputs */}
        <div style={{ display: "flex", gap: 10, marginBottom: 14, marginTop: 12 }}>
          <div style={{ ...card, flex: 1, marginBottom: 0 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 5 }}>
              <div style={{ fontSize: 11, color: "var(--text-soft)" }}>Base / Current EC</div>
              <div style={{ fontSize: 9, color: "var(--text-soft)" }}>tap, RO, or top-up</div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <input type="range" min={0} max={Math.max(1.2, effTarget - 0.05)} step={0.05} value={baseEc} onChange={e => setBaseEc(parseFloat(e.target.value))} aria-label="Base water EC value" aria-valuetext={`${baseEc.toFixed(2)} EC`} style={{ flex: 1, accentColor: "#5cb85c" }} />
              <div style={{ background: "rgba(0,0,0,0.3)", borderRadius: 8, padding: "5px 7px", fontSize: 16, fontWeight: 700, minWidth: 44, textAlign: "center", color: "var(--active)", fontVariantNumeric: "tabular-nums" }}>{baseEc.toFixed(2)}</div>
            </div>
            {/* Quick-select presets */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 4, marginTop: 6 }}>
              {[
                { v: 0.05, label: "RO" },
                { v: 0.30, label: "Rain" },
                { v: 0.50, label: "Tap" },
                { v: Math.max(0, effTarget - 0.5), label: "Top-up" },
              ].map(p => (
                <button key={p.label} onClick={() => setBaseEc(parseFloat(p.v.toFixed(2)))} style={{
                  padding: "5px 2px", borderRadius: 6, fontSize: 10, fontWeight: 600, cursor: "pointer",
                  border: Math.abs(baseEc - p.v) < 0.01 ? "1.5px solid #5cb85c" : "1px solid rgba(255,255,255,0.08)",
                  background: Math.abs(baseEc - p.v) < 0.01 ? "rgba(90,185,90,0.15)" : "rgba(255,255,255,0.02)",
                  color: Math.abs(baseEc - p.v) < 0.01 ? "var(--active)" : "#7a9a7a",
                  fontFamily: "inherit",
                }}>{p.label} {p.v.toFixed(2)}</button>
              ))}
            </div>
          </div>
          <div style={{ ...card, flex: 1, marginBottom: 0 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 7 }}>
              <div style={{ fontSize: 11, color: "var(--text-soft)" }}>Tank (Gal)</div>
              <div style={{ fontSize: 16, fontWeight: 800, color: "var(--active)", fontVariantNumeric: "tabular-nums" }}>{gallons} gal</div>
            </div>
            {/* Quick-select chips */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 5, marginBottom: 7 }}>
              {[10, 40, 50, 55, 100, 150, 200, 250].map(v => (
                <button key={v} onClick={() => setGallons(v)} style={{
                  padding: "8px 4px", borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: "pointer",
                  border: gallons === v ? "2px solid #5cb85c" : "1px solid var(--border-soft)",
                  background: gallons === v ? "rgba(90,185,90,0.18)" : "rgba(255,255,255,0.03)",
                  color: gallons === v ? "var(--active)" : "var(--text-muted)",
                  fontFamily: "inherit",
                }}>{v}</button>
              ))}
            </div>
            {/* Stepper for fine tuning */}
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <button
                onClick={() => setGallons(g => Math.max(1, g - 10))}
                aria-label="Decrease by 10 gallons"
                style={{ width: 36, height: 36, borderRadius: 8, fontSize: 18, fontWeight: 700, cursor: "pointer",
                  border: "1px solid var(--border-soft)", background: "rgba(255,255,255,0.03)", color: "var(--active)",
                  display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "inherit", flexShrink: 0 }}
              >−10</button>
              <button
                onClick={() => setGallons(g => Math.max(1, g - 1))}
                aria-label="Decrease by 1 gallon"
                style={{ width: 32, height: 36, borderRadius: 8, fontSize: 16, fontWeight: 700, cursor: "pointer",
                  border: "1px solid var(--border-soft)", background: "rgba(255,255,255,0.03)", color: "var(--active)",
                  display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "inherit", flexShrink: 0 }}
              >−</button>
              <input
                type="number" min={1} max={9999} value={gallons}
                onChange={e => setGallons(Math.max(1, Math.min(9999, parseInt(e.target.value) || 1)))}
                aria-label="Tank gallons"
                style={{ flex: 1, height: 36, padding: "0 8px", borderRadius: 8, fontSize: 15, fontWeight: 700,
                  border: "1px solid var(--border-soft)", background: "rgba(0,0,0,0.3)", color: "var(--active)",
                  textAlign: "center", outline: "none", fontFamily: "inherit", minWidth: 0 }}
              />
              <button
                onClick={() => setGallons(g => Math.min(9999, g + 1))}
                aria-label="Increase by 1 gallon"
                style={{ width: 32, height: 36, borderRadius: 8, fontSize: 16, fontWeight: 700, cursor: "pointer",
                  border: "1px solid var(--border-soft)", background: "rgba(255,255,255,0.03)", color: "var(--active)",
                  display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "inherit", flexShrink: 0 }}
              >+</button>
              <button
                onClick={() => setGallons(g => Math.min(9999, g + 10))}
                aria-label="Increase by 10 gallons"
                style={{ width: 36, height: 36, borderRadius: 8, fontSize: 18, fontWeight: 700, cursor: "pointer",
                  border: "1px solid var(--border-soft)", background: "rgba(255,255,255,0.03)", color: "var(--active)",
                  display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "inherit", flexShrink: 0 }}
              >+10</button>
            </div>
          </div>
        </div>

        {/* Target EC */}
        <div style={card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <div style={{ ...secLbl, marginBottom: 0 }}>Target EC</div>
            {customEc !== null && <button onClick={() => setCustomEc(null)} style={{ fontSize: 11, color: "var(--active)", background: "none", border: "none", cursor: "pointer", textDecoration: "underline" }}>Reset</button>}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 10, color: "var(--text-soft)" }}>{stage.ecMin}</span>
            <input type="range" min={stage.ecMin} max={stage.ecMax} step={0.05} value={baseTarget} onChange={e => setCustomEc(parseFloat(e.target.value))} aria-label={`Target EC for ${crop.name} ${stage.label}`} aria-valuetext={`${baseTarget.toFixed(2)} EC`} style={{ flex: 1, accentColor: crop.color }} />
            <span style={{ fontSize: 10, color: "var(--text-soft)" }}>{stage.ecMax}</span>
            <div style={{ background: "rgba(0,0,0,0.3)", borderRadius: 8, padding: "5px 12px", textAlign: "center", border: `1px solid ${crop.color}40` }}>
              <div style={{ fontSize: 9, color: "var(--text-soft)" }}>Target</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: crop.color, fontVariantNumeric: "tabular-nums" }}>{effTarget.toFixed(2)}</div>
            </div>
          </div>
          <div style={{ marginTop: 8 }}>
            <div style={{ height: 5, borderRadius: 3, background: "rgba(0,0,0,0.3)", overflow: "hidden", display: "flex" }}>
              <div style={{ width: `${(baseEc / Math.max(stage.ecMax + 0.5, 2)) * 100}%`, background: "#5a5a3a", transition: "width 0.3s" }} />
              <div style={{ width: `${(nutEc / Math.max(stage.ecMax + 0.5, 2)) * 100}%`, background: crop.color, transition: "width 0.3s" }} />
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 3, fontSize: 10, color: "var(--text-soft)" }}>
              <span>Base: {baseEc.toFixed(2)}</span><span>Adding: {nutEc.toFixed(2)} EC</span>
            </div>
          </div>
          {baseEc >= effTarget && <div style={{ marginTop: 6, padding: "7px 10px", borderRadius: 8, background: "rgba(255,80,80,0.1)", border: "1px solid rgba(255,80,80,0.15)", fontSize: 12, color: "#ff8a80" }}>Base water EC ({baseEc.toFixed(2)}) meets or exceeds target. Use RO or filtered water.</div>}
          {exceedsRef && !isFlush && (
            <div style={{ marginTop: 6, padding: "7px 10px", borderRadius: 8, background: "rgba(255,213,79,0.08)", border: "1px solid rgba(255,213,79,0.2)", fontSize: 12, color: "#ffd54f" }}>
              ⓘ Heavy feed: nutrient EC ({nutEc.toFixed(2)}) exceeds the standard reference (2.4). Gram amounts scale beyond the typical 2:2:1 mix — appropriate for {crop.name} but mix in stages and verify EC with your meter.
            </div>
          )}
        </div>

        {/* Conditions to watch — driven by the latest logged readings */}
        {(() => {
          const warm = lastWaterTemp != null && lastWaterTemp > 28;
          const highPh = lastPhReading && parseFloat(lastPhReading.value) > 6.2;
          const fMax = (userLocation.showWeather && weather && Array.isArray(weather.daily) && weather.daily.length)
            ? Math.max(...weather.daily.map(dd => dd.tmax).filter(x => x != null))
            : null;
          const hotAhead = fMax != null && fMax >= 33;
          if (!warm && !highPh && !hotAhead) return null;
          const line = { fontSize: 12, color: "#f0c88a", lineHeight: 1.55 };
          const items = [];
          if (hotAhead) items.push(<div key="hot" style={line}><strong style={{ color: "#f0a820" }}>Heat ahead — forecast up to {Math.round(fMax)}°C.</strong> Pre-cool the reservoir, add shade/airflow, and ease the EC target before the peak; warm water reads EC high.</div>);
          if (warm) items.push(<div key="warm" style={line}><strong style={{ color: "#f0a820" }}>Water {lastWaterTemp}°C is warm (&gt;28°C).</strong> Warm reservoirs hold less oxygen and read EC high — ease the EC target down and increase flow, shade, or cooling.</div>);
          if (highPh) items.push(<div key="ph" style={line}><strong style={{ color: "#f0a820" }}>Last pH {parseFloat(lastPhReading.value)} is above 6.2.</strong> Iron &amp; micronutrient uptake drops above 6.2 (yellowing new leaves) — bring pH back toward {stage.phTarget}.</div>);
          return (
            <div style={{ ...card, background: "rgba(240,168,32,0.08)", border: "1px solid rgba(240,168,32,0.3)" }}>
              <div style={{ ...secLbl, color: "#f0a820", marginBottom: 8 }}>⚠ Conditions to watch</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>{items}</div>
            </div>
          );
        })()}

        </div>{/* end step 2 group */}

        {/* Step 3 group — Add Now result + Mixing procedure */}
        <div style={{ display: showStep3 ? "contents" : "none" }}>
        {/* Add Now — the primary action output */}
        <div style={{ ...card, border: "1px solid rgba(181,224,38,0.35)", background: "linear-gradient(180deg, #10240f 0%, #0a1a0b 100%)" }}>
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8, marginBottom: 8 }}>
            <div style={{ ...secLbl, marginBottom: 0, color: "var(--active)" }}>Add Now</div>
            <div style={{ fontSize: 10.5, color: "var(--text-soft)", whiteSpace: "nowrap" }}>{gallons} gal · {crop.name} · {stage.label}</div>
          </div>
          {isFlush ? (
            <div style={{ textAlign: "center", padding: 20 }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: "#42a5f5" }}>Plain Water Only</div>
              <div style={{ fontSize: 13, color: "var(--text-soft)", marginTop: 4 }}>No nutrients during flush. Run pH-adjusted water only.</div>
            </div>
          ) : (<>
            {results.map((r, i) => (
              <div key={r.key} style={{ display: "flex", alignItems: "center", padding: "11px 0", borderBottom: i < results.length - 1 ? "1px solid rgba(255,255,255,0.06)" : "none" }}>
                <div style={{ width: 6, height: 40, borderRadius: 3, background: r.color, marginRight: 12, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text-bright)" }}>{r.label}</div>
                  <div style={{ fontSize: 10, color: "var(--text-soft)", marginTop: 1 }}>{r.gPerGalEff.toFixed(2)} g/gal</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 27, fontWeight: 900, color: "var(--text-strong)", lineHeight: 1.05, fontVariantNumeric: "tabular-nums" }}>{r.totalGrams.toFixed(1)}<span style={{ fontSize: 13, fontWeight: 500, color: "var(--text-soft)", marginLeft: 2 }}>g</span></div>
                  <div style={{ fontSize: 10, color: "var(--text-soft)" }}>{gMeasure(r.totalGrams)}</div>
                </div>
              </div>
            ))}
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <div style={{ flex: 1, background: "rgba(230,81,0,0.08)", border: "1px solid rgba(230,81,0,0.22)", borderRadius: 9, padding: "8px 10px" }}>
                <div style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: 1, textTransform: "uppercase", color: "#ff9e40" }}>Mix A</div>
                <div style={{ fontSize: 11.5, color: "var(--text-soft2)", marginTop: 2, lineHeight: 1.4 }}>MasterBlend + Epsom, dissolved together</div>
              </div>
              <div style={{ flex: 1, background: "rgba(21,101,194,0.08)", border: "1px solid rgba(21,101,194,0.22)", borderRadius: 9, padding: "8px 10px" }}>
                <div style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: 1, textTransform: "uppercase", color: "#64b5f6" }}>Mix B</div>
                <div style={{ fontSize: 11.5, color: "var(--text-soft2)", marginTop: 2, lineHeight: 1.4 }}>Calcium Nitrate on its own — never combine</div>
              </div>
            </div>
            <div style={{ marginTop: 10, fontSize: 10.5, color: "var(--text-soft)", lineHeight: 1.5 }}>
              EC-guided estimate. Add ~75% first, then mix, circulate, and confirm with your meter before topping up to target.
            </div>
          </>)}
        </div>

        {/* Mixing */}
        {!isFlush && (() => {
          const solAGrams = results[0].totalGrams + results[2].totalGrams; // MB + Epsom
          const solBGrams = results[1].totalGrams; // CalNit
          const dilA = dilutionWater(solAGrams, gallons);
          const dilB = dilutionWater(solBGrams, gallons);
          return (
          <SettingsSection title="Mixing Procedure" open={!isMobile || showMixing} onToggle={() => setShowMixing(v => !v)}>
            <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
              <div style={{ flex: 1, background: "rgba(230,81,0,0.08)", borderRadius: 10, padding: "10px 12px", border: "1px solid rgba(230,81,0,0.15)" }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#ff9e40", marginBottom: 3 }}>Solution A</div>
                <div style={{ fontSize: 12, color: "var(--text-soft2)", lineHeight: 1.6 }}>
                  <span style={{ color: RATIOS.masterblend.color, fontWeight: 600 }}>{results[0].totalGrams.toFixed(1)}g</span> MasterBlend<br />
                  <span style={{ color: RATIOS.epsom.color, fontWeight: 600 }}>{results[2].totalGrams.toFixed(1)}g</span> Epsom Salt
                </div>
                <div style={{ marginTop: 6, padding: "5px 8px", borderRadius: 6, background: "rgba(0,0,0,0.25)", border: "1px solid rgba(230,81,0,0.18)" }}>
                  <div style={{ fontSize: 9, color: "#8a6850", textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>Dilute in</div>
                  <div style={{ fontSize: 13, fontWeight: 800, color: "#ffb070" }}>
                    {dilA.displayGal} <span style={{ fontSize: 10, color: "#8a6850", fontWeight: 500 }}>({dilA.displayL})</span>
                  </div>
                  <div style={{ fontSize: 9, color: "var(--text-soft)", marginTop: 1 }}>warm water, dissolve together</div>
                </div>
              </div>
              <div style={{ flex: 1, background: "rgba(21,101,194,0.08)", borderRadius: 10, padding: "10px 12px", border: "1px solid rgba(21,101,194,0.15)" }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#64b5f6", marginBottom: 3 }}>Solution B</div>
                <div style={{ fontSize: 12, color: "var(--text-soft2)", lineHeight: 1.6 }}>
                  <span style={{ color: RATIOS.calciumNitrate.color, fontWeight: 600 }}>{results[1].totalGrams.toFixed(1)}g</span> CalNit
                </div>
                <div style={{ marginTop: 6, padding: "5px 8px", borderRadius: 6, background: "rgba(0,0,0,0.25)", border: "1px solid rgba(21,101,194,0.18)" }}>
                  <div style={{ fontSize: 9, color: "#50708a", textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>Dilute in</div>
                  <div style={{ fontSize: 13, fontWeight: 800, color: "#7cc7f9" }}>
                    {dilB.displayGal} <span style={{ fontSize: 10, color: "#50708a", fontWeight: 500 }}>({dilB.displayL})</span>
                  </div>
                  <div style={{ fontSize: 9, color: "var(--text-soft)", marginTop: 1 }}>warm water, separately</div>
                </div>
              </div>
            </div>
            {[
              `Fill main tank with ~${(gallons - dilA.gallons - dilB.gallons).toFixed(1)} gal of water (reserve room for stock solutions)`,
              `Pre-dissolve Solution A in ${dilA.displayGal} warm water — stir until clear`,
              `Pre-dissolve Solution B in ${dilB.displayGal} warm water — stir until clear (in a separate container)`,
              "Add Solution A to main tank — circulate 5 min",
              "Add Solution B to main tank — circulate 10–15 min",
              `Verify EC ≈ ${effTarget.toFixed(2)}`,
              `pH between ${stage.phMin}–${stage.phMax} (ideal ${stage.phTarget})`,
            ].map((s, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 5 }}>
                <div style={{ width: 20, height: 20, borderRadius: "50%", background: "rgba(90,185,90,0.18)", border: "1.5px solid #5cb85c", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, color: "#5cb85c", flexShrink: 0 }}>{i + 1}</div>
                <div style={{ fontSize: 12, color: "#b4d4b4" }}>{s}</div>
              </div>
            ))}
          </SettingsSection>
          );
        })()}
        </div>{/* end step 3 group */}
          </div>
        </div>

        {/* Wizard navigation — mobile only */}
        {calcWizard && (
          <div style={{ display: "flex", gap: 10, marginTop: 4, marginBottom: 8 }}>
            {calcStep > 1 && (
              <button onClick={() => setCalcStep(s => Math.max(1, s - 1))} style={{
                flex: 1, padding: "13px", borderRadius: 11, cursor: "pointer", fontFamily: "inherit",
                fontWeight: 700, fontSize: 14, border: "1px solid rgba(255,255,255,0.12)",
                background: "rgba(255,255,255,0.03)", color: "var(--text-muted)",
              }}>‹ Back</button>
            )}
            {calcStep < 3 && (
              <button onClick={() => setCalcStep(s => Math.min(3, s + 1))} style={{
                flex: 2, padding: "13px", borderRadius: 11, cursor: "pointer", fontFamily: "inherit",
                fontWeight: 800, fontSize: 14, border: "none",
                background: "linear-gradient(135deg,#2d6030,#5cb85c)", color: "var(--text-strong)",
              }}>{calcStep === 1 ? "Next — Mix Inputs ›" : "Next — See Result ›"}</button>
            )}
          </div>
        )}

      </>)}

      {/* ═══════ GROWS TAB ═══════ */}
      {tab === "grows" && (<>
        {/* Header with new grow button */}
        <div style={{ ...card, padding: "14px 16px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ ...secLbl, marginBottom: 2 }}>Active Grows</div>
              <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                {activeGrows.length} active · {completedGrows.length} harvested · {failedGrows.length} failed
              </div>
            </div>
            <button onClick={() => { if (showNewGrowForm) { resetGrowForm(); } else { setShowNewGrowForm(true); } }} style={{
              padding: "10px 16px", borderRadius: 10, fontSize: 13, fontWeight: 700,
              border: "none", cursor: "pointer",
              background: showNewGrowForm ? "rgba(239,83,80,0.15)" : "linear-gradient(135deg,#2d6030,#5cb85c)",
              color: showNewGrowForm ? "#ef9a9a" : "var(--text-strong)",
            }}>
              {showNewGrowForm ? "Cancel" : "+ New Grow"}
            </button>
          </div>
        </div>

        {/* New Grow Form */}
        {showNewGrowForm && (
          <div style={card}>
            <div style={secLbl}>{editingGrowId ? "Edit Grow" : "Start a New Grow"}</div>

            {/* Crop */}
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 11, color: "var(--text-soft)", marginBottom: 4 }}>Crop *</div>
              <select value={newGrowCrop} onChange={e => setNewGrowCrop(e.target.value)}
                style={{ width: "100%", padding: "10px 12px", borderRadius: 10, fontSize: 14, fontWeight: 600, border: "1px solid var(--border-soft)", background: "rgba(0,0,0,0.3)", color: "var(--active)", outline: "none", boxSizing: "border-box" }}>
                {visibleCropKeys.map(k => <option key={k} value={k}>{CROPS[k].icon} {CROPS[k].name} ({CROPS[k].daysToHarvest}d)</option>)}
              </select>
            </div>

            {/* Starts In — nursery or direct */}
            {sysSettings.hasNursery !== false && (
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 11, color: "var(--text-soft)", marginBottom: 4 }}>Starts in *</div>
                <div style={{ display: "flex", gap: 6 }}>
                  <button onClick={() => setNewGrowStartsIn("nursery")} style={{
                    flex: 1, padding: "10px 8px", borderRadius: 10, cursor: "pointer", textAlign: "center",
                    border: newGrowStartsIn === "nursery" ? "2px solid #43a047" : "2px solid rgba(255,255,255,0.08)",
                    background: newGrowStartsIn === "nursery" ? "rgba(67,160,71,0.15)" : "rgba(255,255,255,0.02)",
                    fontFamily: "inherit",
                  }}>
                    <div style={{ fontSize: 18 }}>🌱</div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: newGrowStartsIn === "nursery" ? "var(--text-strong)" : "var(--text-muted)", marginTop: 2 }}>Nursery first</div>
                    <div style={{ fontSize: 9, color: "var(--text-soft)", marginTop: 1 }}>
                      {DEFAULT_USES_NURSERY(newGrowCrop) ? "recommended" : "optional"}
                    </div>
                  </button>
                  <button onClick={() => setNewGrowStartsIn("direct")} style={{
                    flex: 1, padding: "10px 8px", borderRadius: 10, cursor: "pointer", textAlign: "center",
                    border: newGrowStartsIn === "direct" ? "2px solid #1976d2" : "2px solid rgba(255,255,255,0.08)",
                    background: newGrowStartsIn === "direct" ? "rgba(25,118,210,0.15)" : "rgba(255,255,255,0.02)",
                    fontFamily: "inherit",
                  }}>
                    <div style={{ fontSize: 18 }}>→</div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: newGrowStartsIn === "direct" ? "var(--text-strong)" : "var(--text-muted)", marginTop: 2 }}>Direct to system</div>
                    <div style={{ fontSize: 9, color: "var(--text-soft)", marginTop: 1 }}>
                      {DEFAULT_USES_NURSERY(newGrowCrop) ? "skip nursery" : "recommended"}
                    </div>
                  </button>
                </div>
              </div>
            )}

            {/* Nursery duration — only shown if starts in nursery */}
            {newGrowStartsIn === "nursery" && sysSettings.hasNursery !== false && (
              <div style={{ marginBottom: 10, padding: "10px 12px", borderRadius: 10, background: "rgba(67,160,71,0.06)", border: "1px solid rgba(67,160,71,0.2)" }}>
                <div style={{ fontSize: 11, color: "#9bc99e", marginBottom: 6 }}>Days in nursery before transplant</div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <button
                    onClick={() => setNewGrowNurseryDays(d => Math.max(1, (parseInt(d) || 7) - 1))}
                    style={{ width: 32, height: 36, borderRadius: 8, fontSize: 16, fontWeight: 700, cursor: "pointer", border: "1px solid var(--border-soft)", background: "rgba(0,0,0,0.3)", color: "var(--active)", fontFamily: "inherit" }}
                  >−</button>
                  <input
                    type="number" min={1} max={60} value={newGrowNurseryDays}
                    onChange={e => setNewGrowNurseryDays(Math.max(1, Math.min(60, parseInt(e.target.value) || 1)))}
                    style={{ flex: 1, height: 36, padding: "0 8px", borderRadius: 8, fontSize: 14, fontWeight: 700, border: "1px solid var(--border-soft)", background: "rgba(0,0,0,0.3)", color: "var(--active)", textAlign: "center", outline: "none", fontFamily: "inherit", minWidth: 0 }}
                  />
                  <button
                    onClick={() => setNewGrowNurseryDays(d => Math.min(60, (parseInt(d) || 7) + 1))}
                    style={{ width: 32, height: 36, borderRadius: 8, fontSize: 16, fontWeight: 700, cursor: "pointer", border: "1px solid var(--border-soft)", background: "rgba(0,0,0,0.3)", color: "var(--active)", fontFamily: "inherit" }}
                  >+</button>
                  <span style={{ fontSize: 11, color: "var(--text-soft)" }}>days</span>
                </div>
                <div style={{ fontSize: 10, color: "var(--text-soft)", marginTop: 6 }}>
                  Default for {CROPS[newGrowCrop]?.name || newGrowCrop}: {NURSERY_DURATIONS[newGrowCrop] || 7}d in nursery (+ {HUMIDITY_TRAY_DAYS}d humidity tray = {(NURSERY_DURATIONS[newGrowCrop] || 7) + HUMIDITY_TRAY_DAYS}d total to transplant)
                </div>
              </div>
            )}

            {/* Destination System */}
            <div style={{ marginBottom: 10 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                <div style={{ fontSize: 11, color: "var(--text-soft)" }}>
                  {newGrowStartsIn === "nursery" ? "Transplants to *" : "Growing in *"}
                </div>
                {SYSTEM_COMPATIBILITY[newGrowCrop] && (() => {
                  // Show the ideal system for this crop as a small hint
                  const compat = SYSTEM_COMPATIBILITY[newGrowCrop];
                  const idealSys = Object.entries(compat).find(([_, v]) => v.rating === "ideal");
                  if (!idealSys) return null;
                  const sysDef = GROW_SYSTEMS.find(s => s.key === idealSys[0]);
                  return (
                    <div style={{ fontSize: 10, color: "#90c870" }}>
                      ⭐ Ideal: <strong style={{ color: "var(--text-strong)" }}>{sysDef?.label}</strong>
                    </div>
                  );
                })()}
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                {GROW_SYSTEMS.filter(s => s.key !== "nursery").map(s => {
                  const compat = SYSTEM_COMPATIBILITY[newGrowCrop];
                  const rating = compat && compat[s.key] ? compat[s.key].rating : null;
                  const meta = rating ? SYSTEM_RATING_META[rating] : null;
                  const isSelected = newGrowSystem === s.key;
                  return (
                    <button key={s.key} onClick={() => setNewGrowSystem(s.key)} style={{
                      flex: 1, padding: "10px 8px", borderRadius: 10, cursor: "pointer", textAlign: "center",
                      position: "relative",
                      border: isSelected
                        ? `2px solid ${s.color}`
                        : rating === "ideal"
                        ? "2px solid rgba(120,200,84,0.4)"
                        : rating === "not_recommended"
                        ? "2px solid rgba(239,83,80,0.3)"
                        : "2px solid rgba(255,255,255,0.08)",
                      background: isSelected
                        ? `${s.color}18`
                        : rating === "ideal"
                        ? "rgba(120,200,84,0.06)"
                        : rating === "not_recommended"
                        ? "rgba(239,83,80,0.04)"
                        : "rgba(255,255,255,0.02)",
                      opacity: rating === "not_recommended" && !isSelected ? 0.65 : 1,
                      fontFamily: "inherit",
                    }}>
                      {/* Corner rating badge */}
                      {meta && rating !== "good" && (
                        <span style={{
                          position: "absolute", top: -6, right: -4,
                          background: meta.color,
                          color: rating === "ideal" ? "var(--bg-inset)" : "var(--text-strong)",
                          fontSize: 10, fontWeight: 700,
                          padding: "1px 5px", borderRadius: 8,
                          border: "1px solid var(--bg-inset)",
                        }} aria-hidden="true">{meta.icon}</span>
                      )}
                      <div style={{ fontSize: 18 }}>{s.icon}</div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: isSelected ? "var(--text-strong)" : "var(--text-muted)", marginTop: 2 }}>{s.label}</div>
                    </button>
                  );
                })}
              </div>

              {/* Compatibility warning/confirmation panel for currently-selected system */}
              {SYSTEM_COMPATIBILITY[newGrowCrop] && SYSTEM_COMPATIBILITY[newGrowCrop][newGrowSystem] && (() => {
                const compat = SYSTEM_COMPATIBILITY[newGrowCrop][newGrowSystem];
                const meta = SYSTEM_RATING_META[compat.rating];
                const sysDef = GROW_SYSTEMS.find(s => s.key === newGrowSystem);
                if (compat.rating === "good") return null; // No banner for plain "good" — keep UI quiet
                return (
                  <div style={{
                    marginTop: 8, padding: "8px 10px", borderRadius: 8,
                    background: meta.bg, border: `1px solid ${meta.border}`,
                    fontSize: 11, lineHeight: 1.5, color: meta.color,
                  }}>
                    <strong>{meta.icon} {compat.rating === "ideal" ? `Excellent choice — ${sysDef?.label} is ideal for ${CROPS[newGrowCrop]?.name.toLowerCase()}` : `${meta.label} for ${CROPS[newGrowCrop]?.name.toLowerCase()} in ${sysDef?.label}`}</strong>
                    {compat.note && <div style={{ marginTop: 3, color: "#c8d0c8" }}>{compat.note}</div>}
                  </div>
                );
              })()}
            </div>

            {/* Date + Quantity */}
            <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: "var(--text-soft)", marginBottom: 4 }}>
                  {newGrowStartsIn === "nursery" ? "Sown on *" : "Planted on *"}
                </div>
                <input type="date" value={newGrowDate} onChange={e => setNewGrowDate(e.target.value)}
                  style={{ width: "100%", padding: "10px 12px", borderRadius: 10, fontSize: 14, fontWeight: 600, border: "1px solid var(--border-soft)", background: "rgba(0,0,0,0.3)", color: "var(--active)", outline: "none", boxSizing: "border-box", colorScheme: "dark" }} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: "var(--text-soft)", marginBottom: 4 }}>Plants (optional)</div>
                <input type="number" min={1} placeholder="e.g. 50" value={newGrowQty} onChange={e => setNewGrowQty(clampNonNegative(e.target.value, 100000))}
                  style={{ width: "100%", padding: "10px 12px", borderRadius: 10, fontSize: 14, fontWeight: 600, border: "1px solid var(--border-soft)", background: "rgba(0,0,0,0.3)", color: "var(--active)", outline: "none", boxSizing: "border-box" }} />
              </div>
            </div>

            {/* Transplant date — nursery grows only. Optional: set it once the
                grow has actually moved into its system (or to log a past move). */}
            {newGrowStartsIn === "nursery" && sysSettings.hasNursery !== false && (
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 11, color: "var(--text-soft)", marginBottom: 4, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span>Transplanted on <span style={{ color: "var(--text-muted)" }}>(optional)</span></span>
                  {newGrowTransplantDate && (
                    <button type="button" onClick={() => setNewGrowTransplantDate("")}
                      style={{ fontSize: 11, color: "#ef9a9a", background: "none", border: "none", cursor: "pointer", fontFamily: "inherit", padding: 0 }}>
                      Clear
                    </button>
                  )}
                </div>
                <input type="date" value={newGrowTransplantDate} min={newGrowDate} onChange={e => setNewGrowTransplantDate(e.target.value)}
                  style={{ width: "100%", padding: "10px 12px", borderRadius: 10, fontSize: 14, fontWeight: 600, border: "1px solid var(--border-soft)", background: "rgba(0,0,0,0.3)", color: "var(--active)", outline: "none", boxSizing: "border-box", colorScheme: "dark" }} />
                <div style={{ fontSize: 10, color: "var(--text-soft)", marginTop: 4, lineHeight: 1.5 }}>
                  {newGrowTransplantDate
                    ? "This grow is in its system — the harvest estimate counts from this date."
                    : "Leave blank while it's still in the nursery. You can also use the Transplant button later."}
                </div>
              </div>
            )}

            {/* Label */}
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 11, color: "var(--text-soft)", marginBottom: 4 }}>Custom label (optional)</div>
              <input type="text" placeholder='e.g. "Buttercrunch row 2"' value={newGrowLabel} onChange={e => setNewGrowLabel(e.target.value)} maxLength={50}
                style={{ width: "100%", padding: "10px 12px", borderRadius: 10, fontSize: 14, border: "1px solid var(--border-soft)", background: "rgba(0,0,0,0.3)", color: "var(--active)", outline: "none", boxSizing: "border-box" }} />
            </div>

            {/* Notes */}
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: "var(--text-soft)", marginBottom: 4 }}>Notes (optional)</div>
              <textarea rows={2} placeholder="Variety, source, observations..." value={newGrowNotes} onChange={e => setNewGrowNotes(e.target.value)}
                style={{ width: "100%", padding: "8px 12px", borderRadius: 10, fontSize: 13, border: "1px solid var(--border-soft)", background: "rgba(0,0,0,0.2)", color: "var(--active)", outline: "none", resize: "vertical", boxSizing: "border-box", fontFamily: "inherit" }} />
            </div>

            <button onClick={editingGrowId ? saveGrowEdits : createGrow} style={{
              width: "100%", padding: "12px", borderRadius: 10, fontSize: 14, fontWeight: 700,
              border: "none", cursor: "pointer",
              background: "linear-gradient(135deg,#2d6030,#5cb85c)",
              color: "var(--text-strong)",
            }}>
              {editingGrowId ? "Save Changes" : "Start Grow & Set Active"}
            </button>
          </div>
        )}

        {/* Nursery Section */}
        {nurseryGrows.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, padding: "0 4px" }}>
              <Icon name="sprout" size={16} color="#43a047" />
              <div style={{ fontSize: 11, fontWeight: 800, color: "#43a047", textTransform: "uppercase", letterSpacing: 1.5 }}>
                Nursery ({nurseryGrows.length})
              </div>
              <div style={{ flex: 1, height: 1, background: "rgba(67,160,71,0.2)" }} />
            </div>
            {nurseryGrows.map(grow => {
              const cropDef = CROPS[grow.crop];
              const daysSinceSow = daysSince(grow.plantingDate);
              const nurseryDuration = grow.nurseryDays || NURSERY_DURATIONS[grow.crop] || 7;
              // Total days from sowing to transplant = humidity tray + nursery channel
              const targetDays = HUMIDITY_TRAY_DAYS + nurseryDuration;
              const daysLeft = targetDays - daysSinceSow;
              const inTrayPhase = daysSinceSow < HUMIDITY_TRAY_DAYS;
              const dayInNursery = Math.max(0, daysSinceSow - HUMIDITY_TRAY_DAYS);
              const dest = GROW_SYSTEMS.find(s => s.key === (grow.destinationSystem || "nft"));
              const isReady = daysLeft <= 3 && !inTrayPhase;
              const isPastDue = daysLeft < -7;
              const targets = NURSERY_TARGETS[grow.crop];
              return (
                <div key={grow.id} style={{
                  ...card,
                  background: isPastDue ? "rgba(240,168,32,0.05)" : "rgba(67,160,71,0.04)",
                  border: `1px solid ${isPastDue ? "rgba(240,168,32,0.3)" : isReady ? "rgba(181,224,38,0.35)" : "rgba(67,160,71,0.2)"}`,
                }}>
                  {/* Header row */}
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                    <CropIcon cropKey={grow.crop} emoji={cropDef?.icon} size={36} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text-strong)" }}>
                        {cropDef?.name || grow.crop} {grow.label && <span style={{ color: "var(--text-muted)", fontWeight: 400 }}>· {grow.label}</span>}
                      </div>
                      <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 1 }}>
                        {inTrayPhase ? "🌰 Humidity Tray" : "🌱 Nursery"} → {dest?.icon} {dest?.label} · {grow.quantity ? `${grow.quantity} plants` : "—"}
                      </div>
                    </div>
                  </div>

                  {/* Progress + targets */}
                  <div style={{ padding: "10px 12px", borderRadius: 9, background: "rgba(0,0,0,0.25)", marginBottom: 10 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                      <span style={{ fontSize: 12, color: "var(--text-soft2)" }}>
                        Day {daysSinceSow} since sowing
                        {!inTrayPhase && <span style={{ color: "var(--text-muted)" }}> · Day {dayInNursery} of {nurseryDuration} in nursery</span>}
                      </span>
                      <span style={{ fontSize: 12, fontWeight: 700, color: isPastDue ? "#f0a820" : isReady ? "var(--active)" : "#43a047" }}>
                        {daysLeft > 0 ? `${daysLeft} days to transplant` : daysLeft === 0 ? "Transplant today!" : `${Math.abs(daysLeft)} days past due`}
                      </span>
                    </div>
                    {/* Progress bar */}
                    <div style={{ height: 6, borderRadius: 3, background: "rgba(255,255,255,0.05)", overflow: "hidden", marginBottom: 8 }}>
                      <div style={{ width: `${Math.min(100, (daysSinceSow / targetDays) * 100)}%`, height: "100%", background: isPastDue ? "#f0a820" : isReady ? "var(--active)" : "#43a047" }} />
                    </div>
                    {targets && (
                      <div style={{ display: "flex", gap: 14, fontSize: 11 }}>
                        <span style={{ color: "var(--text-muted)" }}>Target EC <strong style={{ color: "var(--active)" }}>{targets.ecTarget}</strong> ({targets.ecMin}–{targets.ecMax})</span>
                        <span style={{ color: "var(--text-muted)" }}>pH <strong style={{ color: "#5ab8e8" }}>{targets.phMin}–{targets.phMax}</strong></span>
                      </div>
                    )}
                  </div>

                  {/* Tray-phase informational banner */}
                  {inTrayPhase && (
                    <div style={{ padding: "8px 10px", borderRadius: 8, background: "rgba(120,180,90,0.08)", border: "1px solid rgba(120,180,90,0.25)", marginBottom: 10, fontSize: 11, color: "var(--text-soft2)", lineHeight: 1.5 }}>
                      <strong style={{ color: "#a5d6a7" }}>Germination phase.</strong> Keep covered, warm (24–28°C), and humid. Move to nursery channel once first true leaf forms (~day {HUMIDITY_TRAY_DAYS}).
                    </div>
                  )}

                  {/* Transplant alert when within 3 days */}
                  {isReady && !isPastDue && (
                    <div style={{ padding: "8px 10px", borderRadius: 8, background: "rgba(181,224,38,0.1)", border: "1px solid rgba(181,224,38,0.3)", marginBottom: 10, fontSize: 11, color: "var(--text-soft2)", lineHeight: 1.5 }}>
                      <strong style={{ color: "var(--active)" }}>Transplant window open.</strong> Check before transplanting: 2-3 true leaves visible, white roots emerging from cube, no yellowing.
                    </div>
                  )}
                  {isPastDue && (
                    <div style={{ padding: "8px 10px", borderRadius: 8, background: "rgba(240,168,32,0.1)", border: "1px solid rgba(240,168,32,0.3)", marginBottom: 10, fontSize: 11, color: "#ffc890", lineHeight: 1.5 }}>
                      <strong style={{ color: "#f0a820" }}>Past due.</strong> This grow has been in nursery longer than planned. Transplant soon to avoid stunting.
                    </div>
                  )}

                  {/* Actions */}
                  <div style={{ display: "flex", gap: 6 }}>
                    <button
                      onClick={() => setTransplantingGrowId(grow.id)}
                      style={{
                        flex: 1, padding: "9px 10px", borderRadius: 8, fontSize: 12, fontWeight: 700,
                        border: "1px solid rgba(181,224,38,0.4)",
                        background: isReady ? "linear-gradient(135deg, rgba(181,224,38,0.2), rgba(120,180,90,0.15))" : "rgba(181,224,38,0.08)",
                        color: "var(--active)", cursor: "pointer", fontFamily: "inherit",
                        display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                      }}
                    >
                      <Icon name="sprout" size={13} /> Transplant
                    </button>
                    <button
                      onClick={() => { setTrendGrowFilter(grow.id); setHistoryView("trends"); setTab("history"); }}
                      aria-label="View logs for this grow"
                      style={{
                        padding: "9px 12px", borderRadius: 8, fontSize: 12,
                        border: "1px solid rgba(181,224,38,0.2)", background: "rgba(181,224,38,0.06)",
                        color: "var(--active)", cursor: "pointer", fontFamily: "inherit",
                        display: "flex", alignItems: "center", gap: 4,
                      }}
                    >
                      <Icon name="clipboard" size={13} />
                      <span style={{ fontSize: 11 }}>{logs.filter(l => l.growId === grow.id).length}</span>
                    </button>
                    <button onClick={() => openEditGrow(grow)} aria-label="Edit grow" title="Edit this grow" style={{
                      padding: "9px 12px", borderRadius: 8, fontSize: 13,
                      border: "1px solid rgba(181,224,38,0.2)", background: "rgba(181,224,38,0.06)", color: "var(--active)", cursor: "pointer",
                    }}>
                      ✎
                    </button>
                    <button onClick={() => deleteGrow(grow.id)} aria-label="Delete grow" style={{
                      padding: "9px 12px", borderRadius: 8, fontSize: 12,
                      border: "1px solid rgba(239,83,80,0.2)", background: "rgba(239,83,80,0.05)", color: "#ef9a9a", cursor: "pointer",
                    }}>
                      ✕
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Active Grows Header (when nursery section also visible) */}
        {nurseryGrows.length > 0 && activeGrows.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, padding: "0 4px" }}>
            <Icon name="leaf" size={16} color="#5cb85c" />
            <div style={{ fontSize: 11, fontWeight: 800, color: "#5cb85c", textTransform: "uppercase", letterSpacing: 1.5 }}>
              Active ({activeGrows.length})
            </div>
            <div style={{ flex: 1, height: 1, background: "rgba(92,184,92,0.2)" }} />
          </div>
        )}

        {/* Active Grows List */}
        {activeGrows.length === 0 && nurseryGrows.length === 0 && !showNewGrowForm && (
          <div style={{ ...card, textAlign: "center", padding: 30 }}>
            <div style={{ fontSize: 36, marginBottom: 8 }}>🌱</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "var(--active)", marginBottom: 4 }}>Start your first grow</div>
            <div style={{ fontSize: 12, color: "var(--text-muted)", maxWidth: 320, margin: "0 auto 16px", lineHeight: 1.5 }}>
              Track a planting from seed to harvest — with stage timing, feeding targets, and per-grow costs. It also links your readings and the calculator to the crop.
            </div>
            <button
              onClick={() => setShowNewGrowForm(true)}
              style={{
                background: "linear-gradient(180deg, var(--panel-a) 0%, var(--panel-b) 100%)",
                border: "1px solid rgba(90,185,90,0.45)", borderRadius: 10,
                padding: "10px 20px", cursor: "pointer", color: "var(--active)",
                fontSize: 13, fontWeight: 700, fontFamily: "inherit",
                boxShadow: "inset 0 0 0 1px rgba(90,185,90,0.15)",
              }}>
              + New Grow
            </button>
          </div>
        )}

        {activeGrows.map(grow => {
          const cropDef = CROPS[grow.crop];
          const sys = GROW_SYSTEMS.find(s => s.key === grow.system);
          const tl = growTimeline(grow);
          const days = tl.days;
          const totalDays = tl.span;            // re-anchors off actual transplant date
          const progress = tl.progress;
          const expectedHarvest = tl.harvest;
          const isActive = grow.id === activeGrowId;
          const econ = calculateGrowEconomics(grow);
          // Detect stage transition for this specific grow
          const transition = stageTransitions.find(t => t.grow.id === grow.id);

          return (
            <div key={grow.id} style={{
              ...card,
              border: isActive ? `2px solid ${cropDef?.color}` : "1px solid rgba(255,255,255,0.06)",
              background: isActive ? `${cropDef?.color}10` : "rgba(255,255,255,0.04)",
            }}>
              {/* Header */}
              <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 10 }}>
                <div style={{ fontSize: 28 }}><CropIcon cropKey={grow.crop} emoji={cropDef?.icon} size={42} /></div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: "var(--text-strong)" }}>
                    {grow.label || cropDef?.name}
                    {grow.label && <span style={{ fontSize: 11, color: "var(--text-muted)", fontWeight: 400, marginLeft: 6 }}>({cropDef?.name})</span>}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                    <span>{sys?.icon} {sys?.label}</span>
                    {/* System-fit indicator */}
                    {SYSTEM_COMPATIBILITY[grow.crop] && SYSTEM_COMPATIBILITY[grow.crop][grow.system] && (() => {
                      const r = SYSTEM_COMPATIBILITY[grow.crop][grow.system].rating;
                      if (r === "good") return null;
                      const m = SYSTEM_RATING_META[r];
                      return (
                        <span
                          title={SYSTEM_COMPATIBILITY[grow.crop][grow.system].note || m.label}
                          style={{
                            fontSize: 9, fontWeight: 700, padding: "1px 5px", borderRadius: 6,
                            background: m.bg, color: m.color, border: `1px solid ${m.border}`,
                          }}
                        >{m.icon} {m.label}</span>
                      );
                    })()}
                    <span>· sown {fmtGrowDate(grow.plantingDate)}</span>
                    {grow.transplantedAt && <span>· transplanted {fmtGrowDate(grow.transplantedAt, { month: "short", day: "numeric" })}</span>}
                    {grow.quantity && <span>· {grow.quantity} plants</span>}
                  </div>
                </div>
                {isActive && (
                  <span style={{ background: "#5cb85c", color: "var(--text-strong)", borderRadius: 12, padding: "3px 8px", fontSize: 10, fontWeight: 700 }}>
                    ACTIVE
                  </span>
                )}
              </div>

              {/* Progress */}
              <div style={{ marginBottom: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "var(--text-muted)", marginBottom: 4 }}>
                  <span>Day {days} of ~{totalDays}</span>
                  <span>
                    Harvest est: {expectedHarvest.toLocaleDateString("en-JM", { month: "short", day: "numeric" })}
                    {tl.reanchored && (
                      <span
                        title={`Adjusted for the actual transplant date — ${tl.span > tl.totalDays ? `${tl.span - tl.totalDays} day(s) later` : `${tl.totalDays - tl.span} day(s) earlier`} than the nominal ${tl.totalDays}-day cycle.`}
                        style={{ color: "var(--text-soft)", marginLeft: 4 }}
                      >·&nbsp;adj.</span>
                    )}
                  </span>
                </div>
                <div style={{ height: 8, borderRadius: 4, background: "rgba(0,0,0,0.3)", overflow: "hidden" }}>
                  <div style={{
                    height: "100%", width: `${progress}%`, background: cropDef?.color, transition: "width 0.3s",
                    boxShadow: progress >= 90 ? `0 0 8px ${cropDef?.color}` : "none",
                  }} />
                </div>
                <div style={{ fontSize: 10, color: "var(--text-soft)", textAlign: "right", marginTop: 2 }}>
                  {progress >= 100 ? "🧺 Ready to harvest!" : `${(100 - progress).toFixed(0)}% to go`}
                </div>
              </div>

              {/* Stage transition alert */}
              {transition && (
                <div style={{
                  padding: "10px 12px", borderRadius: 9, marginBottom: 10,
                  background: "linear-gradient(135deg, rgba(181,224,38,0.15), rgba(120,180,90,0.08))",
                  border: "1px solid rgba(181,224,38,0.4)",
                  boxShadow: "0 0 12px rgba(181,224,38,0.12)",
                }}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 9, marginBottom: 8 }}>
                    <div style={{ flexShrink: 0, marginTop: 1 }}>
                      <Icon name="sprout" size={16} color="var(--active)" />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 10, fontWeight: 800, color: "var(--active)", textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 3 }}>
                        Stage Change: {transition.fromStage.label} → {transition.toStage.label}
                      </div>
                      <div style={{ fontSize: 11, color: "var(--text-soft2)", lineHeight: 1.5 }}>
                        EC target now <strong style={{ color: "var(--text-strong)" }}>{transition.toStage.ecTarget}</strong> ({transition.toStage.ecMin}–{transition.toStage.ecMax}) · pH <strong style={{ color: "var(--text-strong)" }}>{transition.toStage.phMin}–{transition.toStage.phMax}</strong>
                      </div>
                    </div>
                  </div>
                  {/* Stage tip */}
                  {STAGE_TIPS[grow.crop]?.[transition.toStage.label] && (
                    <div style={{ padding: "8px 10px", borderRadius: 7, background: "rgba(0,0,0,0.25)", fontSize: 11, color: "#a0c8a0", lineHeight: 1.6, marginBottom: 8 }}>
                      <div style={{ marginBottom: 4 }}><strong style={{ color: "var(--active)" }}>Action:</strong> {STAGE_TIPS[grow.crop][transition.toStage.label].action}</div>
                      <div><strong style={{ color: "var(--active)" }}>Watch:</strong> {STAGE_TIPS[grow.crop][transition.toStage.label].watch}</div>
                    </div>
                  )}
                  <div style={{ display: "flex", gap: 6 }}>
                    <button
                      onClick={() => acknowledgeStage(grow.id, transition.currentIdx)}
                      style={{
                        flex: 1, padding: "7px 10px", borderRadius: 7, fontSize: 11, fontWeight: 700,
                        border: "1px solid rgba(181,224,38,0.4)", background: "rgba(181,224,38,0.15)",
                        color: "var(--active)", cursor: "pointer", fontFamily: "inherit",
                      }}
                    >Got it</button>
                    <button
                      onClick={() => {
                        acknowledgeStage(grow.id, transition.currentIdx);
                        switchToGrow(grow.id);
                        setStageIdx(transition.currentIdx);
                        setTab("calc");
                      }}
                      style={{
                        flex: 1, padding: "7px 10px", borderRadius: 7, fontSize: 11, fontWeight: 700,
                        border: "1px solid rgba(52,201,255,0.4)", background: "rgba(52,201,255,0.12)",
                        color: "#5ab8e8", cursor: "pointer", fontFamily: "inherit",
                      }}
                    >Open Calculator →</button>
                  </div>
                </div>
              )}

              {/* Stage suggestion */}
              <div style={{ padding: "8px 10px", borderRadius: 8, background: "rgba(0,0,0,0.2)", fontSize: 11, color: "var(--text-muted)", marginBottom: 10 }}>
                Suggested stage: <strong style={{ color: "var(--text-strong)" }}>{cropDef?.stages[suggestStage(cropDef, days)]?.label}</strong>
                {" · "}EC target {cropDef?.stages[suggestStage(cropDef, days)]?.ecTarget}
                {" · "}pH {cropDef?.stages[suggestStage(cropDef, days)]?.phMin}–{cropDef?.stages[suggestStage(cropDef, days)]?.phMax} (ideal {cropDef?.stages[suggestStage(cropDef, days)]?.phTarget})
              </div>

              {/* Economics so far */}
              {econ && (
                <div style={{ padding: "8px 10px", borderRadius: 8, background: "rgba(0,0,0,0.15)", marginBottom: 10 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "var(--text-muted)" }}>
                    <span>📊 So far</span>
                    <span>{econ.logCount} log entries</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4, fontSize: 11, color: "var(--text-muted)" }}>
                    <span>Nutrients: <strong style={{ color: "var(--text-strong)" }}>{econ.totalNutrientGrams.toFixed(0)}g</strong></span>
                    <span>Power: <strong style={{ color: "var(--text-strong)" }}>{formatCurrency(econ.powerCostJmd, userLocation.currency)}</strong></span>
                  </div>
                </div>
              )}

              {grow.notes && (
                <div style={{ padding: "8px 10px", borderRadius: 8, background: "rgba(255,255,255,0.03)", fontSize: 11, color: "var(--text-muted)", marginBottom: 10, fontStyle: "italic" }}>
                  "{grow.notes}"
                </div>
              )}

              {/* Actions */}
              <div style={{ display: "flex", gap: 6 }}>
                {!isActive && (
                  <button onClick={() => switchToGrow(grow.id)} style={{
                    flex: 1, padding: "8px", borderRadius: 8, fontSize: 12, fontWeight: 600,
                    border: `1px solid ${cropDef?.color}40`, background: `${cropDef?.color}15`, color: cropDef?.color, cursor: "pointer",
                  }}>
                    Set Active
                  </button>
                )}
                <button onClick={() => { setHarvestingGrowId(grow.id); setHarvestStatus("harvested"); }} style={{
                  flex: 1, padding: "8px", borderRadius: 8, fontSize: 12, fontWeight: 600,
                  border: "1px solid rgba(165,214,167,0.3)", background: "rgba(165,214,167,0.1)", color: "#90c870", cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 5, fontFamily: "inherit",
                }}>
                  <Icon name="harvest" size={13} /> Harvest
                </button>
                <button
                  onClick={() => { setTrendGrowFilter(grow.id); setHistoryView("trends"); setTab("history"); }}
                  aria-label="View logs for this grow"
                  title="View logs &amp; trends for this grow"
                  style={{
                    padding: "8px 12px", borderRadius: 8, fontSize: 12,
                    border: "1px solid rgba(181,224,38,0.2)", background: "rgba(181,224,38,0.06)", color: "var(--active)", cursor: "pointer",
                    display: "flex", alignItems: "center", gap: 4, fontFamily: "inherit",
                  }}>
                  <Icon name="clipboard" size={13} />
                  <span style={{ fontSize: 11 }}>{logs.filter(l => l.growId === grow.id).length}</span>
                </button>
                <button onClick={() => openEditGrow(grow)} aria-label="Edit grow" title="Edit this grow" style={{
                  padding: "8px 12px", borderRadius: 8, fontSize: 13,
                  border: "1px solid rgba(181,224,38,0.2)", background: "rgba(181,224,38,0.06)", color: "var(--active)", cursor: "pointer",
                }}>
                  ✎
                </button>
                <button onClick={() => deleteGrow(grow.id)} aria-label="Delete grow" style={{
                  padding: "8px 12px", borderRadius: 8, fontSize: 12,
                  border: "1px solid rgba(239,83,80,0.2)", background: "rgba(239,83,80,0.05)", color: "#ef9a9a", cursor: "pointer",
                }}>
                  ✕
                </button>
              </div>
            </div>
          );
        })}

        {/* Transplant Modal */}
        {transplantingGrowId && (() => {
          const grow = grows.find(g => g.id === transplantingGrowId);
          if (!grow) return null;
          const cropDef = CROPS[grow.crop];
          const days = daysSince(grow.plantingDate);
          const dest = GROW_SYSTEMS.find(s => s.key === (grow.destinationSystem || "nft"));
          return (
            <div
              role="dialog" aria-modal="true" aria-label="Transplant grow"
              onClick={e => { if (e.target === e.currentTarget) setTransplantingGrowId(null); }}
              style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 100,
                display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
            >
              <div style={{ background: "linear-gradient(180deg, #0a1f10, var(--shell-b))", borderRadius: 16, padding: 20, maxWidth: 440, width: "100%", border: "1px solid rgba(67,160,71,0.4)", boxShadow: "0 0 40px rgba(67,160,71,0.15)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
                  <CropIcon cropKey={grow.crop} emoji={cropDef?.icon} size={48} />
                  <div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: "var(--text-strong)" }}>Transplant {cropDef?.name || grow.crop}</div>
                    <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{grow.label ? `${grow.label} · ` : ""}{grow.quantity ? `${grow.quantity} plants` : ""}</div>
                  </div>
                </div>

                {/* From → To */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 14px", borderRadius: 10, background: "rgba(0,0,0,0.3)", marginBottom: 14, border: "1px solid rgba(255,255,255,0.05)" }}>
                  <div style={{ textAlign: "center", flex: 1 }}>
                    <div style={{ fontSize: 9, color: "var(--text-soft)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>From</div>
                    <div style={{ fontSize: 22, marginBottom: 2 }}>🌱</div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#43a047" }}>Nursery</div>
                    <div style={{ fontSize: 10, color: "var(--text-soft)" }}>Day {days}</div>
                  </div>
                  <div style={{ color: "var(--text-soft)", fontSize: 20, padding: "0 14px" }}>→</div>
                  <div style={{ textAlign: "center", flex: 1 }}>
                    <div style={{ fontSize: 9, color: "var(--text-soft)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>To</div>
                    <div style={{ fontSize: 22, marginBottom: 2 }}>{dest?.icon}</div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: dest?.color || "var(--text-strong)" }}>{dest?.label}</div>
                    <div style={{ fontSize: 10, color: "var(--text-soft)" }}>Day 1</div>
                  </div>
                </div>

                {/* Checklist */}
                <div style={{ padding: "10px 12px", borderRadius: 9, background: "rgba(181,224,38,0.06)", border: "1px solid rgba(181,224,38,0.2)", marginBottom: 14, fontSize: 11, color: "var(--text-soft2)", lineHeight: 1.6 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "var(--active)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>Before you transplant</div>
                  ✓ 2-3 true leaves visible<br/>
                  ✓ White roots emerging from cube<br/>
                  ✓ No yellowing or stretching<br/>
                  ✓ Destination reservoir mixed &amp; pH-checked
                </div>

                {/* Notes */}
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 11, color: "var(--text-soft)", marginBottom: 4 }}>Notes (optional)</div>
                  <input
                    type="text" value={transplantNotes} onChange={e => setTransplantNotes(e.target.value)}
                    placeholder="e.g. all 50 plants transplanted, 3 weak ones discarded"
                    style={{ width: "100%", padding: "8px 12px", borderRadius: 8, fontSize: 13,
                      border: "1px solid var(--border-soft)", background: "rgba(0,0,0,0.3)", color: "var(--text-main)",
                      outline: "none", boxSizing: "border-box", fontFamily: "inherit" }}
                  />
                </div>

                {/* Actions */}
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={() => { setTransplantingGrowId(null); setTransplantNotes(""); }} style={{
                    flex: 1, padding: "10px", borderRadius: 9, fontSize: 13, fontWeight: 600,
                    border: "1px solid var(--border-soft)", background: "rgba(255,255,255,0.03)",
                    color: "var(--text-muted)", cursor: "pointer", fontFamily: "inherit",
                  }}>Cancel</button>
                  <button onClick={() => transplantGrow(transplantingGrowId)} style={{
                    flex: 2, padding: "10px", borderRadius: 9, fontSize: 13, fontWeight: 700,
                    border: "none", background: "linear-gradient(135deg, #2d6030, #5cb85c)",
                    color: "var(--text-strong)", cursor: "pointer", fontFamily: "inherit",
                  }}>Confirm Transplant</button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Harvest Modal */}
        {harvestingGrowId && (() => {
          const grow = grows.find(g => g.id === harvestingGrowId);
          if (!grow) return null;
          const cropDef = CROPS[grow.crop];
          return (
            <div style={{
              position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 100,
              display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
            }} onClick={() => setHarvestingGrowId(null)}>
              <div onClick={e => e.stopPropagation()} style={{
                ...card, marginBottom: 0, maxWidth: 460, width: "100%",
                background: "var(--bg-inset)", border: "1px solid var(--border-soft)",
                maxHeight: "90vh", overflowY: "auto",
              }}>
                <div style={{ ...secLbl, marginBottom: 4 }}>Close Out Grow</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text-strong)", marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>
                  <CropIcon cropKey={grow.crop} emoji={cropDef?.icon} size={18} /> {grow.label || cropDef?.name}
                </div>

                {/* Status */}
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 11, color: "var(--text-soft)", marginBottom: 4 }}>Outcome</div>
                  <div style={{ display: "flex", gap: 6 }}>
                    {[
                      { key: "harvested", label: "🧺 Harvested", color: "#5cb85c" },
                      { key: "failed", label: "❌ Failed", color: "#ef5350" },
                    ].map(s => (
                      <button key={s.key} onClick={() => setHarvestStatus(s.key)} style={{
                        flex: 1, padding: "10px 8px", borderRadius: 10, cursor: "pointer", fontSize: 13, fontWeight: 600,
                        border: harvestStatus === s.key ? `2px solid ${s.color}` : "2px solid rgba(255,255,255,0.08)",
                        background: harvestStatus === s.key ? `${s.color}18` : "rgba(255,255,255,0.02)",
                        color: harvestStatus === s.key ? "var(--text-strong)" : "var(--text-muted)",
                      }}>
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>

                {harvestStatus === "harvested" && (
                  <>
                    {/* Weight */}
                    <div style={{ marginBottom: 10 }}>
                      <div style={{ fontSize: 11, color: "var(--text-soft)", marginBottom: 4 }}>Weight harvested</div>
                      <div style={{ display: "flex", gap: 6 }}>
                        <input type="number" step="0.01" min={0} placeholder="e.g. 5.2" value={harvestWeight} onChange={e => setHarvestWeight(clampNonNegative(e.target.value, 99999))}
                          style={{ flex: 2, padding: "10px 12px", borderRadius: 10, fontSize: 16, fontWeight: 700, border: "1px solid var(--border-soft)", background: "rgba(0,0,0,0.3)", color: "var(--active)", outline: "none", boxSizing: "border-box", fontVariantNumeric: "tabular-nums" }} />
                        <select value={harvestUnit} onChange={e => setHarvestUnit(e.target.value)}
                          style={{ flex: 1, padding: "10px 12px", borderRadius: 10, fontSize: 14, fontWeight: 600, border: "1px solid var(--border-soft)", background: "rgba(0,0,0,0.3)", color: "var(--active)", outline: "none", boxSizing: "border-box" }}>
                          <option value="kg">kg</option>
                          <option value="lb">lb</option>
                          <option value="bunches">bunches</option>
                          <option value="heads">heads</option>
                        </select>
                      </div>
                    </div>

                    {/* Sale price */}
                    <div style={{ marginBottom: 10 }}>
                      <div style={{ fontSize: 11, color: "var(--text-soft)", marginBottom: 4 }}>Total sale value ({userLocation.currency}, optional)</div>
                      <input type="number" step="100" min={0} placeholder="e.g. 5000" value={harvestPrice} onChange={e => setHarvestPrice(clampNonNegative(e.target.value, 99999999))}
                        style={{ width: "100%", padding: "10px 12px", borderRadius: 10, fontSize: 16, fontWeight: 700, border: "1px solid var(--border-soft)", background: "rgba(0,0,0,0.3)", color: "#90c870", outline: "none", boxSizing: "border-box" }} />
                    </div>
                  </>
                )}

                {/* Notes */}
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 11, color: "var(--text-soft)", marginBottom: 4 }}>
                    {harvestStatus === "harvested" ? "Quality / observations" : "What went wrong?"} (optional)
                  </div>
                  <textarea rows={2} placeholder="..." value={harvestNotes} onChange={e => setHarvestNotes(e.target.value)}
                    style={{ width: "100%", padding: "8px 12px", borderRadius: 10, fontSize: 13, border: "1px solid var(--border-soft)", background: "rgba(0,0,0,0.2)", color: "var(--active)", outline: "none", resize: "vertical", boxSizing: "border-box", fontFamily: "inherit" }} />
                </div>

                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={() => setHarvestingGrowId(null)} style={{
                    flex: 1, padding: "12px", borderRadius: 10, fontSize: 13, fontWeight: 600,
                    border: "1px solid var(--border-soft)", background: "rgba(255,255,255,0.04)", color: "var(--text-muted)", cursor: "pointer",
                  }}>
                    Cancel
                  </button>
                  <button onClick={closeGrow} disabled={harvestStatus === "harvested" && !harvestWeight} style={{
                    flex: 2, padding: "12px", borderRadius: 10, fontSize: 13, fontWeight: 700, border: "none",
                    cursor: (harvestStatus === "harvested" && !harvestWeight) ? "not-allowed" : "pointer",
                    background: harvestStatus === "harvested" ? "linear-gradient(135deg,#2d6030,#5cb85c)" : "linear-gradient(135deg,#c62828,#ef5350)",
                    color: "var(--text-strong)", opacity: (harvestStatus === "harvested" && !harvestWeight) ? 0.5 : 1,
                  }}>
                    {harvestStatus === "harvested" ? "Confirm Harvest" : "Mark as Failed"}
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Completed Grows */}
        {completedGrows.length > 0 && (
          <div style={card}>
            <div style={secLbl}>Completed Grows</div>
            {completedGrows.slice(0, 10).map(grow => {
              const cropDef = CROPS[grow.crop];
              const sys = GROW_SYSTEMS.find(s => s.key === grow.system);
              const econ = calculateGrowEconomics(grow);
              const profit = econ ? (grow.harvestPrice || 0) - econ.powerCostJmd : 0;
              return (
                <div key={grow.id} style={{ padding: "10px 0", borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                    <div style={{ fontSize: 22 }}><CropIcon cropKey={grow.crop} emoji={cropDef?.icon} size={30} /></div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-bright)" }}>
                        {grow.label || cropDef?.name} <span style={{ fontSize: 10, color: "var(--text-soft)" }}>· {sys?.icon}</span>
                      </div>
                      <div style={{ fontSize: 11, color: "var(--text-soft)", marginTop: 1 }}>
                        {econ?.daysActive}d · {grow.harvestWeight} {grow.harvestUnit}
                        {grow.harvestPrice && ` · ${formatCurrency(grow.harvestPrice, userLocation.currency)}`}
                      </div>
                      {grow.harvestPrice && econ && (
                        <div style={{ fontSize: 11, marginTop: 2, color: profit >= 0 ? "#90c870" : "#ef9a9a" }}>
                          Net: {formatCurrency(profit, userLocation.currency)} (after {formatCurrency(econ.powerCostJmd, userLocation.currency)} power)
                        </div>
                      )}
                    </div>
                    <button onClick={() => deleteGrow(grow.id)} aria-label="Delete grow" style={{ background: "none", border: "none", color: "#c97070", fontSize: 14, cursor: "pointer", padding: 4 }}>
                      <span aria-hidden="true">✕</span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Failed Grows */}
        {failedGrows.length > 0 && (
          <div style={card}>
            <div style={secLbl}>Failed Grows</div>
            {failedGrows.slice(0, 5).map(grow => {
              const cropDef = CROPS[grow.crop];
              return (
                <div key={grow.id} style={{ padding: "8px 0", borderBottom: "1px solid rgba(255,255,255,0.04)", display: "flex", alignItems: "center", gap: 10, opacity: 0.7 }}>
                  <div style={{ fontSize: 18 }}><CropIcon cropKey={grow.crop} emoji={cropDef?.icon} size={26} /></div>
                  <div style={{ flex: 1, fontSize: 12, color: "var(--text-muted)" }}>
                    {grow.label || cropDef?.name} · {grow.harvestNotes || "no notes"}
                  </div>
                  <button onClick={() => deleteGrow(grow.id)} aria-label="Delete grow" style={{ background: "none", border: "none", color: "#c97070", fontSize: 14, cursor: "pointer", padding: 4 }}>
                    <span aria-hidden="true">✕</span>
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </>)}

      {/* ═══════ PUMPS TAB ═══════ */}
      {tab === "pumps" && (<>
        {/* Pump Status — Now / Next per system */}
        <div style={card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
            <div style={{ ...secLbl, marginBottom: 0 }}>Pump Status — Now</div>
            <div style={{ fontSize: 10, color: "var(--text-soft)" }}>{SEASONS[activeSeason].label} · updates live</div>
          </div>
          {pumpNowNext.map((p, i) => {
            const isOn = !!p.current;
            const at = isOn ? p.current.end : (p.next ? p.next.start : null);
            const atStr = at ? at.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }) : "—";
            return (
              <div key={p.sys} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 0", borderBottom: i < pumpNowNext.length - 1 ? "1px solid rgba(255,255,255,0.05)" : "none" }}>
                <span style={{ width: 10, height: 10, borderRadius: "50%", flexShrink: 0, background: isOn ? "#78c854" : p.meta.color, boxShadow: isOn ? "0 0 8px rgba(120,200,84,0.85)" : "none" }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text-main)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.meta.name}</div>
                  <div style={{ fontSize: 10.5, color: "var(--text-soft)" }}>{p.runMin} min/day · {p.cycles} cycles</div>
                </div>
                <div style={{ textAlign: "right", flexShrink: 0 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 800, letterSpacing: 0.5, textTransform: "uppercase", color: isOn ? "#78c854" : "var(--text-muted)" }}>{isOn ? "On" : "Off"}</div>
                  <div style={{ fontSize: 10.5, color: "var(--text-soft)" }}>{isOn ? `ends ${atStr}` : p.next ? `next ${atStr}` : `next ${atStr} tmrw`}</div>
                </div>
              </div>
            );
          })}
        </div>

        {/* System Selector */}
        <div style={card}>
          <div style={secLbl}>Pump System</div>
          <div style={{ display: "flex", gap: 8 }}>
            {Object.entries(PUMP_SCHEDULES).filter(([key]) => systemEnabled[key]).map(([key, sys]) => {
              const sel = pumpSystem === key;
              return (
                <button key={key} onClick={() => setPumpSystem(key)} aria-label={`Select ${sys.name}`} style={{
                  flex: 1, padding: "14px 10px", borderRadius: 12, cursor: "pointer", textAlign: "center",
                  border: sel ? `2px solid ${sys.color}` : "2px solid rgba(255,255,255,0.08)",
                  background: sel ? `${sys.color}20` : "rgba(255,255,255,0.02)",
                }}>
                  <div style={{ fontSize: 24, marginBottom: 4 }}>{sys.icon}</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: sel ? "var(--text-strong)" : "var(--text-muted)" }}>{sys.name}</div>
                </button>
              );
            })}
          </div>
          <div style={{ marginTop: 10, padding: "8px 12px", borderRadius: 8, background: `${PUMP_SCHEDULES[pumpSystem].color}10`, border: `1px solid ${PUMP_SCHEDULES[pumpSystem].color}25`, fontSize: 12, color: "var(--text-muted)", lineHeight: 1.5 }}>
            ℹ {PUMP_SCHEDULES[pumpSystem].notes}
          </div>
        </div>

        {/* Combined All-Systems Timeline */}
        <div style={card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <div style={{ ...secLbl, marginBottom: 0 }}>All Systems · {SEASONS[activeSeason].label}</div>
            <div style={{ fontSize: 10, color: "var(--text-soft)" }}>24 hours · incl. night pulses</div>
          </div>
          <CombinedScheduleTimeline
            seasonKey={activeSeason}
            hasNft={hasNft}
            hasTower={hasTower}
            hasNursery={sysSettings.hasNursery !== false}
            nowMin={(() => { const n = new Date(); return n.getHours() * 60 + n.getMinutes(); })()}
          />
          {/* Legend + surge confirmation */}
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 8, marginBottom: 8 }}>
            {hasNft && (
              <span style={{ fontSize: 10, color: "var(--text-muted)", display: "flex", alignItems: "center", gap: 4 }}>
                <span style={{ width: 10, height: 10, borderRadius: 2, background: "#1976d2", display: "inline-block" }} /> NFT (Plug A · {nftWattage.toFixed(0)}W)
              </span>
            )}
            {hasTower && (
              <span style={{ fontSize: 10, color: "var(--text-muted)", display: "flex", alignItems: "center", gap: 4 }}>
                <span style={{ width: 10, height: 10, borderRadius: 2, background: "#7b1fa2", display: "inline-block" }} /> Tower (Plug B · {towerWattage.toFixed(0)}W)
              </span>
            )}
            {sysSettings.hasNursery !== false && (
              <span style={{ fontSize: 10, color: "var(--text-muted)", display: "flex", alignItems: "center", gap: 4 }}>
                <span style={{ width: 10, height: 10, borderRadius: 2, background: "#43a047", display: "inline-block" }} /> Nursery (own timer · {nurseryWattage.toFixed(0)}W)
              </span>
            )}
          </div>
        </div>

        {/* Season Selector */}
        <div style={card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <div style={{ ...secLbl, marginBottom: 0 }}>Season</div>
            {seasonOverride && (
              <button onClick={() => setSeasonOverride(null)} style={{ fontSize: 11, color: "var(--active)", background: "none", border: "none", cursor: "pointer", textDecoration: "underline" }}>
                Reset to auto ({SEASONS[autoSeason].label})
              </button>
            )}
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            {Object.entries(SEASONS).map(([key, s]) => {
              const sel = activeSeason === key;
              const isAuto = autoSeason === key && !seasonOverride;
              return (
                <button key={key} onClick={() => setSeasonOverride(key)} aria-label={`${s.label}, ${s.sub}`} style={{
                  flex: 1, padding: "10px 6px", borderRadius: 10, cursor: "pointer", textAlign: "center",
                  border: sel ? `2px solid ${s.color}` : "2px solid rgba(255,255,255,0.08)",
                  background: sel ? `${s.color}18` : "rgba(255,255,255,0.02)",
                  position: "relative",
                }}>
                  <div style={{ fontSize: 18 }}>{s.icon}</div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: sel ? "var(--text-strong)" : "var(--text-muted)", marginTop: 2 }}>{s.label}</div>
                  <div style={{ fontSize: 10, color: "var(--text-soft)", marginTop: 1 }}>{s.sub}</div>
                  {isAuto && <div style={{ fontSize: 9, color: s.color, marginTop: 2, fontWeight: 700 }}>● AUTO</div>}
                </button>
              );
            })}
          </div>
        </div>

        {/* Live Status */}
        <div style={{ ...card, background: currentPumpEvent ? "rgba(76,175,80,0.08)" : "rgba(255,255,255,0.04)", border: currentPumpEvent ? "1px solid rgba(76,175,80,0.3)" : "1px solid rgba(255,255,255,0.06)" }}>
          <div style={secLbl}>Live Status</div>
          <div style={{ textAlign: "center", padding: "8px 0" }}>
            {currentPumpEvent ? (
              <>
                <div style={{ fontSize: 11, color: "var(--text-soft)", textTransform: "uppercase", letterSpacing: 2 }}>Pump Status</div>
                <div style={{ fontSize: 36, fontWeight: 800, color: "#5cb85c", marginTop: 4, letterSpacing: 1 }}>● ON</div>
                <div style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 4 }}>
                  Turns OFF in <strong style={{ color: "var(--text-strong)" }}>{formatDuration(currentPumpEvent.end - new Date())}</strong>
                </div>
                <div style={{ fontSize: 11, color: "var(--text-soft)", marginTop: 2 }}>
                  Cycle: {formatTime(currentPumpEvent.start)} → {formatTime(currentPumpEvent.end)}
                </div>
              </>
            ) : nextPumpEvent ? (
              <>
                <div style={{ fontSize: 11, color: "var(--text-soft)", textTransform: "uppercase", letterSpacing: 2 }}>Pump Status</div>
                <div style={{ fontSize: 36, fontWeight: 800, color: "var(--text-soft)", marginTop: 4, letterSpacing: 1 }}>○ OFF</div>
                <div style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 4 }}>
                  Turns ON in <strong style={{ color: "var(--text-strong)" }}>{formatDuration(nextPumpEvent.start - new Date())}</strong>
                </div>
                <div style={{ fontSize: 11, color: "var(--text-soft)", marginTop: 2 }}>
                  Next cycle: {formatTime(nextPumpEvent.start)} → {formatTime(nextPumpEvent.end)}
                </div>
              </>
            ) : (
              <div style={{ fontSize: 14, color: "var(--text-soft)" }}>No more cycles today</div>
            )}
          </div>
        </div>

        {/* Schedule Details */}
        <div style={card}>
          <div style={secLbl}>Schedule — {PUMP_SCHEDULES[pumpSystem].name}</div>
          {(() => {
            const sched = PUMP_SCHEDULES[pumpSystem].schedules[activeSeason];
            const summary = summarizeSchedule(sched);
            const lastH = Math.floor(summary.lastEndMin / 60);
            const lastM = summary.lastEndMin % 60;
            return (
              <>
                <div style={{ fontSize: 13, color: "var(--text-bright)", fontWeight: 600, marginBottom: 4 }}>{sched.label}</div>
                <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 12 }}>
                  Active window: <strong style={{ color: "var(--active)" }}>{formatHourMin(summary.firstHour, summary.firstMin)} – {formatHourMin(lastH, lastM)}</strong> · {SEASONS[activeSeason].label}
                </div>

                {/* Stats */}
                <div style={{ padding: "10px 12px", borderRadius: 8, background: "rgba(0,0,0,0.2)", display: "flex", justifyContent: "space-around", textAlign: "center", marginBottom: 12 }}>
                  <div>
                    <div style={{ fontSize: 10, color: "var(--text-soft)" }}>Cycles/day</div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: "var(--active)" }}>{summary.totalCycles}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 10, color: "var(--text-soft)" }}>Run time</div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: "var(--active)" }}>{Math.floor(summary.totalRunMin / 60)}h {summary.totalRunMin % 60}m</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 10, color: "var(--text-soft)" }}>Off overnight</div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: "#64b5f6" }}>{24 - Math.ceil((summary.lastEndMin - summary.firstHour * 60 - summary.firstMin) / 60)}h+</div>
                  </div>
                </div>

                {/* Cycle list grouped by intensity */}
                {(() => {
                  // Group consecutive cycles with same on-time
                  const groups = [];
                  let cur = null;
                  for (const c of sched.cycles) {
                    const startMinAbs = c.hour * 60 + (c.startMin || 0);
                    if (cur && cur.onMin === c.on) {
                      cur.lastStartMinAbs = startMinAbs;
                      cur.lastOn = c.on;
                      cur.count += 1;
                    } else {
                      if (cur) groups.push(cur);
                      cur = {
                        firstStartMinAbs: startMinAbs,
                        lastStartMinAbs: startMinAbs,
                        onMin: c.on, count: 1,
                      };
                    }
                  }
                  if (cur) groups.push(cur);

                  // Different intensity scales for NFT vs Tower
                  // NFT cycles range 15–30 min, Tower cycles 3 min each
                  const isTower = pumpSystem === "tower";
                  const intensityColor = (m) => {
                    if (isTower) return "#78c854"; // tower cycles all "active flow"
                    return m >= 25 ? "#ef5350" : m >= 15 ? "#ffd54f" : "#78c854";
                  };
                  const intensityLabel = (m) => {
                    if (isTower) return "Active flow";
                    return m >= 25 ? "Peak heat" : m >= 15 ? "Active" : "Light";
                  };

                  return groups.map((g, i) => {
                    const fh = Math.floor(g.firstStartMinAbs / 60);
                    const fm = g.firstStartMinAbs % 60;
                    const endMinAbs = g.lastStartMinAbs + g.onMin;
                    const eh = Math.floor(endMinAbs / 60);
                    const em = endMinAbs % 60;
                    const color = intensityColor(g.onMin);
                    return (
                      <div key={i} style={{
                        padding: "10px 12px", borderRadius: 8, marginBottom: 6,
                        background: `${color}10`, border: `1px solid ${color}25`,
                        display: "flex", alignItems: "center", gap: 10,
                      }}>
                        <div style={{ width: 4, height: 32, borderRadius: 2, background: color, flexShrink: 0 }} />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-bright)" }}>
                            {formatHourMin(fh, fm)} – {formatHourMin(eh, em)}
                          </div>
                          <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 1 }}>
                            {intensityLabel(g.onMin)} · {g.count} {g.count === 1 ? "cycle" : "cycles"}
                            {g.count > 1 && ` · every ${Math.round((g.lastStartMinAbs - g.firstStartMinAbs) / (g.count - 1))} min`}
                          </div>
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <div style={{ fontSize: 16, fontWeight: 700, color: color, fontVariantNumeric: "tabular-nums" }}>
                            {g.onMin} min
                          </div>
                          <div style={{ fontSize: 10, color: "var(--text-soft)" }}>per cycle</div>
                        </div>
                      </div>
                    );
                  });
                })()}

                <div style={{ marginTop: 10, padding: "8px 12px", borderRadius: 8, background: "rgba(66,165,245,0.06)", border: "1px solid rgba(66,165,245,0.15)", fontSize: 11, color: "#90caf9" }}>
                  🌙 Pumps OFF overnight — saves power and lets reservoir cool naturally
                </div>

              </>
            );
          })()}
        </div>

        {/* Today's Cycles Timeline */}
        <div style={card}>
          <div style={secLbl}>Today's Cycles ({pumpEvents.length} total)</div>
          <div style={{ maxHeight: 300, overflowY: "auto", paddingRight: 4 }}>
            {pumpEvents.map((e, i) => {
              const now = new Date();
              const isPast = e.end < now;
              const isCurrent = currentPumpEvent && e.start.getTime() === currentPumpEvent.start.getTime();
              const isNext = nextPumpEvent && e.start.getTime() === nextPumpEvent.start.getTime();
              return (
                <div key={i} style={{
                  display: "flex", alignItems: "center", gap: 10, padding: "6px 10px",
                  marginBottom: 3, borderRadius: 6,
                  background: isCurrent ? "rgba(90,185,90,0.18)" : isNext ? "rgba(255,213,79,0.08)" : isPast ? "transparent" : "rgba(255,255,255,0.02)",
                  border: isCurrent ? "1px solid rgba(76,175,80,0.3)" : isNext ? "1px solid rgba(255,213,79,0.2)" : "1px solid transparent",
                  opacity: isPast && !isCurrent ? 0.4 : 1,
                }}>
                  <div style={{ fontSize: 12, color: "var(--text-soft)", minWidth: 16 }}>{i + 1}</div>
                  <div style={{ fontSize: 12, color: "var(--text-muted)", minWidth: 50 }}>{e.period === "morning" ? "🌅" : e.period === "evening" ? "🌇" : "☀️"}</div>
                  <div style={{ flex: 1, fontSize: 12, color: "var(--text-bright)", fontVariantNumeric: "tabular-nums" }}>
                    {formatTime(e.start)} → {formatTime(e.end)}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--text-soft)" }}>{Math.round((e.end - e.start) / 60000)}m</div>
                  {isCurrent && <div style={{ fontSize: 10, fontWeight: 700, color: "#5cb85c" }}>NOW</div>}
                  {isNext && <div style={{ fontSize: 10, fontWeight: 700, color: "#ffd54f" }}>NEXT</div>}
                </div>
              );
            })}
          </div>
        </div>

        {/* Power Cost */}
        <div style={card}>
          <button
            onClick={() => setShowPowerCost(o => !o)}
            aria-expanded={showPowerCost}
            aria-controls="power-cost-content"
            style={{
              width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center",
              background: "transparent", border: "none", cursor: "pointer", padding: 0,
              color: "#eaffea", fontFamily: "inherit",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 18 }}>⚡</span>
              <div style={{ textAlign: "left" }}>
                <div style={{ ...secLbl, marginBottom: 2 }}>Power & Cost</div>
                <div style={{ fontSize: 12, color: "var(--text-muted)", fontVariantNumeric: "tabular-nums" }}>
                  {powerCost.totalKwh.toFixed(2)} kWh/day · {formatCurrency(powerCost.totalCost, userLocation.currency)}/day
                </div>
              </div>
            </div>
            <span style={{ fontSize: 16, color: "var(--text-soft)", transform: showPowerCost ? "rotate(90deg)" : "rotate(0deg)", display: "inline-block" }} aria-hidden="true">▶</span>
          </button>

          {showPowerCost && (
            <div id="power-cost-content" style={{ marginTop: 14 }}>
              {/* Settings row */}
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, color: "var(--text-soft)", marginBottom: 6, textTransform: "uppercase", letterSpacing: 1 }}>Settings</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
                  <div>
                    <div style={{ fontSize: 10, color: "var(--text-soft)", marginBottom: 2 }}>{userLocation.currency}/kWh</div>
                    <input
                      type="number" step="0.5" min={0} max={200}
                      value={electricityRate}
                      onChange={e => setElectricityRate(Math.max(0, parseFloat(e.target.value) || 0))}
                      aria-label="Electricity rate per kWh"
                      style={{
                        width: "100%", padding: "6px 8px", borderRadius: 8, fontSize: 14, fontWeight: 700,
                        border: "1px solid var(--border-soft)", background: "rgba(0,0,0,0.3)",
                        color: "var(--active)", outline: "none", boxSizing: "border-box", fontVariantNumeric: "tabular-nums",
                      }}
                    />
                  </div>
                  <div>
                    <div style={{ fontSize: 10, color: "var(--text-soft)", marginBottom: 2 }}>NFT Watts</div>
                    <input
                      type="number" step="0.5" min={0} max={5000}
                      value={nftWattage}
                      onChange={e => setNftWattage(Math.max(0, parseFloat(e.target.value) || 0))}
                      aria-label="NFT total wattage"
                      style={{
                        width: "100%", padding: "6px 8px", borderRadius: 8, fontSize: 14, fontWeight: 700,
                        border: "1px solid var(--border-soft)", background: "rgba(0,0,0,0.3)",
                        color: "#1976d2", outline: "none", boxSizing: "border-box", fontVariantNumeric: "tabular-nums",
                      }}
                    />
                  </div>
                  <div>
                    <div style={{ fontSize: 10, color: "var(--text-soft)", marginBottom: 2 }}>Tower Watts</div>
                    <input
                      type="number" step="5" min={0} max={5000}
                      value={towerWattage}
                      onChange={e => setTowerWattage(Math.max(0, parseFloat(e.target.value) || 0))}
                      aria-label="Tower total wattage"
                      style={{
                        width: "100%", padding: "6px 8px", borderRadius: 8, fontSize: 14, fontWeight: 700,
                        border: "1px solid var(--border-soft)", background: "rgba(0,0,0,0.3)",
                        color: "#7b1fa2", outline: "none", boxSizing: "border-box", fontVariantNumeric: "tabular-nums",
                      }}
                    />
                  </div>
                </div>
                <div style={{ marginTop: 6, fontSize: 10, color: "var(--text-soft)", lineHeight: 1.5 }}>
                  Defaults assume tropical climate. NFT measured at 658.5W, towers spec'd at 425W (5 × 85W). Adjust electricity rate to match your local utility.
                </div>
              </div>

              {/* Per-system breakdown */}
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, color: "var(--text-soft)", marginBottom: 6, textTransform: "uppercase", letterSpacing: 1 }}>Daily — {SEASONS[activeSeason].label}</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <div style={{ padding: "10px 12px", borderRadius: 8, background: "rgba(25,118,210,0.08)", border: "1px solid rgba(25,118,210,0.2)" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "#90caf9" }}>🟦 NFT System</div>
                        <div style={{ fontSize: 11, color: "var(--text-soft)" }}>
                          {Math.floor(powerCost.nftMin / 60)}h {powerCost.nftMin % 60}m runtime · {nftWattage.toFixed(0)}W
                        </div>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <div style={{ fontSize: 16, fontWeight: 700, color: "var(--text-strong)", fontVariantNumeric: "tabular-nums" }}>
                          {powerCost.nftKwh.toFixed(2)} kWh
                        </div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "#90caf9", fontVariantNumeric: "tabular-nums" }}>
                          {formatCurrency(powerCost.nftCost, userLocation.currency)}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div style={{ padding: "10px 12px", borderRadius: 8, background: "rgba(123,31,162,0.08)", border: "1px solid rgba(123,31,162,0.2)" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "#ce93d8" }}>🗼 Tower System</div>
                        <div style={{ fontSize: 11, color: "var(--text-soft)" }}>
                          {Math.floor(powerCost.towerMin / 60)}h {powerCost.towerMin % 60}m runtime · {towerWattage.toFixed(0)}W
                        </div>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <div style={{ fontSize: 16, fontWeight: 700, color: "var(--text-strong)", fontVariantNumeric: "tabular-nums" }}>
                          {powerCost.towerKwh.toFixed(2)} kWh
                        </div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "#ce93d8", fontVariantNumeric: "tabular-nums" }}>
                          {formatCurrency(powerCost.towerCost, userLocation.currency)}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div style={{ padding: "10px 12px", borderRadius: 8, background: "rgba(90,185,90,0.12)", border: "1px solid rgba(76,175,80,0.25)" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "var(--active)" }}>Combined Daily Total</div>
                      <div style={{ textAlign: "right" }}>
                        <div style={{ fontSize: 18, fontWeight: 700, color: "var(--text-strong)", fontVariantNumeric: "tabular-nums" }}>
                          {powerCost.totalKwh.toFixed(2)} kWh
                        </div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: "var(--active)", fontVariantNumeric: "tabular-nums" }}>
                          {formatCurrency(powerCost.totalCost, userLocation.currency)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Period summary */}
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, color: "var(--text-soft)", marginBottom: 6, textTransform: "uppercase", letterSpacing: 1 }}>Projected Cost</div>
                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(3, 1fr)", gap: 6 }}>
                  <div style={{ padding: "10px 8px", borderRadius: 8, background: "rgba(0,0,0,0.2)", textAlign: "center" }}>
                    <div style={{ fontSize: 10, color: "var(--text-soft)" }}>Daily</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "var(--active)", fontVariantNumeric: "tabular-nums" }}>{formatCurrency(powerCost.totalCost, userLocation.currency)}</div>
                    <div style={{ fontSize: 10, color: "var(--text-soft)", fontVariantNumeric: "tabular-nums" }}>{powerCost.totalKwh.toFixed(2)} kWh</div>
                  </div>
                  <div style={{ padding: "10px 8px", borderRadius: 8, background: "rgba(0,0,0,0.2)", textAlign: "center" }}>
                    <div style={{ fontSize: 10, color: "var(--text-soft)" }}>Weekly</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "var(--active)", fontVariantNumeric: "tabular-nums" }}>{formatCurrency(powerCost.weekCost, userLocation.currency)}</div>
                    <div style={{ fontSize: 10, color: "var(--text-soft)", fontVariantNumeric: "tabular-nums" }}>{powerCost.weekKwh.toFixed(2)} kWh</div>
                  </div>
                  <div style={{ padding: "10px 8px", borderRadius: 8, background: "rgba(0,0,0,0.2)", textAlign: "center" }}>
                    <div style={{ fontSize: 10, color: "var(--text-soft)" }}>Monthly</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "var(--active)", fontVariantNumeric: "tabular-nums" }}>{formatCurrency(powerCost.monthCost, userLocation.currency)}</div>
                    <div style={{ fontSize: 10, color: "var(--text-soft)", fontVariantNumeric: "tabular-nums" }}>{powerCost.monthKwh.toFixed(1)} kWh</div>
                  </div>
                </div>
              </div>

              {/* Calculated vs Actual comparison */}
              <div>
                <div style={{ fontSize: 11, color: "var(--text-soft)", marginBottom: 6, textTransform: "uppercase", letterSpacing: 1 }}>Reality Check (optional)</div>
                <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 8, lineHeight: 1.5 }}>
                  Enter actual measured kWh from your smart plug to compare against the schedule's predicted draw.
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  <div>
                    <div style={{ fontSize: 10, color: "var(--text-soft)", marginBottom: 2 }}>Actual NFT kWh/day</div>
                    <input
                      type="number" step="0.01" min={0} max={50}
                      placeholder="e.g. 3.36"
                      value={actualNftKwh}
                      onChange={e => setActualNftKwh(clampNonNegative(e.target.value, 9999))}
                      aria-label="Actual measured NFT kWh per day"
                      style={{
                        width: "100%", padding: "6px 8px", borderRadius: 8, fontSize: 14, fontWeight: 700,
                        border: "1px solid var(--border-soft)", background: "rgba(0,0,0,0.3)",
                        color: "#90caf9", outline: "none", boxSizing: "border-box", fontVariantNumeric: "tabular-nums",
                      }}
                    />
                    {actualNftKwh && parseFloat(actualNftKwh) > 0 && (
                      <div style={{ marginTop: 4, fontSize: 11, color: "var(--text-soft)" }}>
                        {(() => {
                          const a = parseFloat(actualNftKwh);
                          const c = powerCost.nftKwh;
                          const diff = ((a - c) / c) * 100;
                          const color = Math.abs(diff) <= 15 ? "#78c854" : "#ffd54f";
                          return (
                            <span style={{ color }}>
                              {diff >= 0 ? "+" : ""}{diff.toFixed(0)}% vs predicted ({c.toFixed(2)})
                            </span>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                  <div>
                    <div style={{ fontSize: 10, color: "var(--text-soft)", marginBottom: 2 }}>Actual Tower kWh/day</div>
                    <input
                      type="number" step="0.01" min={0} max={50}
                      placeholder="optional"
                      value={actualTowerKwh}
                      onChange={e => setActualTowerKwh(clampNonNegative(e.target.value, 9999))}
                      aria-label="Actual measured Tower kWh per day"
                      style={{
                        width: "100%", padding: "6px 8px", borderRadius: 8, fontSize: 14, fontWeight: 700,
                        border: "1px solid var(--border-soft)", background: "rgba(0,0,0,0.3)",
                        color: "#ce93d8", outline: "none", boxSizing: "border-box", fontVariantNumeric: "tabular-nums",
                      }}
                    />
                    {actualTowerKwh && parseFloat(actualTowerKwh) > 0 && (
                      <div style={{ marginTop: 4, fontSize: 11, color: "var(--text-soft)" }}>
                        {(() => {
                          const a = parseFloat(actualTowerKwh);
                          const c = powerCost.towerKwh;
                          const diff = ((a - c) / c) * 100;
                          const color = Math.abs(diff) <= 15 ? "#78c854" : "#ffd54f";
                          return (
                            <span style={{ color }}>
                              {diff >= 0 ? "+" : ""}{diff.toFixed(0)}% vs predicted ({c.toFixed(2)})
                            </span>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                </div>
                {actualNftKwh && parseFloat(actualNftKwh) > 0 && (
                  <div style={{ marginTop: 8, padding: "8px 10px", borderRadius: 8, background: "rgba(255,255,255,0.03)", fontSize: 11, color: "var(--text-muted)" }}>
                    Actual NFT cost: <strong style={{ color: "var(--text-strong)" }}>{formatCurrency((parseFloat(actualNftKwh) * electricityRate), userLocation.currency)}/day</strong>
                    {actualTowerKwh && parseFloat(actualTowerKwh) > 0 && (
                      <> · Tower: <strong style={{ color: "var(--text-strong)" }}>{formatCurrency((parseFloat(actualTowerKwh) * electricityRate), userLocation.currency)}/day</strong></>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Smart Plug Tips */}
        <div style={card}>
          <div style={secLbl}>Smart Plug Setup Tips</div>
          <div style={{ fontSize: 12, color: "var(--text-muted)", lineHeight: 1.7 }}>
            <div style={{ marginBottom: 6 }}>📋 <strong style={{ color: "var(--text-bright)" }}>Use explicit ON/OFF events</strong> rather than a single repeating timer — more reliable across firmware updates.</div>
            <div style={{ marginBottom: 6 }}>⚡ <strong style={{ color: "var(--text-bright)" }}>Power monitoring alerts</strong> — set zero-watt alerts during ON cycles. Critical for NFT (roots dry in 30–60 min).</div>
            <div style={{ marginBottom: 6 }}>🔌 <strong style={{ color: "var(--text-bright)" }}>Mechanical backup timer</strong> — keep one wired in series. Smart plugs lose schedules on WiFi drops.</div>
            <div>🌡️ <strong style={{ color: "var(--text-bright)" }}>Pre-load all 3 seasons</strong> — most plugs (Tuya, Kasa) support scheduled scene activation by date.</div>
          </div>
        </div>

        {/* Quick log button */}
        <button onClick={() => { setLogType("pump_check"); setTab("log"); }} style={{
          width: "100%", padding: "12px", borderRadius: 10, fontSize: 13, fontWeight: 700,
          border: "1px solid rgba(255,167,38,0.3)", background: "rgba(255,167,38,0.1)",
          color: "#ffa726", cursor: "pointer",
        }}>
          🔌 Log a Pump Check
        </button>
      </>)}
      {tab === "log" && (<>
        <div style={card}>
          <div style={secLbl}>Log Event — {crop.name} · {stage.label}</div>
          {activeGrow ? (
            <div style={{ fontSize: 10, color: "#78c854", marginBottom: 8, marginTop: -4 }}>
              ✓ Linked to active grow: {activeGrow.label || crop.name} (Day {daysSince(activeGrow.plantingDate)})
            </div>
          ) : grows.length > 0 ? (
            <div style={{ fontSize: 10, color: "#ffd54f", marginBottom: 8, marginTop: -4 }}>
              ⚠ No active grow — entry won't link to a specific grow. <button onClick={() => setTab("grows")} style={{ background: "none", border: "none", color: "#ffd54f", textDecoration: "underline", cursor: "pointer", padding: 0, fontSize: 10 }}>Set one</button>
            </div>
          ) : null}
          <div style={{ fontSize: 10, color: "var(--text-soft)", marginBottom: 8, marginTop: -4 }}>
            Live guidance uses currently selected crop/stage. The log entry stores this snapshot.
          </div>
          <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 12 }}>
            {EVENT_TYPES.map(et => (<button key={et.key} onClick={() => { setLogType(et.key); setLogValue(""); }} style={{ padding: "5px 9px", borderRadius: 8, fontSize: 11, fontWeight: 600, cursor: "pointer", border: logType === et.key ? `2px solid ${et.color}` : "1px solid var(--border-soft)", background: logType === et.key ? `${et.color}20` : "rgba(255,255,255,0.03)", color: logType === et.key ? "var(--text-strong)" : "#6d9e6d" }}>{et.icon} {et.label}</button>))}
          </div>

          {/* Consolidated Reservoir Check — EC + pH + temp in one pass */}
          {logType === "reservoir_check" && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 10, lineHeight: 1.5 }}>
                Your daily check in one go. Fill what you measured — each value is saved as its own reading, so trends stay intact. Leave a field blank to skip it.
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 10 }}>
                <div>
                  <div style={{ fontSize: 10, color: "#ffd54f", marginBottom: 3, fontWeight: 600 }}>⚡ EC</div>
                  <input type="number" step="0.01" min={0} max={5} placeholder="1.05" value={rcEc} onChange={e => setRcEc(e.target.value)} style={{ width: "100%", padding: "9px 8px", borderRadius: 9, fontSize: 16, fontWeight: 700, border: "1px solid var(--border-soft)", background: "rgba(0,0,0,0.3)", color: "#ffd54f", outline: "none", boxSizing: "border-box", fontVariantNumeric: "tabular-nums" }} />
                </div>
                <div>
                  <div style={{ fontSize: 10, color: "#64b5f6", marginBottom: 3, fontWeight: 600 }}>🧪 pH</div>
                  <input type="number" step="0.1" min={0} max={14} placeholder="5.8" value={rcPh} onChange={e => setRcPh(e.target.value)} style={{ width: "100%", padding: "9px 8px", borderRadius: 9, fontSize: 16, fontWeight: 700, border: "1px solid var(--border-soft)", background: "rgba(0,0,0,0.3)", color: "#64b5f6", outline: "none", boxSizing: "border-box", fontVariantNumeric: "tabular-nums" }} />
                </div>
                <div>
                  <div style={{ fontSize: 10, color: "#4fc3f7", marginBottom: 3, fontWeight: 600 }}>🌡️ Temp °C</div>
                  <input type="number" step="0.5" min={0} max={45} placeholder="26" value={rcTemp} onChange={e => setRcTemp(e.target.value)} style={{ width: "100%", padding: "9px 8px", borderRadius: 9, fontSize: 16, fontWeight: 700, border: "1px solid var(--border-soft)", background: "rgba(0,0,0,0.3)", color: "#4fc3f7", outline: "none", boxSizing: "border-box", fontVariantNumeric: "tabular-nums" }} />
                </div>
              </div>
              {/* Live guidance from the entered values */}
              {(() => {
                const notes = [];
                if (rcEc !== "" && Number.isFinite(parseFloat(rcEc))) {
                  const raw = parseFloat(rcEc);
                  const t = rcTemp !== "" ? parseFloat(rcTemp) : lastWaterTemp;
                  const compOn = sysSettings.ecTempComp !== false && t != null && Number.isFinite(t);
                  const corr = compOn ? compensateEc(raw, t, sysSettings.ecRefTemp ?? 25, sysSettings.ecTempCoeff ?? 2) : null;
                  const cmp = corr != null ? corr : raw;
                  const close = Math.abs(cmp - effTarget) <= 0.15;
                  notes.push({ color: close ? "#78c854" : "#ffd54f", text: `EC ${close ? "on target" : cmp < effTarget ? "below target" : "above target"} (${effTarget.toFixed(2)})${corr != null && Math.abs(raw - corr) >= 0.02 ? ` · temp-corrected to ${corr.toFixed(2)} at ${t}°C` : ""}` });
                }
                if (rcPh !== "" && Number.isFinite(parseFloat(rcPh))) {
                  const s = phStatus(rcPh);
                  if (s) notes.push({ color: s.ok ? "#78c854" : "#ffd54f", text: s.msg });
                }
                if (rcTemp !== "" && Number.isFinite(parseFloat(rcTemp))) {
                  const tv = parseFloat(rcTemp);
                  if (tv > 26) notes.push({ color: "#ffb74d", text: `Water ${tv}°C is warm — above 26°C stresses roots and lowers dissolved oxygen. Shade the reservoir.` });
                }
                return notes.length ? (
                  <div style={{ padding: "8px 12px", borderRadius: 8, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", marginBottom: 10 }}>
                    {notes.map((n, i) => <div key={i} style={{ fontSize: 11.5, color: n.color, lineHeight: 1.5 }}>{n.text}</div>)}
                  </div>
                ) : null;
              })()}
              {/* Fresh change flag */}
              <button onClick={() => setRcFreshChange(v => !v)} style={{
                width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 9, cursor: "pointer",
                border: rcFreshChange ? "1px solid rgba(38,198,218,0.5)" : "1px solid var(--border-soft)",
                background: rcFreshChange ? "rgba(38,198,218,0.1)" : "rgba(255,255,255,0.02)", textAlign: "left",
              }}>
                <span style={{ width: 20, height: 20, borderRadius: 6, flexShrink: 0, border: `2px solid ${rcFreshChange ? "#26c6da" : "rgba(255,255,255,0.2)"}`, background: rcFreshChange ? "#26c6da" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--bg-card2)", fontSize: 13, fontWeight: 900 }}>{rcFreshChange ? "✓" : ""}</span>
                <span>
                  <span style={{ fontSize: 12.5, fontWeight: 600, color: rcFreshChange ? "#4fd6e8" : "var(--text-soft2)" }}>💧 This is a fresh reservoir change</span>
                  <span style={{ display: "block", fontSize: 10.5, color: "var(--text-soft)", marginTop: 1 }}>Resets the refresh timer{reservoirStatus.hasHistory ? ` (last change ${reservoirStatus.daysSince}d ago)` : ""}.</span>
                </span>
              </button>
            </div>
          )}

          {(logType === "ec_reading" || logType === "ph_reading") && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 11, color: "var(--text-soft)", marginBottom: 4 }}>{logType === "ec_reading" ? "EC Value" : "pH Value"} <span style={{ color: "#ef5350" }}>*</span></div>
              <input type="number" step={logType === "ec_reading" ? "0.01" : "0.1"} min={0} max={logType === "ec_reading" ? 5 : 14} placeholder={logType === "ec_reading" ? "e.g. 1.05" : "e.g. 5.8"} value={logValue} onChange={e => setLogValue(e.target.value)} style={{ width: "100%", padding: "10px 12px", borderRadius: 10, fontSize: 18, fontWeight: 700, border: "1px solid var(--border-soft)", background: "rgba(0,0,0,0.3)", color: "var(--active)", outline: "none", boxSizing: "border-box", fontVariantNumeric: "tabular-nums" }} />
              {logType === "ph_reading" && logValue && (() => { const s = phStatus(logValue); return s ? <div style={{ marginTop: 5, fontSize: 12, color: s.ok ? "#78c854" : "#ffd54f" }}>{s.msg}</div> : null; })()}
              {logType === "ec_reading" && logValue && (() => {
                const raw = parseFloat(logValue);
                if (!Number.isFinite(raw)) return null;
                const compOn = sysSettings.ecTempComp !== false && lastWaterTemp != null;
                const corrected = compOn ? compensateEc(raw, lastWaterTemp, sysSettings.ecRefTemp ?? 25, sysSettings.ecTempCoeff ?? 2) : null;
                const compareEc = corrected != null ? corrected : raw;
                const close = Math.abs(compareEc - effTarget) <= 0.15;
                const delta = raw - (corrected ?? raw);
                return (<>
                  <div style={{ marginTop: 5, fontSize: 12, color: close ? "#78c854" : "#ffd54f" }}>
                    {close ? `✓ Close to target (${effTarget.toFixed(2)})` : compareEc < effTarget ? `⚠ Below target (${effTarget.toFixed(2)})` : `⚠ Above target (${effTarget.toFixed(2)})`}
                  </div>
                  {corrected != null && Math.abs(delta) >= 0.02 && (
                    <div style={{ marginTop: 6, padding: "7px 10px", borderRadius: 8, background: "rgba(79,195,247,0.08)", border: "1px solid rgba(79,195,247,0.2)", fontSize: 11, color: "#8ec5e8", lineHeight: 1.5 }}>
                      🌡️ At {lastWaterTemp}°C, your <strong style={{ color: "var(--text-strong)" }}>{raw.toFixed(2)}</strong> reads as <strong style={{ color: "var(--text-strong)" }}>{corrected.toFixed(2)}</strong> corrected to {sysSettings.ecRefTemp ?? 25}°C.
                      {corrected < raw ? " Solution is weaker than the raw number suggests — you likely need to add more nutrient." : " Solution is slightly stronger than the raw number suggests."}
                      <span style={{ color: "var(--text-soft)" }}> (comparison above uses the corrected value.)</span>
                    </div>
                  )}
                  {compOn === false && sysSettings.ecTempComp !== false && (
                    <div style={{ marginTop: 5, fontSize: 10.5, color: "var(--text-soft)" }}>Log a water-temp reading to auto-correct EC for reservoir heat.</div>
                  )}
                </>);
              })()}
            </div>
          )}
          {logType === "ph_down" && <div style={{ marginBottom: 10 }}><div style={{ fontSize: 11, color: "var(--text-soft)", marginBottom: 4 }}>35% H₂SO₄ (ml) <span style={{ color: "#ef5350" }}>*</span></div><input type="number" step="0.5" min={0} placeholder="e.g. 2.5" value={logValue} onChange={e => setLogValue(e.target.value)} style={{ width: "100%", padding: "10px 12px", borderRadius: 10, fontSize: 16, fontWeight: 700, border: "1px solid var(--border-soft)", background: "rgba(0,0,0,0.3)", color: "#ef5350", outline: "none", boxSizing: "border-box" }} /></div>}
          {logType === "ph_up" && <div style={{ marginBottom: 10 }}><div style={{ fontSize: 11, color: "var(--text-soft)", marginBottom: 4 }}>Water Added (gal) <span style={{ color: "#ef5350" }}>*</span></div><input type="number" step="1" min={0} placeholder="e.g. 10" value={logValue} onChange={e => setLogValue(e.target.value)} style={{ width: "100%", padding: "10px 12px", borderRadius: 10, fontSize: 16, fontWeight: 700, border: "1px solid var(--border-soft)", background: "rgba(0,0,0,0.3)", color: "#42a5f5", outline: "none", boxSizing: "border-box" }} /></div>}
          {logType === "water_change" && <div style={{ marginBottom: 10 }}><div style={{ fontSize: 11, color: "var(--text-soft)", marginBottom: 4 }}>Gallons Changed <span style={{ color: "#ef5350" }}>*</span></div><input type="number" step="1" min={0} placeholder="e.g. 200" value={logValue} onChange={e => setLogValue(e.target.value)} style={{ width: "100%", padding: "10px 12px", borderRadius: 10, fontSize: 16, fontWeight: 700, border: "1px solid var(--border-soft)", background: "rgba(0,0,0,0.3)", color: "#26c6da", outline: "none", boxSizing: "border-box" }} /></div>}
          {logType === "harvest" && <div style={{ marginBottom: 10 }}><div style={{ fontSize: 11, color: "var(--text-soft)", marginBottom: 4 }}>Quantity (e.g. "2.5 kg" or "12 heads") <span style={{ color: "#ef5350" }}>*</span></div><input type="text" placeholder="e.g. 2.5 kg" value={logValue} onChange={e => setLogValue(e.target.value)} style={{ width: "100%", padding: "10px 12px", borderRadius: 10, fontSize: 16, fontWeight: 700, border: "1px solid var(--border-soft)", background: "rgba(0,0,0,0.3)", color: "#90c870", outline: "none", boxSizing: "border-box" }} /><div style={{ marginTop: 6, fontSize: 11, color: "var(--text-muted)" }}>💡 For full harvest tracking with sale price, use the Grows tab → 🧺 Harvest button</div></div>}
          {logType === "water_temp" && <div style={{ marginBottom: 10 }}><div style={{ fontSize: 11, color: "var(--text-soft)", marginBottom: 4 }}>Water Temperature (°C) <span style={{ color: "#ef5350" }}>*</span></div><input type="number" step="0.1" min={0} max={50} placeholder="e.g. 25.5" value={logValue} onChange={e => setLogValue(e.target.value)} style={{ width: "100%", padding: "10px 12px", borderRadius: 10, fontSize: 18, fontWeight: 700, border: "1px solid var(--border-soft)", background: "rgba(0,0,0,0.3)", color: "#4fc3f7", outline: "none", boxSizing: "border-box", fontVariantNumeric: "tabular-nums" }} />{logValue && <div style={{ marginTop: 5, fontSize: 12, color: parseFloat(logValue) >= 30 ? "#ef5350" : parseFloat(logValue) >= 28 ? "#ffd54f" : parseFloat(logValue) < 15 ? "#64b5f6" : "#78c854" }}>{parseFloat(logValue) >= 30 ? "⚠ Critical — root rot risk, very low O₂" : parseFloat(logValue) >= 28 ? "⚠ Hot — watch for tip burn" : parseFloat(logValue) < 15 ? "⚠ Cold — uptake slowed" : "✓ Optimal range"}</div>}</div>}
          {logType === "farm_temp" && <div style={{ marginBottom: 10 }}><div style={{ fontSize: 11, color: "var(--text-soft)", marginBottom: 4 }}>Farm / Ambient Temperature (°C) <span style={{ color: "#ef5350" }}>*</span></div><input type="number" step="0.1" min={0} max={50} placeholder="e.g. 30.0" value={logValue} onChange={e => setLogValue(e.target.value)} style={{ width: "100%", padding: "10px 12px", borderRadius: 10, fontSize: 18, fontWeight: 700, border: "1px solid var(--border-soft)", background: "rgba(0,0,0,0.3)", color: "#ffb74d", outline: "none", boxSizing: "border-box", fontVariantNumeric: "tabular-nums" }} /></div>}

          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 11, color: "var(--text-soft)", marginBottom: 4 }}>Notes (optional)</div>
            <textarea rows={2} placeholder="Observations..." value={logNotes} onChange={e => setLogNotes(e.target.value)} style={{ width: "100%", padding: "8px 12px", borderRadius: 10, fontSize: 13, border: "1px solid var(--border-soft)", background: "rgba(0,0,0,0.2)", color: "var(--active)", outline: "none", resize: "vertical", boxSizing: "border-box", fontFamily: "inherit" }} />
          </div>

          <div style={{ marginBottom: 10, padding: "7px 12px", borderRadius: 8, background: "rgba(0,0,0,0.15)", fontSize: 11, color: "var(--text-soft)", display: "flex", gap: 10, flexWrap: "wrap" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><CropIcon cropKey={cropKey} emoji={crop.icon} size={14} /> {crop.name}</span><span>{stage.label}</span><span>EC: {effTarget.toFixed(2)}</span><span>pH: {stage.phMin}–{stage.phMax}</span>
          </div>

          {(() => {
            const rcAny = rcEc !== "" || rcPh !== "" || rcTemp !== "";
            const rcMode = logType === "reservoir_check";
            const active = rcMode ? rcAny : canSubmit;
            const rcCount = [rcEc, rcPh, rcTemp].filter(v => v !== "").length + (rcFreshChange ? 1 : 0);
            return (
              <button onClick={addLog} disabled={!active} style={{ width: "100%", padding: "12px", borderRadius: 10, fontSize: 14, fontWeight: 700, border: "none", cursor: active ? "pointer" : "not-allowed", background: active ? `linear-gradient(135deg,${crop.color},${crop.color}cc)` : "rgba(80,80,80,0.3)", color: active ? "var(--text-strong)" : "var(--text-soft)", opacity: active ? 1 : 0.6 }}>
                {rcMode
                  ? (rcAny ? `Save Reservoir Check — ${rcCount} record${rcCount === 1 ? "" : "s"}` : "Enter at least one reading")
                  : (canSubmit ? `Log Event — ${new Date().toLocaleTimeString("en-JM", { hour: "2-digit", minute: "2-digit", hour12: true })}` : "Enter a value to log")}
              </button>
            );
          })()}
        </div>

        <div style={card}>
          <div style={secLbl}>Recent Entries</div>
          {logs.length === 0 && <div style={{ fontSize: 13, color: "var(--text-soft)", textAlign: "center", padding: 20 }}>No entries yet.</div>}
          {logs.slice(0, 10).map(log => {
            const et = EVENT_TYPES.find(e => e.key === log.type) || EVENT_TYPES[6];
            const { day, time } = fmtDate(log.timestamp);
            return (
              <div key={log.id} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 0", borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                <div style={{ fontSize: 16, lineHeight: 1, marginTop: 2 }}>{et.icon}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: et.color }}>{et.label}</div>
                    <div style={{ fontSize: 10, color: "var(--text-soft)" }}>{day} · {time}</div>
                  </div>
                  {log.crop && <div style={{ fontSize: 10, color: "var(--text-soft)" }}>{log.crop} · {log.stage}</div>}
                  {log.value && <div style={{ fontSize: 15, fontWeight: 700, color: "var(--text-bright)", marginTop: 1, fontVariantNumeric: "tabular-nums" }}>{log.type === "ec_reading" ? `EC ${log.value}` : log.type === "ph_reading" ? `pH ${log.value}` : log.type === "water_temp" ? `${log.value}°C water` : log.type === "farm_temp" ? `${log.value}°C ambient` : log.type === "ph_down" ? `${log.value} ml H₂SO₄` : log.type === "ph_up" ? `${log.value} gal water` : log.type === "water_change" ? `${log.value} gal changed` : log.type === "harvest" ? `🧺 ${log.value}${log.harvestPrice ? ` · ${formatCurrency(log.harvestPrice, userLocation.currency)}` : ""}` : log.value}</div>}
                  {log.amounts && <div style={{ fontSize: 11, color: "#6d9e6d", marginTop: 1 }}>{log.amounts.map(a => `${a.label}: ${a.grams}g`).join(" · ")}{log.calcState && ` — EC ${log.calcState.targetEc.toFixed(2)}`}</div>}
                  {log.location && <div style={{ fontSize: 10, color: "var(--text-soft)", marginTop: 1 }}>📍 {log.location}</div>}
                  {log.waterTemp != null && <div style={{ fontSize: 10, color: "var(--text-soft)", marginTop: 1 }}>Water: {log.waterTemp}°C · Farm: {log.farmTemp}°C</div>}
                  {log.notes && <div style={{ fontSize: 12, color: "var(--text-soft)", marginTop: 1 }}>{log.notes}</div>}
                </div>
                <button onClick={() => deleteLog(log.id)} aria-label={`Delete ${log.type.replace("_", " ")} log entry`} title="Delete entry" style={{ background: "none", border: "none", color: "#c97070", fontSize: 14, cursor: "pointer", padding: 4, marginTop: 2 }}><span aria-hidden="true">✕</span></button>
              </div>
            );
          })}
          {logs.length > 10 && <div style={{ fontSize: 12, color: "var(--text-soft)", textAlign: "center", padding: 8 }}>Showing 10 of {logs.length} — see History</div>}
        </div>
      </>)}

      {/* ═══════ HISTORY ═══════ */}
      {tab === "history" && (<>
        <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
          {[
            { label: "Total Logs", val: logs.length, color: "var(--active)" },
            { label: "Last EC", val: lastEcReading ? lastEcReading.value : "—", color: "#ffd54f" },
            { label: "Last pH", val: lastPhReading ? lastPhReading.value : "—", color: "#64b5f6" },
          ].map((s, i) => (
            <div key={i} style={{ ...card, flex: 1, textAlign: "center", marginBottom: 0 }}>
              <div style={{ fontSize: 10, color: "#6d9e6d" }}>{s.label}</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: s.color, fontVariantNumeric: "tabular-nums" }}>{s.val}</div>
            </div>
          ))}
        </div>

        {/* View toggle: Trends vs Events */}
        <div style={{ display: "flex", gap: 6, marginBottom: 14, background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: 11, padding: 4 }}>
          {[
            { key: "trends", label: "Trends",  icon: "chart" },
            { key: "events", label: "Events",  icon: "clipboard" },
          ].map(v => {
            const active = historyView === v.key;
            return (
              <button key={v.key} onClick={() => setHistoryView(v.key)} style={{
                flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                padding: "10px 12px", borderRadius: 8, border: "none", cursor: "pointer",
                background: active ? "linear-gradient(180deg, var(--panel-a) 0%, var(--panel-b) 100%)" : "transparent",
                boxShadow: active ? "inset 0 0 0 1px rgba(90,185,90,0.35)" : "none",
                color: active ? "var(--active)" : "var(--text-soft)",
                fontFamily: "inherit", fontWeight: active ? 700 : 500, fontSize: 13,
              }}>
                <Icon name={v.icon === "chart" ? "bolt" : v.icon} size={15} />
                {v.label}
              </button>
            );
          })}
        </div>

        {/* TRENDS VIEW */}
        {historyView === "trends" && (
          <>
            {/* Filters */}
            <div style={card}>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div style={{ fontSize: 9, color: "var(--text-soft)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 5 }}>Date range</div>
                  <div style={{ display: "flex", gap: 5 }}>
                    {[7, 14, 30, 90].map(d => (
                      <button key={d} onClick={() => setTrendRange(d)} style={{
                        flex: 1, padding: "7px 8px", borderRadius: 7, fontSize: 11, fontWeight: 600, cursor: "pointer",
                        border: trendRange === d ? "1.5px solid var(--active)" : "1px solid var(--border-soft)",
                        background: trendRange === d ? "rgba(181,224,38,0.08)" : "rgba(0,0,0,0.2)",
                        color: trendRange === d ? "var(--active)" : "var(--text-muted)",
                        fontFamily: "inherit",
                      }}>{d}d</button>
                    ))}
                  </div>
                </div>
                <div style={{ flex: 2, minWidth: 200 }}>
                  <div style={{ fontSize: 9, color: "var(--text-soft)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 5 }}>Filter by grow</div>
                  <select value={trendGrowFilter} onChange={e => setTrendGrowFilter(e.target.value)} style={{
                    width: "100%", padding: "7px 10px", borderRadius: 7, fontSize: 12, fontWeight: 600,
                    border: "1px solid var(--border-soft)", background: "rgba(0,0,0,0.3)", color: "var(--text-main)",
                    outline: "none", fontFamily: "inherit",
                  }}>
                    <option value="all">All grows ({logs.length} logs)</option>
                    {grows.map(g => (
                      <option key={g.id} value={g.id}>
                        {CROPS[g.crop]?.name || g.crop} · {g.name || g.system} ({logs.filter(l => l.growId === g.id).length})
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Interpretation — what the trends say */}
            {trendSummary.length > 0 && (
              <div style={{ ...card, border: "1px solid rgba(181,224,38,0.25)" }}>
                <div style={secLbl}>What the trends say</div>
                {trendSummary.map((s, i) => (
                  <div key={i} style={{ display: "flex", gap: 9, alignItems: "flex-start", marginBottom: i < trendSummary.length - 1 ? 8 : 0 }}>
                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: s.tone, marginTop: 4, flexShrink: 0 }} />
                    <div style={{ fontSize: 12, color: "var(--text-soft2)", lineHeight: 1.5 }}>{s.text}</div>
                  </div>
                ))}
              </div>
            )}

            {/* EC chart */}
            <div style={card}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                <div style={secLbl}>EC / Nutrient Strength</div>
                <span style={{ fontSize: 10, color: "var(--text-soft)" }}>{trendSeries.ec.length} readings</span>
              </div>
              <TrendChart
                data={trendSeries.ec}
                color="#ffd54f"
                label="EC"
                unit=""
                safeMin={stage.ecMin}
                safeMax={stage.ecMax}
              />
              <div style={{ marginTop: 8, fontSize: 10, color: "var(--text-soft)" }}>
                Shaded band shows target range for current crop &amp; stage ({stage.ecMin}–{stage.ecMax})
              </div>
            </div>

            {/* pH chart */}
            <div style={card}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                <div style={secLbl}>pH</div>
                <span style={{ fontSize: 10, color: "var(--text-soft)" }}>{trendSeries.ph.length} readings</span>
              </div>
              <TrendChart
                data={trendSeries.ph}
                color="#64b5f6"
                label="pH"
                unit=""
                minY={4} maxY={8}
                safeMin={stage.phMin}
                safeMax={stage.phMax}
              />
              <div style={{ marginTop: 8, fontSize: 10, color: "var(--text-soft)" }}>
                Shaded band shows safe range for current crop &amp; stage ({stage.phMin}–{stage.phMax})
              </div>
            </div>

            {/* Temp chart */}
            {trendSeries.temp.length > 0 && (
              <div style={card}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                  <div style={secLbl}>Water Temperature</div>
                  <span style={{ fontSize: 10, color: "var(--text-soft)" }}>{trendSeries.temp.length} readings</span>
                </div>
                <TrendChart data={trendSeries.temp} color="#ff9a50" label="temperature" unit="°C" />
              </div>
            )}
          </>
        )}

        {/* EVENTS VIEW */}
        {historyView === "events" && (
        <div style={card}>
          <div style={secLbl}>All Events</div>
          {logs.length === 0 ? (
            <div style={{ textAlign: "center", padding: "24px 20px" }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>📋</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: "var(--active)", marginBottom: 4 }}>No readings logged yet</div>
              <div style={{ fontSize: 12, color: "var(--text-muted)", maxWidth: 320, margin: "0 auto 16px", lineHeight: 1.5 }}>
                Log an EC, pH, or temperature reading and it&rsquo;ll show up here — and on the trend charts — so you can spot drift before it costs you a crop.
              </div>
              <button
                onClick={() => setTab("log")}
                style={{
                  background: "linear-gradient(180deg, var(--panel-a) 0%, var(--panel-b) 100%)",
                  border: "1px solid rgba(90,185,90,0.45)", borderRadius: 10,
                  padding: "10px 20px", cursor: "pointer", color: "var(--active)",
                  fontSize: 13, fontWeight: 700, fontFamily: "inherit",
                }}>
                Log a reading
              </button>
            </div>
          ) : (
            <>
              {groupedLogs.map(([date, entries]) => (
                  <div key={date}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "var(--active)", padding: "8px 0 4px", borderBottom: "1px solid rgba(255,255,255,0.06)", marginBottom: 4 }}>{date}</div>
                    {entries.map(log => {
                      const et = EVENT_TYPES.find(e => e.key === log.type) || EVENT_TYPES[6];
                      const tm = new Date(log.timestamp).toLocaleTimeString("en-JM", { hour: "2-digit", minute: "2-digit", hour12: true });
                      return (
                        <div key={log.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 0", borderBottom: "1px solid rgba(255,255,255,0.03)" }}>
                          <div style={{ fontSize: 13 }}>{et.icon}</div>
                          <div style={{ fontSize: 10, color: "var(--text-soft)", minWidth: 60 }}>{tm}</div>
                          <div style={{ flex: 1, fontSize: 12, color: "#b4d4b4" }}>
                            <span style={{ color: et.color, fontWeight: 600 }}>{et.label}</span>
                            {log.crop && <span style={{ marginLeft: 4, fontSize: 10, color: "var(--text-soft)" }}>({log.crop})</span>}
                            {log.value && <span style={{ marginLeft: 6, color: "var(--text-bright)", fontWeight: 700 }}>{log.type === "ec_reading" ? `EC ${log.value}` : log.type === "ph_reading" ? `pH ${log.value}` : log.type === "water_temp" ? `${log.value}°C` : log.type === "farm_temp" ? `${log.value}°C` : log.type === "ph_down" ? `${log.value}ml` : log.type === "ph_up" ? `${log.value}gal` : log.type === "water_change" ? `${log.value}gal` : log.type === "harvest" ? `🧺 ${log.value}` : log.value}</span>}
                            {log.amounts && <span style={{ marginLeft: 4, fontSize: 10, color: "var(--text-soft)" }}>({log.amounts.map(a => `${a.label}:${a.grams}g`).join(", ")})</span>}
                            {log.notes && <span style={{ color: "#6d9e6d", marginLeft: 6 }}>— {log.notes}</span>}
                          </div>
                          <button onClick={() => deleteLog(log.id)} aria-label={`Delete ${log.type.replace("_", " ")} log entry`} title="Delete entry" style={{ background: "none", border: "none", color: "#c97070", fontSize: 12, cursor: "pointer", padding: 2 }}><span aria-hidden="true">✕</span></button>
                        </div>
                      );
                    })}
                  </div>
                ))}
              <button onClick={clearLogs} style={{ marginTop: 16, width: "100%", padding: "10px", borderRadius: 8, fontSize: 12, border: "1px solid rgba(239,83,80,0.2)", cursor: "pointer", background: "rgba(239,83,80,0.06)", color: "#ef5350" }}>Clear All Logs</button>
            </>
          )}
        </div>
        )}
      </>)}

      {/* ═══════ SETTINGS TAB ═══════ */}
      {tab === "settings" && (<>
        {/* Account & sync — optional Google sign-in. Placed first because it is
            the only setting that changes where data lives; everything below it
            is local preference. Hidden entirely when the deployment has no
            client id, so the app degrades to device-local with no dead UI. */}
        <div style={{ marginBottom: 14 }}>
          <AccountPanel
            clientId={GOOGLE_CLIENT_ID}
            onToast={(m) => showToast(m)}
            onDataReplaced={() => window.location.reload()}
          />
        </div>

        {/* Location & Climate */}
        <SettingsSection title="Location & Climate" open={settingsOpen.loc} onToggle={() => toggleSettings("loc")}>
          <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 12, lineHeight: 1.5 }}>
            Where is your farm? This drives climate-specific crop advice, currency formatting, and pest expectations.
          </div>

          {/* Location name */}
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 10, color: "var(--text-soft)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 5 }}>Farm Location</div>
            <input
              type="text" value={userLocation.name}
              onChange={e => updateLocation("name", e.target.value)}
              placeholder="e.g. Portmore, Jamaica"
              aria-label="Farm location name"
              style={{ width: "100%", padding: "8px 12px", borderRadius: 8, fontSize: 13, fontWeight: 600,
                border: "1px solid var(--border-soft)", background: "rgba(0,0,0,0.3)", color: "var(--text-main)",
                outline: "none", boxSizing: "border-box", fontFamily: "inherit" }}
            />
            {/* Auto-detect — a helper to SET the farm location (with confirmation) */}
            <button
              onClick={detectLocation}
              disabled={geoStatus === "locating"}
              aria-label="Detect my location to set the farm location"
              style={{ marginTop: 7, display: "inline-flex", alignItems: "center", gap: 6,
                padding: "7px 12px", borderRadius: 8, fontSize: 12, fontWeight: 700,
                border: "1px solid rgba(181,224,38,0.3)", background: "rgba(181,224,38,0.08)",
                color: "var(--active)", cursor: geoStatus === "locating" ? "default" : "pointer",
                opacity: geoStatus === "locating" ? 0.7 : 1, fontFamily: "inherit" }}
            >
              📍 {geoStatus === "locating" ? "Locating…" : "Use my current location"}
            </button>

            {/* Confirmation — never overwrite the farm location without asking */}
            {geoStatus === "confirm" && geoPending && (
              <div style={{ marginTop: 8, padding: "10px 12px", borderRadius: 9, background: "rgba(181,224,38,0.06)", border: "1px solid rgba(181,224,38,0.25)" }}>
                <div style={{ fontSize: 11.5, color: "var(--text-soft2)", lineHeight: 1.5, marginBottom: 8 }}>
                  Set the farm location to <strong style={{ color: "var(--text-strong)" }}>{geoPending.name}</strong>?
                  {userLocation.name && userLocation.name !== geoPending.name && (
                    <> This replaces <strong style={{ color: "var(--text-strong)" }}>{userLocation.name}</strong>. Only do this if you're at the farm.</>
                  )}
                  {/* Never change the currency silently — cost figures are read
                      at a glance and a swapped unit is easy to miss. */}
                  {geoPending.currency && geoPending.currency !== userLocation.currency && (
                    <> Costs will switch to <strong style={{ color: "var(--text-strong)" }}>
                      {(CURRENCIES[geoPending.currency] || {}).name || geoPending.currency}
                    </strong>.</>
                  )}
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    onClick={confirmDetectedLocation}
                    aria-label="Set as farm location"
                    style={{ flex: 1, padding: "8px 10px", borderRadius: 8, fontSize: 12, fontWeight: 800, cursor: "pointer",
                      border: "none", background: "linear-gradient(135deg,#2d6030,#5cb85c)", color: "var(--text-strong)", fontFamily: "inherit" }}
                  >Set as farm location</button>
                  <button
                    onClick={cancelDetectedLocation}
                    aria-label="Cancel"
                    style={{ padding: "8px 12px", borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: "pointer",
                      border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.03)", color: "var(--text-muted)", fontFamily: "inherit" }}
                  >Cancel</button>
                </div>
              </div>
            )}

            <div style={{ fontSize: 10.5, color: "var(--text-soft)", marginTop: 5, lineHeight: 1.5 }}>
              {geoStatus === "denied"
                ? "Location permission denied — type your farm above instead."
                : geoStatus === "unavailable"
                ? "Couldn't get a location fix — type your farm above instead."
                : geoStatus === "done"
                ? "Farm location updated. You can still edit the name above."
                : geoStatus === "confirm"
                ? "The farm location drives all weather and growing advice, so confirm before it changes."
                : "The farm location is what drives weather and advice. Detect only if you're at the farm — otherwise type it above."}
            </div>
          </div>

          {/* Climate type selector */}
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 10, color: "var(--text-soft)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 5 }}>Climate Type</div>
            <select
              value={userLocation.climateType}
              onChange={e => updateLocation("climateType", e.target.value)}
              aria-label="Climate type"
              style={{ width: "100%", padding: "8px 12px", borderRadius: 8, fontSize: 13, fontWeight: 600,
                border: "1px solid var(--border-soft)", background: "rgba(0,0,0,0.3)", color: "var(--text-main)",
                outline: "none", boxSizing: "border-box", fontFamily: "inherit", cursor: "pointer" }}
            >
              {Object.entries(CLIMATE_TYPES).map(([k, c]) => (
                <option key={k} value={k}>{c.label}</option>
              ))}
            </select>
            <div style={{ marginTop: 6, padding: "8px 10px", borderRadius: 7, background: "rgba(0,0,0,0.2)", fontSize: 11, color: "#a9bdaa", lineHeight: 1.5 }}>
              {CLIMATE_TYPES[userLocation.climateType]?.description}
            </div>
          </div>

          {/* Currency selector */}
          <div>
            <div style={{ fontSize: 10, color: "var(--text-soft)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 5 }}>Currency</div>
            <select
              value={userLocation.currency}
              onChange={e => updateLocation("currency", e.target.value)}
              aria-label="Currency"
              style={{ width: "100%", padding: "8px 12px", borderRadius: 8, fontSize: 13, fontWeight: 600,
                border: "1px solid var(--border-soft)", background: "rgba(0,0,0,0.3)", color: "var(--text-main)",
                outline: "none", boxSizing: "border-box", fontFamily: "inherit", cursor: "pointer" }}
            >
              {Object.entries(CURRENCIES).map(([k, c]) => (
                <option key={k} value={k}>{c.symbol} — {c.name} ({c.code})</option>
              ))}
            </select>
            <div style={{ marginTop: 6, fontSize: 11, color: "var(--text-muted)" }}>
              Sample: {formatCurrency(12500, userLocation.currency)} · {formatCurrency(75, userLocation.currency)}
            </div>
          </div>

          {/* Local weather toggle — the only feature that leaves the device */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 14, paddingTop: 14, borderTop: "1px solid var(--border)" }}>
            <div style={{ paddingRight: 12 }}>
              <div style={{ fontSize: 12, color: "var(--text-soft2)", fontWeight: 600 }}>Show local weather &amp; clock</div>
              <div style={{ fontSize: 10.5, color: "var(--text-soft)", marginTop: 1, lineHeight: 1.5 }}>
                Displays the current time and live weather for your farm in the header. When on, your location is sent to Open-Meteo (a free weather service) to fetch the forecast. That — plus a one-time coordinate lookup if you tap “Use my location” above — is the only thing YaadGrow ever sends off your device. The clock works offline; turn this off to keep everything local.
              </div>
            </div>
            <button
              onClick={() => updateLocation("showWeather", !userLocation.showWeather)}
              aria-label="Toggle local weather"
              style={{
                width: 46, height: 26, borderRadius: 13, border: "none", cursor: "pointer", flexShrink: 0,
                background: userLocation.showWeather ? "var(--active)" : "rgba(255,255,255,0.15)",
                position: "relative", transition: "background 0.2s",
              }}>
              <span style={{
                position: "absolute", top: 3, left: userLocation.showWeather ? 23 : 3,
                width: 20, height: 20, borderRadius: "50%", background: "var(--bg-card2)", transition: "left 0.2s",
              }} />
            </button>
          </div>
        </SettingsSection>

        {/* Systems Overview */}
        <SettingsSection title="Systems Overview" open={settingsOpen.sys} onToggle={() => toggleSettings("sys")} cardStyle={{ ...card, background: "linear-gradient(180deg, rgba(12,48,24,0.97), rgba(8,32,16,0.99))", border: "1px solid rgba(120,255,74,0.28)" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 10, marginTop: 4 }}>
            {/* NFT summary */}
            <div style={{ padding: "12px 14px", borderRadius: 10, background: "rgba(25,118,210,0.1)", border: "1px solid rgba(52,201,255,0.25)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <span style={{ fontSize: 20 }}>🟦</span>
                <div style={{ fontSize: 13, fontWeight: 800, color: "#90caf9" }}>NFT System</div>
              </div>
              <div style={{ fontSize: 11, color: "var(--text-muted)", lineHeight: 1.7 }}>
                <div><strong style={{ color: "var(--text-strong)" }}>{sysSettings.nftChannels}</strong> channels</div>
                <div><strong style={{ color: "var(--text-strong)" }}>{sysSettings.nftChannels * sysSettings.nftPlantsPerChannel}</strong> total plant sites</div>
                <div><strong style={{ color: "var(--text-strong)" }}>{sysSettings.nftReservoirGal} gal</strong> shared reservoir</div>
              </div>
            </div>
            {/* Tower summary */}
            <div style={{ padding: "12px 14px", borderRadius: 10, background: "rgba(123,31,162,0.1)", border: "1px solid rgba(198,92,255,0.25)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <span style={{ fontSize: 20 }}>🗼</span>
                <div style={{ fontSize: 13, fontWeight: 800, color: "#ce93d8" }}>Tower System</div>
              </div>
              <div style={{ fontSize: 11, color: "var(--text-muted)", lineHeight: 1.7 }}>
                <div><strong style={{ color: "var(--text-strong)" }}>{sysSettings.towerCount}</strong> towers</div>
                <div><strong style={{ color: "var(--text-strong)" }}>{sysSettings.towerCount * sysSettings.towerBucketGal} gal</strong> total bucket volume</div>
                <div><strong style={{ color: "var(--text-strong)" }}>{sysSettings.towerMixTankGal} gal</strong> mixing tank</div>
              </div>
            </div>
          </div>
          {/* Nursery summary — only if enabled */}
          {sysSettings.hasNursery !== false && (
            <div style={{ marginTop: 10, padding: "12px 14px", borderRadius: 10, background: "rgba(67,160,71,0.08)", border: "1px solid rgba(165,214,167,0.25)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <span style={{ fontSize: 20 }}>🌱</span>
                <div style={{ fontSize: 13, fontWeight: 800, color: "#a5d6a7" }}>Nursery System</div>
              </div>
              <div style={{ fontSize: 11, color: "var(--text-muted)", display: "flex", gap: 14, flexWrap: "wrap" }}>
                <span><strong style={{ color: "var(--text-strong)" }}>{sysSettings.nurseryGal ?? SYSTEM_DEFAULTS.nurseryGal} gal</strong> reservoir</span>
                <span><strong style={{ color: "var(--text-strong)" }}>{sysSettings.nurserySlots ?? SYSTEM_DEFAULTS.nurserySlots}</strong> cube slots</span>
                <span><strong style={{ color: "var(--text-strong)" }}>{nurseryWattage}W</strong> pump</span>
                <span><strong style={{ color: "var(--text-strong)" }}>{nurseryGrows.length}</strong> grows in nursery</span>
              </div>
            </div>
          )}
          <div style={{ marginTop: 10, padding: "8px 12px", borderRadius: 8, background: "rgba(0,0,0,0.2)", fontSize: 11, color: "var(--text-muted)", display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 6 }}>
            <span>⚡ Combined pump draw: <strong style={{ color: ((hasNft ? nftWattage : 0) + (hasTower ? towerWattage : 0) + (sysSettings.hasNursery !== false ? nurseryWattage : 0)) > POWER_DEFAULTS.smartPlugMaxW ? "#ff6a6a" : "var(--active)" }}>{((hasNft ? nftWattage : 0) + (hasTower ? towerWattage : 0) + (sysSettings.hasNursery !== false ? nurseryWattage : 0)).toFixed(0)}W</strong> / {POWER_DEFAULTS.smartPlugMaxW}W limit</span>
            <span>🌿 Active grows: <strong style={{ color: "var(--text-strong)" }}>{activeGrows.length + nurseryGrows.length}</strong></span>
          </div>
        </SettingsSection>

        {/* Meter & Readings */}
        <SettingsSection title="Meter & Readings" open={settingsOpen.meter} onToggle={() => toggleSettings("meter")}>
          <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 14, lineHeight: 1.5 }}>
            Match these to your actual meter so ppm and temperature-corrected EC read correctly.
          </div>

          {/* ppm scale */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, color: "var(--text-soft2)", fontWeight: 600, marginBottom: 2 }}>ppm / TDS conversion scale</div>
            <div style={{ fontSize: 10.5, color: "var(--text-soft)", marginBottom: 8, lineHeight: 1.5 }}>
              How your meter turns EC into ppm. If unsure, check the meter manual — using the wrong scale can be off by up to 40%.
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              {[
                { v: 500, label: "500", note: "US / Truncheon" },
                { v: 640, label: "640", note: "EU / Eutech" },
                { v: 700, label: "700", note: "Hanna / AU" },
              ].map(o => {
                const active = (sysSettings.ppmScale || 700) === o.v;
                return (
                  <button key={o.v} onClick={() => updateSysSetting("ppmScale", o.v)} style={{
                    flex: 1, padding: "9px 6px", borderRadius: 9, cursor: "pointer",
                    border: active ? "2px solid var(--active)" : "1px solid var(--border-soft)",
                    background: active ? "rgba(181,224,38,0.12)" : "rgba(255,255,255,0.03)",
                  }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: active ? "var(--active)" : "var(--text-muted)" }}>×{o.label}</div>
                    <div style={{ fontSize: 9.5, color: "var(--text-soft)", marginTop: 1 }}>{o.note}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* EC temp compensation */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
            <div>
              <div style={{ fontSize: 12, color: "var(--text-soft2)", fontWeight: 600 }}>EC temperature compensation</div>
              <div style={{ fontSize: 10.5, color: "var(--text-soft)", marginTop: 1, maxWidth: 260, lineHeight: 1.5 }}>
                Corrects EC readings to a 25°C-equivalent using your latest water-temp log. Warm reservoirs read high — this reveals true strength.
              </div>
            </div>
            <button
              onClick={() => updateSysSetting("ecTempComp", sysSettings.ecTempComp === false)}
              style={{
                width: 46, height: 26, borderRadius: 13, border: "none", cursor: "pointer", flexShrink: 0,
                background: sysSettings.ecTempComp !== false ? "#4fc3f7" : "rgba(255,255,255,0.15)",
                position: "relative", transition: "background 0.2s",
              }}>
              <span style={{
                position: "absolute", top: 3, left: sysSettings.ecTempComp !== false ? 23 : 3,
                width: 20, height: 20, borderRadius: "50%", background: "#fff", transition: "left 0.2s",
              }} />
            </button>
          </div>
          {sysSettings.ecTempComp !== false && (
            <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 10, color: "var(--text-soft)", marginBottom: 3 }}>Reference temp (°C)</div>
                <input type="number" step="1" min={10} max={30} value={sysSettings.ecRefTemp ?? 25}
                  onChange={e => updateSysSetting("ecRefTemp", Math.max(10, Math.min(30, parseInt(e.target.value) || 25)))}
                  style={{ width: "100%", padding: "8px 10px", borderRadius: 8, fontSize: 14, fontWeight: 700, border: "1px solid var(--border-soft)", background: "rgba(0,0,0,0.3)", color: "#4fc3f7", outline: "none", boxSizing: "border-box" }} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 10, color: "var(--text-soft)", marginBottom: 3 }}>Coefficient (%/°C)</div>
                <input type="number" step="0.1" min={1} max={3} value={sysSettings.ecTempCoeff ?? 2}
                  onChange={e => updateSysSetting("ecTempCoeff", Math.max(1, Math.min(3, parseFloat(e.target.value) || 2)))}
                  style={{ width: "100%", padding: "8px 10px", borderRadius: 8, fontSize: 14, fontWeight: 700, border: "1px solid var(--border-soft)", background: "rgba(0,0,0,0.3)", color: "#4fc3f7", outline: "none", boxSizing: "border-box" }} />
              </div>
            </div>
          )}
        </SettingsSection>

        {/* System Layout */}
        <SettingsSection title="System Layout" open={settingsOpen.layout} onToggle={() => toggleSettings("layout")}>
          <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 12, lineHeight: 1.5 }}>
            These drive the gallon quick-select buttons, pump schedule notes, and power cost calculations across the app.
          </div>

          {/* Reservoir refresh interval */}
          <div style={{ marginBottom: 16, paddingBottom: 14, borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
            <div style={{ fontSize: 11, color: "#26c6da", textTransform: "uppercase", letterSpacing: 1.5, fontWeight: 700, marginBottom: 8 }}>💧 Reservoir Refresh</div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
              <div style={{ fontSize: 11.5, color: "var(--text-muted)", lineHeight: 1.5, flex: 1 }}>
                Days between full solution changes. A reminder appears when you're due. Most growers refresh every 7–14 days.
              </div>
              <input type="number" min={1} max={60} value={sysSettings.reservoirChangeDays ?? 10}
                onChange={e => updateSysSetting("reservoirChangeDays", Math.max(1, Math.min(60, parseInt(e.target.value) || 10)))}
                style={{ width: 70, padding: "9px 10px", borderRadius: 9, fontSize: 16, fontWeight: 700, textAlign: "center", border: "1px solid var(--border-soft)", background: "rgba(0,0,0,0.3)", color: "#26c6da", outline: "none", boxSizing: "border-box" }} />
            </div>
          </div>

          {/* NFT layout */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
            <div style={{ fontSize: 11, color: "#90caf9", textTransform: "uppercase", letterSpacing: 1.5, fontWeight: 700 }}>🟦 NFT System</div>
            <button
              onClick={() => updateSysSetting("hasNft", !hasNft)}
              aria-label="Toggle NFT system"
              style={{
                padding: "4px 10px", borderRadius: 14, fontSize: 10, fontWeight: 700, cursor: "pointer",
                border: `1px solid ${hasNft ? "rgba(144,202,249,0.4)" : "rgba(255,255,255,0.15)"}`,
                background: hasNft ? "rgba(144,202,249,0.15)" : "rgba(255,255,255,0.03)",
                color: hasNft ? "#90caf9" : "var(--text-soft)", fontFamily: "inherit",
              }}
            >{hasNft ? "✓ Enabled" : "○ Disabled"}</button>
          </div>
          {hasNft ? (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginBottom: 14 }}>
            {[
              { key: "nftChannels", label: "Channels", min: 1, max: 50, step: 1 },
              { key: "nftPlantsPerChannel", label: "Plants / channel", min: 1, max: 2000, step: 1 },
              { key: "nftReservoirGal", label: "Reservoir (gal)", min: 1, max: 5000, step: 1 },
            ].map(f => (
              <div key={f.key}>
                <div style={{ fontSize: 10, color: "var(--text-soft)", marginBottom: 3 }}>{f.label}</div>
                <input
                  type="number" min={f.min} max={f.max} step={f.step}
                  value={sysSettings[f.key]}
                  onChange={e => updateSysSetting(f.key, Math.max(f.min, Math.min(f.max, parseInt(e.target.value) || f.min)))}
                  aria-label={f.label}
                  style={{ width: "100%", padding: "8px 10px", borderRadius: 8, fontSize: 15, fontWeight: 700, border: "1px solid var(--border-soft)", background: "rgba(0,0,0,0.3)", color: "#90caf9", outline: "none", boxSizing: "border-box", fontVariantNumeric: "tabular-nums" }}
                />
              </div>
            ))}
          </div>
          ) : (
            <div style={{ fontSize: 11, color: "var(--text-soft)", lineHeight: 1.5, padding: "8px 10px", borderRadius: 8, background: "rgba(0,0,0,0.2)", marginBottom: 14 }}>
              NFT disabled — hidden from pump schedules, power totals, and the stat strip. Enable if you run an NFT system.
            </div>
          )}

          {/* Tower layout */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
            <div style={{ fontSize: 11, color: "#ce93d8", textTransform: "uppercase", letterSpacing: 1.5, fontWeight: 700 }}>🗼 Tower System</div>
            <button
              onClick={() => updateSysSetting("hasTower", !hasTower)}
              aria-label="Toggle tower system"
              style={{
                padding: "4px 10px", borderRadius: 14, fontSize: 10, fontWeight: 700, cursor: "pointer",
                border: `1px solid ${hasTower ? "rgba(206,147,216,0.4)" : "rgba(255,255,255,0.15)"}`,
                background: hasTower ? "rgba(206,147,216,0.15)" : "rgba(255,255,255,0.03)",
                color: hasTower ? "#ce93d8" : "var(--text-soft)", fontFamily: "inherit",
              }}
            >{hasTower ? "✓ Enabled" : "○ Disabled"}</button>
          </div>
          {hasTower ? (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginBottom: 14 }}>
            {[
              { key: "towerCount", label: "Towers", min: 1, max: 50, step: 1 },
              { key: "towerBucketGal", label: "Bucket (gal)", min: 1, max: 200, step: 1 },
              { key: "towerMixTankGal", label: "Mix tank (gal)", min: 1, max: 1000, step: 1 },
            ].map(f => (
              <div key={f.key}>
                <div style={{ fontSize: 10, color: "var(--text-soft)", marginBottom: 3 }}>{f.label}</div>
                <input
                  type="number" min={f.min} max={f.max} step={f.step}
                  value={sysSettings[f.key]}
                  onChange={e => updateSysSetting(f.key, Math.max(f.min, Math.min(f.max, parseInt(e.target.value) || f.min)))}
                  aria-label={f.label}
                  style={{ width: "100%", padding: "8px 10px", borderRadius: 8, fontSize: 15, fontWeight: 700, border: "1px solid var(--border-soft)", background: "rgba(0,0,0,0.3)", color: "#ce93d8", outline: "none", boxSizing: "border-box", fontVariantNumeric: "tabular-nums" }}
                />
              </div>
            ))}
          </div>
          ) : (
            <div style={{ fontSize: 11, color: "var(--text-soft)", lineHeight: 1.5, padding: "8px 10px", borderRadius: 8, background: "rgba(0,0,0,0.2)", marginBottom: 14 }}>
              Tower disabled — hidden from pump schedules, power totals, and the stat strip. Enable if you run a tower system.
            </div>
          )}

          {/* Nursery layout */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
            <div style={{ fontSize: 11, color: "#a5d6a7", textTransform: "uppercase", letterSpacing: 1.5, fontWeight: 700 }}>🌱 Nursery System</div>
            <button
              onClick={() => updateSysSetting("hasNursery", !sysSettings.hasNursery)}
              style={{
                padding: "4px 10px", borderRadius: 14, fontSize: 10, fontWeight: 700, cursor: "pointer",
                border: `1px solid ${sysSettings.hasNursery !== false ? "rgba(67,160,71,0.4)" : "rgba(255,255,255,0.15)"}`,
                background: sysSettings.hasNursery !== false ? "rgba(67,160,71,0.15)" : "rgba(255,255,255,0.03)",
                color: sysSettings.hasNursery !== false ? "#a5d6a7" : "var(--text-soft)",
                fontFamily: "inherit",
              }}
            >{sysSettings.hasNursery !== false ? "✓ Enabled" : "○ Disabled"}</button>
          </div>
          {sysSettings.hasNursery !== false ? (
            <>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 8 }}>
              {[
                { key: "nurseryGal", label: "Reservoir (gal)", min: 1, max: 100, step: 1 },
                { key: "nurserySlots", label: "Cube slots (capacity)", min: 1, max: 1000, step: 1 },
              ].map(f => (
                <div key={f.key}>
                  <div style={{ fontSize: 10, color: "var(--text-soft)", marginBottom: 3 }}>{f.label}</div>
                  <input
                    type="number" min={f.min} max={f.max} step={f.step}
                    value={sysSettings[f.key] ?? SYSTEM_DEFAULTS[f.key]}
                    onChange={e => updateSysSetting(f.key, Math.max(f.min, Math.min(f.max, parseInt(e.target.value) || f.min)))}
                    aria-label={f.label}
                    style={{ width: "100%", padding: "8px 10px", borderRadius: 8, fontSize: 15, fontWeight: 700, border: "1px solid var(--border-soft)", background: "rgba(0,0,0,0.3)", color: "#a5d6a7", outline: "none", boxSizing: "border-box", fontVariantNumeric: "tabular-nums" }}
                  />
                </div>
              ))}
            </div>
            <div style={{ fontSize: 10.5, color: "#7fa87f", marginTop: 6, lineHeight: 1.45 }}>
              <strong style={{ color: "#a5d6a7" }}>Cube slots</strong> is your seedling capacity — how many starter-cube positions the nursery holds (a count, not a cube size).
            </div>
            </>
          ) : (
            <div style={{ fontSize: 11, color: "var(--text-soft)", lineHeight: 1.5, padding: "8px 10px", borderRadius: 8, background: "rgba(0,0,0,0.2)" }}>
              Nursery disabled — new grows will go direct to NFT/Tower. Enable if you have a separate nursery area for seedlings before transplant.
            </div>
          )}
        </SettingsSection>

        {/* Custom Pump Schedules */}
        <SettingsSection title="Pump Schedules" open={settingsOpen.pumpsched} onToggle={() => toggleSettings("pumpsched")}>
          <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 12, lineHeight: 1.5 }}>
            Fine-tune the ON cycles for any system and season. Your edits replace the built-in program for that system + season only — everything else stays on the default. Saved on this device.
          </div>
          {(() => {
            const system = pumpEditSystem, season = pumpEditSeason;
            const custom = sysSettings.customPumpSchedules?.[season]?.[system];
            const cycles = custom ?? DEFAULT_PUMP_SCHEDULES[season][system];
            const isCustom = !!custom;
            const runtime = cycles.reduce((s, c) => s + c.on, 0);
            const meta = PUMP_META[system];
            const pad = (n) => String(n).padStart(2, "0");
            const sorted = cycles.map((c, i) => ({ c, i })).sort((a, b) => (a.c.hour * 60 + (a.c.startMin || 0)) - (b.c.hour * 60 + (b.c.startMin || 0)));
            const setCycles = (next) => setCustomCycles(season, system, next);
            const editCycle = (idx, patch) => setCycles(cycles.map((c, j) => j === idx ? { ...c, ...patch } : c));
            const removeCycle = (idx) => setCycles(cycles.filter((_, j) => j !== idx));
            const addCycle = () => setCycles([...cycles, { hour: 12, startMin: 0, on: system === "nursery" ? 5 : system === "tower" ? 15 : 30 }]);
            return (
              <>
                <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
                  {[{ k: "nft", l: "NFT" }, { k: "tower", l: "Tower" }, { k: "nursery", l: "Nursery" }].map(o => (
                    <button key={o.k} onClick={() => setPumpEditSystem(o.k)} style={{ flex: 1, padding: "7px 6px", borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                      border: system === o.k ? `2px solid ${meta.color}` : "1px solid var(--border-soft)",
                      background: system === o.k ? `${meta.color}22` : "var(--bg-card2)",
                      color: system === o.k ? meta.color : "var(--text-muted)" }}>{o.l}</button>
                  ))}
                </div>
                <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
                  {Object.keys(PUMP_CONFIG.seasons).map(k => (
                    <button key={k} onClick={() => setPumpEditSeason(k)} style={{ flex: 1, padding: "7px 6px", borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                      border: season === k ? "2px solid var(--active)" : "1px solid var(--border-soft)",
                      background: season === k ? "var(--active-soft)" : "var(--bg-card2)",
                      color: season === k ? "var(--active)" : "var(--text-muted)" }}>{k[0].toUpperCase() + k.slice(1)}</button>
                  ))}
                </div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                  <div style={{ fontSize: 11, color: "var(--text-soft)" }}>
                    {cycles.length} cycles · {Math.floor(runtime / 60)}h {runtime % 60}m/day{isCustom && <strong style={{ color: meta.color }}> · custom</strong>}
                  </div>
                  {isCustom && (
                    <button onClick={() => setCustomCycles(season, system, null)} style={{ fontSize: 11, color: "var(--active)", background: "none", border: "none", cursor: "pointer", textDecoration: "underline", fontFamily: "inherit" }}>Reset to default</button>
                  )}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 280, overflowY: "auto" }}>
                  {sorted.map(({ c, i }) => {
                    const dMax = 60 - (c.startMin || 0);
                    return (
                      <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 8px", borderRadius: 8, background: "var(--bg-inset)", border: "1px solid var(--border)" }}>
                        <input type="time" value={`${pad(c.hour)}:${pad(c.startMin || 0)}`} aria-label="Cycle start time"
                          onChange={e => { const [h, m] = e.target.value.split(":").map(Number); editCycle(i, { hour: h || 0, startMin: m || 0, on: Math.min(c.on, 60 - (m || 0)) }); }}
                          style={{ padding: "5px 6px", borderRadius: 6, fontSize: 12, border: "1px solid var(--border-soft)", background: "var(--bg-card)", color: "var(--text-main)", fontFamily: "inherit" }} />
                        <input type="number" min={1} max={dMax} value={c.on} aria-label="Cycle duration (minutes)"
                          onChange={e => editCycle(i, { on: Math.max(1, Math.min(dMax, parseInt(e.target.value) || 1)) })}
                          style={{ width: 54, padding: "5px 6px", borderRadius: 6, fontSize: 12, fontWeight: 700, textAlign: "center", border: "1px solid var(--border-soft)", background: "var(--bg-card)", color: "var(--text-main)", fontFamily: "inherit" }} />
                        <span style={{ fontSize: 10, color: "var(--text-soft)" }}>min</span>
                        <button onClick={() => removeCycle(i)} aria-label="Remove cycle" style={{ marginLeft: "auto", background: "transparent", border: "none", color: "#c97070", fontSize: 15, cursor: "pointer", padding: 2, fontFamily: "inherit" }}>✕</button>
                      </div>
                    );
                  })}
                </div>
                <button onClick={addCycle} style={{ marginTop: 8, width: "100%", padding: "8px", borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: "pointer", border: "1px dashed var(--active)", background: "transparent", color: "var(--active)", fontFamily: "inherit" }}>+ Add cycle</button>
                <div style={{ fontSize: 10, color: "var(--text-soft)", marginTop: 8, lineHeight: 1.45 }}>
                  Times are 24-hour. A cycle can't cross the top of the hour (duration caps at 60 − start minute); use two cycles for a longer run. Overnight times (e.g. 22:00, 02:00) are fine — they count toward the daily runtime but don't show on the 6 AM–8 PM timeline.
                </div>
              </>
            );
          })()}
        </SettingsSection>

        {/* Power Settings */}
        <SettingsSection title="Power & Electricity" open={settingsOpen.power} onToggle={() => toggleSettings("power")}>
          <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 12, lineHeight: 1.5 }}>
            Used for power cost projections and the smart-plug overload warnings in the Pumps tab.
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
            <div>
              <div style={{ fontSize: 10, color: "var(--text-soft)", marginBottom: 3 }}>{userLocation.currency} / kWh</div>
              <input type="number" step="0.5" min={0} max={500} value={electricityRate}
                onChange={e => setElectricityRate(Math.max(0, parseFloat(e.target.value) || 0))}
                aria-label="Electricity rate per kWh"
                style={{ width: "100%", padding: "8px 10px", borderRadius: 8, fontSize: 15, fontWeight: 700, border: "1px solid var(--border-soft)", background: "rgba(0,0,0,0.3)", color: "var(--active)", outline: "none", boxSizing: "border-box", fontVariantNumeric: "tabular-nums" }} />
            </div>
            <div>
              <div style={{ fontSize: 10, color: "var(--text-soft)", marginBottom: 3 }}>NFT pumps (W)</div>
              <input type="number" step="0.5" min={0} max={10000} value={nftWattage}
                onChange={e => setNftWattage(Math.max(0, parseFloat(e.target.value) || 0))}
                aria-label="NFT total wattage"
                style={{ width: "100%", padding: "8px 10px", borderRadius: 8, fontSize: 15, fontWeight: 700, border: "1px solid var(--border-soft)", background: "rgba(0,0,0,0.3)", color: "#90caf9", outline: "none", boxSizing: "border-box", fontVariantNumeric: "tabular-nums" }} />
            </div>
            <div>
              <div style={{ fontSize: 10, color: "var(--text-soft)", marginBottom: 3 }}>Tower pumps (W)</div>
              <input type="number" step="5" min={0} max={10000} value={towerWattage}
                onChange={e => setTowerWattage(Math.max(0, parseFloat(e.target.value) || 0))}
                aria-label="Tower total wattage"
                style={{ width: "100%", padding: "8px 10px", borderRadius: 8, fontSize: 15, fontWeight: 700, border: "1px solid var(--border-soft)", background: "rgba(0,0,0,0.3)", color: "#ce93d8", outline: "none", boxSizing: "border-box", fontVariantNumeric: "tabular-nums" }} />
            </div>
          </div>
          {sysSettings.hasNursery !== false && (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 10, color: "var(--text-soft)", marginBottom: 3 }}>Nursery pump (W)</div>
              <input type="number" step="1" min={0} max={1000} value={nurseryWattage}
                onChange={e => setNurseryWattage(Math.max(0, parseFloat(e.target.value) || 0))}
                aria-label="Nursery pump wattage"
                style={{ width: "100%", padding: "8px 10px", borderRadius: 8, fontSize: 15, fontWeight: 700, border: "1px solid var(--border-soft)", background: "rgba(0,0,0,0.3)", color: "#a5d6a7", outline: "none", boxSizing: "border-box", fontVariantNumeric: "tabular-nums" }} />
            </div>
          )}
          <div style={{ marginTop: 8, fontSize: 10, color: "var(--text-soft)", lineHeight: 1.5 }}>
            Defaults: NFT measured at 658.5W (2× "1HP" pumps at 106.65V), towers spec'd at 425W (5 × 85W){sysSettings.hasNursery !== false ? ", nursery 25W small submersible" : ""}. Adjust electricity rate to match your local utility.
          </div>
        </SettingsSection>

        {/* Display Preferences */}
        <SettingsSection title="Display Preferences" open={settingsOpen.display} onToggle={() => toggleSettings("display")}>
          <div style={{ fontSize: 11, color: "var(--text-soft)", marginBottom: 6 }}>Appearance</div>
          <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
            {[{ key: "dark", label: "Dark", icon: "🌙" }, { key: "light", label: "Light", icon: "☀️" }].map(o => {
              const sel = (sysSettings.theme || "dark") === o.key;
              return (
                <button key={o.key} onClick={() => updateSysSetting("theme", o.key)} aria-label={`${o.label} theme`} aria-pressed={sel} style={{
                  flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
                  padding: "10px 8px", borderRadius: 10, cursor: "pointer", fontFamily: "inherit", fontSize: 13, fontWeight: 700,
                  border: sel ? "2px solid var(--active)" : "1px solid var(--border-soft)",
                  background: sel ? "var(--active-soft)" : "var(--bg-card2)",
                  color: sel ? "var(--active)" : "var(--text-muted)",
                }}>
                  <span>{o.icon}</span>{o.label}
                </button>
              );
            })}
          </div>
          <div style={{ fontSize: 11, color: "var(--text-soft)", marginBottom: 6 }}>Tab to open on app load</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6 }}>
            {[
              { key: "calc", label: "Calculator", icon: "🧮" },
              { key: "grows", label: "Grows", icon: "🌱" },
              { key: "pumps", label: "Pumps", icon: "💧" },
              { key: "log", label: "Log", icon: "📋" },
              { key: "history", label: "History", icon: "🕐" },
              { key: "settings", label: "Settings", icon: "⚙️" },
            ].map(t => {
              const sel = sysSettings.defaultTab === t.key;
              return (
                <button key={t.key} onClick={() => updateSysSetting("defaultTab", t.key)} style={{
                  display: "flex", alignItems: "center", gap: 6, padding: "8px 10px", borderRadius: 8, cursor: "pointer",
                  border: sel ? "2px solid var(--active)" : "2px solid rgba(255,255,255,0.08)",
                  background: sel ? "rgba(181,224,38,0.1)" : "rgba(255,255,255,0.02)",
                  color: sel ? "var(--text-strong)" : "var(--text-muted)", fontFamily: "inherit", fontSize: 11, fontWeight: 600,
                }}>
                  <span style={{ fontSize: 13 }}>{t.icon}</span>{t.label}
                </button>
              );
            })}
          </div>
        </SettingsSection>

        {/* Data Management */}
        <SettingsSection title="Data & Backup" open={settingsOpen.data} onToggle={() => toggleSettings("data")}>
          <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 12, lineHeight: 1.5 }}>
            Export a full backup of your logs, grows, and settings as a JSON file. Import it later to restore, or move your data to another device.
          </div>
          <div className="yg-backup-btns" style={{ display: "flex", gap: 8, marginBottom: 10 }}>
            <button onClick={exportData} style={{
              flex: 1, padding: "12px", borderRadius: 10, fontSize: 13, fontWeight: 700, border: "none", cursor: "pointer",
              background: "linear-gradient(135deg,#2d6030,#5cb85c)", color: "var(--text-strong)", fontFamily: "inherit",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            }}>
              <Icon name="download" size={16} /> Export Backup (JSON)
            </button>
            <label style={{
              flex: 1, padding: "12px", borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: "pointer",
              background: "rgba(52,201,255,0.12)", border: "1px solid rgba(52,201,255,0.3)", color: "#34c9ff",
              textAlign: "center", display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            }}>
              <Icon name="upload" size={16} /> Import Backup
              <input type="file" accept="application/json,.json" onChange={importData} style={{ display: "none" }} />
            </label>
          </div>
          <div style={{ display: "flex", gap: 14, padding: "8px 12px", borderRadius: 8, background: "rgba(0,0,0,0.2)", fontSize: 11, color: "var(--text-muted)", marginBottom: 12 }}>
            <span style={{ display: "flex", alignItems: "center", gap: 5 }}><Icon name="clipboard" size={13} /> <strong style={{ color: "var(--text-strong)" }}>{logs.length}</strong> log entries</span>
            <span style={{ display: "flex", alignItems: "center", gap: 5 }}><Icon name="sprout" size={13} /> <strong style={{ color: "var(--text-strong)" }}>{grows.length}</strong> grows</span>
            <span style={{ display: "flex", alignItems: "center", gap: 5 }}><strong style={{ color: "var(--text-strong)" }}>{completedGrows.length}</strong> harvested</span>
          </div>
          <button onClick={() => {
            if (confirm("Reset ALL data — logs, grows, settings — to defaults? This cannot be undone. Export a backup first if you want to keep anything.")) {
              saveLogs([]);
              saveGrows([]);
              setActiveGrowId(null);
              setSysSettings(SYSTEM_DEFAULTS);
              setUserLocation(LOCATION_DEFAULTS);
              setNftWattage(POWER_DEFAULTS.nftWattage);
              setTowerWattage(POWER_DEFAULTS.towerWattage);
              setNurseryWattage(POWER_DEFAULTS.nurseryWattage);
              setElectricityRate(POWER_DEFAULTS.electricityRate);
              setActualNftKwh(""); setActualTowerKwh("");
              setStorageError(null);
            }
          }} style={{
            width: "100%", padding: "10px", borderRadius: 8, fontSize: 12, fontWeight: 600,
            border: "1px solid rgba(239,83,80,0.3)", cursor: "pointer",
            background: "rgba(239,83,80,0.08)", color: "#ef5350", fontFamily: "inherit",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
          }}>
            <Icon name="warning" size={15} /> Reset All Data to Defaults
          </button>
        </SettingsSection>

        {/* Safety & Privacy */}
        <SettingsSection title="Safety & Privacy" open={settingsOpen.safety} onToggle={() => toggleSettings("safety")}>

          {/* Chemical safety */}
          <div style={{ padding: "10px 12px", borderRadius: 9, background: "rgba(239,83,80,0.06)", border: "1px solid rgba(239,83,80,0.2)", marginBottom: 10 }}>
            <div style={{ fontSize: 11.5, color: "#ef9a9a", fontWeight: 700, marginBottom: 4 }}>⚠ Handling acids &amp; nutrients</div>
            <div style={{ fontSize: 11, color: "#c8a0a0", lineHeight: 1.55 }}>
              pH-down dosing figures are starting estimates, not prescriptions. Concentrated acids (including 35% H₂SO₄) can cause serious burns. Always add acid to water — never water to acid — wear eye protection and gloves, work in a ventilated area, and keep chemicals away from children and animals. Verify every dose against your own meter. You are responsible for safe handling.
            </div>
          </div>

          {/* Electrical safety */}
          <div style={{ padding: "10px 12px", borderRadius: 9, background: "rgba(240,168,32,0.06)", border: "1px solid rgba(240,168,32,0.2)", marginBottom: 10 }}>
            <div style={{ fontSize: 11.5, color: "#f0c060", fontWeight: 700, marginBottom: 4 }}>⚡ Electrical guidance</div>
            <div style={{ fontSize: 11, color: "#c8b880", lineHeight: 1.55 }}>
              Wattage, breaker, and surge figures are planning aids to help you avoid obvious overloads — not a substitute for a licensed electrician. Circuit ratings, wiring condition, and local code vary. Mixing water and mains power carries real risk; have a qualified electrician confirm your setup and use RCD/GFCI protection on any circuit near water.
            </div>
          </div>

          {/* Privacy */}
          <div style={{ padding: "10px 12px", borderRadius: 9, background: "rgba(79,195,247,0.06)", border: "1px solid rgba(79,195,247,0.2)" }}>
            <div style={{ fontSize: 11.5, color: "#8ec5e8", fontWeight: 700, marginBottom: 4 }}>🔒 Your data stays on this device</div>
            <div style={{ fontSize: 11, color: "#9ab8c8", lineHeight: 1.55 }}>
              Everything you log — readings, grows, harvests, and prices — is stored locally on this device only. It isn't uploaded to any server or shared. Clearing your browser data or the app's storage erases it, so use Export Backup regularly. A backup file contains your business data; keep it somewhere safe. Two opt-in exceptions: if you enable <strong style={{ color: "#8ec5e8" }}>Show local weather</strong> (Settings → Location &amp; Climate), your location is sent to Open-Meteo to fetch the forecast; and if you tap <strong style={{ color: "#8ec5e8" }}>Use my location</strong>, your coordinates are sent once to a geocoding service to name the place. Leave weather off and skip that button to keep everything on-device.
            </div>
          </div>

          {/* No cookies */}
          <div style={{ marginTop: 10, padding: "10px 12px", borderRadius: 9, background: "rgba(181,224,38,0.05)", border: "1px solid rgba(181,224,38,0.18)" }}>
            <div style={{ fontSize: 11.5, color: "var(--active)", fontWeight: 700, marginBottom: 4 }}>🍪 No cookies</div>
            <div style={{ fontSize: 11, color: "#a9bdaa", lineHeight: 1.55 }}>
              YaadGrow does not use cookies. There are no tracking, advertising, or analytics cookies, and no third-party trackers. Your settings and logs are kept in your browser's local storage on this device — never in a cookie and never sent to us. Because nothing personal is collected via cookies, no cookie-consent banner is needed.
            </div>
          </div>
        </SettingsSection>
      </>)}

      {/* ═══════ AD SLOT (placeholder) ═══════ */}
      {/* Drop your ad network's embed inside this box. NOTE: real ad scripts /
          iframes will require loosening the strict CSP in public/_headers
          (script-src / frame-src / img-src / connect-src for the ad domains),
          and ads conflict with the app's device-local privacy stance — see the
          privacy note in Settings. */}
      <div aria-label="Advertisement" style={{
        marginTop: 24, minHeight: 90,
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4,
        border: "1px dashed rgba(255,255,255,0.14)", borderRadius: 12,
        background: "rgba(255,255,255,0.02)",
      }}>
        <div style={{ fontSize: 9, letterSpacing: 2, textTransform: "uppercase", color: "var(--text-soft)", fontWeight: 700 }}>Advertisement</div>
        <div style={{ fontSize: 11, color: "#5f7d5f" }}>Ad space</div>
      </div>

      {/* ═══════ FOOTER ═══════ */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
        marginTop: 24, paddingTop: 16, borderTop: "1px solid rgba(255,255,255,0.06)",
        fontSize: 11, color: "var(--text-soft)", flexWrap: "wrap",
      }}>
        <Icon name="leaf" size={13} color="#5cb85c" />
        <span>YaadGrow · Hydroponic Farm Management System</span>
        <span style={{ color: "#2d4030" }}>·</span>
        <a href="https://systemsrubixjm.com/" target="_blank" rel="noopener noreferrer"
          style={{ color: "var(--text-soft)", textDecoration: "none" }}>
          Systems Rubix
        </a>
        <span style={{ color: "#2d4030" }}>·</span>
        {/* Static documents under public/, not app routes — plain links, so they
            navigate out of the SPA. The PWA navigate-fallback denylist in
            vite.config.js is what keeps the service worker from serving the app
            shell here instead. */}
        <a href="/privacy" style={{ color: "var(--text-soft)", textDecoration: "none" }}>
          Privacy
        </a>
        <span style={{ color: "#2d4030" }}>·</span>
        <a href="/terms" style={{ color: "var(--text-soft)", textDecoration: "none" }}>
          Terms
        </a>
      </div>

      {/* ═══════ TOAST ═══════ */}
      {toast && (
        <div
          key={toast.id}
          className="yg-toast"
          role="status"
          aria-live="polite"
          style={{
            position: "fixed", left: "50%", bottom: 24, transform: "translateX(-50%)",
            zIndex: 1000, maxWidth: "92vw",
            display: "flex", alignItems: "center", gap: 10,
            background: "linear-gradient(180deg, #12301a 0%, var(--bg-card) 100%)",
            border: `1px solid ${toast.tone === "error" ? "rgba(239,83,80,0.5)" : "rgba(90,185,90,0.5)"}`,
            borderRadius: 12, padding: "11px 16px",
            boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
            animation: "fadeIn 0.22s ease both",
          }}>
          <span style={{ color: toast.tone === "error" ? "#ef5350" : "#78e05a", display: "flex", flexShrink: 0 }}>
            <Icon name={toast.tone === "error" ? "warning" : "leaf"} size={16} />
          </span>
          <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-main)" }}>{toast.message}</span>
        </div>
      )}
    </div>
  );
}
